import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
// Pagina a parte da Recuperacao de orcamentos (21/10/2026), aberta pelo botao da aba Orcamentos.
import Recuperacao from './Recuperacao';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// A ordem aqui manda em tres lugares de uma vez: as colunas do Kanban, as barras
// do funil do Dashboard e os menus de status. Por isso `carteira` entra depois de
// `ganho` e antes de `perdido` - agrupa os desfechos positivos e deixa o perdido
// no fim.
//
// `carteira` existe desde 14/09/2026 porque faltava onde marcar "esse ja e nosso
// cliente". Sem esse lugar, o time usava `ganho`: 20 dos 29 ganhos estavam assim,
// sem valor nenhum, inflando a taxa de conversao e derrubando o ticket medio.
// Nenhum contador precisou mudar - todos contam `status === 'ganho'`, e a
// carteira simplesmente nao entra.
const STATUS_OPTIONS = [
  { value: 'novo', label: 'Novo' },
  { value: 'contato', label: 'Em contato' },
  { value: 'proposta', label: 'Proposta enviada' },
  { value: 'negociacao', label: 'Negociação' },
  { value: 'ganho', label: 'Ganho' },
  { value: 'carteira', label: 'Carteira' },
  { value: 'perdido', label: 'Perdido' },
];

const LEAD_TEMPERATURE_OPTIONS = [
  { value: 'frio', label: 'Frio' },
  { value: 'morno', label: 'Morno' },
  { value: 'quente', label: 'Quente' },
];

const SLA_STATUS_OPTIONS = [
  { value: 'normal', label: 'No prazo' },
  { value: 'warning', label: 'Atenção' },
  { value: 'overdue', label: 'Estourado' },
];

const SLA_TARGET_LABELS = {
  frio: '24h',
  morno: '4h',
  quente: '30 min',
};

// Orçamento que entrou por representante: o time marca o campo "Orçamentista"
// com este valor fixo. Assim dá para contar depois quantos orçamentos vieram
// por representante. Os ids dos usuários são numéricos ("1".."6"), então esta
// sentinela de texto não colide com ninguém.
const ESTIMATOR_REPRESENTANTE_ID = 'representante';
const ESTIMATOR_REPRESENTANTE_LABEL = 'Representante';

const BUDGET_STATUS_OPTIONS = [
  { value: 'novo', label: 'Novo' },
  { value: 'em_orcamento', label: 'Em orçamento' },
  { value: 'enviado', label: 'Enviado' },
  { value: 'aprovado', label: 'Aprovado' },
  { value: 'reprovado', label: 'Reprovado' },
  { value: 'nao_feito', label: 'Não feito' },
];

const BUDGET_LOSS_REASON_OPTIONS = [
  { value: 'preco', label: 'Preço' },
  { value: 'prazo', label: 'Prazo' },
  { value: 'sem_retorno', label: 'Sem retorno' },
  { value: 'concorrente', label: 'Concorrente' },
  { value: 'escopo', label: 'Escopo' },
  { value: 'cliente_desistiu', label: 'Cliente desistiu' },
  { value: 'sem_viabilidade', label: 'Sem viabilidade' },
  { value: 'sem_material', label: 'Sem material' },
  { value: 'outros', label: 'Outros' },
];

const EMAIL_EVENT_OPTIONS = [
  { value: 'all', label: 'Todos os eventos' },
  { value: 'open', label: 'Aberturas' },
  { value: 'click', label: 'Cliques' },
  { value: 'unsubscribe', label: 'Descadastros' },
];


// Formata telefone para (DD) 99999-9999 ou (DD) 9999-9999
const formatPhone = (value) => {
  const digits = (value || '').replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

const toDateInput = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

const toDateTimeLocalInput = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const offsetMs = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - offsetMs).toISOString().slice(0, 16);
};

// Hora local padrao para leads antigos que so tem data, sem horario.
const FOLLOWUP_DEFAULT_HOUR_LOCAL = 9;

// next_contact pode ser 'YYYY-MM-DD' (legado) ou ISO completo em UTC.
const resolveFollowupDate = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) {
    return new Date(
      Number(dateOnly[1]),
      Number(dateOnly[2]) - 1,
      Number(dateOnly[3]),
      FOLLOWUP_DEFAULT_HOUR_LOCAL,
      0,
      0
    );
  }
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

// Valor para <input type="datetime-local"> (horario local do navegador).
const followupToInput = (value) => {
  const date = resolveFollowupDate(value);
  if (!date) return '';
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
};

// Sempre enviar ISO em UTC: o backend repassa ao OmniChat, que le como UTC.
const followupToIso = (value) => {
  const date = resolveFollowupDate(value);
  return date ? date.toISOString() : '';
};

const formatFollowupBR = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '-';
  // Lead antigo, salvo so com data: nao inventar horario na tela. O lembrete
  // dele continua com o agendamento antigo no OmniChat; exibir uma hora aqui
  // seria mostrar algo que nao vai acontecer.
  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) return `${dateOnly[3]}/${dateOnly[2]}/${dateOnly[1]}`;
  const date = resolveFollowupDate(raw);
  if (!date) return '-';
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDateBR = (value) => {
  if (!value) return '-';
  const dateStr = String(value).split('T')[0];
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  try {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) {
      const dd = String(d.getUTCDate()).padStart(2, '0');
      const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
      const yyyy = d.getUTCFullYear();
      return `${dd}/${mm}/${yyyy}`;
    }
  } catch (e) { }
  return value;
};

const formatDateTimeBR = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return formatDateBR(value);
  return date.toLocaleString('pt-BR');
};

// Rotulos do resultado de ligacao da Recuperacao (21/10/2026), para a lista de orcamentos.
const ROTULO_RESULTADO_LIGACAO = {
  nao_atendeu: 'Não atendeu',
  falou: 'Falou',
  pedir_retorno: 'Pedir retorno',
  recuperado: 'Recuperado',
  sem_interesse: 'Sem interesse',
};
const formatCurrencyBR = (value) =>
  `R$ ${Number(value || 0).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatRatio = (value) =>
  Number.isFinite(Number(value)) && Number(value) > 0
    ? `${Number(value).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}x`
    : '-';

const formatCurrencyInputBR = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return '';
  const numeric = Number(digits) / 100;
  return numeric.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const parseCurrencyInputBR = (value) => {
  const normalized = String(value || '')
    .replace(/\./g, '')
    .replace(',', '.')
    .replace(/[^\d.-]/g, '');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

// NUMERO ja gravado -> texto do campo. Existe porque `formatCurrencyInputBR`
// NAO serve para isso, e a diferenca e silenciosa e cara:
//
//   formatCurrencyInputBR(4835)    -> "48,35"      (errado, 100x menor)
//   formatCurrencyInputBR(4835.5)  -> "483,55"     (errado)
//   formatCurrencyInputBR(4835.25) -> "4.835,25"   (certo por acaso)
//
// Ela le os DIGITOS do que recebe, o que so bate quando o numero ja tem duas
// casas decimais. Serve para digitacao, nunca para carregar valor ja gravado.
// Medido em 23/09/2026.
const numeroParaTextoBR = (valor) => {
  const n = Number(valor);
  if (!Number.isFinite(n) || n === 0) return '';
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const deriveMediaChannelLabel = (value) => {
  const normalized = normalizeOptionValue(value);
  if (!normalized) return '';
  if (
    normalized.includes('meta') ||
    normalized.includes('facebook') ||
    normalized.includes('instagram')
  ) return 'Meta Ads';
  if (normalized.includes('google') || normalized.includes('youtube')) return 'Google Ads';
  if (normalized.includes('linkedin')) return 'LinkedIn Ads';
  if (normalized.includes('tiktok')) return 'TikTok Ads';
  if (normalized.includes('whatsapp')) return 'WhatsApp';
  if (normalized.includes('landing')) return 'Landing Page';
  if (normalized.includes('manychat')) return 'Manychat';
  if (normalized.includes('organico') || normalized.includes('organic')) return 'Orgânico';
  if (normalized.includes('indicacao') || normalized.includes('refer')) return 'Indicação';
  return String(value || '').trim();
};

const normalizeSpreadsheetHeader = (value) =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const excelDateToIso = (value) => {
  if (!value && value !== 0) return '';
  if (typeof value === 'number') {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (!parsed) return '';
    const date = new Date(Date.UTC(parsed.y, parsed.m - 1, parsed.d));
    return date.toISOString();
  }
  const text = String(value).trim();
  if (!text) return '';
  const brMatch = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (brMatch) {
    const [, dd, mm, yyyy] = brMatch;
    return new Date(Date.UTC(Number(yyyy), Number(mm) - 1, Number(dd))).toISOString();
  }
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString();
};

// ===========================================================================
// Leitura da planilha de clientes do AM Gestor (23/09/2026)
//
// A planilha sai do ERP com uma linha de titulo antes do cabecalho, a coluna A
// vazia, e o cabecalho na linha 2: Codigo | Razao social | Fantasia | Telefone
// | Cidade | UF | Vendedor | Ultima compra | Ultimo contato | Status | Opcoes |
// e-mail | Contato.
//
// Medido no primeiro arquivo real (1.698 clientes, 23/09/2026):
//   - 1.414 linhas NAO tem o campo "Contato" — o nome da pessoa;
//   - 381 linhas trazem VARIOS e-mails na mesma celula, separados por ";";
//   - 24 linhas nao tem telefone; 66 nao tem e-mail; 4 nao tem nenhum dos dois.
//
// Decisoes dele, 23/09/2026:
//   - nome do cartao = Contato, e faltando ele, Fantasia, e faltando ela, a
//     Razao social. A Razao social vai sempre para o campo Empresa;
//   - so o PRIMEIRO e-mail vai para o campo (e o que o sistema usa para casar
//     lead e para o e-mail marketing); os outros ficam na observacao;
//   - cliente com Status diferente de "Ativo" entra igual, com o status do ERP
//     escrito na observacao, como aviso para quem for ligar.
//
// O codigo do cliente NAO e escrito aqui: quem monta essa linha e o servidor,
// justamente para que ela nao dependa da tela lembrar de mandar.
// ===========================================================================

const semAcento = (valor) =>
  String(valor || '').normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase().trim();

// Cada campo aceita mais de um nome de coluna porque o ERP muda o rotulo
// conforme o relatorio de onde a lista foi exportada.
const COLUNAS_CLIENTES_ERP = {
  codigo: ['codigo', 'cod', 'cod.', 'codigo cliente', 'cod cliente'],
  razao: ['razao social', 'razao', 'cliente', 'nome'],
  fantasia: ['fantasia', 'nome fantasia'],
  telefone: ['telefone', 'telefone1', 'telefone 1', 'fone', 'tel'],
  telefone2: ['telefone2', 'telefone 2', 'fone 2', 'celular'],
  cidade: ['cidade'],
  uf: ['uf', 'estado'],
  vendedor: ['vendedor', 'representante'],
  ultimaCompra: ['ultima compra', 'ult. compra', 'ult compra', 'data ultima compra'],
  ultimoContato: ['ultimo contato', 'ult. contato'],
  status: ['status', 'situacao'],
  email: ['e-mail', 'email', 'e mail'],
  contato: ['contato', 'nome do contato'],
};

const acharCabecalhoDeClientes = (linhas) => {
  const limite = Math.min((linhas || []).length, 20);
  for (let i = 0; i < limite; i += 1) {
    const linha = linhas[i];
    if (!Array.isArray(linha)) continue;
    const nomes = linha.map((c) => semAcento(normalizeSpreadsheetHeader(c)));
    const temRazao = nomes.some((n) => COLUNAS_CLIENTES_ERP.razao.includes(n));
    const temFantasia = nomes.some((n) => COLUNAS_CLIENTES_ERP.fantasia.includes(n));
    const temCodigo = nomes.some((n) => COLUNAS_CLIENTES_ERP.codigo.includes(n));
    if (temRazao || (temFantasia && temCodigo)) return { indice: i, nomes };
  }
  return null;
};

// Data: a celula pode vir como texto "02/05/2024" ou como numero de serie do
// Excel. A observacao mostra sempre no formato brasileiro, que e o que a pessoa
// que vai ligar entende.
const dataDaPlanilhaEmBR = (valor) => {
  if (valor === '' || valor === null || valor === undefined) return '';
  const texto = String(valor).trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(texto)) return texto;
  const iso = excelDateToIso(valor);
  if (!iso) return texto;
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return texto;
  const dd = String(data.getUTCDate()).padStart(2, '0');
  const mm = String(data.getUTCMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${data.getUTCFullYear()}`;
};

const separarEmails = (celula) =>
  String(celula || '')
    .split(/[;,\s]+/)
    .map((endereco) => endereco.trim())
    .filter((endereco) => endereco.includes('@'));

// Telefone que nao chega a 8 digitos nao serve para ligar nem para casar lead.
// Medido na lista de 23/09/2026: 24 celulas com "-----", "-", "X", "XXXXXX" ou
// texto solto, e mais 6 com 1 a 7 digitos ("000000", "11", "11-2081").
//
// Elas NAO entram no campo Telefone — se entrassem, o cartao mostraria "-----"
// como se fosse contato e o aviso "Falta telefone" nunca apareceria, que e
// justamente o contrario do que esta rotina existe para fazer. O que estava
// escrito na celula vai para a observacao: nada se perde, nada se inventa.
const TELEFONE_UTIL_MINIMO = 8;
const telefoneQueServe = (valor) =>
  String(valor || '').replace(/\D/g, '').length >= TELEFONE_UTIL_MINIMO ? String(valor).trim() : '';

const mapearPlanilhaDeClientes = (linhas) => {
  const cabecalho = acharCabecalhoDeClientes(linhas);
  if (!cabecalho) {
    throw new Error('Não achei o cabeçalho da planilha. Esperava uma coluna "Razão social".');
  }
  const coluna = (chave) => cabecalho.nomes.findIndex((n) => COLUNAS_CLIENTES_ERP[chave].includes(n));
  const c = {};
  Object.keys(COLUNAS_CLIENTES_ERP).forEach((chave) => { c[chave] = coluna(chave); });
  if (c.razao === -1 && c.fantasia === -1) {
    throw new Error('Não achei a coluna "Razão social" nem "Fantasia" na planilha.');
  }

  const pegar = (linha, i) => (i >= 0 ? String(linha[i] ?? '').trim() : '');
  const itens = [];
  const ignorados = [];

  (linhas || []).slice(cabecalho.indice + 1).forEach((linha, offset) => {
    if (!Array.isArray(linha)) return;
    const numeroDaLinha = cabecalho.indice + offset + 2;
    const codigo = pegar(linha, c.codigo);
    const razao = pegar(linha, c.razao);
    const fantasia = pegar(linha, c.fantasia);
    const contato = pegar(linha, c.contato);
    if (!razao && !fantasia && !contato && !codigo) return; // linha em branco
    if (!razao && !fantasia && !contato) {
      ignorados.push(`Linha ${numeroDaLinha}: só tem código, sem nome nem empresa`);
      return;
    }

    const celulaEmail = pegar(linha, c.email);
    const emails = separarEmails(celulaEmail);
    const celulaTelefone = pegar(linha, c.telefone);
    const celulaTelefone2 = pegar(linha, c.telefone2);
    const telefone = telefoneQueServe(celulaTelefone);
    const telefone2 = telefoneQueServe(celulaTelefone2);
    const cidade = pegar(linha, c.cidade);
    const uf = pegar(linha, c.uf);
    const vendedor = pegar(linha, c.vendedor);
    const ultimaCompra = dataDaPlanilhaEmBR(c.ultimaCompra >= 0 ? linha[c.ultimaCompra] : '');
    const ultimoContato = dataDaPlanilhaEmBR(c.ultimoContato >= 0 ? linha[c.ultimoContato] : '');
    const statusErp = pegar(linha, c.status);

    const observacao = [
      ultimaCompra ? `Última compra: ${ultimaCompra}` : '',
      ultimoContato ? `Último contato: ${ultimoContato}` : '',
      cidade || uf ? `Cidade: ${[cidade, uf].filter(Boolean).join('/')}` : '',
      vendedor ? `Vendedor ERP: ${vendedor}` : '',
      // Status so aparece quando NAO e "Ativo": escrever "Status ERP: Ativo" em
      // 1.695 de 1.698 cartoes seria ruido que ninguem le.
      statusErp && semAcento(statusErp) !== 'ativo' ? `Status ERP: ${statusErp}` : '',
      emails.length > 1 ? `Outros e-mails: ${emails.slice(1).join('; ')}` : '',
      // O que a celula trazia e nao serviu como telefone ou e-mail fica
      // registrado: as vezes e o nome de uma pessoa digitado na coluna errada.
      celulaTelefone && !telefone ? `Na lista, coluna Telefone: ${celulaTelefone}` : '',
      celulaTelefone2 && !telefone2 ? `Na lista, coluna Telefone 2: ${celulaTelefone2}` : '',
      celulaEmail && !emails.length ? `Na lista, coluna e-mail: ${celulaEmail}` : '',
    ].filter(Boolean).join('\n');

    itens.push({
      linha: numeroDaLinha,
      codigo,
      nome: contato || fantasia || razao,
      empresa: razao || fantasia,
      email: emails[0] || '',
      telefone,
      telefone2,
      segmento: '',
      observacao,
    });
  });

  return { itens, ignorados, linhaDoCabecalho: cabecalho.indice + 1 };
};

// A coluna "Lib" do ERP carrega DUAS coisas diferentes no mesmo lugar:
//   1. um motivo de perda por extenso, ou
//   2. "situacao + etapa" grudadas: "Pendente Prospeccao", "Perdido
//      Fechamento", "Conluido Fechamento".
//
// Valores reais, medidos na base em 08/09/2026 sobre 1.158 orcamentos:
//   Pendente Prospeccao 1.137 | Perdido Prospeccao 10 | Pendente Qualificacao 6
//   Perdido Fechamento 2 | Conluido Fechamento 2 | Perdido Negociacao 1
//
// Ate 09/09/2026 este mapeamento entendia UMA combinacao — pendente mais
// prospeccao — e jogava todo o resto em "novo". Consequencia medida: os 11
// orcamentos que o ERP da como PERDIDOS e os 2 que ele da como CONCLUIDOS
// estavam no CRM como "Novo". O CRM nunca enxergava uma venda vinda do ERP,
// e isso alimentava o "0 ganhos" que aparecia no Dashboard.
//
// Agora as duas partes sao lidas separadamente, entao qualquer combinacao
// funciona, inclusive as que ainda nao apareceram na base.
//
// "Conluido", sem o segundo C, e erro de digitacao do proprio ERP. As duas
// grafias sao aceitas DE PROPOSITO: no dia em que o ERP corrigir, ninguem vai
// precisar lembrar de voltar aqui — e o erro seria silencioso justamente nos
// ganhos, que sao o dado mais caro de perder.
const BUDGET_SITUACOES = [
  { chaves: ['conluido', 'concluido'], status: 'aprovado' },
  { chaves: ['perdido'], status: 'reprovado' },
  { chaves: ['pendente'], status: 'em_orcamento' },
];
const BUDGET_ETAPAS = ['prospeccao', 'qualificacao', 'negociacao', 'fechamento'];

const mapBudgetImportStatus = (rawLib) => {
  // O texto limpo serve so para INTERPRETAR. O que vai gravado em raw_status
  // continua sendo o valor original, com &nbsp; e tudo: a importacao no
  // backend compara o raw que chega com o raw ja gravado para decidir se o
  // ERP tem novidade, e limpar o valor aqui faria 84 registros da base
  // parecerem alterados de uma vez, sobrescrevendo status marcados a mao.
  const limpo = String(rawLib || '').replace(/&nbsp;/gi, ' ').replace(/\s+/g, ' ').trim();
  const normalized = normalizeOptionValue(limpo);
  const original = String(rawLib || '').trim();
  const lossMap = {
    'cliente nao retornou os contatos': 'sem_retorno',
    'cliente desistiu da compra': 'cliente_desistiu',
    'cliente possui restricoes no mercado': 'sem_viabilidade',
    'precos maiores que a concorrencia': 'concorrente',
    'os produtos nao preenchem os requisitos do cliente': 'escopo',
    'as formas de pagamento oferecidas nao suprem a necessidade do cliente': 'outros',
  };

  if (!normalized) {
    return { stage: '', status: 'novo', loss_reason: '', raw_status: '', raw_loss_reason: '' };
  }

  if (lossMap[normalized]) {
    return {
      stage: '',
      status: 'reprovado',
      loss_reason: lossMap[normalized],
      raw_status: '',
      raw_loss_reason: original,
    };
  }

  const etapa = BUDGET_ETAPAS.find((item) => normalized.includes(item)) || '';
  const situacao = BUDGET_SITUACOES.find((item) => item.chaves.some((chave) => normalized.includes(chave)));

  if (situacao) {
    return {
      stage: etapa,
      status: situacao.status,
      loss_reason: '',
      raw_status: original,
      raw_loss_reason: '',
    };
  }

  // Situacao que o CRM nao conhece. Nao inventa: cai em "novo", como antes,
  // mas a etapa e o valor cru ficam gravados para alguem poder decidir.
  return {
    stage: etapa,
    status: 'novo',
    loss_reason: '',
    raw_status: original,
    raw_loss_reason: '',
  };
};

const parseLeadDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

// Data de CALENDARIO (09/10/2026). Vale tambem (09/10, varredura) para o
// proximo contato e o primeiro contato do lead gravados so com o dia
// ("2026-02-11"; medido: 1.076 e 1.488) e para a data do gasto de anuncio (22).
// As datas do orcamento (solicitado, enviado,
// fechado) sao so dia, mas ficam gravadas como meia-noite UTC
// ("2026-10-06T00:00:00.000Z"; medido: as 1.623 assim). O new Date() leva isso
// para o horario de Brasilia e o dia vira 05/10 as 21h: o filtro de 05/10
// mostrava os de 06/10 e escondia os de 05/10, e os do dia 1o caiam no mes
// anterior nos graficos. Aqui o dia e lido como esta escrito, meia-noite local.
// Data com hora de verdade (created_at, updated_at) segue o caminho normal.
const dataDeCalendario = (value) => {
  const m = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.0+)?Z)?$/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return parseLeadDate(value);
};

// Filtro de periodo da tela de Leads, por MOVIMENTACAO — 23/09/2026.
//
// Decisao dele: o periodo aqui olha "quando o lead foi trabalhado pela ultima
// vez", nao quando ele entrou. A pergunta que a tela responde e "onde esta a
// grana que esta viva", e proposta de marco que ainda anda tem que aparecer.
//
// A ordem das datas e proposital: interacao REGISTRADA vale mais que "o
// registro mudou", e a data de entrada e o ultimo recurso, para o lead que
// nunca foi tocado depois de criado.
//
// `last_interaction_at` e recalculado na leitura, a partir da aba de
// interacoes — nao e campo digitado por ninguem.
const dataDeMovimentacaoDoLead = (lead) =>
  parseLeadDate(lead?.last_interaction_at) ||
  parseLeadDate(lead?.updated_at) ||
  parseLeadDate(lead?.created_at) ||
  null;

const PERIODO_LEAD_OPTIONS = [
  { value: 'all', label: 'Período: tudo' },
  { value: '7d', label: 'Últimos 7 dias' },
  { value: '30d', label: 'Últimos 30 dias' },
  { value: 'mes', label: 'Este mês' },
  { value: 'mes_passado', label: 'Mês passado' },
  { value: '90d', label: 'Últimos 90 dias' },
  { value: 'custom', label: 'Escolher datas' },
];

// Devolve a faixa de DIAS (sem hora) que o periodo cobre, ou null quando o
// periodo nao filtra nada. `ate: null` significa "ate hoje, inclusive".
const faixaDoPeriodoDeLeads = (periodo, inicioTexto, fimTexto, agora = new Date()) => {
  const hoje = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate());
  const diasAtras = (n) => new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - n);
  switch (periodo) {
    // "Ultimos 7 dias" inclui hoje: sao 7 dias, nao 8.
    case '7d': return { de: diasAtras(6), ate: null };
    case '30d': return { de: diasAtras(29), ate: null };
    case '90d': return { de: diasAtras(89), ate: null };
    case 'mes': return { de: new Date(agora.getFullYear(), agora.getMonth(), 1), ate: null };
    // Dia 0 do mes atual e o ULTIMO dia do mes passado — vale para fevereiro,
    // para mes de 31 e para a virada de ano.
    case 'mes_passado': return {
      de: new Date(agora.getFullYear(), agora.getMonth() - 1, 1),
      ate: new Date(agora.getFullYear(), agora.getMonth(), 0),
    };
    case 'custom': return {
      de: inicioTexto ? new Date(`${inicioTexto}T00:00:00`) : null,
      ate: fimTexto ? new Date(`${fimTexto}T00:00:00`) : null,
    };
    default: return null;
  }
};

const leadDentroDoPeriodo = (lead, periodo, inicioTexto, fimTexto, agora = new Date()) => {
  const faixa = faixaDoPeriodoDeLeads(periodo, inicioTexto, fimTexto, agora);
  if (!faixa) return true;
  // "Escolher datas" sem nenhuma data ainda nao pode esvaziar a tela.
  if (!faixa.de && !faixa.ate) return true;
  const movimento = dataDeMovimentacaoDoLead(lead);
  if (!movimento) return false;
  const dia = new Date(movimento.getFullYear(), movimento.getMonth(), movimento.getDate()).getTime();
  if (faixa.de && dia < faixa.de.getTime()) return false;
  if (faixa.ate && dia > faixa.ate.getTime()) return false;
  return true;
};

const monthKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
const monthLabel = (key) => {
  const [year, month] = String(key || '').split('-');
  if (!year || !month) return key;
  return new Date(Number(year), Number(month) - 1, 1).toLocaleDateString('pt-BR', {
    month: 'short',
    year: '2-digit',
  });
};

// "abr. de 26" e o que o toLocaleDateString devolve em pt-BR, e e longo demais
// para o eixo: com 6 meses num cartao estreito os rotulos se encavalam. No eixo
// vira "abr/26". Em tooltip e tabela o rotulo completo continua.
const rotuloCurtoDeEixo = (texto) =>
  String(texto || '').replace(/\.?\s*de\s*/i, '/').replace(/\.$/, '');

const buildStatsSummary = (items) => {
  const validItems = items.filter((lead) => normalizeOptionValue(lead?.segment) !== 'concorrente');
  const negotiationStatuses = ['negociacao', 'proposta'];
  const acc = {
    total: validItems.length,
    novos: 0,
    emContato: 0,
    ganhos: 0,
    // Ganho com valor zero e comum aqui: medido na producao em 14/09/2026,
    // 22 dos 29 ganhos estavam sem valor. Contar os dois separadamente e o que
    // permite dizer na tela sobre quantos registros a soma foi feita.
    ganhosComValor: 0,
    perdidos: 0,
    valorTotal: 0,
    valorPerdido: 0,
    qtdNegociacao: 0,
    valorNegociacao: 0,
  };

  validItems.forEach((lead) => {
    const status = (lead.status || '').toLowerCase();
    if (status === 'novo') acc.novos += 1;
    if (status === 'contato') acc.emContato += 1;
    if (status === 'ganho') {
      acc.ganhos += 1;
      const valorDoGanho = Number(lead.value || 0);
      acc.valorTotal += valorDoGanho;
      if (valorDoGanho > 0) acc.ganhosComValor += 1;
    }
    if (status === 'perdido') {
      acc.perdidos += 1;
      acc.valorPerdido += Number(lead.value || 0);
    }
    if (negotiationStatuses.includes(status)) {
      acc.qtdNegociacao += 1;
      acc.valorNegociacao += Number(lead.value || 0);
    }
  });

  // Uma casa decimal (19/10/2026). Arredondado para inteiro, 7 ganhos em 4.086
  // leads (0,17%) aparecia como "0%" ao lado de "R$ 10.846,63 convertidos".
  acc.taxaConversao = acc.total ? Math.round((acc.ganhos / acc.total) * 1000) / 10 : 0;
  return acc;
};

// Mesma regra do servidor (podeApagarOrcamento, 06/10/2026): admin, dono,
// orcamentista (id ou nome) ou orcamento sem dono. Na tela, o owner_id ja vem
// preenchido com o estimator_id quando o dono esta vazio (hydrateBudgets).
const podeApagarOrcamentoNaTela = (budget, user, ehAdmin) => {
  if (!user) return false;
  if (ehAdmin) return true;
  const norm = (v) => String(v || '').normalize('NFD').replace(/\p{Diacritic}/gu, '').trim().toLowerCase();
  const meuId = String(user.id || '').trim();
  const dono = String(budget?.owner_id || '').trim();
  if (dono && dono === meuId) return true;
  if (String(budget?.estimator_id || '').trim() && String(budget.estimator_id).trim() === meuId) return true;
  const orcamentista = norm(budget?.estimator_name);
  if (orcamentista && [user.name, user.username].filter(Boolean).map(norm).includes(orcamentista)) return true;
  if (!dono && user.role !== 'representante') return true;
  return false;
};

// Rotulo curto de um link clicado em campanha (19/10/2026): ".../produto/contator-blc1"
// vira "contator-blc1"; a home vira "bhseletrica.com.br"; WhatsApp vira "WhatsApp".
const rotuloDoLink = (url) => {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');
    if (/(^|\.)wa\.me$|(^|\.)wa\.link$|whatsapp/.test(host)) return 'WhatsApp';
    const partes = u.pathname.split('/').filter(Boolean);
    return partes.length ? decodeURIComponent(partes[partes.length - 1]) : host;
  } catch {
    return String(url || '').slice(0, 60);
  }
};

// "61,4%": uma casa decimal, com virgula. Sem base (nenhum decidido) mostra traco.
const formatarPercentualBR = (valor) =>
  Number.isFinite(valor)
    ? `${valor.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
    : '—';

const buildBudgetStatsSummary = (items) => {
  const acc = {
    total: items.length,
    enviados: 0,
    aprovados: 0,
    reprovados: 0,
    naoFeitos: 0,
    valorOrcado: 0,
    valorFechado: 0,
    valorReprovado: 0,
    valorEnviado: 0,
    valorNaoFeito: 0,
  };

  items.forEach((item) => {
    const status = normalizeOptionValue(item.status);
    if (status === 'enviado') {
      acc.enviados += 1;
      acc.valorEnviado += Number(item.budget_value || 0);
    }
    if (status === 'aprovado') {
      acc.aprovados += 1;
      acc.valorFechado += Number(item.closed_value || 0);
    }
    if (status === 'reprovado') {
      acc.reprovados += 1;
      // Valor reprovado = valor ORCADO do que foi recusado. Em orcamento
      // reprovado o closed_value fica zerado (nada foi fechado), entao usar
      // closed_value aqui mostraria R$ 0,00 para sempre.
      acc.valorReprovado += Number(item.budget_value || 0);
    }
    if (status === 'nao_feito') {
      acc.naoFeitos += 1;
      acc.valorNaoFeito += Number(item.budget_value || 0);
    }
    acc.valorOrcado += Number(item.budget_value || 0);
  });

  acc.taxaAprovacao = acc.total ? Math.round((acc.aprovados / acc.total) * 100) : 0;
  acc.ticketMedio = acc.aprovados ? acc.valorFechado / acc.aprovados : 0;

  // Fechado x orcado SO entre os decididos (aprovado + reprovado + nao feito),
  // decisao do Leandro em 02/10/2026: os que ainda estao em aberto nao puxam a
  // taxa para baixo. Aprovado sem valor fechado conta pelo valor ORCADO e o
  // card avisa quantos sao (o numero do orcamento no CRM nao e o numero do
  // pedido no AM Gestor, entao nao da para buscar o valor real la).
  acc.decididos = acc.aprovados + acc.reprovados + acc.naoFeitos;
  acc.valorDecidido = 0;
  acc.valorFechadoDecididos = 0;
  acc.aprovadosSemValorFechado = 0;
  items.forEach((item) => {
    const status = normalizeOptionValue(item.status);
    if (!['aprovado', 'reprovado', 'nao_feito'].includes(status)) return;
    const orcado = Number(item.budget_value || 0);
    acc.valorDecidido += orcado;
    if (status !== 'aprovado') return;
    const fechado = Number(item.closed_value || 0);
    if (fechado > 0) {
      acc.valorFechadoDecididos += fechado;
    } else {
      acc.aprovadosSemValorFechado += 1;
      acc.valorFechadoDecididos += orcado;
    }
  });
  acc.taxaFechadoQtd = acc.decididos ? (acc.aprovados / acc.decididos) * 100 : null;
  acc.taxaFechadoValor = acc.valorDecidido ? (acc.valorFechadoDecididos / acc.valorDecidido) * 100 : null;
  return acc;
};

const emptyAdSpend = {
  date: '',
  channel_id: '',
  channel_name: '',
  platform: '',
  campaign: '',
  amount: '',
  notes: '',
};

const escapeXml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

const buildExcelWorkbook = (sheets) => {
  const workbookSheets = sheets
    .map((sheet) => {
      const rows = sheet.rows
        .map((row) => {
          const cells = row
            .map((cell) => {
              const type = typeof cell === 'number' && Number.isFinite(cell) ? 'Number' : 'String';
              return `<Cell><Data ss:Type="${type}">${escapeXml(cell)}</Data></Cell>`;
            })
            .join('');
          return `<Row>${cells}</Row>`;
        })
        .join('');

      return `<Worksheet ss:Name="${escapeXml(sheet.name)}"><Table>${rows}</Table></Worksheet>`;
    })
    .join('');

  return `<?xml version="1.0" encoding="UTF-8"?>
  <Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
    xmlns:o="urn:schemas-microsoft-com:office:office"
    xmlns:x="urn:schemas-microsoft-com:office:excel"
    xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
    xmlns:html="http://www.w3.org/TR/REC-html40">
    ${workbookSheets}
  </Workbook>`;
};

// ---------------------------------------------------------------------------
// Tokens de estilo — Deep Ocean Professional.
// Estas constantes concentram as classes mais repetidas da interface.
// Mudar a aparencia do sistema passa a ser editar aqui, em vez de cacar
// centenas de strings soltas no JSX.
// ---------------------------------------------------------------------------
const UI_CARD = 'bg-surface-card rounded-2xl shadow-card p-5 border border-line';
const UI_LABEL = 'block text-xs font-semibold text-ink-soft mb-1';
const UI_INPUT = 'w-full px-3 py-2 border border-line rounded-lg text-sm';
const UI_INPUT_BG = 'w-full px-3 py-2 border border-line rounded-lg text-sm bg-surface-card';
const UI_INPUT_PILL = 'px-3 py-2 border border-line rounded-xl text-sm bg-surface-card';
const UI_EYEBROW = 'text-xs uppercase tracking-[0.18em] text-ink-faint font-bold';
const UI_H2 = 'text-lg font-bold text-ink mb-4';
const UI_STRONG = 'font-semibold text-ink';
const UI_META = 'text-[11px] text-ink-soft';
const UI_STAT = 'mt-3 text-3xl font-bold text-ink';
const UI_TD = 'py-2 px-2';

// Ordenacao por clique no cabecalho.
//
// Ate 18/09/2026 a lista de Leads so ordenava por dois seletores separados —
// um de campo, outro de direcao — e o de direcao dizia "Mais novo / Mais
// antigo" mesmo quando o campo era Nome, o que nao quer dizer nada. A lista de
// Orcamentos nao ordenava: saia na ordem em que as linhas estavam na planilha.
//
// O tipo da coluna decide o texto da direcao e como o valor e comparado. Sem
// isso, nome ordena como se fosse data e data ordena como se fosse texto.
const SORT_TIPOS = {
  texto: { asc: 'A a Z', desc: 'Z a A' },
  data: { asc: 'Mais antigo', desc: 'Mais novo' },
  numero: { asc: 'Menor', desc: 'Maior' },
};

// De que tipo e cada coluna. E daqui que sai o texto da direcao: escolher
// "Nome" e ler "Mais novo" na direcao foi a queixa que originou esta mudanca.
const TIPO_POR_CAMPO_LEAD = {
  id: 'numero',
  created_at: 'data',
  next_contact: 'data',
  value: 'numero',
  name: 'texto',
  status: 'texto',
  channel_name: 'texto',
  region: 'texto',
  owner: 'texto',
  sla_status: 'texto',
};

// Quanto mostrar de uma coluna do Kanban, e o que escrever no botao.
//
// Existe como funcao com nome, e nao como conta solta dentro do JSX, para
// poder ser testada: o plural do rotulo e a fronteira do "exatamente cheio"
// sao os dois lugares onde esse tipo de conta erra.
const fatiaDoKanban = (total, visiveis) => {
  const mostrados = Math.max(0, Math.min(total, visiveis));
  const restam = Math.max(0, total - mostrados);
  return {
    mostrados,
    restam,
    temBotao: restam > 0,
    rotulo: restam > 0 ? `+ Ver mais (${restam} restante${restam > 1 ? 's' : ''})` : '',
  };
};

// Lista de orcamentos (22/10/2026): quantas linhas mostrar e o rotulo do
// botao, com o numero no formato brasileiro ("Ver mais (1.750 restantes)").
const ORCAMENTOS_POR_PAGINA = 20;
const fatiaDaListaOrcamentos = (total, visiveis) => {
  const mostrados = Math.max(0, Math.min(total, visiveis));
  const restam = Math.max(0, total - mostrados);
  return {
    mostrados,
    restam,
    temBotao: restam > 0,
    rotulo: restam > 0 ? `Ver mais (${restam.toLocaleString('pt-BR')} restante${restam > 1 ? 's' : ''})` : '',
  };
};

const valorParaOrdenar = (item, campo, tipo) => {
  const bruto = typeof campo === 'function' ? campo(item) : item[campo];
  if (tipo === 'numero') return Number(bruto || 0);
  if (tipo === 'data') {
    if (!bruto) return null; // sem data vai para o fim, nos dois sentidos
    const d = new Date(bruto);
    return Number.isNaN(d.getTime()) ? null : d.getTime();
  }
  return String(bruto ?? '').trim().toLowerCase();
};

// `null` (data ausente ou valor vazio) SEMPRE vai para o fim, independente da
// direcao. Um lead sem proximo contato marcado nao e "o mais urgente" so
// porque a lista foi invertida.
const compararParaOrdenar = (a, b, campo, tipo, dir) => {
  const va = valorParaOrdenar(a, campo, tipo);
  const vb = valorParaOrdenar(b, campo, tipo);
  const vazioA = va === null || va === '';
  const vazioB = vb === null || vb === '';
  if (vazioA && vazioB) return 0;
  if (vazioA) return 1;
  if (vazioB) return -1;
  const sinal = dir === 'asc' ? 1 : -1;
  if (tipo === 'texto') return va.localeCompare(vb, 'pt-BR') * sinal;
  if (va < vb) return -1 * sinal;
  if (va > vb) return 1 * sinal;
  return 0;
};

// Cabecalho clicavel. A setinha so aparece na coluna que esta ordenando, para
// nao poluir a tabela com sete setas apagadas.
const ThOrdenavel = ({ chave, tipo = 'texto', ordemAtual, direcao, aoOrdenar, className = '', children }) => {
  const ativo = ordemAtual === chave;
  const proxima = ativo && direcao === 'asc' ? 'desc' : 'asc';
  const rotulo = SORT_TIPOS[tipo] || SORT_TIPOS.texto;
  return (
    <th className={className}>
      <button
        type="button"
        onClick={() => aoOrdenar(chave, proxima)}
        title={`Ordenar: ${rotulo[proxima]}`}
        className="inline-flex items-center gap-1 font-inherit hover:text-ink transition-colors"
      >
        <span>{children}</span>
        <span aria-hidden="true" className={ativo ? 'opacity-100' : 'opacity-0'}>
          {direcao === 'asc' ? '\u25b2' : '\u25bc'}
        </span>
      </button>
    </th>
  );
};
const UI_GRID2 = 'grid grid-cols-1 md:grid-cols-2 gap-3';

// Deep Ocean: card branco com borda fina, como manda o DESIGN.md.
// O codigo de cor sai do fundo e vai para o rotulo, que continua
// distinguindo os grupos de metrica sem transformar a tela num vitral.
// Os 5 tons foram conferidos em contraste sobre branco (pior caso 4,67:1,
// acima do minimo 4,5 para texto pequeno).
// O rotulo era pintado por um `tone` com hexadecimal cravado. Media contra o
// cartao escuro #1a1f25: verde 2,53:1, vermelho 2,57:1, ambar 3,55:1 — todos
// abaixo do minimo de 4,5 para texto. E, mesmo no claro, aquela cor era
// decoracao: verde, ambar e vermelho nao queriam dizer bom, atencao e ruim,
// eram so variacao. Agora o rotulo e neutro sempre. `alerta` e a unica
// excecao: pinta o cartao e poe tarja lateral, o mesmo recurso da tela de
// Leads, e vale para UM cartao por painel — o que exige acao hoje.
const StatCard = ({ label, value, helper, tone = 'slate', alerta = false }) => (
  <div
    className={`rounded-2xl bg-surface-card border border-line p-4 shadow-card ${
      alerta ? 'border-l-[3px] border-l-risk-ink' : ''
    }`}
  >
    <p className={`text-[11px] uppercase tracking-[0.18em] font-bold ${alerta ? 'text-risk-ink' : 'text-ink-faint'}`}>
      {label}
    </p>
    <p className={`mt-3 text-3xl font-bold ${alerta ? 'text-risk-ink' : 'text-ink'}`}>{value}</p>
    {helper && <p className="mt-2 text-xs text-ink-faint">{helper}</p>}
  </div>
);

// Paleta dos graficos, por FUNCAO do dado e nao por decoracao.
// Os quatro tons passaram no validador de paleta (faixa de luminosidade,
// piso de croma, separacao para daltonismo protan/deutan/tritan e contraste
// contra a superficie do card). Nao trocar por gosto sem revalidar.
// Os valores agora vivem em index.css, um conjunto por tema, porque os tons
// claros REPROVAM contra o fundo escuro: o azul #006194 dava 2,48:1 e o
// vermelho #a4262c dava 2,28:1 contra o cartao #1a1f25, sendo 3:1 o minimo
// para uma marca de grafico. Usando var(), a troca de tema leva os graficos
// junto sem nenhuma mudanca aqui.
const CHART_COLORS = {
  volume: 'var(--c-chart-volume)',    // contagem, quantidade, neutro
  positivo: 'var(--c-chart-positivo)',// resultado concretizado: ganho, fechado, aprovado
  risco: 'var(--c-chart-risco)',      // perda e risco: SLA estourado, perdido, reprovado
  atencao: 'var(--c-chart-atencao)',  // em andamento e custo: pipeline, investimento
};

// Alterna entre o grafico e a mesma informacao em texto. Existe por dois
// motivos: acessibilidade (quem nao distingue as cores, ou usa leitor de
// tela, chega ao valor) e uso real (o comercial copia para planilha).
const ChartTableToggle = ({ aberto, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    className="mt-2 text-[11px] font-semibold text-brand-ink hover:underline"
  >
    {aberto ? 'ver como gráfico' : 'ver como tabela'}
  </button>
);

const ChartDataTable = ({ linhas, formatValue, rotuloCategoria = 'Item' }) => (
  <div className="mt-1 max-h-56 overflow-y-auto">
    <table className="w-full text-[11px]">
      <thead>
        <tr className="text-ink-faint">
          <th className="text-left font-semibold py-1 pr-2 border-b border-line">{rotuloCategoria}</th>
          <th className="text-right font-semibold py-1 border-b border-line">Valor</th>
        </tr>
      </thead>
      <tbody>
        {linhas.map((item) => (
          <tr key={item.label}>
            <td className="py-1 pr-2 text-ink-soft border-b border-surface-mid">{item.label}</td>
            <td className="py-1 text-right text-ink font-medium tabular-nums border-b border-surface-mid">
              {formatValue(item.value)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// `limit` corta a lista, mas o que sobra NAO some: vira uma barra "Outros"
// somada, e o rodape declara quantos itens existem no total. Antes o
// componente cortava em 6 silenciosamente, escondendo canais inteiros.
// `showZeros` mantem a categoria com valor zero visivel, necessario em
// conjuntos fixos como o funil de status.
const MiniBarChart = ({
  data,
  color = CHART_COLORS.volume,
  emptyLabel = 'Sem dados',
  formatValue = (value) => value,
  limit = 6,
  showZeros = false,
  aggregateRest = true,
}) => {
  const todos = (data || []).filter((item) => item && (showZeros || Number(item.value) > 0));
  if (!todos.length) {
    return <p className="text-sm text-ink-faint">{emptyLabel}</p>;
  }
  const visiveis = todos.slice(0, limit);
  const ocultos = todos.slice(limit);
  const somaOcultos = ocultos.reduce((total, item) => total + Number(item.value || 0), 0);
  const linhas = ocultos.length && aggregateRest
    ? [...visiveis, { label: `Outros (${ocultos.length})`, value: somaOcultos }]
    : visiveis;
  const max = Math.max(...linhas.map((item) => Number(item.value)), 1);
  return <MiniBarChartView
    linhas={linhas} max={max} color={color} formatValue={formatValue}
    rodape={ocultos.length > 0
      ? (aggregateRest
        ? `Os ${limit} maiores estão detalhados; os outros ${ocultos.length} estão somados em "Outros". Total de ${todos.length} itens.`
        : `Mostrando ${limit} de ${todos.length} itens.`)
      : ''}
  />;
};

const MiniBarChartView = ({ linhas, max, color, formatValue, rodape }) => {
  const [tabela, setTabela] = useState(false);
  if (tabela) {
    return (
      <div>
        <ChartDataTable linhas={linhas} formatValue={formatValue} />
        <ChartTableToggle aberto onToggle={() => setTabela(false)} />
      </div>
    );
  }
  return (
    <div>
      <div className="space-y-2">
        {linhas.map((item) => (
          <div
            key={item.label}
            className="grid grid-cols-[minmax(0,38%)_1fr_auto] items-center gap-2.5 text-xs group"
            title={`${item.label}: ${formatValue(item.value)}`}
          >
            <span className="text-ink-soft truncate">{item.label}</span>
            {/* Ponta esquerda RETA: ancora a barra na linha de base. Com
                rounded-full nas duas pontas, a borda impunha largura minima
                igual a altura e valores pequenos saiam todos do mesmo
                tamanho — 0,7% e 1,6% viravam a mesma barra. */}
            <span className="block h-2.5 bg-surface-low rounded-sm overflow-hidden group-hover:bg-surface-mid">
              <span
                className="block h-full rounded-r-sm"
                style={{
                  width: `${Math.max((Number(item.value) / max) * 100, Number(item.value) > 0 ? 0.6 : 0)}%`,
                  minWidth: Number(item.value) > 0 ? '2px' : 0,
                  backgroundColor: color,
                }}
              />
            </span>
            <span className="text-ink font-semibold tabular-nums text-right min-w-[42px]">
              {formatValue(item.value)}
            </span>
          </div>
        ))}
      </div>
      {rodape && <p className="mt-2 text-[11px] text-ink-faint">{rodape}</p>}
      <ChartTableToggle aberto={false} onToggle={() => setTabela(true)} />
    </div>
  );
};

// Legenda de serie. Nos cards com duas series empilhadas nao havia nada
// dizendo qual grafico era qual: a identidade ficava so na cor, que nem
// legenda tinha. O texto carrega o nome; a bolinha apenas reforca.
const ChartSeriesLabel = ({ color, text }) => (
  <div className="flex items-center gap-2 mb-2">
    <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
    <span className="text-xs font-semibold text-ink-soft">{text}</span>
  </div>
);

// A versao anterior desenhava num viewBox "0 0 100 100" — um quadrado — sem
// preserveAspectRatio. O navegador encaixava o quadrado na ALTURA e
// centralizava: medido em producao, o desenho ocupava 112px dentro de um svg
// de 355px, ou seja, 32% da largura disponivel. Agora o viewBox tem a
// proporcao do espaco real, preserveAspectRatio="none" estica de verdade, e
// vector-effect mantem o traco com espessura constante mesmo esticado.
const LINE_W = 320;
const LINE_H = 96;
const LINE_PAD = 6;

const MiniLineChart = ({ data, color = CHART_COLORS.volume, emptyLabel = 'Sem histórico suficiente', formatValue = (value) => value }) => {
  const [tabela, setTabela] = useState(false);
  const safeData = (data || []).slice(-8);
  if (!safeData.length) {
    return <p className="text-sm text-ink-faint">{emptyLabel}</p>;
  }
  const max = Math.max(...safeData.map((item) => Number(item.value || 0)), 1);
  const pontos = safeData.map((item, index) => {
    const x = LINE_PAD + (index / Math.max(safeData.length - 1, 1)) * (LINE_W - LINE_PAD * 2);
    const y = (LINE_H - LINE_PAD) - (Number(item.value || 0) / max) * (LINE_H - LINE_PAD * 2);
    return { x, y, label: item.label, value: Number(item.value || 0) };
  });
  const linha = pontos.map((pt) => `${pt.x},${pt.y}`).join(' ');
  const area = `M ${pontos[0].x},${LINE_H - LINE_PAD} ${pontos.map((pt) => `L ${pt.x},${pt.y}`).join(' ')} L ${pontos[pontos.length - 1].x},${LINE_H - LINE_PAD} Z`;
  // Rotulo direto so no pico e no ultimo ponto. Um numero em cada ponto vira
  // ruido e ninguem le; o resto fica no hover e na tabela.
  const idxPico = pontos.reduce((melhor, pt, i) => (pt.value > pontos[melhor].value ? i : melhor), 0);
  const idxFim = pontos.length - 1;

  if (tabela) {
    return (
      <div>
        <ChartDataTable linhas={safeData} formatValue={formatValue} rotuloCategoria="Período" />
        <ChartTableToggle aberto onToggle={() => setTabela(false)} />
      </div>
    );
  }

  return (
    <div>
      <svg
        viewBox={`0 0 ${LINE_W} ${LINE_H}`}
        preserveAspectRatio="none"
        className="w-full h-24 block"
        role="img"
        aria-label={`Evolução: ${pontos.map((pt) => `${pt.label} ${formatValue(pt.value)}`).join(', ')}`}
      >
        <line x1="0" y1={LINE_PAD} x2={LINE_W} y2={LINE_PAD} stroke="currentColor" strokeWidth="1"
          vectorEffect="non-scaling-stroke" className="text-line" />
        <line x1="0" y1={LINE_H - LINE_PAD} x2={LINE_W} y2={LINE_H - LINE_PAD} stroke="currentColor" strokeWidth="1"
          vectorEffect="non-scaling-stroke" className="text-line" />
        <path d={area} fill={color} opacity="0.12" />
        <polyline fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          vectorEffect="non-scaling-stroke" points={linha} />
        {[idxPico, idxFim].map((i) => (
          <circle key={`m-${i}`} cx={pontos[i].x} cy={pontos[i].y} r="3" fill={color}
            stroke="currentColor" strokeWidth="2" vectorEffect="non-scaling-stroke" className="text-surface-card" />
        ))}
        {pontos.map((pt, i) => (
          <rect key={`h-${pt.label}-${i}`} x={pt.x - (LINE_W / pontos.length) / 2} y="0"
            width={LINE_W / pontos.length} height={LINE_H} fill="transparent">
            <title>{`${pt.label}: ${formatValue(pt.value)}`}</title>
          </rect>
        ))}
      </svg>
      {/* Os rotulos ficavam numa fileira de celulas iguais (flex-1), mas o ponto
          do grafico nao mora no centro da celula: ele mora em
          LINE_PAD + i * (LINE_W - 2*LINE_PAD) / (n-1). Com 6 meses a diferenca
          chegava a 20 de 320 unidades nas pontas — visivel a olho nu, e sempre
          nos extremos. Agora cada rotulo e posicionado na coordenada real do
          seu ponto. Primeiro e ultimo sao encostados pela borda em vez de
          centrados, se nao metade do texto sairia do grafico. */}
      <div className="relative mt-1 h-4 text-[10px] text-ink-faint">
        {pontos.map((pt, i) => (
          <span
            key={`x-${pt.label}-${i}`}
            className="absolute top-0 overflow-hidden text-ellipsis whitespace-nowrap"
            style={{
              left: `${(pt.x / LINE_W) * 100}%`,
              // Teto de largura igual ao espaco entre dois pontos: posicionar
              // pela coordenada real resolve o alinhamento, mas sem teto os
              // rotulos das pontas passam por cima do vizinho quando o cartao
              // e estreito. Medido: com 6 meses num cartao de 355px, o vao e
              // de 68px e o rotulo curto ocupa cerca de 36px.
              maxWidth: `${(100 * (LINE_W - LINE_PAD * 2)) / (LINE_W * Math.max(pontos.length - 1, 1))}%`,
              transform:
                i === 0
                  ? 'translateX(0)'
                  : i === pontos.length - 1
                    ? 'translateX(-100%)'
                    : 'translateX(-50%)',
            }}
          >
            {rotuloCurtoDeEixo(pt.label)}
          </span>
        ))}
      </div>
      <p className="mt-1.5 text-[11px] text-ink-soft">
        Máximo {formatValue(pontos[idxPico].value)} em {pontos[idxPico].label}
        {idxFim !== idxPico ? ` · ${formatValue(pontos[idxFim].value)} em ${pontos[idxFim].label}` : ''}
      </p>
      <ChartTableToggle aberto={false} onToggle={() => setTabela(true)} />
    </div>
  );
};

// Minutos crus nao se leem: "1160 min atrasado" exige conta de cabeca.
// Acima de 60 vira hora, acima de 24h vira dia.
const formatSlaDuration = (minutes) => {
  const total = Math.abs(Math.round(Number(minutes) || 0));
  if (total < 60) return `${total} min`;
  if (total < 60 * 24) {
    const h = Math.floor(total / 60);
    const m = total % 60;
    return m ? `${h}h${String(m).padStart(2, '0')}` : `${h}h`;
  }
  const d = Math.floor(total / (60 * 24));
  const h = Math.floor((total % (60 * 24)) / 60);
  return h ? `${d}d ${h}h` : `${d}d`;
};

const THEME_OPTIONS = [
  { value: 'light', label: 'Claro', hint: 'Sempre claro' },
  { value: 'dark', label: 'Escuro', hint: 'Sempre escuro' },
  { value: 'auto', label: 'Automático', hint: 'Segue o tema do sistema operacional' },
];

const DASHBOARD_PANES_BASE = [
  { value: 'leads', label: 'Leads' },
  { value: 'orcamentos', label: 'Orçamentos' },
  { value: 'midia', label: 'Mídia paga' },
];

const NAV_TABS = [
  { value: 'crm', label: 'Leads' },
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'orcamentos', label: 'Orçamentos' },
  { value: 'emkt', label: 'E-mail mkt' },
];

// Aviso de contato faltando no cartao.
//
// Nasceu com a importacao da lista de clientes do AM Gestor (23/09/2026): a
// decisao foi que cliente sem telefone ENTRA — com aviso no cartao, nao com
// porta fechada. Mas o aviso vale para a base inteira: medido em 23/09/2026,
// 270 dos 2.254 leads ja estavam sem telefone nenhum, e ninguem via.
//
// E CALCULADO na leitura, olhando o campo vazio. Nao existe coluna nova na
// planilha para isto DE PROPOSITO — coluna nova obriga o `ensureHeaders` a
// reescrever a aba `Leads` inteira no proximo boot. Sendo calculado, o aviso
// some sozinho no instante em que alguem preenche o telefone.
//
// So telefone tem aviso. E-mail nao: 1.371 dos 2.254 leads estao sem e-mail
// (medido na mesma data), e um aviso que aparece em 61% dos cartoes deixa de
// ser aviso e vira ruido.
const LeadContatoFaltandoBadge = ({ lead }) => {
  // Conta DIGITO, nao "campo vazio". Telefone gravado como "-----" ou "X" nao
  // serve para ligar, e se o aviso olhasse so o vazio esses leads passariam
  // batidos — exatamente o caso que a lista do AM Gestor trouxe 24 vezes.
  const digitos = (valor) => String(valor || '').replace(/\D/g, '').length;
  const semTelefone = digitos(lead?.phone) < 8 && digitos(lead?.phone2) < 8;
  if (!semTelefone) return null;
  const semEmail = !String(lead?.email || '').trim();
  const texto = semEmail ? 'Falta telefone e e-mail' : 'Falta telefone';
  return (
    <span
      className="inline-flex items-center gap-1 text-[11px] leading-none whitespace-nowrap px-2 py-[2px] rounded-full border border-warn-line bg-warn text-warn-ink font-semibold"
      title={semEmail ? 'Este lead nao tem telefone nem e-mail. Complete o cadastro para conseguir trabalhar.' : 'Este lead nao tem telefone. Complete o cadastro para conseguir trabalhar.'}
    >
      <span aria-hidden>!</span>
      {texto}
    </span>
  );
};

const LeadTemperatureBadge = ({ value }) => {
  const normalized = normalizeOptionValue(value);
  const label = LEAD_TEMPERATURE_OPTIONS.find((item) => item.value === normalized)?.label || 'Sem temperatura';
  const targetLabel = SLA_TARGET_LABELS[normalized] || '-';
  // Temperatura NAO usa matiz. Antes, 'quente' era vermelho e 'morno' ambar —
  // as mesmas cores que o SLA usa para atraso, na mesma celula. Vermelho
  // significava "lead bom" e "prazo estourado" ao mesmo tempo. Agora a
  // temperatura e um medidor de 3 barras em tinta neutra e o SLA e a unica
  // coisa colorida da linha.
  const filled = { frio: 1, morno: 2, quente: 3 }[normalized] || 1;

  return (
    <span
      className="inline-flex items-center gap-1.5 text-[11px] leading-none whitespace-nowrap text-ink-soft"
      title={`Temperatura: ${label} | SLA alvo: ${targetLabel}`}
    >
      <span className="inline-flex items-end gap-[2px] h-3" aria-hidden>
        <span className={`w-[3px] h-[5px] rounded-[1px] ${filled >= 1 ? 'bg-ink' : 'bg-line'}`} />
        <span className={`w-[3px] h-[9px] rounded-[1px] ${filled >= 2 ? 'bg-ink' : 'bg-line'}`} />
        <span className={`w-[3px] h-3 rounded-[1px] ${filled >= 3 ? 'bg-ink' : 'bg-line'}`} />
      </span>
      <span className="font-medium">{label}</span>
    </span>
  );
};

// Funil no card do lead (06/10/2026). O vendedor dono do lead (ou o admin) clica
// em Frio, Morno ou Quente; a marcacao manda. Sem marcacao, a temperatura
// automatica aparece apagada, como sugestao. Clicar na etapa marcada desmarca.
// Prazo de retorno (servidor): quente 1 dia, morno 3, frio 7. Passou: "Esfriou".
// Temperatura "como o card mostra" (07/10/2026, decisao do Leandro): a etapa
// marcada no funil; sem marcacao, a automatica. E o que o filtro "Temperatura"
// e o Dashboard contam. O SLA do lead marcado ja vem do servidor pelo prazo do
// funil (quente 1 dia, morno 3, frio 7).
const temperaturaDoCard = (lead) => lead?.funil_etapa || normalizeOptionValue(lead?.temperature) || '';

const classeDoFunilNoCard = (lead) => ({
  quente: 'border-l-4 border-l-brand-600',
  morno: 'border-l-4 border-l-brand-300',
  frio: 'border-l-4 border-l-line',
}[lead?.funil_etapa] || '');

const FunilDoLead = ({ lead, podeMarcar, aoMarcar, compacto = false }) => {
  const marcada = lead?.funil_etapa || '';
  const sugerida = marcada ? '' : normalizeOptionValue(lead?.temperature);
  const parar = (e) => e.stopPropagation();
  const prazo = marcada && lead?.funil_prazo_em ? new Date(lead.funil_prazo_em) : null;
  const quando = lead?.funil_marcado_em ? new Date(lead.funil_marcado_em).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : '';
  return (
    <span className="inline-flex flex-wrap items-center gap-1" onClick={parar} onMouseDown={parar}>
      <span role="group" aria-label="Funil do lead" className="inline-flex rounded-md border border-line overflow-hidden text-[10.5px] leading-none">
        {LEAD_TEMPERATURE_OPTIONS.map((op) => {
          const ativa = marcada === op.value;
          const sugestao = !marcada && sugerida === op.value;
          const dica = !podeMarcar
            ? 'So o dono do lead ou o admin marca o funil'
            : ativa
              ? `Marcado ${op.label} por ${lead.funil_marcado_por || '-'} em ${quando}. Clique para desmarcar.`
              : sugestao
                ? `Sugestao automatica: ${op.label}. Clique para marcar.`
                : `Marcar ${op.label}`;
          return (
            <button
              key={op.value}
              type="button"
              disabled={!podeMarcar}
              title={dica}
              aria-pressed={ativa}
              onClick={(e) => { e.stopPropagation(); if (podeMarcar) aoMarcar(lead, ativa ? '' : op.value); }}
              className={`px-1.5 py-1 border-l first:border-l-0 border-line ${ativa ? 'bg-brand-600 text-white font-semibold' : sugestao ? 'text-ink-soft opacity-60 italic' : 'text-ink-faint'} ${!podeMarcar ? 'cursor-default' : ativa ? 'hover:bg-brand-700' : 'hover:bg-surface-low'}`}
            >
              {op.label}
            </button>
          );
        })}
      </span>
      {marcada && lead.funil_esfriou && (
        <span className="px-1.5 py-[2px] rounded text-[10.5px] bg-risk text-risk-ink whitespace-nowrap">
          Esfriou · {lead.funil_dias_parado} dia(s) parado
        </span>
      )}
      {marcada && !lead.funil_esfriou && prazo && !compacto && (
        <span className="text-[10.5px] text-ink-faint whitespace-nowrap">
          retornar até {prazo.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
        </span>
      )}
    </span>
  );
};

const LeadSlaBadge = ({ value, remainingMinutes }) => {
  const normalized = normalizeOptionValue(value);
  const label = SLA_STATUS_OPTIONS.find((item) => item.value === normalized)?.label || 'No prazo';
  // 'No prazo' e o estado esperado: fica neutro. Cor so quando ha algo a fazer.
  const toneClasses = {
    normal: 'border-line bg-surface-low text-ink-soft',
    warning: 'border-warn-line bg-warn text-warn-ink',
    overdue: 'border-risk-line bg-risk text-risk-ink',
  };
  const dotClasses = {
    normal: 'bg-ink-faint',
    warning: 'bg-warn-ink',
    overdue: 'bg-risk-ink',
  };
  const helper =
    typeof remainingMinutes === 'number'
      ? remainingMinutes >= 0
        ? formatSlaDuration(remainingMinutes)
        : `${formatSlaDuration(remainingMinutes)} atrasado`
      : '';

  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1.5 text-[11px] leading-none whitespace-nowrap ${toneClasses[normalized] || toneClasses.normal}`}
    >
      <span className={`h-2 w-2 rounded-full ${dotClasses[normalized] || dotClasses.normal}`} />
      <span className="font-semibold">{label}</span>
      {helper ? <span className="text-[10px] font-medium opacity-70">{helper}</span> : null}
    </span>
  );
};

const getLeadEmailEngagement = (lead) => {
  const openCount = Number(lead?.email_open_count || 0);
  const clickCount = Number(lead?.email_click_count || 0);
  const unsubscribed = Boolean(lead?.email_unsubscribed);
  const hasOpen = openCount > 0 || Boolean(lead?.last_email_open_at);
  const hasClick = clickCount > 0 || Boolean(lead?.last_email_click_at);
  const hasAny = hasOpen || hasClick || unsubscribed || Boolean(lead?.last_email_event_at);
  return { hasAny, hasOpen, hasClick, unsubscribed };
};

const LeadEmailEngagementBadges = ({ lead }) => {
  const engagement = getLeadEmailEngagement(lead);
  if (!engagement.hasAny) return null;

  return (
    <>
      {engagement.hasClick ? (
        <span className="px-2 py-[2px] rounded-full bg-brand-50 text-brand-ink border border-brand-100 text-[10px] font-semibold">
          Clicou email
        </span>
      ) : null}
      {!engagement.hasClick && engagement.hasOpen ? (
        <span className="px-2 py-[2px] rounded-full bg-info text-info-ink border border-info-line text-[10px] font-semibold">
          Abriu email
        </span>
      ) : null}
      {engagement.unsubscribed ? (
        <span className="px-2 py-[2px] rounded-full bg-risk text-risk-ink border border-risk-line text-[10px] font-semibold">
          Descadastrado
        </span>
      ) : null}
    </>
  );
};


const SEGMENT_OPTIONS = [
  { value: '', label: 'Sem perfil' },
  { value: 'revendedor', label: 'Revendedor' },
  { value: 'instalador', label: 'Instalador' },
  { value: 'montador', label: 'Montador de Painel' },
  { value: 'fabricante_maquinas', label: 'Fabricante de Máquinas' },
  { value: 'grupos_geradores', label: 'Grupos Geradores' },
  { value: 'concorrente', label: 'Concorrente' },
  { value: 'representante', label: 'Representante' },
  { value: 'usuario_final', label: 'Usuario Final' },
  { value: 'rep_comercial', label: 'Rep Comercial' },
];
const CLIENT_SEGMENT_OPTIONS = SEGMENT_OPTIONS.filter((option) => option.value !== 'representante');
// Tipo de lead como a tela mostra ("Montador de Painel"). Sem tipo sai "Sem perfil",
// o mesmo rotulo da tela; valor fora da lista sai como esta gravado (02/10/2026).
const rotuloTipoDeLead = (segment) => {
  const valor = String(segment || '').trim();
  return SEGMENT_OPTIONS.find((item) => item.value === valor)?.label || valor;
};

const HIGHLIGHTED_CATEGORIES_OPTIONS = [
  'Automação',
  'Baixa Tensão',
  'Comando e Sinalização',
  'Instrumentos e Medições',
  'Conectividade e Proteção',
  'Ventilação e Filtragem',
  'Ferramentas',
  'Pneumática',
];

const CUSTOMER_TYPE_OPTIONS = ['A', 'B', 'C'];


const parseMultiSelect = (value) => {
  const source = Array.isArray(value) ? value.join(',') : String(value || '');
  return source
    .split(/[,;|]+/)
    .map((item) => item.trim())
    .filter(Boolean);
};

const normalizeListValue = parseMultiSelect;
const ensureArray = (value) => (Array.isArray(value) ? value : normalizeListValue(value));

const normalizeOptionValue = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .trim()
    .toLowerCase();

// ---------------------------------------------------------------------------
// Origem do lead: prospeccao ativa x demanda de entrada.
//
// A BHS trabalha duas frentes que convertem em ritmos diferentes e por isso nao
// podem ser somadas dentro de um mesmo percentual:
//   - prospeccao ativa: listas que a propria BHS subiu para a equipe trabalhar
//     (a Ferramenta de Captura e a carteira importada do Victor);
//   - demanda de entrada: quem procurou a BHS (Meta Ads, Instagram, site,
//     e-mail marketing, prospeccao manual e qualquer origem nova).
//
// A lista abaixo nomeia SO a prospeccao; o que nao estiver nela conta como
// demanda de entrada. Feito nessa direcao de proposito: uma origem nova de
// entrada passa a contar sozinha, sem ninguem precisar cadastrar nada. Se um
// dia subir OUTRA lista de prospeccao, ela precisa ser somada aqui a mao.
//
// Medido na base de producao em 11/09/2026: 1.385 leads da Ferramenta de
// Captura (1.016 ja fora de "novo", 1.176 com anotacao escrita) e 315 da
// carteira do Victor (206 ainda em "novo", nenhuma anotacao) contra 500 de
// demanda de entrada. As duas frentes sao trabalhadas; a classificacao existe
// para comparar uma com a outra, e NAO exclui nenhum lead de nenhum indicador.
const FONTES_DE_PROSPECCAO_ATIVA = ['ferramenta de captura', 'planilha victor'];

const classificarOrigemDoLead = (lead) => {
  const fonte = normalizeOptionValue(lead?.source || lead?.channel_name || '');
  return FONTES_DE_PROSPECCAO_ATIVA.includes(fonte) ? 'prospeccao' : 'demanda';
};

const containsNormalized = (items, option) => {
  const normalizedOption = normalizeOptionValue(option);
  return items.some((item) => normalizeOptionValue(item) === normalizedOption);
};

const toggleSelection = (items, option) => {
  const normalizedOption = normalizeOptionValue(option);
  const exists = items.some((item) => normalizeOptionValue(item) === normalizedOption);
  if (exists) {
    return items.filter((item) => normalizeOptionValue(item) !== normalizedOption);
  }
  return [...items, option];
};

const COOLING_REASON_OPTIONS = [
  'Preço',
  'Problemas técnicos',
  'Não lembrava',
  'Crédito/Outros',
];

const emptyLead = {
  name: '',
  contact: '',
  company: '',
  segment: '',
  owner: '',
  ownerId: null,
  origin: '',
  stage_detail: '',
  next_contact: '',
  email: '',
  phone: '',
  phone2: '',
  channel_id: '',
  campaign: '',
  status: 'novo',
  value: '',
  notes: '',
  operator_notes: '',
  is_private: false,
  is_customer: false,
  is_out_of_scope: false,
  first_contact: '',
  interactions_count: 0,
  last_interaction_at: '',
  last_interaction_channel: '',
  last_interaction_notes: '',
  highlighted_categories: [],
  customer_type: '',
  cooling_reason: [],
  temperature: '',
  sla_status: '',
  sla_due_at: '',
  sla_minutes: 0,
  last_activity_at: '',
  mailrelay_subscriber_id: '',
  last_email_open_at: '',
  last_email_click_at: '',
  email_open_count: 0,
  email_click_count: 0,
  email_unsubscribed: false,
  last_email_campaign: '',
  last_email_campaign_id: '',
  last_email_event_at: '',
};

// A regra de DATA do Dashboard mora FORA destas duas funcoes, de proposito.
//
// Os graficos de evolucao mensal montam seis meses fixos e precisam dos MESMOS
// filtros de vendedor, canal, segmento, campanha e follow-up — mas NAO do
// recorte de data. Sem essa separacao, um grafico de 6 meses alimentado por um
// filtro de 30 dias mostra quatro meses zerados que nao sao zero de verdade:
// sao mes filtrado para fora, exibido como se nao tivesse acontecido nada.
// Os filtros sao conjuntivos, entao partir em duas partes nao muda em nada o
// resultado de quem aplica as duas.
const leadPassaFiltrosDoDashboard = (lead, opts) => {
  const { users, segmentFilter, ownerFilter, channelFilter, campaignFilter, followUpFilter, now } = opts;

  if (normalizeOptionValue(lead.segment) === 'concorrente') return false;

  if (segmentFilter !== 'all') {
    const norm = normalizeOptionValue(lead.segment);
    if (segmentFilter === 'montador') {
      if (!norm.includes('montador')) return false;
    } else if (segmentFilter === 'usuario_final') {
      if (norm !== 'usuario final') return false;
    } else if (norm !== segmentFilter) {
      return false;
    }
  }

  if (ownerFilter !== 'all') {
    const targetUser = users.find((u) => String(u.id) === String(ownerFilter));
    const targetName = normalizeOptionValue(targetUser?.name);
    const oid = String(lead.ownerId || lead.user_id || lead.userId || lead.owner_id || '');
    const oname = normalizeOptionValue(lead.owner || lead.responsible_name);
    if (oid) {
      if (oid !== String(ownerFilter)) return false;
    } else if (targetName) {
      if (!(oname === targetName || oname.includes(targetName) || targetName.includes(oname))) {
        return false;
      }
    } else {
      return false;
    }
  }

  if (channelFilter !== 'all' && String(lead.channel_id || '') !== String(channelFilter)) {
    return false;
  }

  if (campaignFilter.trim()) {
    const term = campaignFilter.toLowerCase();
    const campaign = String(lead.campaign || '').toLowerCase();
    if (!campaign.includes(term)) return false;
  }

  if (followUpFilter === 'overdue') {
    const nextDate = dataDeCalendario(lead.next_contact);
    if (!nextDate) return false;
    const leadDate = new Date(nextDate.getFullYear(), nextDate.getMonth(), nextDate.getDate()).getTime();
    const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const diffDays = (leadDate - todayDate) / (1000 * 60 * 60 * 24);
    if (diffDays >= 0) return false;
  }

  return true;
};

const orcamentoPassaFiltrosDoDashboard = (budget, opts) => {
  const { segmentFilter, ownerFilter, campaignFilter, channelFilter, selectedChannelName } = opts;

  if (segmentFilter !== 'all' && normalizeOptionValue(budget.segment) !== normalizeOptionValue(segmentFilter)) {
    return false;
  }
  if (ownerFilter !== 'all' && String(budget.owner_id || '') !== String(ownerFilter)) {
    return false;
  }
  if (campaignFilter.trim()) {
    const term = campaignFilter.toLowerCase();
    if (!String(budget.campaign || '').toLowerCase().includes(term)) return false;
  }
  if (channelFilter !== 'all' && selectedChannelName) {
    const haystack = `${budget.channel_name || ''} ${budget.campaign || ''}`.toLowerCase();
    if (!haystack.includes(selectedChannelName.toLowerCase())) return false;
  }
  return true;
};

const emptyBudget = {
  external_id: '',
  representante: '',
  lead_id: '',
  client_name: '',
  company: '',
  segment: '',
  stage: '',
  status: 'novo',
  loss_reason: '',
  raw_status: '',
  raw_loss_reason: '',
  owner_id: '',
  owner_name: '',
  estimator_id: '',
  estimator_name: '',
  budget_value: '',
  closed_value: '',
  branch: '',
  customer_order: '',
  payment_terms: '',
  requested_at: '',
  sent_at: '',
  closed_at: '',
  channel_name: '',
  campaign: '',
  notes: '',
};

const App = () => {
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [user, setUser] = useState(null);
  // So login (30/09/2026): o cadastro publico foi desligado no servidor. Conta nova
  // e criada pelo admin. O formulario de cadastro abaixo fica, sem caminho de entrada.
  const [authMode] = useState('login');
  const [authForm, setAuthForm] = useState({ name: '', email: '', phone: '', username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [authHint, setAuthHint] = useState('');

  const [leads, setLeads] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [adSpendItems, setAdSpendItems] = useState([]);
  const [emailEvents, setEmailEvents] = useState([]);
  const [channels, setChannels] = useState([]);
  const [stats, setStats] = useState({});
  const [ownerFilter, setOwnerFilter] = useState('all'); // 'all', 'me', or userId
  const [statusFilter, setStatusFilter] = useState('todos');
  const [urgencyFilter, setUrgencyFilter] = useState('all'); // 'all', 'overdue', 'next3', 'today'
  const [temperatureFilter, setTemperatureFilter] = useState('all');
  const [slaFilter, setSlaFilter] = useState('all');
  const [emailEngagementFilter, setEmailEngagementFilter] = useState('all');
  const [segmentFilter, setSegmentFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState('all');  // Filtro de contato faltando: e por aqui que se acha quem precisa ser
  // completado depois de uma importacao.
  const [contatoFilter, setContatoFilter] = useState('all');
  // Periodo comeca em 'all' DE PROPOSITO: ligar o filtro por padrao mudaria
  // o que a tela mostra para todo mundo sem ninguem ter pedido.
  const [periodoFilter, setPeriodoFilter] = useState('all');
  const [periodoInicio, setPeriodoInicio] = useState('');
  const [periodoFim, setPeriodoFim] = useState('');

  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [channelFilter, setChannelFilter] = useState('all');
  const [campaignFilter, setCampaignFilter] = useState('');
  // Padrao: data de entrada, mais recente em cima (decidido em 18/09/2026).
  const [sortKey, setSortKey] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  // Orcamentos nao tinham ordenacao nenhuma ate 18/09/2026: a lista saia na
  // ordem em que as linhas estavam na planilha. Padrao: data solicitada,
  // mais recente em cima.
  const [budgetSortKey, setBudgetSortKey] = useState('requested_at');
  const [budgetSortDir, setBudgetSortDir] = useState('desc');
  const [viewMode, setViewMode] = useState('kanban'); // 'list' | 'kanban'
  const [activeTab, setActiveTab] = useState('crm'); // 'crm' | 'dashboard' | 'orcamentos' | 'emkt'
  const [dashboardPeriod, setDashboardPeriod] = useState('30d');
  // Sub-abas do dashboard. Antes as tres areas eram secoes empilhadas numa
  // pagina so, e chegar em Midia paga exigia rolar por 22 cartoes e 10
  // graficos de Leads.
  const [dashboardPane, setDashboardPane] = useState('leads');
  const [dashboardStartDate, setDashboardStartDate] = useState('');
  const [dashboardEndDate, setDashboardEndDate] = useState('');
  const [dashboardOwnerFilter, setDashboardOwnerFilter] = useState('all');
  const [dashboardSegmentFilter, setDashboardSegmentFilter] = useState('all');
  const [dashboardChannelFilter, setDashboardChannelFilter] = useState('all');
  const [dashboardCampaignFilter, setDashboardCampaignFilter] = useState('');
  const [dashboardFollowUpFilter, setDashboardFollowUpFilter] = useState('all');
  const [emktPeriod, setEmktPeriod] = useState('90d');
  const [emktStartDate, setEmktStartDate] = useState('');
  const [emktEndDate, setEmktEndDate] = useState('');
  const [emktEventFilter, setEmktEventFilter] = useState('all');
  const [emktCampaignFilter, setEmktCampaignFilter] = useState('');
  const [emktSearch, setEmktSearch] = useState('');
  const [visibleCount, setVisibleCount] = useState(20);
  // Quantos cartoes cada coluna do Kanban mostra. A Lista ja paginava de 20 em
  // 20 desde sempre; o Kanban desenhava TODOS — em 18/09/2026 eram 2.229 de uma
  // vez, e desde a manha daquele dia cada um com uma caixinha de selecao a
  // mais. Sao 7 colunas: 15 por coluna da 105 cartoes na tela, contra 2.229.
  const KANBAN_POR_COLUNA = 15;
  const [kanbanVisivel, setKanbanVisivel] = useState({});

  const [showLeadModal, setShowLeadModal] = useState(false);
  const [leadLoading, setLeadLoading] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [leadForm, setLeadForm] = useState(emptyLead);
  const [savingLead, setSavingLead] = useState(false);
  const [leadInteractions, setLeadInteractions] = useState([]);
  const [leadInteractionsLoading, setLeadInteractionsLoading] = useState(false);
  const [savingLeadInteraction, setSavingLeadInteraction] = useState(false);
  const [leadInteractionForm, setLeadInteractionForm] = useState({
    id: null,
    interaction_at: '',
    channel: '',
    notes: '',
  });
  const [leadInteractionsExpanded, setLeadInteractionsExpanded] = useState(false);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);
  const [budgetForm, setBudgetForm] = useState(emptyBudget);
  const [savingBudget, setSavingBudget] = useState(false);
  // Interacoes do orcamento. Mesmo desenho do bloco que ja existe no lead, mas
  // com armazenamento proprio: um orcamento pode nao ter lead vinculado, e mesmo
  // quando tem, a conversa da proposta nao e a mesma coisa que a da captacao.
  const [budgetInteractions, setBudgetInteractions] = useState([]);
  // Historico do lead vinculado. Somente leitura, para dar contexto.
  const [budgetLeadInteractions, setBudgetLeadInteractions] = useState([]);
  const [budgetInteractionsLoading, setBudgetInteractionsLoading] = useState(false);
  const [savingBudgetInteraction, setSavingBudgetInteraction] = useState(false);
  const [budgetInteractionForm, setBudgetInteractionForm] = useState({
    id: null,
    interaction_at: '',
    channel: '',
    notes: '',
  });
  const [budgetInteractionsExpanded, setBudgetInteractionsExpanded] = useState(false);
  const [budgetLeadHistoryExpanded, setBudgetLeadHistoryExpanded] = useState(false);
  const [importingBudgets, setImportingBudgets] = useState(false);
  const [budgetPeriod, setBudgetPeriod] = useState('30d');
  const [budgetStartDate, setBudgetStartDate] = useState('');
  const [budgetEndDate, setBudgetEndDate] = useState('');
  const [budgetStatusFilter, setBudgetStatusFilter] = useState('all');
  const [budgetOwnerFilter, setBudgetOwnerFilter] = useState('all');
  const [budgetEstimatorFilter, setBudgetEstimatorFilter] = useState('all');
  const [budgetLossReasonFilter, setBudgetLossReasonFilter] = useState('all');
  const [budgetRepresentanteFilter, setBudgetRepresentanteFilter] = useState('');
  const [budgetSearch, setBudgetSearch] = useState('');
  const budgetImportInputRef = useRef(null);
  const clientesImportInputRef = useRef(null);
  // --- Importacao da lista de clientes (AM Gestor), 23/09/2026 -------------
  // O responsavel e o canal NAO tem valor padrao de proposito: quem importa
  // escolhe na hora, e a tela nao deixa confirmar sem os dois. Decisao dele em
  // 23/09/2026 — a importacao serve para mais de uma finalidade, entao fixar um
  // canal no codigo seria errar em silencio nas outras.
  const [importClientesAberto, setImportClientesAberto] = useState(false);
  const [importClientesArquivo, setImportClientesArquivo] = useState('');
  const [importClientesItens, setImportClientesItens] = useState([]);
  const [importClientesIgnorados, setImportClientesIgnorados] = useState([]);
  const [importClientesSimulacao, setImportClientesSimulacao] = useState(null);
  const [importClientesResponsavel, setImportClientesResponsavel] = useState('');
  const [importClientesCanal, setImportClientesCanal] = useState('');
  const [importClientesCampanha, setImportClientesCampanha] = useState('');
  const [importClientesMarcarCliente, setImportClientesMarcarCliente] = useState(true);
  const [importClientesOcupado, setImportClientesOcupado] = useState(false);
  const [importClientesErro, setImportClientesErro] = useState('');
  const [importClientesResultado, setImportClientesResultado] = useState(null);
  // Lixeira de leads (23/09/2026). So admin ve.
  // Qual card do Kanban esta com o menu aberto. Ate 23/09/2026 o 'Excluir' era
  // um alvo de 31x16 pixels DENTRO da area clicavel do card, 50 deles visiveis
  // ao mesmo tempo. Agora precisa abrir o menu antes.
  const [cardMenuId, setCardMenuId] = useState(null);
  // Campanhas do Mailrelay, separadas do engajamento do CRM (23/09/2026).
  const [mrCampanhas, setMrCampanhas] = useState(null);
  const [mrCampanhasCarregando, setMrCampanhasCarregando] = useState(false);
  const [mrCampanhasErro, setMrCampanhasErro] = useState('');
  // Quem engajou com campanha e nao tem lead (24/09/2026). Nada vira lead
  // sozinho: a tela lista e cada pessoa tem o proprio botao.
  const [showEngajou, setShowEngajou] = useState(false);
  // Esc fecha a lista de quem engajou (19/10/2026).
  useEffect(() => {
    if (!showEngajou) return undefined;
    const tecla = (event) => { if (event.key === 'Escape') setShowEngajou(false); };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [showEngajou]);
  const [engajou, setEngajou] = useState(null);
  const [engajouCarregando, setEngajouCarregando] = useState(false);
  const [engajouOcupadoId, setEngajouOcupadoId] = useState(null);
  const [engajouForm, setEngajouForm] = useState({});
  const [showLixeira, setShowLixeira] = useState(false);
  const [lixeira, setLixeira] = useState(null);
  const [lixeiraCarregando, setLixeiraCarregando] = useState(false);
  const [lixeiraOcupadoId, setLixeiraOcupadoId] = useState(null);
  const [showAdSpendModal, setShowAdSpendModal] = useState(false);
  const [editingAdSpend, setEditingAdSpend] = useState(null);
  const [adSpendForm, setAdSpendForm] = useState(emptyAdSpend);
  const [savingAdSpend, setSavingAdSpend] = useState(false);
  const highlightedField = ensureArray(leadForm.highlighted_categories);
  const coolingField = ensureArray(leadForm.cooling_reason);
  const toggleLeadListField = (field, option) => {
    setLeadForm((prev) => {
      const current = ensureArray(prev[field]);
      const next = toggleSelection(current, option);
      return { ...prev, [field]: next };
    });
  };

  const [showChannelModal, setShowChannelModal] = useState(false);
  const [newChannel, setNewChannel] = useState('');

  const [toast, setToast] = useState(null);
  const [showAllAgenda, setShowAllAgenda] = useState(false);
  const [agendaOwnerFilter, setAgendaOwnerFilter] = useState('todos');
  const [showStats, setShowStats] = useState(false);
  // Agenda e follow-up dividem um card so: lado a lado eles competiam, e o de
  // follow-up e cronico (nao muda de um dia para o outro).
  const [agendaPane, setAgendaPane] = useState('agenda');
  // Tema: 'auto' (segue o sistema), 'light' ou 'dark'. Guardado no navegador,
  // por maquina. O valor inicial ja foi aplicado em public/index.html antes do
  // primeiro desenho — aqui so mantemos o React em dia com ele.
  // "Precisa de voce" abre e fecha no clique (24/09/2026, pedido dele: a secao
  // estava "muito grande e nao esta sendo efetiva"). Fecha o CORPO, nunca o
  // cabecalho: fechada, ela continua mostrando Agenda / Follow-up com os
  // contadores, que e a informacao util. Some a parede, fica o numero.
  //
  // Guardado por MAQUINA, no mesmo padrao do tema: o que ele fecha nao fecha
  // para a Ines. Sem isso ele teria de fechar a secao toda vez que abrisse o
  // CRM, e o incomodo voltaria no dia seguinte.
  const [agendaAberta, setAgendaAberta] = useState(() => {
    try {
      return localStorage.getItem('agendaAberta') !== 'fechada';
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('agendaAberta', agendaAberta ? 'aberta' : 'fechada');
    } catch {
      // navegador sem armazenamento: vale so para esta aba
    }
  }, [agendaAberta]);

  const [theme, setTheme] = useState(() => {
    try {
      const saved = localStorage.getItem('theme');
      return saved === 'light' || saved === 'dark' ? saved : 'auto';
    } catch {
      return 'auto';
    }
  });
  // O menu do usuario e um <details> nativo — nao precisa de estado para abrir,
  // mas tambem nao fecha sozinho ao clicar fora, que e o comportamento que
  // qualquer pessoa espera de um menu. Estas duas pecas cobrem isso.
  const userMenuRef = useRef(null);
  const fecharMenuUsuario = useCallback(() => {
    if (userMenuRef.current) userMenuRef.current.open = false;
  }, []);

  const [users, setUsers] = useState([]);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileTab, setProfileTab] = useState('me');
  const [profileForm, setProfileForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
  });
  const [userForm, setUserForm] = useState({
    id: null,
    name: '',
    username: '',
    phone: '',
    email: '',
    password: '',
    role: 'vendedor',
  });
  const [mailrelaySettings, setMailrelaySettings] = useState({
    api_base: '',
    api_key: '',
    configured: false,
    source: 'none',
    has_api_key: false,
    leads_with_engagement: 0,
    email_events: 0,
    last_sync: null,
  });
  const [omnichatSettings, setOmnichatSettings] = useState({
    send_url: '',
    api_key: '',
    auth_header: 'x-api-key',
    enabled: true,
    reminder_minutes: 30,
    configured: false,
    source: 'none',
    has_api_key: false,
  });
  const [savingMailrelaySettings, setSavingMailrelaySettings] = useState(false);
  const [savingOmnichatSettings, setSavingOmnichatSettings] = useState(false);
  const [runningFollowupDispatch, setRunningFollowupDispatch] = useState(false);
  const [syncingMailrelay, setSyncingMailrelay] = useState(false);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const pendingLeadsRef = useRef(null);
  const [pendingLeadId, setPendingLeadId] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('leadId') || '';
    } catch {
      return '';
    }
  });
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkOwnerId, setBulkOwnerId] = useState('');
  const [loadingData, setLoadingData] = useState(false);
  const [draggingLeadId, setDraggingLeadId] = useState(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [agendaUpdatingId, setAgendaUpdatingId] = useState(null);
  const [pingFailCount, setPingFailCount] = useState(0);
  // Queda do servidor NAO desloga mais (19/10/2026, decisao do Leandro). Em
  // 16/10 a VPS reiniciou (manutencao da Hostinger) e a tela deslogou todo
  // mundo: o ping falhava e a regra antiga chamava handleLogout. Agora so
  // desloga quando o servidor RESPONDE que o login nao vale (401/403/404 em
  // /auth/me). Servidor fora = aviso no topo e nova tentativa sozinha.
  const [servidorFora, setServidorFora] = useState(false);
  const [tentativaToken, setTentativaToken] = useState(0);
  const retryTokenRef = useRef(null);
  const buildWhatsappText = (lead) => {
    const origem = lead.channel_name || lead.campaign || 'Nao informado';
    const empresa = lead.company || '';
    const contato = lead.name || 'Lead';
    const telefone = lead.phone || '';
    const telefone2 = lead.phone2 || '';
    const solicitacao = lead.notes || lead.campaign || lead.status || '';
    const telefoneLinha = `Telefone: ${telefone || '-'}`;
    const telefone2Linha = telefone2 ? `\nTelefone 2: ${telefone2}` : '';
    return `*Novo Lead*\nOrigem: ${origem}\nEmpresa: ${empresa || '-'}\nContato: ${contato}\n${telefoneLinha}${telefone2Linha}\nSolicitacao: ${solicitacao || '-'}`;
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast('Texto copiado para área de transferência');
    } catch (err) {
      console.error('Erro ao copiar:', err);
      showToast('Não foi possível copiar', 'error');
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const isAdmin = useMemo(() => {
    if (!user) return false;
    return user.role === 'admin' || user.email === 'marketing@bhseletronica.com.br';
  }, [user]);
  // Mesma regra do servidor (podeVerEmailMkt, 19/10/2026): admin e vendedor veem
  // as campanhas do Mailrelay e a lista de quem engajou, e criam lead dela.
  const podeVerEmailMkt = isAdmin || user?.role === 'vendedor';

  const handleLogout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setLeads([]);
    setBudgets([]);
    setEmailEvents([]);
    setChannels([]);
    setStats({});
    setUsers([]);
    setShowProfileModal(false);
  };

  useEffect(() => {
    const cliqueFora = (event) => {
      const menu = userMenuRef.current;
      if (menu && menu.open && !menu.contains(event.target)) menu.open = false;
    };
    const tecla = (event) => {
      if (event.key === 'Escape') fecharMenuUsuario();
    };
    // 'mousedown' e nao 'click': fecha assim que o botao desce, antes de o
    // clique chegar em outro elemento da tela.
    document.addEventListener('mousedown', cliqueFora);
    document.addEventListener('keydown', tecla);
    return () => {
      document.removeEventListener('mousedown', cliqueFora);
      document.removeEventListener('keydown', tecla);
    };
  }, [fecharMenuUsuario]);

  useEffect(() => {
    const media =
      typeof window !== 'undefined' && window.matchMedia
        ? window.matchMedia('(prefers-color-scheme: dark)')
        : null;
    const aplicar = () => {
      const escuro = theme === 'dark' || (theme === 'auto' && media && media.matches);
      document.documentElement.setAttribute('data-theme', escuro ? 'dark' : 'light');
    };
    aplicar();
    try {
      localStorage.setItem('theme', theme);
    } catch {
      // navegador sem armazenamento: o tema vale so para esta aba
    }
    // Em 'auto' precisamos reagir se a pessoa trocar o tema do sistema com o
    // CRM aberto. Nos outros modos nao ha o que escutar.
    if (theme !== 'auto' || !media) return undefined;
    media.addEventListener('change', aplicar);
    return () => media.removeEventListener('change', aplicar);
  }, [theme]);

  const verifyToken = async () => {
    if (!token) return;
    // Servidor fora (502, 500, rede): guarda o login e tenta de novo em 15 s.
    const tentarDeNovo = () => {
      setAuthHint('Servidor fora do ar. Tentando reconectar sozinho; não precisa entrar de novo.');
      if (retryTokenRef.current) clearTimeout(retryTokenRef.current);
      retryTokenRef.current = setTimeout(() => setTentativaToken((n) => n + 1), 15000);
    };
    try {
      const res = await fetch(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401 || res.status === 403 || res.status === 404) {
        handleLogout();
        return;
      }
      if (!res.ok) {
        tentarDeNovo();
        return;
      }
      const data = await res.json();
      setAuthHint('');
      setUser(data);
    } catch {
      tentarDeNovo();
    }
  };

  useEffect(() => {
    // Pré-aquecer backend (ping público) para reduzir delay inicial
    fetch(`${API_URL}/ping`).catch(() => { });
  }, []);

  const pingServer = useCallback(async (retries = 0) => {
    try {
      const res = await fetch(`${API_URL}/ping`, { method: 'GET' });
      if (!res.ok) throw new Error('ping fail');
      setPingFailCount(0);
      setAuthHint('');
      return true;
    } catch (err) {
      const nextRetries = retries + 1;
      setPingFailCount((c) => c + 1);
      setAuthHint(
        nextRetries === 1
          ? 'Acordando servidor... tentando de novo.'
          : `Tentando novamente (${nextRetries})...`
      );
      if (nextRetries < 3) {
        await sleep(1000 * nextRetries);
        return pingServer(nextRetries);
      }
      return false;
    }
  }, []);

  useEffect(() => {
    if (pingFailCount >= 2 && !servidorFora) setServidorFora(true);
    if (pingFailCount === 0 && servidorFora) {
      setServidorFora(false);
      showToast('Conexão com o servidor restabelecida.', 'success');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pingFailCount]);

  // Enquanto o servidor estiver fora, tenta de 30 em 30 s (em vez de 5 min).
  useEffect(() => {
    if (!servidorFora || !token) return undefined;
    const id = setInterval(() => pingServer(), 30 * 1000);
    return () => clearInterval(id);
  }, [servidorFora, token, pingServer]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (token) pingServer();
    }, 5 * 60 * 1000);
    const onFocus = () => {
      if (token) pingServer();
    };
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [token, pingServer]);

  // Debounce da busca
  useEffect(() => {
    const id = setTimeout(() => setSearchTerm(searchInput), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    verifyToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, tentativaToken]);

  useEffect(() => () => {
    if (retryTokenRef.current) clearTimeout(retryTokenRef.current);
  }, []);

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setAuthHint('');
    await pingServer();
    const endpoint = authMode === 'login' ? '/auth/login' : '/auth/register';
    const body =
      authMode === 'login'
        ? { login: authForm.email, password: authForm.password }
        : {
          name: authForm.name,
          email: authForm.email,
          phone: authForm.phone,
          username: authForm.username,
          password: authForm.password,
        };

    const maxAttempts = 3;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        if (attempt > 1) {
          setAuthHint(`Tentando novamente... (${attempt}/${maxAttempts})`);
        }
        const res = await fetch(`${API_URL}${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
          body: JSON.stringify(body),
        });
        let data = {};
        try {
          data = await res.json();
        } catch (parseErr) {
          console.error('Erro ao ler resposta de auth:', parseErr);
        }
        if (!res.ok) {
          throw new Error(data.error || 'Erro na autenticação');
        }
        if (!data.token || !data.user) {
          throw new Error('Resposta de login inválida');
        }
        localStorage.setItem('token', data.token);
        setToken(data.token);
        setUser(data.user);
        setAuthForm({ name: '', email: '', phone: '', username: '', password: '' });
        setError('');
        setAuthHint('');
        clearTimeout(timeout);
        setLoading(false);
        return;
      } catch (err) {
        clearTimeout(timeout);
        if (err.name === 'AbortError') {
          if (attempt < maxAttempts) {
            setAuthHint('Servidor acordando... nova tentativa em instantes');
            await sleep(1500);
            continue;
          }
          setError('Servidor demorou a responder. Tente novamente agora.');
        } else {
          setError(err.message);
          break;
        }
      }
    }
    setLoading(false);
  };

  const loadLeads = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/leads`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
      });
      const data = await res.json();
      setLeads(Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : []);
      pendingLeadsRef.current = null;
    } catch (err) {
      console.error('Erro ao carregar leads:', err);
    }
  };

  const loadChannels = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/channels`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setChannels(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Erro ao carregar canais:', err);
    }
  };

  const loadBudgets = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/budgets`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
      });
      if (!res.ok) return;
      const data = await res.json();
      setBudgets(Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      console.error('Erro ao carregar orçamentos:', err);
    }
  };

  const loadAdSpend = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/ad-spend`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
      });
      if (!res.ok) return;
      const data = await res.json();
      setAdSpendItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Erro ao carregar gastos de Ads:', err);
    }
  };

  const loadEmailEvents = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/email-events`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
      });
      if (!res.ok) return;
      const data = await res.json();
      setEmailEvents(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Erro ao carregar eventos de email:', err);
    }
  };

  const loadUsers = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      const safeUsers = Array.isArray(data) ? data : [];
      console.log(`[DEBUG] loadUsers returned ${safeUsers.length} users:`, safeUsers);
      setUsers(safeUsers);
    } catch (err) {
      console.error('Erro ao carregar usuarios:', err);
    }
  };

  const loadMailrelaySettings = async () => {
    if (!token || !isAdmin) return;
    try {
      const [settingsRes, statusRes] = await Promise.all([
        fetch(`${API_URL}/settings/mailrelay`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/mailrelay/status`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      if (!settingsRes.ok) return;
      const data = await settingsRes.json();
      const statusData = statusRes.ok ? await statusRes.json() : {};
      setMailrelaySettings({
        api_base: data.api_base || '',
        api_key: '',
        configured: Boolean(data.configured),
        source: data.source || 'none',
        has_api_key: Boolean(data.has_api_key),
        leads_with_engagement: Number(statusData.leads_with_engagement || 0),
        email_events: Number(statusData.email_events || 0),
        last_sync: statusData.last_sync || null,
      });
    } catch (err) {
      console.error('Erro ao carregar Mailrelay:', err);
    }
  };

  const loadOmnichatSettings = async () => {
    if (!token || !isAdmin) return;
    try {
      const res = await fetch(`${API_URL}/settings/omnichat`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setOmnichatSettings({
        send_url: data.api_base || data.send_url || '',
        api_key: '',
        auth_header: data.auth_header || 'x-api-key',
        enabled: data.enabled !== undefined ? Boolean(data.enabled) : true,
        reminder_minutes: Number(data.reminder_minutes || 30),
        configured: Boolean(data.configured),
        source: data.source || 'none',
        has_api_key: Boolean(data.has_api_key),
      });
    } catch (err) {
      console.error('Erro ao carregar Omnichat:', err);
    }
  };

  const loadStats = async () => {
    if (!token) return;
    try {
      const params = new URLSearchParams();
      if (isAdmin) {
        if (ownerFilter === 'all') {
          params.append('scope', 'all');
        } else if (ownerFilter && ownerFilter !== 'me') {
          params.append('userId', ownerFilter);
        }
      }
      const res = await fetch(`${API_URL}/stats?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setStats(data);
    } catch (err) {
      console.error('Erro ao carregar estatísticas:', err);
    }
  };

  const loadAll = async () => {
    setLoadingData(true);
    try {
      await Promise.all([
        loadLeads(),
        loadBudgets(),
        loadAdSpend(),
        loadEmailEvents(),
        loadChannels(),
        loadUsers(),
        loadStats(),
        loadMailrelaySettings(),
        loadOmnichatSettings(),
      ]);
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!pendingLeadId || !user || !Array.isArray(leads) || !leads.length || showLeadModal || leadLoading) {
      return;
    }
    const targetLead = leads.find((lead) => String(lead.id) === String(pendingLeadId));
    if (!targetLead) return;
    setPendingLeadId('');
    void openEditLeadModal(targetLead);
  }, [pendingLeadId, user, leads, showLeadModal, leadLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!loadingData) return undefined;
    const timeout = setTimeout(() => setLoadingData(false), 8000);
    return () => clearTimeout(timeout);
  }, [loadingData]);

  // Removemos o reset automático de seleção para evitar perda de dados ao atualizar
  /*
  useEffect(() => {
    setSelectedLeadIds([]);
  }, [leads, user]);
  */

  useEffect(() => {
    try {
      const saved = localStorage.getItem('leadFilters');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.ownerFilter) setOwnerFilter(parsed.ownerFilter);
        if (parsed.statusFilter) setStatusFilter(parsed.statusFilter);
        if (parsed.urgencyFilter) setUrgencyFilter(parsed.urgencyFilter);
        if (parsed.temperatureFilter) setTemperatureFilter(parsed.temperatureFilter);
        if (parsed.slaFilter) setSlaFilter(parsed.slaFilter);
        if (parsed.emailEngagementFilter) setEmailEngagementFilter(parsed.emailEngagementFilter);
        if (parsed.searchTerm) {
          setSearchTerm(parsed.searchTerm);
          setSearchInput(parsed.searchTerm);
        }
        if (parsed.channelFilter) setChannelFilter(parsed.channelFilter);
        if (parsed.campaignFilter) setCampaignFilter(parsed.campaignFilter);
        if (parsed.segmentFilter) setSegmentFilter(parsed.segmentFilter);
        if (parsed.customerFilter) setCustomerFilter(parsed.customerFilter);        if (parsed.contatoFilter) setContatoFilter(parsed.contatoFilter);
        if (parsed.periodoFilter) setPeriodoFilter(parsed.periodoFilter);
        if (parsed.periodoInicio) setPeriodoInicio(parsed.periodoInicio);
        if (parsed.periodoFim) setPeriodoFim(parsed.periodoFim);

        if (parsed.sortKey) setSortKey(parsed.sortKey);
        if (parsed.sortDir) setSortDir(parsed.sortDir);
        if (parsed.viewMode) setViewMode(parsed.viewMode);
        if (parsed.activeTab) setActiveTab(parsed.activeTab);
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    try {
      const saved = localStorage.getItem('dashboardFilters');
      if (!saved) return;
      const parsed = JSON.parse(saved);
      if (parsed.period) setDashboardPeriod(parsed.period);
      if (parsed.startDate) setDashboardStartDate(parsed.startDate);
      if (parsed.endDate) setDashboardEndDate(parsed.endDate);
      if (parsed.ownerFilter) setDashboardOwnerFilter(parsed.ownerFilter);
      if (parsed.segmentFilter) setDashboardSegmentFilter(parsed.segmentFilter);
      if (parsed.channelFilter) setDashboardChannelFilter(parsed.channelFilter);
      if (parsed.campaignFilter) setDashboardCampaignFilter(parsed.campaignFilter);
      if (parsed.followUpFilter) setDashboardFollowUpFilter(parsed.followUpFilter);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    const payload = {
      ownerFilter,
      statusFilter,
      urgencyFilter,
      temperatureFilter,
      slaFilter,
      emailEngagementFilter,
      searchTerm: searchInput,
      channelFilter,
      campaignFilter,
      segmentFilter,
      customerFilter,
      contatoFilter,
      periodoFilter,
      periodoInicio,
      periodoFim,
      sortKey,
      sortDir,
      viewMode,
      activeTab,
    };
    localStorage.setItem('leadFilters', JSON.stringify(payload));
  }, [ownerFilter, statusFilter, urgencyFilter, temperatureFilter, slaFilter, emailEngagementFilter, searchInput, channelFilter, campaignFilter, segmentFilter, customerFilter, contatoFilter, periodoFilter, periodoInicio, periodoFim, sortKey, sortDir, viewMode, activeTab]);

  useEffect(() => {
    const payload = {
      period: dashboardPeriod,
      startDate: dashboardStartDate,
      endDate: dashboardEndDate,
      ownerFilter: dashboardOwnerFilter,
      segmentFilter: dashboardSegmentFilter,
      channelFilter: dashboardChannelFilter,
      campaignFilter: dashboardCampaignFilter,
      followUpFilter: dashboardFollowUpFilter,
    };
    localStorage.setItem('dashboardFilters', JSON.stringify(payload));
  }, [dashboardPeriod, dashboardStartDate, dashboardEndDate, dashboardOwnerFilter, dashboardSegmentFilter, dashboardChannelFilter, dashboardCampaignFilter, dashboardFollowUpFilter]);

  useEffect(() => {
    if (!user) return;
    loadStats();
  }, [ownerFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user) return undefined;
    const interval = setInterval(() => {
      if (document.hidden) return;
      if (showLeadModal || showProfileModal || savingLead) return;
      loadLeads();
      loadStats();
    }, 30 * 1000); // Aumentado para 30 segundos para reduzir piscadas e distrações
    return () => clearInterval(interval);
  }, [user, showLeadModal, showProfileModal, savingLead]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportCsv = () => {
    if (!filteredLeads.length) {
      showToast('Nenhum lead para exportar', 'error');
      return;
    }
    const cleanDigits = (value) => String(value || '').replace(/\D/g, '');
    const cleanEmail = (value) => String(value || '').trim().toLowerCase();
    const normalizeContactPhone = (value) => {
      const digits = cleanDigits(value);
      if (!digits) return '';
      if (digits.length === 13 && digits.startsWith('55')) return digits;
      if (digits.length === 12 && digits.startsWith('55')) return digits;
      if (digits.length === 11) return digits;
      if (digits.length === 10) return digits;
      if (digits.length > 11 && digits.startsWith('55')) return digits.slice(0, 13);
      return digits.slice(0, 11);
    };

    const cleanRows = filteredLeads
      .map((l) => ({
        Nome: String(l.name || '').trim(),
        Empresa: String(l.company || '').trim(),
        Email: cleanEmail(l.email),
        Telefone: normalizeContactPhone(l.phone),
        'Telefone 2': normalizeContactPhone(l.phone2),
        Responsável: String(l.owner || l.responsible_name || '').trim(),
        Status: String(l.status || '').trim(),
        'Tipo de lead': rotuloTipoDeLead(l.segment),
        Campanha: String(l.campaign || '').trim(),
        Fonte: String(l.source || '').trim(),
      }))
      .filter((row) => row.Email || row.Telefone || row['Telefone 2']);

    if (!cleanRows.length) {
      showToast('Nenhum contato válido com email ou telefone para exportar', 'error');
      return;
    }

    const ws = XLSX.utils.json_to_sheet(cleanRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Contatos');
    XLSX.writeFile(wb, `contatos-limpos-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const filteredLeads = useMemo(() => {
    let base = leads.map((l) => ({
      ...l,
      // normaliza possíveis campos de owner vindos da planilha/API
      _ownerId: l.ownerId || l.user_id || l.userId || l.owner_id,
      _status: (l.status || '').toLowerCase(),
    }));
    if (ownerFilter === 'me') {
      base = base.filter((l) => {
        const oid = l._ownerId ? String(l._ownerId) : '';
        const oname = normalizeOptionValue(l.owner || l.responsible_name);
        const uname = normalizeOptionValue(user?.name);
        if (oid) return oid === String(user?.id);
        return uname && (oname === uname || oname.includes(uname) || uname.includes(oname));
      });
    } else if (ownerFilter === 'unassigned') {
      base = base.filter((l) => {
        const oid = l._ownerId ? String(l._ownerId) : '';
        const oname = (l.owner || l.responsible_name || '').trim();
        return !oid && !oname;
      });
    } else if (ownerFilter !== 'all') {
      const targetUser = users.find((u) => String(u.id) === String(ownerFilter));
      const targetName = normalizeOptionValue(targetUser?.name);
      const targetId = String(ownerFilter);
      base = base.filter((l) => {
        const oid = l._ownerId ? String(l._ownerId) : '';
        const oname = normalizeOptionValue(l.owner || l.responsible_name);
        if (oid) return oid === targetId;
        if (targetName) return oname === targetName || oname.includes(targetName) || targetName.includes(oname);
        // fallback: compare owner name/id with raw filter value
        return oname === targetId.toLowerCase();
      });
    }

    if (statusFilter !== 'todos') {
      base = base.filter((l) => l._status === statusFilter);
    }

    if (temperatureFilter === 'esfriou') {
      base = base.filter((l) => !!l.funil_etapa && !!l.funil_esfriou);
    } else if (temperatureFilter === 'sem_marcacao') {
      base = base.filter((l) => !l.funil_etapa);
    } else if (temperatureFilter !== 'all') {
      base = base.filter((l) => temperaturaDoCard(l) === temperatureFilter);
    }

    if (slaFilter !== 'all') {
      base = base.filter((l) => normalizeOptionValue(l.sla_status) === slaFilter);
    }

    if (emailEngagementFilter !== 'all') {
      base = base.filter((l) => {
        const engagement = getLeadEmailEngagement(l);
        if (emailEngagementFilter === 'engaged') return engagement.hasAny;
        if (emailEngagementFilter === 'opened') return engagement.hasOpen;
        if (emailEngagementFilter === 'clicked') return engagement.hasClick;
        if (emailEngagementFilter === 'unsubscribed') return engagement.unsubscribed;
        return true;
      });
    }

    if (segmentFilter !== 'all') {
      base = base.filter((l) => {
        const norm = normalizeOptionValue(l.segment);
        if (segmentFilter === 'montador') return norm.includes('montador');
        if (segmentFilter === 'usuario_final') return norm === 'usuario final';
        return norm === segmentFilter;
      });
    }

    // Contato faltando. `sem_telefone` inclui quem tambem nao tem e-mail;
    // `sem_nada` e o subconjunto de quem nao tem forma nenhuma de ser
    // contatado — 172 leads em 23/09/2026.
    // Periodo por movimentacao — o lead entra pela ultima vez que foi
    // trabalhado, nao por quando chegou. Decisao dele, 23/09/2026.
    if (periodoFilter !== 'all') {
      const agora = new Date();
      base = base.filter((l) => leadDentroDoPeriodo(l, periodoFilter, periodoInicio, periodoFim, agora));
    }

    if (contatoFilter !== 'all') {
      const dig = (v) => String(v || '').replace(/\D/g, '').length;
      const semTel = (l) => dig(l.phone) < 8 && dig(l.phone2) < 8;
      const semMail = (l) => !String(l.email || '').trim();
      if (contatoFilter === 'sem_telefone') base = base.filter(semTel);
      if (contatoFilter === 'sem_email') base = base.filter(semMail);
      if (contatoFilter === 'sem_nada') base = base.filter((l) => semTel(l) && semMail(l));
    }

    if (customerFilter !== 'all') {
      const wantsCustomer = customerFilter === 'customer';
      base = base.filter((l) => Boolean(l.is_customer) === wantsCustomer);
    }

    if (channelFilter !== 'all') {
      base = base.filter((l) => String(l.channel_id || '') === String(channelFilter));
    }

    if (campaignFilter.trim()) {
      const termCamp = campaignFilter.toLowerCase();
      base = base.filter((l) => (l.campaign || '').toLowerCase().includes(termCamp));
    }

    if (urgencyFilter !== 'all') {
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      base = base.filter((l) => {
        if (!l.next_contact) return false;
        const d = dataDeCalendario(l.next_contact) || new Date(NaN);
        if (Number.isNaN(d.getTime())) return false;
        const target = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
        const today = now.getTime();
        const diffDays = (target - today) / (1000 * 60 * 60 * 24);
        if (urgencyFilter === 'overdue') return diffDays < 0;
        if (urgencyFilter === 'today') return diffDays === 0;
        if (urgencyFilter === 'next3') return diffDays >= 0 && diffDays <= 3;
        return true;
      });
    }

    const term = searchTerm.trim().toLowerCase();
    if (term) {
      base = base.filter((l) => {
        return (
          (l.name || '').toLowerCase().includes(term) ||
          (l.company || '').toLowerCase().includes(term) ||
          (l.email || '').toLowerCase().includes(term) ||
          (l.phone || '').toLowerCase().includes(term) ||
          (l.phone2 || '').toLowerCase().includes(term) ||
          (l.owner || l.responsible_name || '').toLowerCase().includes(term) ||
          (l.campaign || '').toLowerCase().includes(term) ||
          (l.notes || '').toLowerCase().includes(term)
        );
      });
    }
    return base;
  }, [leads, ownerFilter, statusFilter, urgencyFilter, temperatureFilter, slaFilter, emailEngagementFilter, user?.id, searchTerm, channelFilter, campaignFilter, segmentFilter, customerFilter, contatoFilter, periodoFilter, periodoInicio, periodoFim]);


  // Leads e Orcamentos ordenam pela MESMA regra. Antes cada tela tinha a sua,
  // e a de Leads tratava data vazia como zero — o que jogava lead sem proximo
  // contato para o topo da fila de urgencia, exatamente ao contrario.
  const sorter = useCallback(
    (a, b) => {
      const tipo = TIPO_POR_CAMPO_LEAD[sortKey] || 'texto';
      const campo = sortKey === 'created_at'
        ? ((lead) => lead.created_at || lead.first_contact || lead.updated_at)
        : (sortKey === 'channel_name' ? ((lead) => lead.channel_name || lead.channel || '') : sortKey);
      return compararParaOrdenar(a, b, campo, tipo, sortDir);
    },
    [sortDir, sortKey]
  );

  // O cabecalho e os dois seletores mexem no MESMO estado: clicar na coluna
  // muda o que o seletor mostra, e vice-versa. Sao duas portas para a mesma
  // sala, nao dois controles que disputam.
  const aplicarOrdenacaoLead = useCallback((chave, direcao) => {
    setSortKey(chave);
    setSortDir(direcao);
  }, []);

  const displayedLeads = useMemo(() => {
    const clone = [...filteredLeads];
    return clone.sort(sorter);
  }, [filteredLeads, sorter]);

  useEffect(() => {
    setVisibleCount(20);
    // O Kanban volta ao inicio junto: trocar o filtro e comecar outra leitura.
    setKanbanVisivel({});
  }, [
    ownerFilter,
    statusFilter,
    urgencyFilter,
    temperatureFilter,
    slaFilter,
    emailEngagementFilter,
    segmentFilter,
    customerFilter,
    contatoFilter,
    periodoFilter,
    periodoInicio,
    periodoFim,
    channelFilter,
    campaignFilter,
    sortKey,
    sortDir,
    searchTerm,
  ]);

  const canEditLead = useCallback(
    (lead) => {
      if (isAdmin) return true;
      const leadOwnerId = lead._ownerId || lead.ownerId || lead.user_id || lead.userId;
      if (leadOwnerId && String(leadOwnerId) === String(user?.id)) return true;
      const ownerName = (lead.owner || lead.responsible_name || '').toLowerCase().trim();
      const userName = (user?.name || '').toLowerCase().trim();
      return ownerName && userName && ownerName === userName;
    },
    [isAdmin, user?.id, user?.name]
  );

  const canReassignLead = useCallback(
    (lead) => {
      if (isAdmin) return true;
      const leadOwnerId = lead._ownerId || lead.ownerId || lead.user_id || lead.userId;
      const isOwner = leadOwnerId && String(leadOwnerId) === String(user?.id);
      const ownerName = (lead.owner || lead.responsible_name || '').toLowerCase().trim();
      const userName = (user?.name || '').toLowerCase().trim();
      const nameOwnerMatch = ownerName && userName && ownerName === userName;
      if (isOwner || nameOwnerMatch) return true;
      return !lead.is_private;
    },
    [isAdmin, user?.id, user?.name]
  );

  const agendaBase = useMemo(() => {
    return filteredLeads
      .filter((lead) => lead.next_contact)
      .map((lead) => ({ ...lead, _nextDate: (dataDeCalendario(lead.next_contact) || new Date(NaN)) }))
      .filter((lead) => !Number.isNaN(lead._nextDate.getTime()))
      .sort((a, b) => a._nextDate - b._nextDate);
  }, [filteredLeads]);

  const agendaStats = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return agendaBase.reduce(
      (acc, lead) => {
        const date = (dataDeCalendario(lead.next_contact) || new Date(NaN));
        if (Number.isNaN(date.getTime())) return acc;
        const target = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
        const today = now.getTime();
        const diffDays = (target - today) / (1000 * 60 * 60 * 24);
        if (diffDays < 0) acc.overdue += 1;
        else if (diffDays === 0) acc.today += 1;
        else if (diffDays > 0 && diffDays <= 3) acc.next3 += 1;
        acc.total += 1;
        return acc;
      },
      { total: 0, overdue: 0, today: 0, next3: 0 }
    );
  }, [agendaBase]);

  const statsSegments = useMemo(() => {
    const bySegment = {};
    let empresas = 0;
    leads.filter((l) => normalizeOptionValue(l.segment) !== 'concorrente').forEach((l) => {
      if (l.company) empresas += 1;
      const seg = l.segment || '';
      if (!bySegment[seg]) bySegment[seg] = 0;
      bySegment[seg] += 1;
    });
    return { empresas, bySegment };
  }, [leads]);

  const agendaLeads = useMemo(() => {
    return showAllAgenda ? agendaBase : agendaBase.slice(0, 5);
  }, [agendaBase, showAllAgenda]);
  const localStats = useMemo(() => {
    return buildStatsSummary(filteredLeads);
  }, [filteredLeads]);

  // Base do Dashboard SEM o recorte de data: todos os outros filtros aplicados.
  // E o que alimenta os graficos de evolucao mensal.
  const dashboardLeadsBase = useMemo(() => {
    const now = new Date();
    const opts = {
      users,
      segmentFilter: dashboardSegmentFilter,
      ownerFilter: dashboardOwnerFilter,
      channelFilter: dashboardChannelFilter,
      campaignFilter: dashboardCampaignFilter,
      followUpFilter: dashboardFollowUpFilter,
      now,
    };
    return leads.filter((lead) => leadPassaFiltrosDoDashboard(lead, opts));
  }, [leads, users, dashboardOwnerFilter, dashboardSegmentFilter, dashboardChannelFilter, dashboardCampaignFilter, dashboardFollowUpFilter]);

  // A base acima, agora com o recorte de data. E o que alimenta os cartoes de
  // numero e todo grafico que NAO e de evolucao mensal — comportamento igual
  // ao de antes desta separacao.
  const dashboardFilteredLeads = useMemo(() => {
    const now = new Date();
    const daysByPeriod = {
      '30d': 30,
      '90d': 90,
      '6m': 183,
      '12m': 365,
    };

    return dashboardLeadsBase.filter((lead) => {
      const baseDate = dataDeCalendario(lead.created_at || lead.first_contact || lead.next_contact);
      if (dashboardPeriod === 'custom') {
        if (!baseDate) return false;
        const leadTime = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate()).getTime();
        const startTime = dashboardStartDate ? new Date(`${dashboardStartDate}T00:00:00`).getTime() : null;
        const endTime = dashboardEndDate ? new Date(`${dashboardEndDate}T23:59:59`).getTime() : null;
        if (startTime && leadTime < startTime) return false;
        if (endTime && leadTime > endTime) return false;
      } else if (dashboardPeriod !== 'all') {
        const periodDays = daysByPeriod[dashboardPeriod];
        if (!baseDate) return false;
        const diffDays = (now.getTime() - baseDate.getTime()) / (1000 * 60 * 60 * 24);
        if (diffDays > periodDays) return false;
      }
      return true;
    });
  }, [dashboardLeadsBase, dashboardPeriod, dashboardStartDate, dashboardEndDate]);

  const dashboardLocalStats = useMemo(() => buildStatsSummary(dashboardFilteredLeads), [dashboardFilteredLeads]);

  // Os mesmos numeros dos cartoes acima, separados por origem. Nao substitui e
  // nao esconde nada: o total continua sendo o total. Serve para nao comparar
  // prospeccao ativa com quem levantou a mao dentro de um percentual so.
  const dashboardStatsPorOrigem = useMemo(() => {
    const demanda = [];
    const prospeccao = [];
    dashboardFilteredLeads.forEach((lead) => {
      if (classificarOrigemDoLead(lead) === 'prospeccao') prospeccao.push(lead);
      else demanda.push(lead);
    });
    return {
      demanda: buildStatsSummary(demanda),
      prospeccao: buildStatsSummary(prospeccao),
    };
  }, [dashboardFilteredLeads]);

  // Pipeline Ativo NAO obedece o filtro de periodo — decisao dele, 23/09/2026.
  //
  // Pipeline e ESTOQUE, nao fluxo: "quanto tem de proposta na mesa" nao depende
  // de quando o lead entrou. Medido em 23/09/2026, com o filtro de periodo o
  // cartao mostrava R$ 18.471,81 de 7 leads, enquanto havia R$ 169.489,86 em 42
  // leads em proposta/negociacao — 11% do que existe de verdade. Um lead do
  // Meta Ads que entrou em marco e tem proposta aberta hoje ficava de fora.
  //
  // Os outros filtros do Dashboard (responsavel, perfil, canal, campanha)
  // continuam valendo: o que sai da conta e SO o periodo.
  const dashboardPipelineAberto = useMemo(
    () => buildStatsSummary(dashboardLeadsBase),
    [dashboardLeadsBase]
  );

  // A tabela "Por origem" diz que traz os mesmos numeros dos cartoes. Para essa
  // frase continuar verdadeira, a linha "Em negociacao" tem que sair da mesma
  // base do cartao — sem periodo —, e nao da base filtrada.
  const dashboardPipelinePorOrigem = useMemo(() => {
    const demanda = [];
    const prospeccao = [];
    dashboardLeadsBase.forEach((lead) => {
      if (classificarOrigemDoLead(lead) === 'prospeccao') prospeccao.push(lead);
      else demanda.push(lead);
    });
    return {
      demanda: buildStatsSummary(demanda),
      prospeccao: buildStatsSummary(prospeccao),
    };
  }, [dashboardLeadsBase]);

  const dashboardData = useMemo(() => {
    const channelMap = new Map();
    const campaignMap = new Map();
    const segmentMap = new Map();
    const temperatureMap = new Map();
    const monthlyMap = new Map();
    const convertedMonthlyMap = new Map();
    const pipelineMonthlyMap = new Map();
    const statusMap = new Map();
    const overdueByOwnerMap = new Map();
    const source = dashboardFilteredLeads;
    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const sixMonths = [];

    for (let offset = 5; offset >= 0; offset -= 1) {
      const date = new Date(today.getFullYear(), today.getMonth() - offset, 1);
      const key = monthKey(date);
      monthlyMap.set(key, 0);
      convertedMonthlyMap.set(key, 0);
      pipelineMonthlyMap.set(key, 0);
      sixMonths.push(key);
    }

    let customers = 0;
    let leadsThisMonth = 0;
    let overdueFollowups = 0;
    let funilMarcados = 0;
    let funilEsfriou = 0;
    const esfriouPorDonoMap = new Map();

    source.forEach((lead) => {
      const channel = lead.channel_name || lead.campaign || 'Sem canal';
      const campaign = lead.campaign || 'Sem campanha';
      const segment = lead.segment || 'sem_perfil';
      const status = lead.status || 'sem_status';
      const statusNormalized = normalizeOptionValue(status);
      const temperatureNormalized = temperaturaDoCard(lead) || 'frio';
      const createdDate = dataDeCalendario(lead.created_at || lead.first_contact);
      const nextDate = dataDeCalendario(lead.next_contact);

      channelMap.set(channel, (channelMap.get(channel) || 0) + 1);
      campaignMap.set(campaign, (campaignMap.get(campaign) || 0) + 1);
      segmentMap.set(segment, (segmentMap.get(segment) || 0) + 1);
      // Chaveia pelo status NORMALIZADO para casar com os values de
      // STATUS_OPTIONS na montagem do funil, logo abaixo.
      statusMap.set(statusNormalized || 'sem_status', (statusMap.get(statusNormalized || 'sem_status') || 0) + 1);
      temperatureMap.set(temperatureNormalized, (temperatureMap.get(temperatureNormalized) || 0) + 1);

      if (lead.is_customer) customers += 1;
      // Os tres mapas mensais NAO sao preenchidos aqui: saem do laco de baixo,
      // que roda sobre a base sem recorte de data.
      if (createdDate && createdDate >= monthStart) leadsThisMonth += 1;
      // Proximo contato so com o dia vence quando o DIA passa (igual ao filtro
      // "Vencidos" da lista); com hora, quando a hora passa (09/10/2026).
      const soDia = /^\d{4}-\d{2}-\d{2}$/.test(String(lead.next_contact || '').trim());
      const limiteVencido = soDia ? new Date(today.getFullYear(), today.getMonth(), today.getDate()) : today;
      if (nextDate && nextDate < limiteVencido) overdueFollowups += 1;
      if (normalizeOptionValue(lead.sla_status) === 'overdue') {
        overdueByOwnerMap.set(lead.owner || lead.responsible_name || 'Sem responsável', (overdueByOwnerMap.get(lead.owner || lead.responsible_name || 'Sem responsável') || 0) + 1);
      }
      if (lead.funil_etapa) {
        funilMarcados += 1;
        if (lead.funil_esfriou) {
          funilEsfriou += 1;
          const dono = lead.owner || lead.responsible_name || 'Sem responsável';
          esfriouPorDonoMap.set(dono, (esfriouPorDonoMap.get(dono) || 0) + 1);
        }
      }
    });

    // Evolucao mensal. Em 09/09/2026 este laco rodava sobre `dashboardLeadsBase`,
    // ou seja, IGNORAVA o recorte de data — mudanca feita para resolver os quatro
    // meses zerados que apareciam com o padrao de 30 dias.
    //
    // REVERTIDO em 10/09/2026, por decisao do dono do produto. Ignorar a data
    // trazia para dentro do grafico os 1.700 leads herdados de um vendedor que
    // nao trabalha mais na empresa (origens "Ferramenta de Captura" e "Planilha
    // Victor", 78% da base, importados entre janeiro e julho). Medido: o grafico
    // "Entradas por mes" mostrava 323 em abril contra 31 reais, e desenhava uma
    // captacao DESPENCANDO de 323 para 58 quando ela na verdade SUBIU de 31 para
    // 176. Mes zerado e um problema menor do que grafico com o sinal invertido.
    dashboardFilteredLeads.forEach((lead) => {
      const createdDate = dataDeCalendario(lead.created_at || lead.first_contact);
      if (!createdDate) return;
      const key = monthKey(createdDate);
      if (!monthlyMap.has(key)) return;
      const statusNormalized = normalizeOptionValue(lead.status || 'sem_status');
      const leadValue = Number(lead.value || 0);
      monthlyMap.set(key, (monthlyMap.get(key) || 0) + 1);
      if (statusNormalized === 'ganho') {
        convertedMonthlyMap.set(key, (convertedMonthlyMap.get(key) || 0) + leadValue);
      }
      if (['negociacao', 'proposta'].includes(statusNormalized)) {
        pipelineMonthlyMap.set(key, (pipelineMonthlyMap.get(key) || 0) + leadValue);
      }
    });

    const sortEntries = (map) =>
      Array.from(map.entries())
        .map(([label, value]) => ({ label, value }))
        .sort((a, b) => b.value - a.value);

    // O ticket medio dividia a soma por TODOS os ganhos, inclusive os que estao
    // com valor zero. Medido na producao em 14/09/2026: 29 ganhos, apenas 7 com
    // valor, R$ 10.847 somados. A conta antiga dava R$ 374 — que nao era o
    // ticket de nada, era a soma diluida em registros que ninguem preencheu.
    // Dividindo por quem tem valor da R$ 1.550. A quantidade de ganhos sem
    // valor vai escrita no proprio cartao, para o numero nunca ser lido sozinho.
    const avgTicket = dashboardLocalStats.ganhosComValor
      ? dashboardLocalStats.valorTotal / dashboardLocalStats.ganhosComValor
      : 0;
    const monthlyEvolution = sixMonths.map((key) => ({
      label: monthLabel(key),
      value: monthlyMap.get(key) || 0,
    }));
    const convertedEvolution = sixMonths.map((key) => ({
      label: monthLabel(key),
      value: convertedMonthlyMap.get(key) || 0,
    }));
    const pipelineEvolution = sixMonths.map((key) => ({
      label: monthLabel(key),
      value: pipelineMonthlyMap.get(key) || 0,
    }));
    const conversionByTemperature = LEAD_TEMPERATURE_OPTIONS.map((item) => {
      const totalByTemp = source.filter((lead) => temperaturaDoCard(lead) === item.value);
      const gains = totalByTemp.filter((lead) => normalizeOptionValue(lead.status) === 'ganho').length;
      return {
        label: item.label,
        value: totalByTemp.length ? Math.round((gains / totalByTemp.length) * 100) : 0,
      };
    });

    return {
      source,
      leadsThisMonth,
      customers,
      prospects: Math.max(source.length - customers, 0),
      overdueFollowups,
      avgTicket,
      byChannel: sortEntries(channelMap),
      byCampaign: sortEntries(campaignMap),
      bySegment: sortEntries(segmentMap).map((item) => ({
        ...item,
        // 'sem_perfil' e o valor de fallback usado acima; em SEGMENT_OPTIONS
        // o mesmo caso e representado por value '', entao a busca nunca casava
        // e o token cru aparecia na tela.
        label:
          item.label === 'sem_perfil'
            ? 'Sem perfil'
            : SEGMENT_OPTIONS.find((opt) => opt.value === item.label)?.label || item.label,
      })),
      byTemperature: LEAD_TEMPERATURE_OPTIONS.map((item) => ({
        label: item.label,
        value: temperatureMap.get(item.value) || 0,
      })),
      // Funil em ORDEM DE FUNIL, nao por tamanho. Ordenar por volume
      // transformava o funil em ranking (Perdido aparecia entre Proposta e
      // Novo). Status zerado permanece visivel, com zero.
      byStatus: [
        ...STATUS_OPTIONS.map((opt) => ({
          label: opt.label,
          value: statusMap.get(opt.value) || 0,
        })),
        ...Array.from(statusMap.entries())
          .filter(([chave]) => !STATUS_OPTIONS.some((opt) => opt.value === chave))
          .map(([chave, valor]) => ({
            label: chave === 'sem_status' ? 'Sem status' : chave,
            value: valor,
          })),
      ],
      overdueByOwner: sortEntries(overdueByOwnerMap),
      funilMarcados,
      funilEsfriou,
      esfriouPorDono: sortEntries(esfriouPorDonoMap),
      conversionByTemperature,
      monthlyEvolution,
      convertedEvolution,
      pipelineEvolution,
    };
  }, [dashboardFilteredLeads, dashboardLocalStats]);

  // Mesma separacao dos leads: base sem data para os graficos mensais...
  const dashboardBudgetsBase = useMemo(() => {
    const selectedChannelName = channels.find((c) => String(c.id) === String(dashboardChannelFilter))?.name || '';
    const opts = {
      segmentFilter: dashboardSegmentFilter,
      ownerFilter: dashboardOwnerFilter,
      campaignFilter: dashboardCampaignFilter,
      channelFilter: dashboardChannelFilter,
      selectedChannelName,
    };
    return budgets.filter((budget) => orcamentoPassaFiltrosDoDashboard(budget, opts));
  }, [budgets, channels, dashboardSegmentFilter, dashboardOwnerFilter, dashboardCampaignFilter, dashboardChannelFilter]);

  // ...e a mesma base com o recorte de data para os cartoes e os demais graficos.
  const dashboardMediaBudgets = useMemo(() => {
    const now = new Date();
    const daysByPeriod = {
      '30d': 30,
      '90d': 90,
      '6m': 183,
      '12m': 365,
    };

    return dashboardBudgetsBase.filter((budget) => {
      const baseDate = dataDeCalendario(budget.requested_at || budget.created_at || budget.sent_at || budget.closed_at);
      if (dashboardPeriod === 'custom') {
        if (!baseDate) return false;
        const budgetTime = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate()).getTime();
        const startTime = dashboardStartDate ? new Date(`${dashboardStartDate}T00:00:00`).getTime() : null;
        const endTime = dashboardEndDate ? new Date(`${dashboardEndDate}T23:59:59`).getTime() : null;
        if (startTime && budgetTime < startTime) return false;
        if (endTime && budgetTime > endTime) return false;
      } else if (dashboardPeriod !== 'all') {
        const periodDays = daysByPeriod[dashboardPeriod];
        if (!baseDate) return false;
        const diffDays = (now.getTime() - baseDate.getTime()) / (1000 * 60 * 60 * 24);
        if (diffDays > periodDays) return false;
      }
      return true;
    });
  }, [dashboardBudgetsBase, dashboardPeriod, dashboardStartDate, dashboardEndDate]);

  const dashboardBudgetStats = useMemo(
    () => buildBudgetStatsSummary(dashboardMediaBudgets),
    [dashboardMediaBudgets]
  );

  const dashboardAdSpendFiltered = useMemo(() => {
    const now = new Date();
    const daysByPeriod = {
      '30d': 30,
      '90d': 90,
      '6m': 183,
      '12m': 365,
    };
    const selectedChannelName = channels.find((c) => String(c.id) === String(dashboardChannelFilter))?.name || '';

    return adSpendItems.filter((entry) => {
      const baseDate = dataDeCalendario(entry.date || entry.created_at);
      if (dashboardPeriod === 'custom') {
        if (!baseDate) return false;
        const spendTime = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate()).getTime();
        const startTime = dashboardStartDate ? new Date(`${dashboardStartDate}T00:00:00`).getTime() : null;
        const endTime = dashboardEndDate ? new Date(`${dashboardEndDate}T23:59:59`).getTime() : null;
        if (startTime && spendTime < startTime) return false;
        if (endTime && spendTime > endTime) return false;
      } else if (dashboardPeriod !== 'all') {
        const periodDays = daysByPeriod[dashboardPeriod];
        if (!baseDate) return false;
        const diffDays = (now.getTime() - baseDate.getTime()) / (1000 * 60 * 60 * 24);
        if (diffDays > periodDays) return false;
      }

      if (dashboardCampaignFilter.trim()) {
        const term = dashboardCampaignFilter.toLowerCase();
        if (!String(entry.campaign || '').toLowerCase().includes(term)) return false;
      }

      if (dashboardChannelFilter !== 'all' && selectedChannelName) {
        const entryChannel = entry.channel_name || entry.platform || '';
        if (normalizeOptionValue(entryChannel) !== normalizeOptionValue(selectedChannelName)) {
          return false;
        }
      }

      return true;
    });
  }, [adSpendItems, channels, dashboardPeriod, dashboardStartDate, dashboardEndDate, dashboardCampaignFilter, dashboardChannelFilter]);

  const dashboardMediaData = useMemo(() => {
    const normalize = (value) => normalizeOptionValue(value);
    const groupMap = new Map();
    let leadsComValor = 0;
    let leadsGanhos = 0;
    let orcamentosNaConta = 0;
    const spendByMonth = new Map();
    const leadsByMonth = new Map();
    const estimatedByMonth = new Map();
    const closedByMonth = new Map();
    const today = new Date();
    const sixMonths = [];

    for (let offset = 5; offset >= 0; offset -= 1) {
      const date = new Date(today.getFullYear(), today.getMonth() - offset, 1);
      const key = monthKey(date);
      sixMonths.push(key);
      spendByMonth.set(key, 0);
      leadsByMonth.set(key, 0);
      estimatedByMonth.set(key, 0);
      closedByMonth.set(key, 0);
    }

    const getGroupKey = (entry) =>
      normalize(entry.channel_name) ||
      normalize(entry.platform) ||
      normalize(deriveMediaChannelLabel(entry.campaign)) ||
      'sem-canal';
    const ensureGroup = (entry) => {
      const key = getGroupKey(entry);
      const channelLabel = entry.channel_name || entry.platform || deriveMediaChannelLabel(entry.campaign) || 'Sem canal';
      if (!groupMap.has(key)) {
        groupMap.set(key, {
          key,
          channel: channelLabel,
          campaign: entry.campaign || '',
          platform: channelLabel,
          investment: 0,
          leads: 0,
          estimated: 0,
          closed: 0,
        });
      }
      return groupMap.get(key);
    };

    dashboardAdSpendFiltered.forEach((entry) => {
      const group = ensureGroup(entry);
      const amount = Number(entry.amount || 0);
      group.investment += amount;
      const date = dataDeCalendario(entry.date || entry.created_at);
      const key = date ? monthKey(date) : '';
      if (spendByMonth.has(key)) spendByMonth.set(key, (spendByMonth.get(key) || 0) + amount);
    });

    dashboardFilteredLeads.forEach((lead) => {
      const key =
        normalize(lead.channel_name) ||
        normalize(lead.source) ||
        normalize(deriveMediaChannelLabel(lead.campaign)) ||
        'sem-canal';
      const group = groupMap.get(key);
      if (!group) return;
      group.leads += 1;
      const leadValue = Number(lead.value || 0);
      const status = normalize(lead.status);
      const date = dataDeCalendario(lead.created_at || lead.first_contact);
      const month = date ? monthKey(date) : '';
      if (status !== 'perdido') {
        group.estimated += leadValue;
        if (estimatedByMonth.has(month)) estimatedByMonth.set(month, (estimatedByMonth.get(month) || 0) + leadValue);
      }
      if (status === 'ganho') {
        group.closed += leadValue;
        if (closedByMonth.has(month)) closedByMonth.set(month, (closedByMonth.get(month) || 0) + leadValue);
      }
      if (leadsByMonth.has(month)) leadsByMonth.set(month, (leadsByMonth.get(month) || 0) + 1);
      // Base do calculo, para o rodape do cartao. Medido em 09/09/2026: apenas
      // 9 dos 147 leads de Meta Ads em 30 dias tinham valor preenchido, e o
      // ROAS estimado saia em 11,85 sem dizer isso a ninguem.
      if (leadValue > 0) leadsComValor += 1;
      if (status === 'ganho') leadsGanhos += 1;
    });

    dashboardMediaBudgets.forEach((budget) => {
      const key =
        normalize(budget.channel_name) ||
        normalize(deriveMediaChannelLabel(budget.campaign)) ||
        'sem-canal';
      const group = groupMap.get(key);
      if (!group) return;
      // Quantos orcamentos de fato entram nesta aba. Medido em 09/09/2026:
      // ZERO de 1.189. Os grupos de midia sao montados apenas a partir dos
      // lancamentos de gasto, e o orcamento chega do ERP sem canal e sem
      // campanha. Sem canal, nao ha a que atribuir.
      orcamentosNaConta += 1;
      const status = normalize(budget.status);
      const budgetValue = Number(budget.budget_value || 0);
      const closedValue = Number(budget.closed_value || budget.budget_value || 0);
      const date = dataDeCalendario(budget.requested_at || budget.created_at || budget.closed_at);
      const month = date ? monthKey(date) : '';

      // CORRECAO: o status gravado e 'nao_feito' (underscore), como declarado em
      // BUDGET_STATUS_OPTIONS. A comparacao antiga usava 'nao feito' (espaco) e
      // nunca casava, entao orcamento nao feito entrava no valor estimado e
      // inflava o ROAS estimado do canal.
      if (!group.estimated && !['reprovado', 'nao_feito'].includes(status)) {
        group.estimated += budgetValue;
        if (estimatedByMonth.has(month)) {
          estimatedByMonth.set(month, (estimatedByMonth.get(month) || 0) + budgetValue);
        }
      }
      if (!group.closed && status === 'aprovado') {
        group.closed += closedValue;
        if (closedByMonth.has(month)) {
          closedByMonth.set(month, (closedByMonth.get(month) || 0) + closedValue);
        }
      }
    });

    const rows = Array.from(groupMap.values())
      .map((item) => ({
        ...item,
        cpl: item.leads ? item.investment / item.leads : 0,
        roasEstimated: item.investment ? item.estimated / item.investment : 0,
        roasClosed: item.investment ? item.closed / item.investment : 0,
      }))
      .sort((a, b) => b.investment - a.investment);

    const investment = rows.reduce((sum, item) => sum + item.investment, 0);
    const leadsGenerated = rows.reduce((sum, item) => sum + item.leads, 0);
    const estimatedReturn = rows.reduce((sum, item) => sum + item.estimated, 0);
    const closedReturn = rows.reduce((sum, item) => sum + item.closed, 0);

    return {
      rows,
      investment,
      leadsGenerated,
      campaignsWithSpend: rows.length,
      cpl: leadsGenerated ? investment / leadsGenerated : 0,
      estimatedReturn,
      closedReturn,
      roasEstimated: investment ? estimatedReturn / investment : 0,
      roasClosed: investment ? closedReturn / investment : 0,
      estimatedPerLead: leadsGenerated ? estimatedReturn / leadsGenerated : 0,
      closedPerLead: leadsGenerated ? closedReturn / leadsGenerated : 0,
      leadsComValor,
      leadsGanhos,
      orcamentosNaConta,
      orcamentosNoPeriodo: dashboardMediaBudgets.length,
      topPlatform: rows[0]?.platform || '-',
      spendVsLeads: sixMonths.map((key) => ({
        label: monthLabel(key),
        spend: spendByMonth.get(key) || 0,
        leads: leadsByMonth.get(key) || 0,
      })),
      roasEvolution: sixMonths.map((key) => {
        const spend = spendByMonth.get(key) || 0;
        const estimated = estimatedByMonth.get(key) || 0;
        const closed = closedByMonth.get(key) || 0;
        return {
          label: monthLabel(key),
          estimated: spend ? estimated / spend : 0,
          closed: spend ? closed / spend : 0,
        };
      }),
    };
  }, [dashboardAdSpendFiltered, dashboardFilteredLeads, dashboardMediaBudgets]);

  // O contador ao lado do nome da sub-aba diz o volume daquela area, para a
  // pessoa saber onde tem coisa antes de clicar.
  const DASHBOARD_PANES = useMemo(() => {
    const contadores = {
      leads: (dashboardLocalStats.total || 0).toLocaleString('pt-BR'),
      orcamentos: (dashboardBudgetStats.total || 0).toLocaleString('pt-BR'),
      midia: formatCurrencyBR(dashboardMediaData.investment || 0),
    };
    return DASHBOARD_PANES_BASE.map((pane) => ({ ...pane, contador: contadores[pane.value] }));
  }, [dashboardLocalStats, dashboardBudgetStats, dashboardMediaData]);

  const emktFilteredEvents = useMemo(() => {
    const now = new Date();
    const daysByPeriod = {
      '30d': 30,
      '90d': 90,
      '6m': 183,
      '12m': 365,
    };

    return emailEvents.filter((event) => {
      const baseDate = parseLeadDate(event.event_at || event.created_at);
      if (emktPeriod === 'custom') {
        if (!baseDate) return false;
        const eventTime = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate()).getTime();
        const startTime = emktStartDate ? new Date(`${emktStartDate}T00:00:00`).getTime() : null;
        const endTime = emktEndDate ? new Date(`${emktEndDate}T23:59:59`).getTime() : null;
        if (startTime && eventTime < startTime) return false;
        if (endTime && eventTime > endTime) return false;
      } else if (emktPeriod !== 'all') {
        const periodDays = daysByPeriod[emktPeriod];
        if (periodDays && baseDate) {
          const diff = now.getTime() - baseDate.getTime();
          if (diff > periodDays * 24 * 60 * 60 * 1000) return false;
        }
      }

      if (emktEventFilter !== 'all' && normalizeOptionValue(event.event_type) !== emktEventFilter) {
        return false;
      }

      if (emktCampaignFilter.trim()) {
        const term = emktCampaignFilter.toLowerCase();
        if (!(event.campaign_name || '').toLowerCase().includes(term)) return false;
      }

      if (emktSearch.trim()) {
        const term = emktSearch.toLowerCase();
        const lead = leads.find((item) => String(item.id) === String(event.lead_id));
        const haystack = [
          event.email,
          event.campaign_name,
          lead?.name,
          lead?.company,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(term)) return false;
      }

      return true;
    });
  }, [emailEvents, emktPeriod, emktStartDate, emktEndDate, emktEventFilter, emktCampaignFilter, emktSearch, leads]);

  const emktSummary = useMemo(() => {
    const campaigns = new Set();
    const impactedLeads = new Set();
    let opens = 0;
    let clicks = 0;
    let unsubscribes = 0;

    emktFilteredEvents.forEach((event) => {
      if (event.campaign_name) campaigns.add(event.campaign_name);
      if (event.lead_id) impactedLeads.add(String(event.lead_id));
      const type = normalizeOptionValue(event.event_type);
      if (type === 'open') opens += 1;
      if (type === 'click') clicks += 1;
      if (type === 'unsubscribe') unsubscribes += 1;
    });

    return {
      campaigns: campaigns.size,
      impactedLeads: impactedLeads.size,
      totalEvents: emktFilteredEvents.length,
      opens,
      clicks,
      unsubscribes,
      clickRate: opens ? Number(((clicks / opens) * 100).toFixed(1)) : 0,
      unsubscribeRate: opens ? Number(((unsubscribes / opens) * 100).toFixed(1)) : 0,
    };
  }, [emktFilteredEvents]);

  const emktEventEvolution = useMemo(() => {
    const bucket = new Map();
    emktFilteredEvents.forEach((event) => {
      const date = parseLeadDate(event.event_at || event.created_at);
      if (!date) return;
      const label = date.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });
      if (!bucket.has(label)) bucket.set(label, { label, opens: 0, clicks: 0, unsubscribes: 0 });
      const row = bucket.get(label);
      const type = normalizeOptionValue(event.event_type);
      if (type === 'open') row.opens += 1;
      if (type === 'click') row.clicks += 1;
      if (type === 'unsubscribe') row.unsubscribes += 1;
    });
    return Array.from(bucket.values());
  }, [emktFilteredEvents]);

  const emktCampaignRows = useMemo(() => {
    const bucket = new Map();
    emktFilteredEvents.forEach((event) => {
      const key = event.campaign_name || '(Sem campanha)';
      if (!bucket.has(key)) {
        bucket.set(key, {
          campaign: key,
          opens: 0,
          clicks: 0,
          unsubscribes: 0,
          leads: new Set(),
          lastEventAt: '',
        });
      }
      const row = bucket.get(key);
      const type = normalizeOptionValue(event.event_type);
      if (type === 'open') row.opens += 1;
      if (type === 'click') row.clicks += 1;
      if (type === 'unsubscribe') row.unsubscribes += 1;
      if (event.lead_id) row.leads.add(String(event.lead_id));
      const currentTime = new Date(event.event_at || event.created_at || 0).getTime();
      const previousTime = new Date(row.lastEventAt || 0).getTime();
      if (!row.lastEventAt || currentTime >= previousTime) row.lastEventAt = event.event_at || event.created_at || '';
    });

    return Array.from(bucket.values())
      .map((row) => ({
        campaign: row.campaign,
        opens: row.opens,
        clicks: row.clicks,
        unsubscribes: row.unsubscribes,
        impactedLeads: row.leads.size,
        clickRate: row.opens ? Number(((row.clicks / row.opens) * 100).toFixed(1)) : 0,
        lastEventAt: row.lastEventAt,
      }))
      .sort((a, b) => (b.opens + b.clicks) - (a.opens + a.clicks));
  }, [emktFilteredEvents]);

  const emktLeadRows = useMemo(() => {
    const bucket = new Map();
    emktFilteredEvents.forEach((event) => {
      const key = String(event.lead_id || event.email || '');
      if (!key) return;
      const lead = leads.find((item) => String(item.id) === String(event.lead_id));
      if (!bucket.has(key)) {
        bucket.set(key, {
          leadId: event.lead_id || '',
          name: lead?.name || '-',
          company: lead?.company || '-',
          email: event.email || lead?.email || '-',
          campaign: event.campaign_name || '-',
          opens: 0,
          clicks: 0,
          unsubscribes: 0,
          lastEventAt: '',
        });
      }
      const row = bucket.get(key);
      const type = normalizeOptionValue(event.event_type);
      if (type === 'open') row.opens += 1;
      if (type === 'click') row.clicks += 1;
      if (type === 'unsubscribe') row.unsubscribes += 1;
      if (event.campaign_name && row.campaign === '-') row.campaign = event.campaign_name;
      const currentTime = new Date(event.event_at || event.created_at || 0).getTime();
      const previousTime = new Date(row.lastEventAt || 0).getTime();
      if (!row.lastEventAt || currentTime >= previousTime) row.lastEventAt = event.event_at || event.created_at || '';
    });
    return Array.from(bucket.values()).sort((a, b) => new Date(b.lastEventAt || 0).getTime() - new Date(a.lastEventAt || 0).getTime());
  }, [emktFilteredEvents, leads]);

  // Representantes ja registrados, para sugerir no autocompletar.
  // Evita que a mesma pessoa vire varias grafias e suje a metrica.
  const representanteOptions = useMemo(() => {
    const set = new Map();
    budgets.forEach((b) => {
      const nome = String(b.representante || '').trim();
      if (nome) set.set(nome.toLowerCase(), nome);
    });
    return [...set.values()].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [budgets]);

  const budgetFilteredItems = useMemo(() => {
    const now = new Date();
    const daysByPeriod = {
      '30d': 30,
      '90d': 90,
      '6m': 183,
      '12m': 365,
    };

    return budgets.filter((budget) => {
      const baseDate = dataDeCalendario(budget.requested_at || budget.created_at || budget.sent_at || budget.closed_at);
      if (budgetPeriod === 'custom') {
        if (!baseDate) return false;
        const budgetTime = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate()).getTime();
        const startTime = budgetStartDate ? new Date(`${budgetStartDate}T00:00:00`).getTime() : null;
        const endTime = budgetEndDate ? new Date(`${budgetEndDate}T23:59:59`).getTime() : null;
        if (startTime && budgetTime < startTime) return false;
        if (endTime && budgetTime > endTime) return false;
      } else if (budgetPeriod !== 'all') {
        const periodDays = daysByPeriod[budgetPeriod];
        if (!baseDate) return false;
        const diffDays = (now.getTime() - baseDate.getTime()) / (1000 * 60 * 60 * 24);
        if (diffDays > periodDays) return false;
      }

      if (budgetStatusFilter !== 'all' && normalizeOptionValue(budget.status) !== normalizeOptionValue(budgetStatusFilter)) {
        return false;
      }
      if (budgetOwnerFilter !== 'all') {
        const ownerId = String(budget.owner_id || '');
        if (budgetOwnerFilter.startsWith('exceto:')) {
          // "Todos, exceto X". Orcamento sem vendedor definido CONTINUA aparecendo:
          // ele nao e do X, e o filtro promete apenas tirar o X.
          if (ownerId === budgetOwnerFilter.slice('exceto:'.length)) return false;
        } else if (ownerId !== String(budgetOwnerFilter)) {
          return false;
        }
      }
      if (budgetEstimatorFilter !== 'all' && String(budget.estimator_id || '') !== String(budgetEstimatorFilter)) {
        return false;
      }
      if (
        budgetLossReasonFilter !== 'all' &&
        normalizeOptionValue(budget.loss_reason) !== normalizeOptionValue(budgetLossReasonFilter)
      ) {
        return false;
      }
      if (budgetRepresentanteFilter.trim()) {
        const alvo = budgetRepresentanteFilter.trim().toLowerCase();
        if (!String(budget.representante || '').toLowerCase().includes(alvo)) return false;
      }

      if (budgetSearch.trim()) {
        const term = budgetSearch.toLowerCase();
        const haystack = [
          budget.client_name,
          budget.company,
          budget.owner_name,
          budget.estimator_name,
          budget.representante,
          budget.campaign,
          budget.channel_name,
          budget.notes,
        ]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [
    budgets,
    budgetPeriod,
    budgetStartDate,
    budgetEndDate,
    budgetStatusFilter,
    budgetOwnerFilter,
    budgetEstimatorFilter,
    budgetLossReasonFilter,
    budgetSearch,
    budgetRepresentanteFilter,
  ]);

  const aplicarOrdenacaoOrcamento = useCallback((chave, direcao) => {
    setBudgetSortKey(chave);
    setBudgetSortDir(direcao);
  }, []);

  // A tabela consome esta lista, nao a filtrada. Ordenar e passo separado do
  // filtrar de proposito: assim trocar a ordem nao recalcula o filtro.
  const budgetSortedItems = useMemo(() => {
    const tipo = budgetSortKey === 'requested_at'
      ? 'data'
      : (budgetSortKey === 'budget_value' || budgetSortKey === 'closed_value' ? 'numero' : 'texto');
    const campo = budgetSortKey === 'requested_at'
      ? ((b) => b.requested_at || b.created_at || b.sent_at || b.closed_at)
      : budgetSortKey;
    return [...budgetFilteredItems].sort((a, b) => compararParaOrdenar(a, b, campo, tipo, budgetSortDir));
  }, [budgetFilteredItems, budgetSortKey, budgetSortDir]);

  // Lista de orcamentos (22/10/2026): mostra 20 linhas e o botao "Ver mais"
  // acrescenta mais 20. Pedido do Leandro: a lista inteira aberta ocupava a
  // pagina toda. Volta para 20 quando muda filtro, busca ou ordem; NAO volta
  // quando a lista e recarregada (editar um orcamento nao fecha o que ja abriu).
  const [budgetVisiveis, setBudgetVisiveis] = useState(ORCAMENTOS_POR_PAGINA);
  useEffect(() => {
    setBudgetVisiveis(ORCAMENTOS_POR_PAGINA);
  }, [
    budgetPeriod,
    budgetStartDate,
    budgetEndDate,
    budgetStatusFilter,
    budgetOwnerFilter,
    budgetEstimatorFilter,
    budgetLossReasonFilter,
    budgetSearch,
    budgetRepresentanteFilter,
    budgetSortKey,
    budgetSortDir,
  ]);
  const budgetFatia = useMemo(() => fatiaDaListaOrcamentos(budgetSortedItems.length, budgetVisiveis), [budgetSortedItems.length, budgetVisiveis]);

  const budgetStats = useMemo(() => buildBudgetStatsSummary(budgetFilteredItems), [budgetFilteredItems]);

  // Exporta exatamente o que esta na tela: usa budgetFilteredItems, entao
  // respeita todos os filtros ativos do modulo (periodo, datas, status,
  // vendedor, exceto-vendedor, orcamentista, representante e busca).
  const exportBudgetsXlsx = () => {
    if (!budgetFilteredItems.length) {
      showToast('Nenhum orçamento para exportar com os filtros atuais', 'error');
      return;
    }

    const rotuloStatus = (valor) =>
      BUDGET_STATUS_OPTIONS.find((opt) => opt.value === valor)?.label || valor || '';
    const rotuloPerda = (valor) =>
      BUDGET_LOSS_REASON_OPTIONS.find((opt) => opt.value === valor)?.label || valor || '';
    // formatDateBR devolve '-' quando vazio, o que poluiria a planilha.
    const dataOuVazio = (valor) => (valor ? formatDateBR(valor) : '');

    const linhas = budgetFilteredItems.map((b) => ({
      ID: b.id || '',
      'ID externo (ERP)': b.external_id || '',
      Cliente: b.client_name || '',
      Empresa: b.company || '',
      Status: rotuloStatus(b.status),
      Vendedor: b.owner_name || '',
      Orçamentista: b.estimator_name || '',
      Representante: b.representante || '',
      Filial: b.branch || '',
      'Pedido do cliente': b.customer_order || '',
      'Plano de pagamento': b.payment_terms || '',
      'Motivo da perda': rotuloPerda(b.loss_reason),
      Canal: b.channel_name || '',
      Campanha: b.campaign || '',
      // Valores como numero, para a planilha somar sem conversao manual.
      'Valor orçado': Number(b.budget_value || 0),
      'Valor fechado': Number(b.closed_value || 0),
      'Solicitado em': dataOuVazio(b.requested_at),
      'Enviado em': dataOuVazio(b.sent_at),
      'Fechado em': dataOuVazio(b.closed_at),
      Observações: b.notes || '',
    }));

    const ws = XLSX.utils.json_to_sheet(linhas);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Orçamentos');
    XLSX.writeFile(wb, `orcamentos-${new Date().toISOString().slice(0, 10)}.xlsx`);
    showToast(`${linhas.length} orçamento(s) exportado(s)`, 'success');
  };

  const budgetDashboardData = useMemo(() => {
    const statusMap = new Map();
    let reprovados = 0;
    let reprovadosComMotivo = 0;
    const lossReasonMap = new Map();
    const ownerMap = new Map();
    const estimatorMap = new Map();
    const monthlyMap = new Map();
    const closedMonthlyMap = new Map();
    const estimatedMonthlyMap = new Map();
    const lossValueMap = new Map();
    const desempenhoOwner = new Map();
    const desempenhoEstimator = new Map();
    const desempenhoRepresentante = new Map();
    const today = new Date();
    const sixMonths = [];

    for (let offset = 5; offset >= 0; offset -= 1) {
      const date = new Date(today.getFullYear(), today.getMonth() - offset, 1);
      const key = monthKey(date);
      monthlyMap.set(key, 0);
      closedMonthlyMap.set(key, 0);
      estimatedMonthlyMap.set(key, 0);
      sixMonths.push(key);
    }

    const acumularDesempenho = (mapa, chave, aprovado, valorOrcado, valorFechado) => {
      const atual = mapa.get(chave) || { total: 0, aprovados: 0, valorOrcado: 0, valorFechado: 0 };
      atual.total += 1;
      if (aprovado) atual.aprovados += 1;
      atual.valorOrcado += valorOrcado;
      atual.valorFechado += valorFechado;
      mapa.set(chave, atual);
    };

    dashboardMediaBudgets.forEach((budget) => {
      const status = budget.status || 'sem_status';
      const lossReason = budget.loss_reason || 'sem_motivo';
      const owner = budget.owner_name || 'Sem vendedor';
      const estimator = budget.estimator_name || 'Sem orçamentista';
      const statusNorm = normalizeOptionValue(status);
      const aprovado = statusNorm === 'aprovado';
      const valorOrcado = Number(budget.budget_value || 0);
      const valorFechado = Number(budget.closed_value || 0);
      const representante = String(budget.representante || '').trim() || 'Sem representante';

      statusMap.set(statusNorm || 'sem_status', (statusMap.get(statusNorm || 'sem_status') || 0) + 1);
      if (statusNorm === 'reprovado') {
        reprovados += 1;
        if (!['sem_motivo', 'sem motivo', ''].includes(normalizeOptionValue(lossReason))) reprovadosComMotivo += 1;
      }
      // BUG CORRIGIDO (mesma familia do 'nao_feito' e do 'sem_perfil'):
      // o fallback usado acima e 'sem_motivo' com underscore, e a comparacao
      // era contra 'sem motivo' com espaco. normalizeOptionValue nao troca
      // underscore por espaco, entao a condicao era sempre verdadeira e
      // orcamento SEM motivo de perda entrava no grafico de perdas como se
      // fosse um motivo. Medido em producao: 781 registros, R$ 3.078.302,79 —
      // varias vezes maior que todos os motivos reais somados.
      if (!['sem_motivo', 'sem motivo', ''].includes(normalizeOptionValue(lossReason))) {
        lossReasonMap.set(lossReason, (lossReasonMap.get(lossReason) || 0) + 1);
        // Peso financeiro do motivo: usa o valor ORCADO, porque orcamento
        // perdido nao tem valor fechado (conferido na base: closed_value = 0
        // em todo reprovado).
        lossValueMap.set(lossReason, (lossValueMap.get(lossReason) || 0) + valorOrcado);
      }
      ownerMap.set(owner, (ownerMap.get(owner) || 0) + 1);
      estimatorMap.set(estimator, (estimatorMap.get(estimator) || 0) + 1);
      acumularDesempenho(desempenhoOwner, owner, aprovado, valorOrcado, valorFechado);
      acumularDesempenho(desempenhoEstimator, estimator, aprovado, valorOrcado, valorFechado);
      acumularDesempenho(desempenhoRepresentante, representante, aprovado, valorOrcado, valorFechado);

      // Os tres mapas mensais saem do laco de baixo, sobre a base sem data.
    });

    // Evolucao mensal do orcamento: mesma regra dos leads, e revertida junto
    // em 10/09/2026 — volta a respeitar o recorte de data.
    dashboardMediaBudgets.forEach((budget) => {
      const createdDate = dataDeCalendario(budget.requested_at || budget.created_at);
      const closedDate = dataDeCalendario(budget.closed_at || budget.updated_at);
      const aprovado = normalizeOptionValue(budget.status || 'sem_status') === 'aprovado';
      const valorOrcado = Number(budget.budget_value || 0);

      if (createdDate) {
        const key = monthKey(createdDate);
        if (monthlyMap.has(key)) monthlyMap.set(key, (monthlyMap.get(key) || 0) + 1);
        if (estimatedMonthlyMap.has(key)) {
          estimatedMonthlyMap.set(key, (estimatedMonthlyMap.get(key) || 0) + valorOrcado);
        }
      }
      if (closedDate && aprovado) {
        const key = monthKey(closedDate);
        if (closedMonthlyMap.has(key)) {
          closedMonthlyMap.set(key, (closedMonthlyMap.get(key) || 0) + Number(budget.closed_value || 0));
        }
      }
    });

    const sortEntries = (map) =>
      Array.from(map.entries())
        .map(([label, value]) => ({ label, value }))
        .sort((a, b) => b.value - a.value);

    // Taxa de aprovacao por pessoa. ORDENA POR VOLUME, nao pela taxa: quem tem
    // 1 orcamento e 1 aprovacao marca 100% e apareceria no topo, o que engana.
    // O total vai no rotulo justamente para a taxa nunca ser lida sozinha.
    const taxaPorPessoa = (mapa) =>
      Array.from(mapa.entries())
        .map(([nome, d]) => ({
          label: `${nome} (${d.total})`,
          value: d.total ? Math.round((d.aprovados / d.total) * 100) : 0,
          _total: d.total,
        }))
        .sort((a, b) => b._total - a._total);

    return {
      // Funil em ordem de funil, com etapa zerada visivel.
      byStatus: [
        ...BUDGET_STATUS_OPTIONS.map((opt) => ({
          label: opt.label,
          value: statusMap.get(opt.value) || 0,
        })),
        ...Array.from(statusMap.entries())
          .filter(([chave]) => !BUDGET_STATUS_OPTIONS.some((opt) => opt.value === chave))
          .map(([chave, valor]) => ({
            label: chave === 'sem_status' ? 'Sem status' : chave,
            value: valor,
          })),
      ],
      // Motivo de perda pesado pelo VALOR perdido; a quantidade vai no rotulo.
      byLossReason: Array.from(lossReasonMap.entries())
        .map(([chave, quantidade]) => ({
          label: `${BUDGET_LOSS_REASON_OPTIONS.find((opt) => opt.value === chave)?.label || chave} (${quantidade})`,
          value: lossValueMap.get(chave) || 0,
        }))
        .sort((a, b) => b.value - a.value),
      // Base do grafico de perdas. Medido em 09/09/2026: 3 de 21 reprovados em
      // 30 dias tinham motivo. O ERP manda situacao e motivo na MESMA coluna,
      // entao quando ele diz "Perdido Prospeccao" o motivo nao vem junto.
      reprovadosTotal: reprovados,
      reprovadosComMotivo: reprovadosComMotivo,
      byOwner: sortEntries(ownerMap),
      byEstimator: sortEntries(estimatorMap),
      aprovacaoPorVendedor: taxaPorPessoa(desempenhoOwner),
      aprovacaoPorOrcamentista: taxaPorPessoa(desempenhoEstimator),
      aprovacaoPorRepresentante: taxaPorPessoa(desempenhoRepresentante),
      monthlyEvolution: sixMonths.map((key) => ({ label: monthLabel(key), value: monthlyMap.get(key) || 0 })),
      estimatedEvolution: sixMonths.map((key) => ({ label: monthLabel(key), value: estimatedMonthlyMap.get(key) || 0 })),
      closedEvolution: sixMonths.map((key) => ({ label: monthLabel(key), value: closedMonthlyMap.get(key) || 0 })),
    };
  }, [dashboardMediaBudgets]);

  const exportDashboardExcel = () => {
    const workbook = buildExcelWorkbook([
      {
        name: 'Resumo',
        rows: [
          ['Indicador', 'Valor'],
          ['Total de leads', dashboardLocalStats.total || 0],
          ['Leads no mês', dashboardData.leadsThisMonth || 0],
          ['Prospects', dashboardData.prospects || 0],
          ['Clientes', dashboardData.customers || 0],
          ['Ganhos', dashboardLocalStats.ganhos || 0],
          ['Perdidos', dashboardLocalStats.perdidos || 0],
          ['Taxa de conversão (%)', dashboardLocalStats.taxaConversao || 0],
          ['Valor convertido', Number(dashboardLocalStats.valorTotal || 0)],
          ['Valor em negociação (aberto hoje, sem filtro de período)', Number(dashboardPipelineAberto.valorNegociacao || 0)],
          ['Ticket médio ganho', Number(dashboardData.avgTicket || 0)],
          ['Follow-ups vencidos', dashboardData.overdueFollowups || 0],
          ['Investimento em Ads', Number(dashboardMediaData.investment || 0)],
          ['Leads gerados (Ads)', dashboardMediaData.leadsGenerated || 0],
          ['CPL', Number(dashboardMediaData.cpl || 0)],
          ['ROAS estimado', Number(dashboardMediaData.roasEstimated || 0)],
          ['ROAS fechado', Number(dashboardMediaData.roasClosed || 0)],
        ],
      },
      {
        name: 'Marketing',
        rows: [
          ['Canal', 'Leads'],
          ...dashboardData.byChannel.map((item) => [item.label, item.value]),
          [],
          ['Campanha', 'Leads'],
          ...dashboardData.byCampaign.map((item) => [item.label, item.value]),
          [],
          ['Mês', 'Entradas'],
          ...dashboardData.monthlyEvolution.map((item) => [item.label, item.value]),
          [],
          ['Mês', 'Valor convertido (por mês de entrada do lead)'],
          ...dashboardData.convertedEvolution.map((item) => [item.label, item.value]),
          [],
          ['Mês', 'Pipeline ativo'],
          ...dashboardData.pipelineEvolution.map((item) => [item.label, item.value]),
        ],
      },
      {
        name: 'Comercial',
        rows: [
          ['Status', 'Quantidade'],
          ...dashboardData.byStatus.map((item) => [item.label, item.value]),
          [],
          ['Perfil', 'Quantidade'],
          ...dashboardData.bySegment.map((item) => [item.label, item.value]),
        ],
      },
      {
        name: 'Ads',
        rows: [
          ['Campanha', 'Plataforma', 'Investimento', 'Leads', 'CPL', 'Estimado', 'Fechado', 'ROAS estimado', 'ROAS fechado'],
          ...dashboardMediaData.rows.map((item) => [
            item.campaign || '-',
            item.platform || '-',
            Number(item.investment || 0),
            item.leads || 0,
            Number(item.cpl || 0),
            Number(item.estimated || 0),
            Number(item.closed || 0),
            Number(item.roasEstimated || 0),
            Number(item.roasClosed || 0),
          ]),
        ],
      },
      {
        name: 'Leads',
        rows: [
          ['ID', 'Nome', 'Empresa', 'Perfil', 'Canal', 'Campanha', 'Status', 'Responsável', 'Valor', 'Cliente', 'Criado em'],
          ...dashboardData.source.map((lead) => [
            lead.id,
            lead.name || '',
            lead.company || '',
            SEGMENT_OPTIONS.find((item) => item.value === lead.segment)?.label || lead.segment || '',
            lead.channel_name || '',
            lead.campaign || '',
            lead.status || '',
            lead.owner || lead.responsible_name || '',
            Number(lead.value || 0),
            lead.is_customer ? 'Sim' : 'Não',
            lead.created_at || lead.first_contact || '',
          ]),
        ],
      },
    ]);

    const blob = new Blob([workbook], {
      type: 'application/vnd.ms-excel;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `relatorio-diretoria-${new Date().toISOString().slice(0, 10)}.xls`;
    link.click();
    URL.revokeObjectURL(url);
  };


  const followUpLeads = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return filteredLeads
      .map((l) => {
        const baseDate = l.first_contact || l.created_at;
        const d = baseDate ? (dataDeCalendario(baseDate) || new Date(NaN)) : null;
        const days = d && !Number.isNaN(d.getTime()) ? Math.floor((now - d) / (1000 * 60 * 60 * 24)) : null;
        return {
          ...l,
          _daysSince: days,
        };
      })
      .filter(
        (l) =>
          (l._status === 'novo' || (l.status || '').toLowerCase() === 'novo') &&
          l._daysSince !== null &&
          l._daysSince >= 10
      )
      .sort((a, b) => (b._daysSince || 0) - (a._daysSince || 0));
  }, [filteredLeads]);

  const editableLeadIds = useMemo(
    () => filteredLeads.filter((l) => canEditLead(l)).map((l) => String(l.id)),
    [filteredLeads, canEditLead]
  );
  const selectableLeadIds = useMemo(
    () => filteredLeads.filter((l) => canReassignLead(l)).map((l) => String(l.id)),
    [filteredLeads, canReassignLead]
  );

  const toggleSelectLead = (id) => {
    const normalized = String(id);
    if (!selectableLeadIds.includes(normalized)) return;
    setSelectedLeadIds((prev) =>
      prev.includes(normalized) ? prev.filter((v) => v !== normalized) : [...prev, normalized]
    );
  };

  const toggleSelectAll = () => {
    const allVisible = selectableLeadIds;
    if (!allVisible.length) return;
    const hasAll = allVisible.every((id) => selectedLeadIds.includes(id));
    setSelectedLeadIds(hasAll ? [] : allVisible);
  };

  const selectedCount = selectedLeadIds.length;
  const selectedEditableCount = selectedLeadIds.filter((id) => editableLeadIds.includes(id)).length;
  const allSelectableSelected =
    selectableLeadIds.length > 0 && selectableLeadIds.every((id) => selectedLeadIds.includes(id));

  // O que falta para a acao em massa funcionar, em palavras. A ordem importa:
  // primeiro o que o usuario tem que fazer ANTES (marcar), depois o que ele faz
  // na propria barra (escolher o destino).
  const faltaParaAcaoEmMassa = useMemo(() => {
    if (!selectedCount) {
      return viewMode === 'kanban'
        ? 'Marque os leads na caixinha do canto do cartão'
        : 'Marque os leads na lista abaixo';
    }
    if (!selectedEditableCount) return 'Você não pode editar os leads marcados';
    if (!bulkStatus && !bulkOwnerId) return 'Escolha o status ou o responsável aqui na barra';
    return '';
  }, [viewMode, selectedCount, selectedEditableCount, bulkStatus, bulkOwnerId]);

  const handleCardDragStart = (id) => setDraggingLeadId(id);
  const handleCardDragEnd = () => setDraggingLeadId(null);
  const handleDropStatus = async (status) => {
    const lead = filteredLeads.find((l) => String(l.id) === String(draggingLeadId));
    if (!lead || lead.status === status) return;
    setStatusUpdatingId(lead.id);
    try {
      await handleStatusChange(lead, status);
    } finally {
      setStatusUpdatingId(null);
      setDraggingLeadId(null);
    }
  };

  const openNewLeadModal = () => {
    setEditingLead(null);
    setLeadForm({ ...emptyLead, owner: user?.name || '', ownerId: user?.id || null });
    setLeadInteractions([]);
    setLeadInteractionsExpanded(false);
    setLeadInteractionForm({
      id: null,
      interaction_at: toDateTimeLocalInput(new Date().toISOString()),
      channel: '',
      notes: '',
    });
    setShowLeadModal(true);
  };

  const buildLeadFormFromLead = (lead) => {
    const nextContact = followupToInput(lead.next_contact);
    const firstContact = toDateInput(lead.first_contact);
    return {
      name: lead.name || '',
      contact: lead.contact || '',
      company: lead.company || '',
      segment: lead.segment || '',
      owner: lead.owner || lead.responsible_name || '',
      ownerId: lead.ownerId || lead.user_id || user?.id || null,
      origin: lead.origin || '',
      stage_detail: lead.stage_detail || '',
      next_contact: nextContact,
      first_contact: firstContact,
      email: lead.email || '',
      phone: lead.phone || '',
      phone2: lead.phone2 || '',
      channel_id: lead.channel_id || '',
      campaign: lead.campaign || '',
      status: lead.status || 'novo',
      value: numeroParaTextoBR(lead.value),
      notes: lead.notes || '',
      operator_notes: lead.operator_notes || '',
      is_private: !!lead.is_private,
      is_customer: !!lead.is_customer,
      is_out_of_scope: !!lead.is_out_of_scope,
      highlighted_categories: parseMultiSelect(lead.highlighted_categories),
      customer_type: lead.customer_type || '',
      cooling_reason: parseMultiSelect(lead.cooling_reason),
      temperature: lead.temperature || '',
      sla_status: lead.sla_status || '',
      sla_due_at: lead.sla_due_at || '',
      sla_minutes: Number(lead.sla_minutes || 0),
      last_activity_at: lead.last_activity_at || '',
      mailrelay_subscriber_id: lead.mailrelay_subscriber_id || '',
      last_email_open_at: lead.last_email_open_at || '',
      last_email_click_at: lead.last_email_click_at || '',
      email_open_count: Number(lead.email_open_count || 0),
      email_click_count: Number(lead.email_click_count || 0),
      email_unsubscribed: !!lead.email_unsubscribed,
      last_email_campaign: lead.last_email_campaign || '',
      last_email_campaign_id: lead.last_email_campaign_id || '',
      last_email_event_at: lead.last_email_event_at || '',
      interactions_count: Number(lead.interactions_count || 0),
      last_interaction_at: lead.last_interaction_at || '',
      last_interaction_channel: lead.last_interaction_channel || '',
      last_interaction_notes: lead.last_interaction_notes || '',
    };
  };

  const closeLeadModal = () => {
    setShowLeadModal(false);
    setEditingLead(null);
    setLeadInteractions([]);
    setLeadInteractionsExpanded(false);
    setLeadInteractionForm({ id: null, interaction_at: '', channel: '', notes: '' });
  };

  const openEditLeadModal = async (lead) => {
    if (!lead || !token || leadLoading) return;
    setLeadLoading(true);
    setLeadInteractionsLoading(true);
    try {
      const [leadRes, interactionsRes] = await Promise.all([
        fetch(`${API_URL}/leads/${lead.id}`, {
          headers: { Authorization: `Bearer ${token}` },
          cache: 'no-store',
        }),
        fetch(`${API_URL}/leads/${lead.id}/interactions`, {
          headers: { Authorization: `Bearer ${token}` },
          cache: 'no-store',
        }),
      ]);
      const data = await leadRes.json().catch(() => ({}));
      if (!leadRes.ok) {
        showToast(data.error || 'Erro ao carregar lead', 'error');
        return;
      }
      const interactionsData = interactionsRes.ok ? await interactionsRes.json().catch(() => ({})) : {};
      const interactions = Array.isArray(interactionsData)
        ? interactionsData
        : Array.isArray(interactionsData?.items)
          ? interactionsData.items
          : [];
      const form = buildLeadFormFromLead(data);
      setEditingLead(data);
      setLeadForm(form);
      setLeadInteractions(interactions);
      setLeadInteractionsExpanded(false);
      setLeadInteractionForm({
        id: null,
        interaction_at: toDateTimeLocalInput(new Date().toISOString()),
        channel: data.last_interaction_channel || data.channel_name || '',
        notes: '',
      });
      setShowLeadModal(true);
    } catch (err) {
      console.error('Erro ao carregar lead:', err);
      showToast('Erro ao carregar lead', 'error');
    } finally {
      setLeadLoading(false);
      setLeadInteractionsLoading(false);
    }
  };

  const saveLead = async () => {
    if (savingLead) return;
    const normalizedPhone = (leadForm.phone || '').replace(/\D/g, '');
    if (!leadForm.name || !normalizedPhone) {
      showToast('Nome e telefone são obrigatórios', 'error');
      return;
    }
    // Ganho sem valor e o que esvazia o indicador de valor convertido: 22 dos
    // 29 ganhos estavam assim em 14/09/2026. O CRM pergunta na hora de gravar,
    // em vez de a falta aparecer semanas depois num relatorio. Nao bloqueia:
    // existe ganho que de fato nao tem valor para lancar.
    if (normalizeOptionValue(leadForm.status) === 'ganho' && !(parseCurrencyInputBR(leadForm.value) > 0)) {
      const seguirAssim = window.confirm(
        'Este lead está marcado como GANHO e está sem valor.\n\n' +
          'Sem valor ele não entra no "Valor Convertido" nem no ticket médio do Dashboard.\n\n' +
          'Salvar assim mesmo?'
      );
      if (!seguirAssim) return;
    }
    const method = editingLead ? 'PUT' : 'POST';
    const url = editingLead
      ? `${API_URL}/leads/${editingLead.id}`
      : `${API_URL}/leads`;
    const payload = {
      ...leadForm,
      highlighted_categories: normalizeListValue(leadForm.highlighted_categories).join(','),
      cooling_reason: normalizeListValue(leadForm.cooling_reason).join(','),
      ownerId: leadForm.ownerId || user?.id || null,
      first_contact: leadForm.first_contact || '',
      next_contact: followupToIso(leadForm.next_contact),
      value: parseCurrencyInputBR(leadForm.value),
    };
    // Na edicao, `notes` nunca vai no corpo. O campo da origem e readOnly na
    // tela, entao o valor so seria devolvido igual - mas se o lead abrisse com o
    // campo vazio por falha de carga, salvar apagaria o bloco de origem. Na
    // criacao ele segue, porque e por ali que a integracao escreve a origem.
    if (editingLead) delete payload.notes;
    try {
      setSavingLead(true);
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      let data = {};
      try {
        data = await res.json();
      } catch {
        data = {};
      }
      if (!res.ok) {
        const message =
          res.status === 429
            ? 'Muitas requisições. Tente novamente em alguns segundos.'
            : data.error || 'Erro ao salvar lead';
        showToast(message, 'error');
        return;
      }
      if (data?.id) {
        setLeads((prev) => {
          const targetId = String(data.id);
          const mapped = prev.map((l) =>
            String(l.id) === targetId ? { ...l, ...data } : l
          );
          if (editingLead) {
            return mapped;
          }
          const exists = prev.some((l) => String(l.id) === targetId);
          return exists ? mapped : [...prev, data];
        });
      }
      await Promise.all([loadLeads(), loadStats()]);
      setShowLeadModal(false);
      setEditingLead(null);
      const followupDateLabel = leadForm.next_contact ? formatFollowupBR(leadForm.next_contact) : '';
      const followupStatus = String(data?.followup_status || '').toLowerCase();
      const followupError = String(
        data?.followup_error_msg ||
          data?.followup_sync?.error ||
          data?.followup_sync?.reason ||
          ''
      ).trim();
      if (followupDateLabel && followupStatus === 'error') {
        showToast(
          `Lead salvo, mas o follow-up não foi confirmado${followupError ? ` (${followupError})` : ''}`,
          'error'
        );
      } else if (followupDateLabel && data?.followup_sync?.skipped) {
        showToast(
          `Lead salvo. Follow-up não executado${followupError ? ` (${followupError})` : ''}`,
          'error'
        );
      } else if (followupDateLabel) {
        showToast(`${editingLead ? 'Lead atualizado' : 'Lead criado'} e follow-up agendado para ${followupDateLabel}`);
      } else {
        showToast(editingLead ? 'Lead atualizado' : 'Lead criado', 'success');
      }
    } catch (err) {
      console.error('Erro ao salvar lead:', err);
      showToast('Erro ao salvar lead', 'error');
    } finally {
      setSavingLead(false);
    }
  };

  const saveLeadInteraction = async () => {
    if (savingLeadInteraction || !editingLead?.id) return;
    const interactionId = leadInteractionForm.id ? String(leadInteractionForm.id) : '';
    const interactionAt = leadInteractionForm.interaction_at || toDateTimeLocalInput(new Date().toISOString());
    const channel = (leadInteractionForm.channel || '').trim();
    const notes = (leadInteractionForm.notes || '').trim();
    if (!channel) {
      showToast('Informe o canal da interação', 'error');
      return;
    }

    try {
      setSavingLeadInteraction(true);
      const res = await fetch(
        `${API_URL}/leads/${editingLead.id}/interactions${interactionId ? `/${interactionId}` : ''}`,
        {
          method: interactionId ? 'PUT' : 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            interaction_at: interactionAt,
            channel,
            notes,
          }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showToast(data.error || 'Erro ao salvar interação', 'error');
        return;
      }

      const nextLead = data?.lead || null;
      const nextInteraction = data?.interaction || null;
      if (nextInteraction) {
        setLeadInteractions((prev) => [nextInteraction, ...prev.filter((item) => String(item.id) !== String(nextInteraction.id))]);
      }
      if (nextLead) {
        setEditingLead(nextLead);
        setLeadForm(buildLeadFormFromLead(nextLead));
        setLeads((prev) =>
          prev.map((item) => (String(item.id) === String(nextLead.id) ? { ...item, ...nextLead } : item))
        );
      }
      setLeadInteractionForm({
        id: null,
        interaction_at: toDateTimeLocalInput(new Date().toISOString()),
        channel: '',
        notes: '',
      });
      void loadStats();
      showToast(interactionId ? 'Interação atualizada' : 'Interação registrada', 'success');
    } catch (err) {
      console.error('Erro ao salvar interação:', err);
      showToast('Erro ao salvar interação', 'error');
    } finally {
      setSavingLeadInteraction(false);
    }
  };

  const editLeadInteraction = (interaction) => {
    if (!interaction) return;
    setLeadInteractionsExpanded(true);
    setLeadInteractionForm({
      id: interaction.id || null,
      interaction_at: toDateTimeLocalInput(interaction.interaction_at || interaction.created_at || new Date().toISOString()),
      channel: interaction.channel || '',
      notes: interaction.notes || '',
    });
  };

  const deleteLeadInteraction = async (interaction) => {
    if (!editingLead?.id || !interaction?.id) return;
    if (!window.confirm('Deseja excluir esta interação?')) return;

    try {
      setSavingLeadInteraction(true);
      const res = await fetch(`${API_URL}/leads/${editingLead.id}/interactions/${interaction.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showToast(data.error || 'Erro ao excluir interação', 'error');
        return;
      }

      const nextLead = data?.lead || null;
      setLeadInteractions((prev) => prev.filter((item) => String(item.id) !== String(interaction.id)));
      if (nextLead) {
        setEditingLead(nextLead);
        setLeadForm(buildLeadFormFromLead(nextLead));
        setLeads((prev) =>
          prev.map((item) => (String(item.id) === String(nextLead.id) ? { ...item, ...nextLead } : item))
        );
      }
      setLeadInteractionForm({
        id: null,
        interaction_at: toDateTimeLocalInput(new Date().toISOString()),
        channel: '',
        notes: '',
      });
      void loadStats();
      showToast('Interação excluída', 'success');
    } catch (err) {
      console.error('Erro ao excluir interação:', err);
      showToast('Erro ao excluir interação', 'error');
    } finally {
      setSavingLeadInteraction(false);
    }
  };

  // Busca as campanhas que o Mailrelay REALMENTE enviou. Separada do resumo de
  // engajamento de proposito: sao duas perguntas diferentes e misturar as duas
  // foi o que gerou a duvida de 23/09/2026.
  const carregarCampanhasMailrelay = async (dias) => {
    if (!podeVerEmailMkt) return;
    setMrCampanhasCarregando(true);
    setMrCampanhasErro('');
    try {
      const janela = { '30d': 30, '90d': 90, '6m': 183, '12m': 365, all: 3650, custom: 3650 }[dias] || 30;
      const res = await fetch(`${API_URL}/mailrelay/campanhas?dias=${janela}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMrCampanhasErro(data.error || 'Nao consegui falar com o Mailrelay');
        setMrCampanhas(null);
        return;
      }
      setMrCampanhas(data);
    } catch (err) {
      console.error('Erro ao ler campanhas do Mailrelay:', err);
      setMrCampanhasErro('Nao consegui falar com o Mailrelay');
      setMrCampanhas(null);
    } finally {
      setMrCampanhasCarregando(false);
    }
  };

  const carregarLixeira = async () => {
    if (!isAdmin) return;
    setLixeiraCarregando(true);
    try {
      const res = await fetch(`${API_URL}/lixeira`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showToast(data.error || 'Erro ao abrir a lixeira', 'error');
        return;
      }
      setLixeira(data);
    } catch (err) {
      console.error('Erro ao abrir a lixeira:', err);
      showToast('Erro ao abrir a lixeira', 'error');
    } finally {
      setLixeiraCarregando(false);
    }
  };

  const carregarEngajou = async () => {
    setEngajouCarregando(true);
    try {
      const res = await fetch(`${API_URL}/engajou-sem-lead`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showToast(data.error || 'Erro ao ler a lista', 'error');
        setEngajou(null);
        return;
      }
      setEngajou(data);
    } catch (err) {
      console.error('Erro ao ler engajou sem lead:', err);
      showToast('Erro ao ler a lista', 'error');
      setEngajou(null);
    } finally {
      setEngajouCarregando(false);
    }
  };

  const abrirEngajou = async () => {
    setShowEngajou(true);
    await carregarEngajou();
  };

  // Um lead por vez, e so com responsavel e canal escolhidos. A rota recusa
  // sem os dois; a tela tambem, para o erro aparecer antes da viagem.
  const criarLeadDoEngajou = async (pessoa) => {
    const form = engajouForm[String(pessoa.id)] || {};
    if (!form.ownerId) { showToast('Escolha o responsavel', 'error'); return; }
    if (!form.channel_id) { showToast('Escolha o canal', 'error'); return; }
    const linhas = [
      'Criar lead para esta pessoa?',
      '',
      pessoa.email,
      `${pessoa.aberturas} abertura(s), ${pessoa.cliques} clique(s), em ${pessoa.campanhas_tocadas} campanha(s).`,
      '',
      'ATENCAO: esta pessoa NAO pediu contato. Ela abriu ou clicou num disparo.',
    ];
    if (!String(form.phone || '').trim()) linhas.push('O lead vai nascer sem telefone.');
    if (!window.confirm(linhas.join('\n'))) return;

    setEngajouOcupadoId(String(pessoa.id));
    try {
      const res = await fetch(`${API_URL}/engajou-sem-lead/${pessoa.id}/criar-lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ownerId: form.ownerId,
          channel_id: form.channel_id,
          name: String(form.name || '').trim(),
          company: String(form.company || '').trim(),
          phone: String(form.phone || '').trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showToast(data.error || 'Erro ao criar o lead', 'error');
        // 409 de e-mail repetido tambem muda a lista: a linha foi marcada.
        if (res.status === 409) await carregarEngajou();
        return;
      }
      showToast(`Lead ${data.lead_id} criado`, 'success');
      await Promise.all([loadLeads(), loadStats(), carregarEngajou()]);
    } catch (err) {
      console.error('Erro ao criar lead do engajamento:', err);
      showToast('Erro ao criar o lead', 'error');
    } finally {
      setEngajouOcupadoId(null);
    }
  };

  const abrirLixeira = async () => {
    setShowLixeira(true);
    await carregarLixeira();
  };

  const restaurarDaLixeira = async (item) => {
    const nome = item.name || '(sem nome)';
    if (!window.confirm(`Restaurar ${nome}${item.company ? ' — ' + item.company : ''}?\n\nEle volta para a lista de leads exatamente como estava.`)) return;
    setLixeiraOcupadoId(String(item.id));
    try {
      const res = await fetch(`${API_URL}/lixeira/${item.id}/restaurar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showToast(data.error || 'Erro ao restaurar', 'error');
        return;
      }
      showToast(`${nome} voltou para a lista`, 'success');
      await Promise.all([loadLeads(), loadStats(), carregarLixeira()]);
    } catch (err) {
      console.error('Erro ao restaurar da lixeira:', err);
      showToast('Erro ao restaurar', 'error');
    } finally {
      setLixeiraOcupadoId(null);
    }
  };

  // Ate 23/09/2026 o aviso era so 'Deseja realmente excluir este lead?'. Nao
  // dizia QUAL. Medido na tela dele no mesmo dia: 50 botoes 'Excluir' de 31x16
  // pixels visiveis ao mesmo tempo no Kanban, dentro da area clicavel do card.
  // Quem clicasse errado e confirmasse no automatico nao tinha como perceber.
  const deleteLead = async (id) => {
    const alvo = leads.find((l) => String(l.id) === String(id));
    const linhas = ['Excluir este lead?', ''];
    if (alvo) {
      linhas.push(alvo.name || '(sem nome)');
      if (alvo.company) linhas.push(alvo.company);
      const partes = [];
      if (alvo.status) partes.push('status: ' + alvo.status);
      if (alvo.owner) partes.push('responsavel: ' + alvo.owner);
      if (Number(alvo.value) > 0) partes.push('valor: ' + formatCurrencyBR(Number(alvo.value)));
      if (partes.length) linhas.push(partes.join('  |  '));
    } else {
      linhas.push('(nao consegui ler os dados deste lead)');
    }
    linhas.push('');
    linhas.push('Ele vai para a Lixeira e pode ser restaurado pelo administrador.');
    if (!window.confirm(linhas.join('\n'))) return;
    try {
      const res = await fetch(`${API_URL}/leads/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showToast(data.error || 'Erro ao excluir lead', 'error');
        return;
      }
      setLeads((prev) => prev.filter((l) => String(l.id) !== String(id)));
      setSelectedLeadIds((prev) => prev.filter((l) => String(l) !== String(id)));
      await loadStats();
      showToast('Lead excluido', 'success');
    } catch (err) {
      console.error('Erro ao excluir lead:', err);
      showToast('Erro ao excluir lead', 'error');
    }
  };

  const resetBudgetInteractionForm = () => {
    setBudgetInteractionForm({
      id: null,
      interaction_at: toDateTimeLocalInput(new Date().toISOString()),
      channel: '',
      notes: '',
    });
  };

  // Aplica o resumo devolvido pela API no orcamento aberto e na linha da lista,
  // para o contador "N int." mudar na hora, sem recarregar a tabela inteira.
  const applyBudgetInteractionSummary = (budgetId, summary) => {
    if (!budgetId || !summary) return;
    const patch = {
      interactions_count: Number(summary.interactions_count || 0),
      last_interaction_at: summary.last_interaction_at || '',
      last_interaction_channel: summary.last_interaction_channel || '',
      last_interaction_notes: summary.last_interaction_notes || '',
    };
    setBudgets((prev) =>
      prev.map((item) => (String(item.id) === String(budgetId) ? { ...item, ...patch } : item))
    );
    setEditingBudget((prev) =>
      prev && String(prev.id) === String(budgetId) ? { ...prev, ...patch } : prev
    );
  };

  const loadBudgetInteractions = async (budgetId) => {
    if (!budgetId) {
      setBudgetInteractions([]);
      setBudgetLeadInteractions([]);
      return;
    }
    try {
      setBudgetInteractionsLoading(true);
      const res = await fetch(`${API_URL}/budgets/${budgetId}/interactions`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
      });
      const data = res.ok ? await res.json().catch(() => ({})) : {};
      setBudgetInteractions(Array.isArray(data?.items) ? data.items : []);
      setBudgetLeadInteractions(Array.isArray(data?.lead_items) ? data.lead_items : []);
    } catch (err) {
      console.error('Erro ao carregar interações do orçamento:', err);
      setBudgetInteractions([]);
      setBudgetLeadInteractions([]);
    } finally {
      setBudgetInteractionsLoading(false);
    }
  };

  const saveBudgetInteraction = async () => {
    if (savingBudgetInteraction || !editingBudget?.id) return;
    const interactionId = budgetInteractionForm.id ? String(budgetInteractionForm.id) : '';
    const interactionAt = budgetInteractionForm.interaction_at || toDateTimeLocalInput(new Date().toISOString());
    const channel = (budgetInteractionForm.channel || '').trim();
    const notes = (budgetInteractionForm.notes || '').trim();
    if (!channel) {
      showToast('Informe o canal da interação', 'error');
      return;
    }

    try {
      setSavingBudgetInteraction(true);
      const res = await fetch(
        `${API_URL}/budgets/${editingBudget.id}/interactions${interactionId ? `/${interactionId}` : ''}`,
        {
          method: interactionId ? 'PUT' : 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            interaction_at: interactionAt,
            channel,
            notes,
          }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showToast(data.error || 'Erro ao salvar interação', 'error');
        return;
      }

      const nextInteraction = data?.interaction || null;
      if (nextInteraction) {
        setBudgetInteractions((prev) => {
          const semAntiga = prev.filter((item) => String(item.id) !== String(nextInteraction.id));
          return [nextInteraction, ...semAntiga].sort(
            (a, b) =>
              new Date(b.interaction_at || b.created_at || 0).getTime() -
              new Date(a.interaction_at || a.created_at || 0).getTime()
          );
        });
      }
      applyBudgetInteractionSummary(editingBudget.id, data?.summary);
      resetBudgetInteractionForm();
      setBudgetInteractionsExpanded(true);
      showToast(interactionId ? 'Interação atualizada' : 'Interação registrada', 'success');
    } catch (err) {
      console.error('Erro ao salvar interação do orçamento:', err);
      showToast('Erro ao salvar interação', 'error');
    } finally {
      setSavingBudgetInteraction(false);
    }
  };

  const editBudgetInteraction = (interaction) => {
    if (!interaction) return;
    setBudgetInteractionsExpanded(true);
    setBudgetInteractionForm({
      id: interaction.id || null,
      interaction_at: toDateTimeLocalInput(interaction.interaction_at || interaction.created_at || new Date().toISOString()),
      channel: interaction.channel || '',
      notes: interaction.notes || '',
    });
  };

  const deleteBudgetInteraction = async (interaction) => {
    if (!editingBudget?.id || !interaction?.id) return;
    if (!window.confirm('Deseja excluir esta interação?')) return;

    try {
      setSavingBudgetInteraction(true);
      const res = await fetch(`${API_URL}/budgets/${editingBudget.id}/interactions/${interaction.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showToast(data.error || 'Erro ao excluir interação', 'error');
        return;
      }

      setBudgetInteractions((prev) => prev.filter((item) => String(item.id) !== String(interaction.id)));
      applyBudgetInteractionSummary(editingBudget.id, data?.summary);
      resetBudgetInteractionForm();
      showToast('Interação excluída', 'success');
    } catch (err) {
      console.error('Erro ao excluir interação do orçamento:', err);
      showToast('Erro ao excluir interação', 'error');
    } finally {
      setSavingBudgetInteraction(false);
    }
  };

  const openNewBudgetModal = () => {
    setEditingBudget(null);
    setBudgetForm({
      ...emptyBudget,
      owner_id: user?.id || '',
      owner_name: user?.name || '',
      requested_at: toDateInput(new Date().toISOString()),
    });
    // Orcamento ainda nao existe: nao ha onde pendurar interacao. O bloco nem
    // chega a ser renderizado (ver o modal), mas o estado fica limpo assim mesmo.
    setBudgetInteractions([]);
    setBudgetLeadInteractions([]);
    setBudgetInteractionsExpanded(false);
    setBudgetLeadHistoryExpanded(false);
    resetBudgetInteractionForm();
    setShowBudgetModal(true);
  };

  const openEditBudgetModal = (budget) => {
    setEditingBudget(budget);
    setBudgetForm({
      external_id: budget.external_id || '',
      lead_id: budget.lead_id || '',
      client_name: budget.client_name || '',
      company: budget.company || '',
      segment: budget.segment || '',
      stage: budget.stage || '',
      status: budget.status || 'novo',
      loss_reason: budget.loss_reason || '',
      raw_status: budget.raw_status || '',
      raw_loss_reason: budget.raw_loss_reason || '',
      owner_id: budget.owner_id || '',
      owner_name: budget.owner_name || '',
      estimator_id: budget.estimator_id || '',
      estimator_name: budget.estimator_name || '',
      budget_value: numeroParaTextoBR(budget.budget_value),
      closed_value: numeroParaTextoBR(budget.closed_value),
      branch: budget.branch || '',
      customer_order: budget.customer_order || '',
      payment_terms: budget.payment_terms || '',
      requested_at: toDateInput(budget.requested_at),
      sent_at: toDateInput(budget.sent_at),
      closed_at: toDateInput(budget.closed_at),
      channel_name: budget.channel_name || '',
      campaign: budget.campaign || '',
      notes: budget.notes || '',
      representante: budget.representante || '',
    });
    setBudgetInteractions([]);
    setBudgetLeadInteractions([]);
    setBudgetInteractionsExpanded(false);
    setBudgetLeadHistoryExpanded(false);
    resetBudgetInteractionForm();
    void loadBudgetInteractions(budget.id);
    setShowBudgetModal(true);
  };

  const saveBudget = async () => {
    if (!budgetForm.client_name && !budgetForm.company) {
      showToast('Cliente ou empresa obrigatórios', 'error');
      return;
    }
    const method = editingBudget ? 'PUT' : 'POST';
    const url = editingBudget ? `${API_URL}/budgets/${editingBudget.id}` : `${API_URL}/budgets`;
    const payload = {
      ...budgetForm,
      budget_value: parseCurrencyInputBR(budgetForm.budget_value),
      closed_value: parseCurrencyInputBR(budgetForm.closed_value),
    };
    try {
      setSavingBudget(true);
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showToast(data.error || 'Erro ao salvar orçamento', 'error');
        return;
      }
      await Promise.all([loadBudgets(), loadLeads(), loadStats()]);
      setShowBudgetModal(false);
      setEditingBudget(null);
      showToast(editingBudget ? 'Orçamento atualizado' : 'Orçamento criado', 'success');
    } catch (err) {
      console.error('Erro ao salvar orçamento:', err);
      showToast('Erro ao salvar orçamento', 'error');
    } finally {
      setSavingBudget(false);
    }
  };

  // Funil no card (06/10/2026): grava pela rota propria e atualiza so este lead.
  const podeMarcarFunil = (lead) => isAdmin || String(lead?.ownerId || '') === String(user?.id || '');
  const marcarFunil = async (lead, etapa) => {
    try {
      const res = await fetch(`${API_URL}/leads/${lead.id}/funil`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ etapa }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showToast(data.error || 'Erro ao marcar o funil', 'error');
        return;
      }
      const campos = { ...data };
      delete campos.lead_id;
      setLeads((prev) => prev.map((l) => (String(l.id) === String(lead.id) ? { ...l, ...campos } : l)));
    } catch (err) {
      showToast('Erro ao marcar o funil', 'error');
    }
  };

  const deleteBudget = async (id) => {
    if (!window.confirm('Deseja realmente excluir este orçamento?')) return;
    try {
      const res = await fetch(`${API_URL}/budgets/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showToast(data.error || 'Erro ao excluir orçamento', 'error');
        return;
      }
      await loadBudgets();
      showToast('Orçamento excluído', 'success');
    } catch (err) {
      console.error('Erro ao excluir orçamento:', err);
      showToast('Erro ao excluir orçamento', 'error');
    }
  };

  const openNewAdSpendModal = () => {
    const defaultChannel = channels.find((channel) =>
      normalizeOptionValue(channel.name).includes('meta')
    ) || channels[0];
    setEditingAdSpend(null);
    setAdSpendForm({
      ...emptyAdSpend,
      date: toDateInput(new Date().toISOString()),
      channel_id: defaultChannel?.id ? String(defaultChannel.id) : '',
      channel_name: defaultChannel?.name || '',
      platform: defaultChannel?.name || '',
    });
    setShowAdSpendModal(true);
  };

  const openEditAdSpendModal = (entry) => {
    setEditingAdSpend(entry);
    setAdSpendForm({
      date: toDateInput(entry.date),
      channel_id: entry.channel_id ? String(entry.channel_id) : '',
      channel_name: entry.channel_name || entry.platform || '',
      platform: entry.platform || entry.channel_name || '',
      campaign: entry.campaign || '',
      // Era `formatCurrencyInputBR` aqui, e isso estragava o valor ao ABRIR
      // um gasto para editar: R$ 4.835,00 virava R$ 48,35 na tela, e gravava
      // assim se a pessoa salvasse. Corrigido em 23/09/2026.
      amount: numeroParaTextoBR(entry.amount),
      notes: entry.notes || '',
    });
    setShowAdSpendModal(true);
  };

  const saveAdSpend = async () => {
    if (!adSpendForm.date) {
      showToast('Data obrigatória', 'error');
      return;
    }
    if (!adSpendForm.channel_id && !adSpendForm.channel_name) {
      showToast('Canal obrigatório', 'error');
      return;
    }

    const method = editingAdSpend ? 'PUT' : 'POST';
    const url = editingAdSpend ? `${API_URL}/ad-spend/${editingAdSpend.id}` : `${API_URL}/ad-spend`;
    try {
      setSavingAdSpend(true);
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...adSpendForm,
          platform: adSpendForm.channel_name || adSpendForm.platform || '',
          amount: parseCurrencyInputBR(adSpendForm.amount),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showToast(data.error || 'Erro ao salvar gasto de Ads', 'error');
        return;
      }
      await loadAdSpend();
      setShowAdSpendModal(false);
      setEditingAdSpend(null);
      showToast(editingAdSpend ? 'Gasto atualizado' : 'Gasto lançado', 'success');
    } catch (err) {
      console.error('Erro ao salvar gasto de Ads:', err);
      showToast('Erro ao salvar gasto de Ads', 'error');
    } finally {
      setSavingAdSpend(false);
    }
  };

  const deleteAdSpend = async (id) => {
    if (!window.confirm('Deseja realmente excluir este lançamento de Ads?')) return;
    try {
      const res = await fetch(`${API_URL}/ad-spend/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showToast(data.error || 'Erro ao excluir gasto de Ads', 'error');
        return;
      }
      await loadAdSpend();
      showToast('Gasto excluído', 'success');
    } catch (err) {
      console.error('Erro ao excluir gasto de Ads:', err);
      showToast('Erro ao excluir gasto de Ads', 'error');
    }
  };

  const unifyMetaAdsChannel = async () => {
    if (!window.confirm('Isso vai unificar Instagram Ads e Facebook Ads em Meta Ads. Deseja continuar?')) return;
    try {
      const res = await fetch(`${API_URL}/channels/unify-meta`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showToast(data.error || 'Erro ao unificar canais Meta Ads', 'error');
        return;
      }
      await Promise.all([loadChannels(), loadLeads(), loadBudgets(), loadAdSpend(), loadStats()]);
      showToast(`Meta Ads unificado. Leads: ${data.leadsUpdated || 0}, Ads: ${data.adSpendUpdated || 0}`, 'success');
    } catch (err) {
      console.error('Erro ao unificar Meta Ads:', err);
      showToast('Erro ao unificar Meta Ads', 'error');
    }
  };

  const parseBudgetWorkbook = async (file) => {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '', raw: true });
    const headerRowIndex = rows.findIndex((row) =>
      Array.isArray(row) && row.some((cell) => normalizeSpreadsheetHeader(cell) === 'número' || normalizeSpreadsheetHeader(cell) === 'numero')
    );

    if (headerRowIndex === -1) {
      throw new Error('Cabeçalho da planilha não encontrado');
    }

    const headerRow = rows[headerRowIndex].map((cell) => normalizeSpreadsheetHeader(cell));
    const colIndex = (names) => headerRow.findIndex((header) => names.includes(header));

    const idxNumero = colIndex(['número', 'numero']);
    const idxData = colIndex(['data']);
    const idxFilial = colIndex(['filial']);
    const idxCliente = colIndex(['cliente']);
    const idxVendedor = colIndex(['vendedor']);
    const idxPedidoCliente = colIndex(['ped. cliente', 'ped cliente', 'pedido cliente']);
    const idxPlanoPagto = colIndex(['plano pagto', 'plano pagamento', 'plano pagto.']);
    const idxValorTotal = colIndex(['valor total']);
    const idxLib = colIndex(['lib']);

    if (idxNumero === -1 || idxCliente === -1 || idxValorTotal === -1) {
      throw new Error('Colunas obrigatórias não encontradas na planilha');
    }

    const items = [];
    const skipped = [];

    rows.slice(headerRowIndex + 1).forEach((row, offset) => {
      if (!Array.isArray(row)) return;
      const externalId = String(row[idxNumero] || '').trim();
      const company = String(row[idxCliente] || '').trim();
      if (!externalId && !company) return;
      if (!externalId) {
        skipped.push(`Linha ${headerRowIndex + offset + 2}: sem Número`);
        return;
      }

      const rawLib = idxLib >= 0 ? String(row[idxLib] || '').trim() : '';
      const mapped = mapBudgetImportStatus(rawLib);
      const vendedor = idxVendedor >= 0 ? String(row[idxVendedor] || '').trim() : '';
      const filial = idxFilial >= 0 ? String(row[idxFilial] || '').trim() : '';
      const pedidoCliente = idxPedidoCliente >= 0 ? String(row[idxPedidoCliente] || '').trim() : '';
      const planoPagto = idxPlanoPagto >= 0 ? String(row[idxPlanoPagto] || '').trim() : '';
      const notesParts = [
        filial ? `Filial: ${filial}` : '',
        vendedor ? `Vendedor ERP: ${vendedor}` : '',
        pedidoCliente ? `Pedido do cliente: ${pedidoCliente}` : '',
        planoPagto ? `Plano de pagamento: ${planoPagto}` : '',
        rawLib ? `Lib original: ${rawLib}` : '',
      ].filter(Boolean);

      items.push({
        external_id: externalId,
        client_name: company,
        company,
        stage: mapped.stage,
        status: mapped.status,
        loss_reason: mapped.loss_reason,
        raw_status: mapped.raw_status,
        raw_loss_reason: mapped.raw_loss_reason,
        budget_value: row[idxValorTotal] || 0,
        requested_at: idxData >= 0 ? excelDateToIso(row[idxData]) : '',
        branch: filial,
        customer_order: pedidoCliente,
        payment_terms: planoPagto,
        owner_name: '',
        notes: notesParts.join('\n'),
      });
    });

    return { items, skipped };
  };

  const handleBudgetImportFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    try {
      setImportingBudgets(true);
      const { items, skipped } = await parseBudgetWorkbook(file);
      if (!items.length) {
        showToast('Nenhum orçamento válido encontrado na planilha', 'error');
        return;
      }

      const preview = items.slice(0, 5).map((item) => `#${item.external_id} - ${item.company}`).join('\n');
      const confirmed = window.confirm(
        `Importar ${items.length} orçamento(s)?\n\nPrévia:\n${preview}${skipped.length ? `\n\nIgnorados: ${skipped.length}` : ''}`
      );
      if (!confirmed) return;

      const res = await fetch(`${API_URL}/budgets/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ items }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showToast(data.error || 'Erro ao importar planilha', 'error');
        return;
      }

      await loadBudgets();
      showToast(`Importação concluída: ${data.created || 0} criado(s), ${data.updated || 0} atualizado(s)`, 'success');
      if (data.errors?.length) {
        console.warn('Erros de importação:', data.errors);
      }
    } catch (err) {
      console.error('Erro ao importar planilha de orçamentos:', err);
      showToast(err.message || 'Erro ao importar planilha', 'error');
    } finally {
      setImportingBudgets(false);
    }
  };

  // --- Importacao da lista de clientes (AM Gestor), 23/09/2026 -------------
  //
  // O fluxo tem tres passos, e o do meio e o que importa: NADA e gravado antes
  // de a pessoa ver a conta.
  //   1. le a planilha no proprio navegador e mapeia as colunas;
  //   2. pergunta ao servidor o que ACONTECERIA (`simular: true`) e mostra;
  //   3. so entao grava — mandando junto a conta que foi mostrada, como trava.
  //      Se o numero mudou nesse meio tempo, o servidor recusa e nao grava.
  const simularImportacaoDeClientes = async (itens) => {
    setImportClientesOcupado(true);
    try {
      const res = await fetch(`${API_URL}/leads/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ itens, simular: true }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setImportClientesErro(data.error || 'Erro ao conferir a planilha');
        setImportClientesSimulacao(null);
        return;
      }
      setImportClientesSimulacao(data);
    } catch (err) {
      console.error('Erro ao conferir a importacao:', err);
      setImportClientesErro('Não consegui falar com o servidor para conferir a planilha.');
    } finally {
      setImportClientesOcupado(false);
    }
  };

  const handleImportClientesFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setImportClientesErro('');
    setImportClientesResultado(null);
    setImportClientesSimulacao(null);
    setImportClientesAberto(true);
    setImportClientesArquivo(file.name);
    try {
      setImportClientesOcupado(true);
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const linhas = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '', raw: true });
      const { itens, ignorados } = mapearPlanilhaDeClientes(linhas);
      if (!itens.length) {
        setImportClientesErro('Não achei nenhum cliente nessa planilha.');
        setImportClientesItens([]);
        return;
      }
      setImportClientesItens(itens);
      setImportClientesIgnorados(ignorados);
      await simularImportacaoDeClientes(itens);
    } catch (err) {
      console.error('Erro ao ler a planilha de clientes:', err);
      setImportClientesErro(err.message || 'Erro ao ler a planilha');
      setImportClientesItens([]);
    } finally {
      setImportClientesOcupado(false);
    }
  };

  const confirmarImportacaoDeClientes = async () => {
    if (!importClientesResponsavel) {
      setImportClientesErro('Escolha o responsável pelos leads antes de importar.');
      return;
    }
    if (!importClientesCanal) {
      setImportClientesErro('Escolha o canal antes de importar.');
      return;
    }
    if (!importClientesSimulacao) {
      setImportClientesErro('A conferência ainda não terminou.');
      return;
    }
    setImportClientesErro('');
    setImportClientesOcupado(true);
    try {
      const res = await fetch(`${API_URL}/leads/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          itens: importClientesItens,
          responsavel_id: importClientesResponsavel,
          canal_id: importClientesCanal,
          campanha: importClientesCampanha.trim(),
          marcar_como_cliente: importClientesMarcarCliente,
          esperado: {
            criar: importClientesSimulacao.criar,
            completar: importClientesSimulacao.completar,
          },
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setImportClientesErro(data.error || 'Erro ao importar a planilha');
        return;
      }
      setImportClientesResultado(data);
      await Promise.all([loadLeads(), loadStats()]);
      showToast(
        `Importação concluída: ${data.criados || 0} criado(s), ${data.completados || 0} completado(s)`,
        'success'
      );
    } catch (err) {
      console.error('Erro ao importar a planilha de clientes:', err);
      setImportClientesErro('Não consegui falar com o servidor.');
    } finally {
      setImportClientesOcupado(false);
    }
  };

  const fecharImportClientes = () => {
    setImportClientesAberto(false);
    setImportClientesArquivo('');
    setImportClientesItens([]);
    setImportClientesIgnorados([]);
    setImportClientesSimulacao(null);
    setImportClientesResultado(null);
    setImportClientesErro('');
  };

  // `buildLeadUpdatePayload` saiu em 30/09/2026. Ele devolvia o lead inteiro no
  // PUT com `ownerId: lead.user_id || user?.id` — mas o lead que vem da API nao
  // tem `user_id`, entao ia SEMPRE o id de quem clicou: "contato feito" ou
  // "reagendar" num lead de outra pessoa passava o lead para quem clicou
  // (reproduzido no arnes). Agora a agenda manda so o que muda: next_contact.

  const handleAgendaContactDone = async (lead) => {
    setAgendaUpdatingId(lead.id);
    try {
      const payload = { next_contact: null }; // so o que muda; ver nota acima
      const res = await fetch(`${API_URL}/leads/${lead.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        showToast(data.error || 'Erro ao atualizar contato', 'error');
        return;
      }
      await Promise.all([loadLeads(), loadStats()]);
      showToast('Contato marcado como feito', 'success');
    } catch (err) {
      console.error('Erro ao atualizar contato:', err);
      showToast('Erro ao atualizar contato', 'error');
    } finally {
      setAgendaUpdatingId(null);
    }
  };

  const handleAgendaReschedule = async (lead, days) => {
    if (!lead.next_contact) {
      showToast('Contato sem data agendada', 'error');
      return;
    }
    setAgendaUpdatingId(lead.id);
    try {
      const current = resolveFollowupDate(lead.next_contact);
      if (!current || Number.isNaN(current.getTime())) {
        showToast('Data inválida', 'error');
        return;
      }
      const next = new Date(current);
      next.setDate(next.getDate() + days);
      const payload = { next_contact: next.toISOString() }; // so o que muda
      const res = await fetch(`${API_URL}/leads/${lead.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        showToast(data.error || 'Erro ao reagendar', 'error');
        return;
      }
      await Promise.all([loadLeads(), loadStats()]);
      showToast(`Contato reagendado para ${formatFollowupBR(next.toISOString())}`, 'success');
    } catch (err) {
      console.error('Erro ao reagendar contato:', err);
      showToast('Erro ao reagendar contato', 'error');
    } finally {
      setAgendaUpdatingId(null);
    }
  };

  const handleAddChannel = async () => {
    if (!newChannel.trim()) {
      showToast('Nome do canal é obrigatório', 'error');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/channels`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: newChannel }),
      });
      if (!res.ok) {
        showToast('Erro ao criar canal', 'error');
        return;
      }
      setNewChannel('');
      await loadChannels();
      showToast('Canal criado', 'success');
    } catch (err) {
      console.error('Erro ao criar canal:', err);
      showToast('Erro ao criar canal', 'error');
    }
  };

  const handleDeleteChannel = async (id) => {
    if (!window.confirm('Deseja realmente excluir este canal?')) return;
    try {
      const res = await fetch(`${API_URL}/channels/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        showToast('Erro ao excluir canal', 'error');
        return;
      }
      await loadChannels();
      showToast('Canal excluido', 'success');
    } catch (err) {
      console.error('Erro ao excluir canal:', err);
      showToast('Erro ao excluir canal', 'error');
    }
  };

  // Status e "contato feito" em massa — 30/09/2026. UMA chamada com a lista
  // (POST /leads/editar-em-massa), com a mesma regra do PUT no servidor, em vez
  // de um PUT por lead (que estourava a cota do Google, como na troca de
  // responsavel). Manda so o campo que muda: o caminho antigo mandava o lead
  // inteiro, com o `ownerId` de quem clicou (ver nota da agenda).
  const enviarEdicaoEmMassa = async (campos, rotulo) => {
    if (!selectedCount) {
      showToast('Selecione ao menos um lead', 'error');
      return;
    }
    const ids = selectedLeadIds.filter((id) => {
      const lead = leads.find((l) => String(l.id) === String(id));
      return lead && canEditLead(lead);
    });
    if (!ids.length) {
      showToast('Nenhum dos leads selecionados pode ser editado', 'error');
      return;
    }

    const LOTE = 1000; // mesmo limite da rota
    const ESPERAS_429 = [20000, 45000]; // a cota do Google e por minuto
    let alterados = 0;
    let semMudanca = 0;
    let falhou = null;

    try {
      for (let inicio = 0; inicio < ids.length && !falhou; inicio += LOTE) {
        const parte = ids.slice(inicio, inicio + LOTE);
        let res = null;
        for (let tentativa = 0; tentativa <= ESPERAS_429.length; tentativa += 1) {
          res = await fetch(`${API_URL}/leads/editar-em-massa`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ ids: parte, ...campos }),
          });
          if (res.status !== 429 || tentativa === ESPERAS_429.length) break;
          const espera = ESPERAS_429[tentativa];
          showToast(`O Google pediu uma pausa. Tentando de novo em ${Math.round(espera / 1000)}s — não feche a tela.`);
          await sleep(espera);
        }
        const corpo = await res.json().catch(() => ({}));
        if (!res.ok) {
          falhou = res.status === 429
            ? 'O Google continua recusando (limite por minuto). Espere 1 minuto e aplique de novo — o que já foi feito não se repete.'
            : (corpo.error || `Falha na alteração em massa (HTTP ${res.status})`);
          alterados += Number(corpo.confirmados || 0);
          break;
        }
        alterados += Number(corpo.alterados || 0);
        semMudanca += Array.isArray(corpo.sem_mudanca) ? corpo.sem_mudanca.length : 0;
      }

      if (falhou) {
        showToast(alterados ? `${alterados} lead(s) atualizado(s). ${falhou}` : falhou, 'error');
      } else {
        const extra = semMudanca ? ` (${semMudanca} já estavam assim)` : '';
        showToast(`${rotulo} em ${alterados} lead(s)${extra}`, 'success');
        setSelectedLeadIds([]);
      }
      await Promise.all([loadLeads(), loadStats()]);
    } catch (err) {
      console.error('Erro na alteração em massa:', err);
      showToast('Erro ao atualizar em massa', 'error');
    }
  };

  const bulkChangeStatus = async () => {
    if (!bulkStatus) {
      showToast('Escolha um status para aplicar', 'error');
      return;
    }
    // Marcar em massa como ganho e o caminho mais rapido de criar ganho sem
    // valor, porque a tela de lote nao tem campo de valor. Aqui o CRM conta
    // quantos dos selecionados estao sem valor e pergunta antes.
    if (normalizeOptionValue(bulkStatus) === 'ganho') {
      const semValor = leads.filter(
        (lead) => selectedLeadIds.includes(String(lead.id)) && !(Number(lead.value) > 0)
      ).length;
      if (semValor > 0) {
        const seguirAssim = window.confirm(
          `${semValor} do(s) lead(s) selecionado(s) está(ão) sem valor preenchido.\n\n` +
            'Marcados como ganho sem valor, eles não entram no "Valor Convertido" nem no ticket médio.\n\n' +
            'Aplicar assim mesmo?'
        );
        if (!seguirAssim) return;
      }
    }
    await enviarEdicaoEmMassa({ status: bulkStatus }, 'Status atualizado');
  };

  // Troca de responsavel em massa — 29/09/2026.
  //
  // Antes: um PUT por lead, em sequencia. Cada PUT custa 8 leituras no Google, e
  // o CRM inteiro tem 60 por minuto. Passando leads da Ines para o Felipe, a
  // barra estourava a cota no 10o lead e o resto voltava 429 (medido no arnes).
  //
  // Agora: UMA chamada com a lista (POST /leads/mover-responsavel). Se o Google
  // recusar mesmo assim (outras telas usando a cota), espera o minuto virar e
  // tenta de novo. A rota e idempotente — quem ja foi movido volta como
  // "sem_mudanca" — entao repetir nao estraga nada.
  //
  // Status e "contato feito" em massa: enviarEdicaoEmMassa (30/09/2026).
  const bulkReassignOwner = async () => {
    if (!bulkOwnerId) {
      showToast('Escolha um novo responsável', 'error');
      return;
    }
    if (!selectedCount) {
      showToast('Selecione ao menos um lead', 'error');
      return;
    }
    const ids = selectedLeadIds.filter((id) => {
      const lead = leads.find((l) => String(l.id) === String(id));
      return lead && canReassignLead(lead);
    });
    if (!ids.length) {
      showToast('Nenhum dos leads selecionados pode ser editado', 'error');
      return;
    }

    const LOTE = 1000; // mesmo limite da rota
    const ESPERAS_429 = [20000, 45000]; // a cota do Google e por minuto
    let movidos = 0;
    let semMudanca = 0;
    let falhou = null;

    try {
      for (let inicio = 0; inicio < ids.length && !falhou; inicio += LOTE) {
        const parte = ids.slice(inicio, inicio + LOTE);
        let res = null;
        for (let tentativa = 0; tentativa <= ESPERAS_429.length; tentativa += 1) {
          res = await fetch(`${API_URL}/leads/mover-responsavel`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ ids: parte, ownerId: bulkOwnerId }),
          });
          if (res.status !== 429 || tentativa === ESPERAS_429.length) break;
          const espera = ESPERAS_429[tentativa];
          showToast(`O Google pediu uma pausa. Tentando de novo em ${Math.round(espera / 1000)}s — não feche a tela.`); // aviso neutro: a tela so tem os tipos success e error
          await sleep(espera);
        }
        const corpo = await res.json().catch(() => ({}));
        if (!res.ok) {
          falhou = res.status === 429
            ? 'O Google continua recusando (limite por minuto). Espere 1 minuto e aplique de novo — o que já foi movido não se repete.'
            : (corpo.error || `Falha ao trocar responsável (HTTP ${res.status})`);
          movidos += Number(corpo.confirmados || 0);
          break;
        }
        movidos += Number(corpo.movidos || 0);
        semMudanca += Array.isArray(corpo.sem_mudanca) ? corpo.sem_mudanca.length : 0;
      }

      if (falhou) {
        showToast(movidos ? `${movidos} lead(s) movido(s). ${falhou}` : falhou, 'error');
      } else {
        const extra = semMudanca ? ` (${semMudanca} já eram desse responsável)` : '';
        showToast(`Responsável atualizado em ${movidos} lead(s)${extra}`, 'success');
        setSelectedLeadIds([]);
      }
      await Promise.all([loadLeads(), loadStats()]);
    } catch (err) {
      console.error('Erro ao trocar responsável em massa:', err);
      showToast('Erro ao trocar responsável em massa', 'error');
    }
  };

  const bulkMarkContactDone = async () => {
    await enviarEdicaoEmMassa({ contato_feito: true }, 'Contato marcado como feito');
  };

  const clearFilters = () => {
    setOwnerFilter('all');
    setStatusFilter('todos');
    setUrgencyFilter('all');
    setTemperatureFilter('all');
    setSlaFilter('all');
    setSegmentFilter('all');
    setCustomerFilter('all');    setContatoFilter('all');
    setPeriodoFilter('all');
    setPeriodoInicio('');
    setPeriodoFim('');

    setChannelFilter('all');
    setCampaignFilter('');
    setSearchInput('');
    setSearchTerm('');
    setSortKey('id');
    setSortDir('desc');
    setVisibleCount(20);
    setAgendaOwnerFilter('todos');
  };

  const handleStatusChange = async (lead, newStatus) => {
    if (!lead || lead.status === newStatus) return;
    setStatusUpdatingId(lead.id);
    try {
      const res = await fetch(`${API_URL}/leads/${lead.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });
      const data = await res.json();
      if (!res.ok) {
        showToast(data.error || 'Erro ao atualizar status', 'error');
        return;
      }
      await Promise.all([loadLeads(), loadStats()]);
      showToast('Status atualizado', 'success');
    } catch (err) {
      console.error('Erro ao atualizar status:', err);
      showToast('Erro ao atualizar status', 'error');
    } finally {
      setDraggingLeadId(null);
      setStatusUpdatingId(null);
    }
  };

  const openProfileSettings = async (tab = 'me', editing = null) => {
    setProfileTab(tab);
    setProfileForm({
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      password: '',
    });
    setShowProfileModal(true);
    if (isAdmin) {
      await loadUsers();
      if (editing) {
        setUserForm({
          id: editing.id,
          name: editing.name || '',
          username: editing.username || '',
          phone: editing.phone || '',
          email: editing.email || '',
          password: '',
          role: editing.role || 'vendedor',
        });
      } else {
        setUserForm({
          id: null,
          name: '',
          username: '',
          phone: '',
          email: '',
          password: '',
          role: 'vendedor',
        });
      }
    }
  };

  const saveProfile = async () => {
    if (!profileForm.name || !profileForm.email || !profileForm.phone) {
      showToast('Nome, email e telefone sao obrigatorios', 'error');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/users/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(profileForm),
      });
      const data = await res.json();
      if (!res.ok) {
        showToast(data.error || 'Erro ao atualizar perfil', 'error');
        return;
      }
      if (data.token) {
        localStorage.setItem('token', data.token);
        setToken(data.token);
      }
      setUser(data.user);
      setShowProfileModal(false);
      showToast('? atualizado', 'success');
    } catch (err) {
      console.error('Erro ao atualizar perfil:', err);
      showToast('Erro ao atualizar perfil', 'error');
    }
  };

  const startNewUser = () => {
    setUserForm({
      id: null,
      name: '',
      username: '',
      phone: '',
      email: '',
      password: '',
      role: 'vendedor',
    });
    setProfileTab('users');
    setShowProfileModal(true);
  };

  const editExistingUser = (u) => {
    setUserForm({
      id: u.id,
      name: u.name || '',
      username: u.username || '',
      phone: u.phone || '',
      email: u.email || '',
      password: '',
      role: u.role || 'vendedor',
    });
    setProfileTab('users');
    setShowProfileModal(true);
  };

  const saveAdminUser = async () => {
    if (!userForm.name || !userForm.phone || !userForm.email) {
      showToast('Nome, telefone e email sao obrigatorios', 'error');
      return;
    }
    if (!userForm.id && !userForm.password) {
      showToast('Senha obrigatoria para novo usuario', 'error');
      return;
    }
    const payload = {
      name: userForm.name,
      username: userForm.username || userForm.email.split('@')[0],
      phone: userForm.phone,
      email: userForm.email,
      role: userForm.role,
    };
    if (userForm.password) {
      payload.password = userForm.password;
    }

    const method = userForm.id ? 'PUT' : 'POST';
    const endpoint = userForm.id ? `/users/${userForm.id}` : '/users';

    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        showToast(data.error || 'Erro ao salvar usuario', 'error');
        return;
      }
      await loadUsers();
      if (userForm.id === user?.id) {
        await verifyToken();
      }
      setShowProfileModal(false);
      showToast(userForm.id ? 'Usuario atualizado' : 'Usuario criado', 'success');
    } catch (err) {
      console.error('Erro ao salvar usuario:', err);
      showToast('Erro ao salvar usuario', 'error');
    }
  };

  const saveMailrelaySettings = async () => {
    if (!isAdmin) return;
    if (!mailrelaySettings.api_base || !mailrelaySettings.api_key) {
      showToast('Preencha URL e chave da API do Mailrelay', 'error');
      return;
    }

    setSavingMailrelaySettings(true);
    try {
      const res = await fetch(`${API_URL}/settings/mailrelay`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          api_base: mailrelaySettings.api_base,
          api_key: mailrelaySettings.api_key,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        showToast(data.error || 'Erro ao salvar Mailrelay', 'error');
        return;
      }
      setMailrelaySettings((prev) => ({
        ...prev,
        api_key: '',
        configured: Boolean(data.configured),
        source: data.source || 'crm',
        has_api_key: Boolean(data.has_api_key),
        api_base: data.api_base || prev.api_base,
      }));
      await loadMailrelaySettings();
      showToast('Configurações do Mailrelay salvas com sucesso');
    } catch (err) {
      console.error('Erro ao salvar Mailrelay:', err);
      showToast('Erro ao salvar Mailrelay', 'error');
    } finally {
      setSavingMailrelaySettings(false);
    }
  };

  const saveOmnichatSettings = async () => {
    if (!isAdmin) return;
    const normalizedBaseUrl = String(omnichatSettings.send_url || '').trim();
    if (!normalizedBaseUrl || !omnichatSettings.api_key) {
      showToast('Preencha URL e chave da API do Omnichat', 'error');
      return;
    }

    setSavingOmnichatSettings(true);
    try {
      const res = await fetch(`${API_URL}/settings/omnichat`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          api_base: normalizedBaseUrl,
          send_url: normalizedBaseUrl,
          api_key: omnichatSettings.api_key,
          auth_header: omnichatSettings.auth_header || 'x-api-key',
          enabled: omnichatSettings.enabled,
          reminder_minutes: omnichatSettings.reminder_minutes,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        showToast(data.error || 'Erro ao salvar Omnichat', 'error');
        return;
      }
      setOmnichatSettings((prev) => ({
        ...prev,
        api_key: '',
        configured: Boolean(data.configured),
        source: data.source || 'crm',
        has_api_key: Boolean(data.has_api_key),
        send_url: data.api_base || data.send_url || prev.send_url,
        auth_header: data.auth_header || prev.auth_header,
        enabled: data.enabled !== undefined ? Boolean(data.enabled) : prev.enabled,
        reminder_minutes: Number(data.reminder_minutes || prev.reminder_minutes || 30),
      }));
      await loadOmnichatSettings();
      showToast('Configurações do Omnichat salvas com sucesso');
    } catch (err) {
      console.error('Erro ao salvar Omnichat:', err);
      showToast('Erro ao salvar Omnichat', 'error');
    } finally {
      setSavingOmnichatSettings(false);
    }
  };

  const runFollowupDispatchNow = async () => {
    if (!isAdmin || runningFollowupDispatch) return;
    setRunningFollowupDispatch(true);
    try {
      const res = await fetch(`${API_URL}/followups/run`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showToast(data.error || 'Erro ao executar follow-up', 'error');
        return;
      }
      const sent = Number(data.sent || 0);
      const skipped = Number(data.skipped || 0);
      const errors = Number(data.errors || 0);
      showToast(`Follow-up executado: ${sent} enviado(s), ${skipped} ignorado(s), ${errors} erro(s)`);
    } catch (err) {
      console.error('Erro ao executar follow-up:', err);
      showToast('Erro ao executar follow-up', 'error');
    } finally {
      setRunningFollowupDispatch(false);
    }
  };

  const syncMailrelayEngagement = async () => {
    if (!isAdmin) return;
    setSyncingMailrelay(true);
    try {
      const res = await fetch(`${API_URL}/mailrelay/sync-engagement?campaignLimit=50`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await res.json();
      if (!res.ok) {
        showToast(data.error || 'Erro ao sincronizar Mailrelay', 'error');
        return;
      }
      setMailrelaySettings((prev) => ({
        ...prev,
        last_sync: {
          synced_at: data.synced_at,
          campaign_limit: data.campaign_limit,
          campaigns_processed: data.campaigns_processed,
          events_processed: data.events_processed,
          email_events_created: data.email_events_created,
          leads_updated: data.leads_updated,
          duplicate_events_skipped: data.duplicate_events_skipped,
          missing_identity_skipped: data.missing_identity_skipped,
          unmatched_leads_count: data.unmatched_leads_count,
          unmatched_emails_sample: data.unmatched_emails_sample || [],
          unmatched_subscriber_ids_sample: data.unmatched_subscriber_ids_sample || [],
        },
      }));
      await Promise.all([loadMailrelaySettings(), loadLeads(), loadEmailEvents()]);
      showToast(`Mailrelay sincronizado: ${data.events_processed || 0} evento(s), ${data.leads_updated || 0} lead(s), ${data.unmatched_leads_count || 0} sem match`);
    } catch (err) {
      console.error('Erro ao sincronizar Mailrelay:', err);
      showToast('Erro ao sincronizar Mailrelay', 'error');
    } finally {
      setSyncingMailrelay(false);
    }
  };

  const deleteUser = async (id) => {
    if (!window.confirm('Deseja realmente excluir este usuario?')) return;
    try {
      const res = await fetch(`${API_URL}/users/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        showToast(data.error || 'Erro ao excluir usuario', 'error');
        return;
      }
      await loadUsers();
      showToast('Usuario excluido', 'success');
    } catch (err) {
      console.error('Erro ao excluir usuario:', err);
      showToast('Erro ao excluir usuario', 'error');
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-surface-card rounded-xl shadow-2xl max-w-md w-full p-8">
          <h1 className="text-2xl font-bold text-ink mb-4 text-center">
            Leads - BHS Eletronica
          </h1>
          {error && (
            <div className="bg-risk border border-risk-line text-risk-ink px-4 py-2 rounded mb-4">
              {error}
            </div>
          )}
          <form onSubmit={handleAuth} className="space-y-4">
            {authMode === 'register' && (
              <>
                <div>
                  <label className="block text-sm font-semibold text-ink-soft mb-1">
                    Nome
                  </label>
                  <input
                    type="text"
                    value={authForm.name}
                    onChange={(e) =>
                      setAuthForm({ ...authForm, name: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-line rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-ink-soft mb-1">
                    Usuário (login)
                  </label>
                  <input
                    type="text"
                    value={authForm.username}
                    onChange={(e) =>
                      setAuthForm({
                        ...authForm,
                        username: e.target.value.toLowerCase(),
                      })
                    }
                    className="w-full px-3 py-2 border border-line rounded-lg"
                    placeholder="ex: leandro, ines..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-ink-soft mb-1">
                    Telefone
                  </label>
                  <input
                    type="tel"
                    value={authForm.phone}
                    onChange={(e) =>
                      setAuthForm({ ...authForm, phone: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-line rounded-lg"
                    required
                  />
                </div>
              </>
            )}
            <div>
              <label className="block text-sm font-semibold text-ink-soft mb-1">
                {authMode === 'login' ? 'Email ou usuário' : 'Email'}
              </label>
              <input
                type={authMode === 'login' ? 'text' : 'email'}
                value={authForm.email}
                onChange={(e) =>
                  setAuthForm({ ...authForm, email: e.target.value })
                }
                className="w-full px-3 py-2 border border-line rounded-lg"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-ink-soft mb-1">
                Senha
              </label>
              <input
                type="password"
                value={authForm.password}
                onChange={(e) =>
                  setAuthForm({ ...authForm, password: e.target.value })
                }
                className="w-full px-3 py-2 border border-line rounded-lg"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2 bg-brand-600 text-white rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-80"
            >
              {loading && (
                <span
                  className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
                  aria-hidden
                />
              )}
              <span>{loading ? 'Aguarde' : authMode === 'login' ? 'Entrar' : 'Criar conta'}</span>
            </button>
            {authHint && (
              <p className="mt-2 text-xs text-brand-ink text-center">{authHint}</p>
            )}
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface">
      <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6">
        {toast && (
          <div
            className={`fixed top-4 right-4 z-[70] max-w-sm px-4 py-3 rounded-xl text-sm font-medium shadow-[0_20px_45px_-18px_rgba(15,23,42,0.55)] ${toast.type === 'error'
              ? 'bg-red-600 text-white'
              : 'bg-emerald-600 text-white'
              }`}
          >
            {toast.message}
          </div>
        )}

        {servidorFora && (
          <div role="status" className="fixed top-0 inset-x-0 z-[65] bg-amber-100 text-amber-900 text-sm font-medium text-center px-4 py-2 shadow">
            Sem conexão com o servidor. Tentando reconectar a cada 30 segundos; você continua logado.
          </div>
        )}

        {loadingData && (
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-40">
            <div className="bg-surface-card rounded-xl shadow-lg px-6 py-4 text-sm text-ink-soft flex items-center gap-3">
              <div className="w-4 h-4 rounded-full border-2 border-brand-600 border-t-transparent animate-spin" />
              <span>Carregando dados...</span>
            </div>
          </div>
        )}

        <header className="rounded-2xl border border-line bg-surface-card shadow-card">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4">
            <div className="flex items-center gap-6 min-w-0">
              <span className="text-[13px] font-bold tracking-[0.1em] text-brand-ink whitespace-nowrap">BHS CRM</span>
              <nav className="flex items-center -mb-px overflow-x-auto scrollbar-hide">
                {NAV_TABS.map((tab) => (
                  <button
                    key={tab.value}
                    onClick={() => setActiveTab(tab.value)}
                    className={`px-3 h-[52px] text-sm whitespace-nowrap border-b-2 transition ${activeTab === tab.value
                      ? 'border-brand-600 text-brand-ink font-semibold'
                      : 'border-transparent text-ink-soft hover:text-ink'
                      }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
            <details ref={userMenuRef} className="relative group">
              <summary className="flex cursor-pointer list-none items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-ink-soft hover:bg-surface-low">
                <span className="grid h-6 w-6 place-items-center rounded-full border border-brand-100 bg-brand-50 text-[10px] font-bold text-brand-ink">
                  {(user.name || '?').trim().charAt(0).toUpperCase()}
                </span>
                <span className="max-w-[160px] truncate">{user.name}</span>
                <span aria-hidden className="text-ink-faint">▾</span>
              </summary>
              <div className="absolute right-0 z-30 mt-1 w-56 overflow-hidden rounded-xl border border-line bg-surface-card py-1 shadow-lift">
                <button
                  onClick={() => {
                    fecharMenuUsuario();
                    openProfileSettings('me');
                  }}
                  className="block w-full px-3 py-2 text-left text-sm text-ink-soft hover:bg-surface-low"
                  title="Configurações e perfil"
                >
                  Perfil e configurações
                </button>
                <button
                  onClick={() => {
                    fecharMenuUsuario();
                    setShowChannelModal(true);
                  }}
                  className="block w-full px-3 py-2 text-left text-sm text-ink-soft hover:bg-surface-low"
                >
                  Canais
                </button>
                {isAdmin && (
                  <button
                    onClick={() => {
                      fecharMenuUsuario();
                      unifyMetaAdsChannel();
                    }}
                    className="block w-full px-3 py-2 text-left text-sm text-ink-soft hover:bg-surface-low"
                  >
                    Unificar Meta Ads
                  </button>
                )}
                <div className="mt-1 border-t border-surface-mid px-3 pb-2 pt-2">
                  <p className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.09em] text-ink-faint">
                    Aparência
                  </p>
                  <div className="inline-flex w-full overflow-hidden rounded-lg border border-line">
                    {THEME_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setTheme(option.value);
                          fecharMenuUsuario();
                        }}
                        className={`flex-1 border-r border-line px-2 py-1.5 text-[11px] last:border-r-0 ${theme === option.value
                          ? 'bg-brand-600 font-semibold text-white'
                          : 'bg-surface-card text-ink-soft hover:bg-surface-low'
                          }`}
                        title={option.hint}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="block w-full border-t border-surface-mid px-3 py-2 text-left text-sm text-ink-soft hover:bg-surface-low"
                >
                  Sair
                </button>
              </div>
            </details>
          </div>
        </header>

        {activeTab === 'dashboard' && (
          <section className="space-y-6">
            <div className="bg-surface-card rounded-3xl shadow-xl p-6 border border-line">
              <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.24em] text-ink-faint font-bold">Executive Dashboard</p>
                  <h2 className="mt-2 text-3xl font-black text-ink">Marketing e Comercial</h2>
                  <p className="mt-2 text-sm text-ink-faint">
                    Os indicadores abaixo respeitam os filtros atuais do CRM e servem para acompanhamento da diretoria.
                  </p>
                </div>
                {/* Gasto de Ads: so o admin lanca, edita e apaga (01/10/2026). O servidor tambem recusa. */}
                {isAdmin && (
                <button
                  onClick={openNewAdSpendModal}
                  className="px-4 py-3 rounded-2xl bg-brand-600 text-white font-semibold hover:bg-brand-700 transition"
                >
                  Lançar Ads
                </button>
                )}
                <button
                  onClick={exportDashboardExcel}
                  className="px-4 py-3 rounded-2xl border border-line bg-surface-card text-ink-soft font-semibold hover:bg-surface-low transition"
                >
                  Baixar relatório em Excel
                </button>
              </div>
              <div className="mt-5 rounded-2xl border border-line bg-surface-low p-4">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div>
                    <p className={UI_EYEBROW}>Filtros da dashboard</p>
                    <p className="text-sm text-ink-faint">Esses filtros são independentes dos filtros operacionais do CRM.</p>
                  </div>
                  <button
                    onClick={() => {
                      setDashboardPeriod('90d');
                      setDashboardStartDate('');
                      setDashboardEndDate('');
                      setDashboardOwnerFilter('all');
                      setDashboardSegmentFilter('all');
                      setDashboardChannelFilter('all');
                      setDashboardCampaignFilter('');
                      setDashboardFollowUpFilter('all');
                    }}
                    className="px-3 py-2 rounded-xl border border-line bg-surface-card text-sm text-ink-soft shadow-sm"
                  >
                    Limpar filtros
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
                  <select
                    value={dashboardPeriod}
                    onChange={(e) => setDashboardPeriod(e.target.value)}
                    className={UI_INPUT_PILL}
                  >
                    <option value="30d">Últimos 30 dias</option>
                    <option value="90d">Últimos 90 dias</option>
                    <option value="6m">Últimos 6 meses</option>
                    <option value="12m">Últimos 12 meses</option>
                    <option value="custom">Período personalizado</option>
                    <option value="all">Todo o período</option>
                  </select>
                  <select
                    value={dashboardOwnerFilter}
                    onChange={(e) => setDashboardOwnerFilter(e.target.value)}
                    className={UI_INPUT_PILL}
                  >
                    <option value="all">Todos os responsáveis</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </select>
                  <select
                    value={dashboardSegmentFilter}
                    onChange={(e) => setDashboardSegmentFilter(e.target.value)}
                    className={UI_INPUT_PILL}
                  >
                    <option value="all">Todos os perfis</option>
                    {CLIENT_SEGMENT_OPTIONS.filter((s) => s.value !== '').map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                  <select
                    value={dashboardChannelFilter}
                    onChange={(e) => setDashboardChannelFilter(e.target.value)}
                    className={UI_INPUT_PILL}
                  >
                    <option value="all">Todos os canais</option>
                    {channels.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={dashboardCampaignFilter}
                    onChange={(e) => setDashboardCampaignFilter(e.target.value)}
                    className={UI_INPUT_PILL}
                    placeholder="Filtrar campanha..."
                  />
                  <select
                    value={dashboardFollowUpFilter}
                    onChange={(e) => setDashboardFollowUpFilter(e.target.value)}
                    className={UI_INPUT_PILL}
                  >
                    <option value="all">Toda agenda</option>
                    <option value="overdue">Follow-up vencido</option>
                  </select>
                </div>
                {dashboardPeriod === 'custom' && (
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="rounded-2xl border border-brand-200 bg-brand-50 p-3">
                      <label className="block text-xs font-semibold text-ink-soft mb-2">Data inicial</label>
                      <input
                        type="date"
                        value={dashboardStartDate}
                        onChange={(e) => setDashboardStartDate(e.target.value)}
                        className="w-full px-3 py-2 border border-line rounded-xl text-sm bg-surface-card"
                      />
                    </div>
                    <div className="rounded-2xl border border-brand-200 bg-brand-50 p-3">
                      <label className="block text-xs font-semibold text-ink-soft mb-2">Data final</label>
                      <input
                        type="date"
                        value={dashboardEndDate}
                        onChange={(e) => setDashboardEndDate(e.target.value)}
                        className="w-full px-3 py-2 border border-line rounded-xl text-sm bg-surface-card"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

          {/* Sub-abas. Os filtros ficam ACIMA desta barra porque valem para as
              tres — trocar de sub-aba nao deve obrigar ninguem a refiltrar. */}
          <div className="flex gap-1 border-b border-line px-0.5" role="tablist">
            {DASHBOARD_PANES.map((pane) => (
              <button
                key={pane.value}
                type="button"
                role="tab"
                aria-selected={dashboardPane === pane.value}
                onClick={() => setDashboardPane(pane.value)}
                className={`-mb-px flex items-center gap-2 rounded-t-lg px-4 py-2.5 text-sm border-b-2 transition ${dashboardPane === pane.value
                  ? 'border-brand-ink text-brand-ink font-semibold'
                  : 'border-transparent text-ink-soft hover:bg-surface-low hover:text-ink'
                  }`}
              >
                {pane.label}
                <span
                  className={`rounded-full border px-2 py-[1px] text-[10.5px] tabular-nums ${dashboardPane === pane.value
                    ? 'border-brand-100 bg-brand-50 text-brand-ink'
                    : 'border-line bg-surface-low text-ink-faint'
                    }`}
                >
                  {pane.contador}
                </span>
              </button>
            ))}
          </div>

          {dashboardPane === 'leads' && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <StatCard label="Leads Totais" value={dashboardLocalStats.total || 0} helper={`${dashboardData.leadsThisMonth || 0} no mês`} tone="slate" />
              <StatCard label="Taxa de Conversão" value={formatarPercentualBR(dashboardLocalStats.taxaConversao || 0)} helper={`${dashboardLocalStats.ganhos || 0} ganhos`} tone="blue" />
              <StatCard
                label="Valor Convertido"
                value={formatCurrencyBR(dashboardLocalStats.valorTotal || 0)}
                helper={`Ticket médio ${formatCurrencyBR(dashboardData.avgTicket || 0)} · soma de ${dashboardLocalStats.ganhosComValor || 0} de ${dashboardLocalStats.ganhos || 0} ganhos com valor preenchido`}
                tone="emerald"
              />
              <StatCard label="Pipeline Ativo" value={formatCurrencyBR(dashboardPipelineAberto.valorNegociacao || 0)} helper={`${dashboardPipelineAberto.qtdNegociacao || 0} em negociação · tudo que está aberto hoje, sem filtro de período`} tone="amber" />
            </div>
            <div className={UI_CARD}>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className={UI_EYEBROW}>Por origem</p>
                <p className="text-xs text-ink-faint">Os mesmos números dos cartões acima, separados — nenhum lead sai da conta.</p>
              </div>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full min-w-[520px] text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-ink-faint">
                      <th className="pb-2 font-semibold">Indicador</th>
                      <th className="pb-2 text-right font-semibold">Demanda de entrada</th>
                      <th className="pb-2 text-right font-semibold">Prospecção e carteira</th>
                    </tr>
                  </thead>
                  <tbody className="tabular-nums">
                    {[
                      ['Leads', (s) => (s.total || 0).toLocaleString('pt-BR')],
                      ['Ganhos', (s) => (s.ganhos || 0).toLocaleString('pt-BR')],
                      ['Taxa de conversão', (s) => formatarPercentualBR(s.taxaConversao || 0)],
                      ['Valor convertido', (s) => formatCurrencyBR(s.valorTotal || 0)],
                      // Esta linha vem da base SEM periodo, para bater com o cartao.
                      ['Em negociação (aberto hoje)', (s) => formatCurrencyBR(s.valorNegociacao || 0), dashboardPipelinePorOrigem],
                      ['Perdidos', (s) => (s.perdidos || 0).toLocaleString('pt-BR')],
                    ].map(([rotulo, formatar, fonte]) => (
                      <tr key={rotulo} className="border-t border-line">
                        <td className="py-2 pr-3 text-ink-faint">{rotulo}</td>
                        <td className="py-2 text-right font-semibold text-ink">{formatar((fonte || dashboardStatsPorOrigem).demanda)}</td>
                        <td className="py-2 text-right font-semibold text-ink">{formatar((fonte || dashboardStatsPorOrigem).prospeccao)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-3 text-xs text-ink-faint">
                Prospecção e carteira = as listas que a BHS subiu para a equipe trabalhar (Ferramenta de Captura e a carteira importada do Victor). Demanda de entrada = todo o resto, quem procurou a BHS. As duas seguem o filtro de período escolhido acima.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <div className={UI_CARD}>
                <p className={UI_EYEBROW}>Prospects</p>
                <p className={UI_STAT}>{dashboardData.prospects || 0}</p>
              </div>
              <div className={UI_CARD}>
                <p className={UI_EYEBROW}>Clientes</p>
                <p className={UI_STAT}>{dashboardData.customers || 0}</p>
              </div>
              <div className={UI_CARD}>
                <p className={UI_EYEBROW}>Perdidos</p>
                <p className={UI_STAT}>{dashboardLocalStats.perdidos || 0}</p>
                <p className="mt-2 text-xs text-ink-faint">{formatCurrencyBR(dashboardLocalStats.valorPerdido || 0)} perdidos</p>
              </div>
              <div className={UI_CARD}>
                <p className={UI_EYEBROW}>Follow-up vencido</p>
                <p className={UI_STAT}>{dashboardData.overdueFollowups || 0}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <StatCard label="Leads quentes" value={dashboardData.byTemperature.find((item) => item.label === 'Quente')?.value || 0} helper="Prioridade máxima" tone="rose" />
              <StatCard label="Leads mornos" value={dashboardData.byTemperature.find((item) => item.label === 'Morno')?.value || 0} helper="Em evolução" tone="amber" />
              <StatCard label="Leads frios" value={dashboardData.byTemperature.find((item) => item.label === 'Frio')?.value || 0} helper="Base de prospecção" tone="slate" />
              <StatCard label="SLA estourado" value={dashboardData.overdueByOwner.reduce((sum, item) => sum + Number(item.value || 0), 0)} helper="Leads fora do prazo (marcado no funil: prazo do funil)" tone="blue" />
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
              <div className={UI_CARD}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className={UI_EYEBROW}>Marketing</p>
                    <h3 className="text-lg font-bold text-ink">Entradas por mês</h3>
                  </div>
                </div>
                <MiniLineChart data={dashboardData.monthlyEvolution} color={CHART_COLORS.volume} />
              </div>

              <div className={UI_CARD}>
                <p className={UI_EYEBROW}>Comercial</p>
                {/* O titulo diz "de entrada" porque o valor e lancado no mes em que o
                    lead ENTROU, nao no mes em que fechou: o lead nao guarda a data em
                    que virou ganho. Chamar isso de "evolucao do valor convertido"
                    fazia o grafico parecer uma linha de faturamento por mes, que ele
                    nao e. Do jeito certo ele mede safra de captacao: quanto rendeu,
                    ate hoje, o que entrou naquele mes. */}
                <h3 className={UI_H2}>Valor convertido por mês de entrada</h3>
                <MiniLineChart data={dashboardData.convertedEvolution} color={CHART_COLORS.positivo} formatValue={formatCurrencyBR} />
                <p className="mt-1 text-[11px] text-ink-faint">
                  Valor dos leads ganhos, somado no mês em que o lead <strong>entrou</strong> — não no mês em que fechou.
                </p>
              </div>

              <div className={UI_CARD}>
                <p className={UI_EYEBROW}>Comercial</p>
                <h3 className={UI_H2}>Evolução do pipeline ativo</h3>
                <MiniLineChart data={dashboardData.pipelineEvolution} color={CHART_COLORS.atencao} formatValue={formatCurrencyBR} />
              </div>
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
              <div className={UI_CARD}>
                <p className={UI_EYEBROW}>Prioridade</p>
                <h3 className={UI_H2}>Temperatura da base</h3>
                <MiniBarChart data={dashboardData.byTemperature} color={CHART_COLORS.volume} showZeros />
                <p className="mt-1 text-[11px] text-ink-faint">Como o card mostra: a etapa marcada no funil; sem marcação, a temperatura automática.</p>
              </div>
              <div className={UI_CARD}>
                <p className={UI_EYEBROW}>SLA</p>
                <h3 className={UI_H2}>Estourados por responsável</h3>
                <MiniBarChart data={dashboardData.overdueByOwner} color={CHART_COLORS.risco} />
              </div>
              <div className={UI_CARD}>
                <p className={UI_EYEBROW}>Conversão</p>
                <h3 className={UI_H2}>Taxa por temperatura</h3>
                <MiniBarChart data={dashboardData.conversionByTemperature} color={CHART_COLORS.positivo} showZeros aggregateRest={false} formatValue={(value) => `${value}%`} />
              </div>
            </div>
            {/* Funil no card (07/10/2026): quanto da base o time ja marcou e
                quanto passou do prazo do funil sem movimentacao. */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
              <StatCard
                label="Funil · marcados"
                value={dashboardData.funilMarcados}
                helper={`${dashboardData.source.length ? formatarPercentualBR((dashboardData.funilMarcados / dashboardData.source.length) * 100) : '0%'} dos ${dashboardData.source.length} leads do recorte. Os outros contam pela temperatura automática.`}
              />
              <StatCard
                label="Funil · esfriaram"
                value={dashboardData.funilEsfriou}
                helper="Marcados que passaram do prazo (quente 1 dia, morno 3, frio 7) sem movimentação."
              />
              <div className={UI_CARD}>
                <p className={UI_EYEBROW}>Funil</p>
                <h3 className={UI_H2}>Esfriaram por responsável</h3>
                <MiniBarChart data={dashboardData.esfriouPorDono} color={CHART_COLORS.risco} emptyLabel="Nenhum lead marcado esfriou" />
              </div>
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
              <div className={UI_CARD}>
                <p className={UI_EYEBROW}>Marketing</p>
                <h3 className={UI_H2}>Leads por canal</h3>
                <MiniBarChart data={dashboardData.byChannel} color={CHART_COLORS.volume} />
              </div>
              <div className={UI_CARD}>
                <p className={UI_EYEBROW}>Marketing</p>
                <h3 className={UI_H2}>Campanhas</h3>
                <MiniBarChart data={dashboardData.byCampaign} color={CHART_COLORS.volume} />
              </div>
              <div className={UI_CARD}>
                <p className={UI_EYEBROW}>Comercial</p>
                <h3 className={UI_H2}>Funil por status</h3>
                <MiniBarChart data={dashboardData.byStatus} color={CHART_COLORS.volume} showZeros limit={9} />
              </div>
              <div className={UI_CARD}>
                <p className={UI_EYEBROW}>Comercial</p>
                <h3 className={UI_H2}>Perfis de cliente</h3>
                <MiniBarChart data={dashboardData.bySegment} color={CHART_COLORS.volume} />
              </div>
            </div>
          </div>
          )}

          {dashboardPane === 'orcamentos' && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <StatCard label="Orçamentos" value={dashboardBudgetStats.total || 0} helper={`${dashboardBudgetStats.enviados || 0} enviados`} tone="slate" />
              <StatCard label="Taxa de Aprovação" value={`${dashboardBudgetStats.taxaAprovacao || 0}%`} helper={`${dashboardBudgetStats.aprovados || 0} aprovados`} tone="blue" />
              <StatCard label="Valor Orçado" value={formatCurrencyBR(dashboardBudgetStats.valorOrcado || 0)} helper={`${dashboardBudgetStats.reprovados || 0} reprovados`} tone="emerald" />
              <StatCard label="Valor Fechado" value={formatCurrencyBR(dashboardBudgetStats.valorFechado || 0)} helper={`Ticket médio ${formatCurrencyBR(dashboardBudgetStats.ticketMedio || 0)}`} tone="amber" />
              <StatCard
                label="Fechado x orçado · quantidade"
                value={formatarPercentualBR(dashboardBudgetStats.taxaFechadoQtd)}
                helper={`${dashboardBudgetStats.aprovados || 0} aprovados de ${dashboardBudgetStats.decididos || 0} decididos (aprovado, reprovado ou não feito)`}
                tone="blue"
              />
              <StatCard
                label="Fechado x orçado · valor"
                value={formatarPercentualBR(dashboardBudgetStats.taxaFechadoValor)}
                helper={`${formatCurrencyBR(dashboardBudgetStats.valorFechadoDecididos || 0)} de ${formatCurrencyBR(dashboardBudgetStats.valorDecidido || 0)} orçados nos decididos${dashboardBudgetStats.aprovadosSemValorFechado ? ` · ${dashboardBudgetStats.aprovadosSemValorFechado} aprovado(s) sem valor fechado, contado(s) pelo orçado` : ''}`}
                tone="emerald"
              />
            </div>
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
            <div className={UI_CARD}>
              <p className={UI_EYEBROW}>Onde trava</p>
              <h3 className={UI_H2}>Funil de orçamentos</h3>
              <MiniBarChart data={budgetDashboardData.byStatus} color={CHART_COLORS.volume} showZeros limit={9} />
              <p className="mt-3 text-[11px] text-ink-faint">
                Etapas em ordem de funil, não por tamanho. Etapa sem nenhum orçamento aparece com zero.
              </p>
            </div>
            <div className={UI_CARD}>
              <p className={UI_EYEBROW}>Entra x fecha</p>
              <h3 className={UI_H2}>Valor orçado e valor fechado por mês</h3>
              <ChartSeriesLabel color={CHART_COLORS.volume} text="Valor orçado (mês da solicitação)" />
              <MiniLineChart data={budgetDashboardData.estimatedEvolution} color={CHART_COLORS.volume} formatValue={formatCurrencyBR} />
              <div className="mt-4">
                <ChartSeriesLabel color={CHART_COLORS.positivo} text="Valor fechado (mês do fechamento)" />
                <MiniLineChart data={budgetDashboardData.closedEvolution} color={CHART_COLORS.positivo} formatValue={formatCurrencyBR} />
              </div>
            </div>
            <div className={UI_CARD}>
              <p className={UI_EYEBROW}>Por que perdemos</p>
              <h3 className={UI_H2}>Perdas por motivo</h3>
              <MiniBarChart
                data={budgetDashboardData.byLossReason}
                color={CHART_COLORS.risco}
                showZeros
                formatValue={formatCurrencyBR}
              />
              <p className="mt-3 text-[11px] text-ink-faint">
                Barra = valor orçado perdido. Entre parênteses, a quantidade de orçamentos.
              </p>
              {/* Declara quantos reprovados o grafico realmente cobre. O ERP manda
                  situacao e motivo na MESMA coluna: quando ele diz "Perdido
                  Prospeccao", o motivo nao vem. Medido em 09/09/2026: 3 de 21. */}
              <p className="mt-1 text-[11px] text-ink-faint">
                {`Base: ${budgetDashboardData.reprovadosComMotivo || 0} de ${budgetDashboardData.reprovadosTotal || 0} reprovados têm motivo registrado.`}
                {(budgetDashboardData.reprovadosTotal || 0) > (budgetDashboardData.reprovadosComMotivo || 0)
                  ? ' Os demais não aparecem aqui — o ERP não envia o motivo junto com a situação.'
                  : ''}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
            <div className={UI_CARD}>
              <p className={UI_EYEBROW}>Quem fecha</p>
              <h3 className={UI_H2}>Aprovação por vendedor</h3>
              <MiniBarChart
                data={budgetDashboardData.aprovacaoPorVendedor}
                color={CHART_COLORS.positivo}
                showZeros
                aggregateRest={false}
                formatValue={(value) => `${value}%`}
              />
              <p className="mt-3 text-[11px] text-ink-faint">
                Ordenado por volume, não pela taxa. Entre parênteses, quantos orçamentos a pessoa tem — 100% de 1 orçamento não é desempenho.
              </p>
            </div>
            <div className={UI_CARD}>
              <p className={UI_EYEBROW}>Quem produz</p>
              <h3 className={UI_H2}>Aprovação por orçamentista</h3>
              <MiniBarChart
                data={budgetDashboardData.aprovacaoPorOrcamentista}
                color={CHART_COLORS.positivo}
                showZeros
                aggregateRest={false}
                formatValue={(value) => `${value}%`}
              />
            </div>
            <div className={UI_CARD}>
              <p className={UI_EYEBROW}>Canal indireto</p>
              <h3 className={UI_H2}>Aprovação por representante</h3>
              <MiniBarChart
                data={budgetDashboardData.aprovacaoPorRepresentante}
                color={CHART_COLORS.positivo}
                showZeros
                aggregateRest={false}
                formatValue={(value) => `${value}%`}
                emptyLabel="Nenhum orçamento com representante registrado ainda."
              />
              <p className="mt-3 text-[11px] text-ink-faint">
                Alimentado pelo campo Representante do orçamento. Enquanto ninguém preencher, aparece tudo em "Sem representante".
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
            <div className={UI_CARD}>
              <p className={UI_EYEBROW}>Evolução</p>
              <h3 className={UI_H2}>Solicitações por mês</h3>
              <MiniLineChart data={budgetDashboardData.monthlyEvolution} color={CHART_COLORS.volume} />
            </div>
            <div className={UI_CARD}>
              <p className={UI_EYEBROW}>Time Comercial</p>
              <h3 className={UI_H2}>Orçamentos por vendedor</h3>
              <MiniBarChart data={budgetDashboardData.byOwner} color={CHART_COLORS.volume} />
            </div>
            <div className={UI_CARD}>
              <p className={UI_EYEBROW}>Time Técnico</p>
              <h3 className={UI_H2}>Orçamentos por orçamentista</h3>
              <MiniBarChart data={budgetDashboardData.byEstimator} color={CHART_COLORS.volume} />
            </div>
          </div>
          </div>
          )}

          {dashboardPane === 'midia' && (
          <div className="space-y-5">
            {/* Aviso CALCULADO, nao escrito na pedra: aparece enquanto nenhum
                orcamento do periodo entrar na conta desta aba. No dia em que
                orcamento voltar a ter canal, ele some sozinho — ninguem precisa
                lembrar de remover. */}
            {(dashboardMediaData.orcamentosNoPeriodo || 0) > 0 &&
             (dashboardMediaData.orcamentosNaConta || 0) === 0 ? (
              <div className="rounded-xl border border-info-line bg-info px-4 py-3">
                <p className="text-sm font-semibold text-info-ink">
                  Os números desta aba cobrem apenas leads — nenhum orçamento entra aqui.
                </p>
                <p className="mt-1 text-xs text-info-ink">
                  {`Dos ${dashboardMediaData.orcamentosNoPeriodo} orçamentos do período, nenhum pôde ser atribuído a uma mídia: o orçamento chega do ERP sem canal e sem campanha. Enquanto for assim, o ROAS e o valor por lead medem o que foi registrado no lead, e não a venda faturada.`}
                </p>
              </div>
            ) : null}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <StatCard label="Investimento" value={formatCurrencyBR(dashboardMediaData.investment || 0)} helper={`${dashboardMediaData.campaignsWithSpend || 0} campanha(s) com gasto`} tone="rose" />
              <StatCard label="Leads gerados" value={dashboardMediaData.leadsGenerated || 0} helper={`CPL ${formatCurrencyBR(dashboardMediaData.cpl || 0)}`} tone="slate" />
              {/* O rodape declara a BASE do calculo. Sem isso o numero engana sem
                  estar errado: medido em 09/09/2026, o ROAS estimado saia em 11,85
                  apoiado em 9 leads de 147 — os unicos com valor preenchido. */}
              <StatCard
                label="ROAS estimado"
                value={formatRatio(dashboardMediaData.roasEstimated)}
                helper={(
                  <>
                    <span className="block">{formatCurrencyBR(dashboardMediaData.estimatedReturn || 0)}</span>
                    <span className="block">
                      {`Base: ${dashboardMediaData.leadsComValor || 0} de ${dashboardMediaData.leadsGenerated || 0} leads com valor`}
                    </span>
                  </>
                )}
                tone="blue"
              />
              <StatCard
                label="ROAS fechado"
                value={formatRatio(dashboardMediaData.roasClosed)}
                helper={(
                  <>
                    <span className="block">{formatCurrencyBR(dashboardMediaData.closedReturn || 0)}</span>
                    <span className="block">
                      {`Base: ${dashboardMediaData.leadsGanhos || 0} ganho(s) de ${dashboardMediaData.leadsGenerated || 0} leads`}
                    </span>
                  </>
                )}
                tone="emerald"
              />
              {/* Os cards "CPL" e "Orçamento estimado" foram removidos por duplicidade:
                  CPL ja aparece no rodape de "Leads gerados" e o orcamento estimado
                  ja e o rodape de "ROAS estimado". Nenhum calculo foi alterado. */}
              <StatCard
                label="Estimado por lead"
                value={formatCurrencyBR(dashboardMediaData.estimatedPerLead || 0)}
                helper={`Orçado ÷ leads gerados · valor vem de ${dashboardMediaData.leadsComValor || 0} lead(s)`}
                tone="blue"
              />
              <StatCard
                label="Fechado por lead"
                value={formatCurrencyBR(dashboardMediaData.closedPerLead || 0)}
                helper={`Fechado ÷ leads gerados · ${dashboardMediaData.leadsGanhos || 0} ganho(s) na base`}
                tone="emerald"
              />
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
              <div className={UI_CARD}>
                <p className={UI_EYEBROW}>Marketing</p>
                <h3 className={UI_H2}>Investimento x Leads</h3>
                <ChartSeriesLabel color={CHART_COLORS.atencao} text="Investimento por mês" />
                <MiniLineChart
                  data={dashboardMediaData.spendVsLeads.map((item) => ({ label: item.label, value: item.spend }))}
                  color={CHART_COLORS.atencao}
                  formatValue={formatCurrencyBR}
                />
                <div className="mt-4">
                  <ChartSeriesLabel color={CHART_COLORS.volume} text="Leads gerados por mês" />
                  <MiniBarChart
                    data={dashboardMediaData.spendVsLeads.map((item) => ({ label: item.label, value: item.leads }))}
                    color={CHART_COLORS.volume}
                    limit={12}
                  />
                </div>
              </div>
              <div className={UI_CARD}>
                <p className={UI_EYEBROW}>Performance</p>
                <h3 className={UI_H2}>ROAS estimado x fechado</h3>
                <ChartSeriesLabel color={CHART_COLORS.volume} text="ROAS estimado" />
                <MiniLineChart
                  data={dashboardMediaData.roasEvolution.map((item) => ({ label: item.label, value: item.estimated }))}
                  color={CHART_COLORS.volume}
                  formatValue={formatRatio}
                />
                <div className="mt-4">
                  <ChartSeriesLabel color={CHART_COLORS.positivo} text="ROAS fechado" />
                  <MiniLineChart
                    data={dashboardMediaData.roasEvolution.map((item) => ({ label: item.label, value: item.closed }))}
                    color={CHART_COLORS.positivo}
                    formatValue={formatRatio}
                  />
                </div>
              </div>
            </div>
            <div className={UI_CARD}>
              <div className="flex items-center justify-between mb-4 gap-3">
                <div>
                  <p className={UI_EYEBROW}>Ads</p>
                  <h3 className="text-lg font-bold text-ink">Performance por campanha</h3>
                </div>
                <span className="text-xs text-ink-faint">
                  Plataforma principal: {dashboardMediaData.topPlatform || '-'}
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-ink-faint">
                      <th className={UI_TD}>Campanha</th>
                      <th className={UI_TD}>Plataforma</th>
                      <th className={UI_TD}>Investimento</th>
                      <th className={UI_TD}>Leads</th>
                      <th className={UI_TD}>CPL</th>
                      <th className={UI_TD}>Estimado</th>
                      <th className={UI_TD}>Fechado</th>
                      <th className={UI_TD}>ROAS est.</th>
                      <th className={UI_TD}>ROAS fech.</th>
                      <th className="py-2 px-2 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardMediaData.rows.map((row) => (
                      <tr key={row.key} className="border-b last:border-none">
                        <td className={UI_TD}>{row.campaign || '-'}</td>
                        <td className={UI_TD}>{row.platform || '-'}</td>
                        <td className={UI_TD}>{formatCurrencyBR(row.investment || 0)}</td>
                        <td className={UI_TD}>{row.leads || 0}</td>
                        <td className={UI_TD}>{formatCurrencyBR(row.cpl || 0)}</td>
                        <td className={UI_TD}>{formatCurrencyBR(row.estimated || 0)}</td>
                        <td className={UI_TD}>{formatCurrencyBR(row.closed || 0)}</td>
                        <td className={UI_TD}>{formatRatio(row.roasEstimated)}</td>
                        <td className={UI_TD}>{formatRatio(row.roasClosed)}</td>
                        <td className="py-2 px-2 text-right text-xs text-ink-faint">—</td>
                      </tr>
                    ))}
                    {dashboardMediaData.rows.length === 0 && (
                      <tr>
                        <td colSpan={10} className="py-4 text-center text-ink-faint text-sm">
                          Nenhum lançamento de Ads encontrado para os filtros atuais.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className={UI_CARD}>
              <div className="flex items-center justify-between mb-4 gap-3">
                <div>
                  <p className={UI_EYEBROW}>Ads</p>
                  <h3 className="text-lg font-bold text-ink">Lançamentos recentes</h3>
                </div>
                {isAdmin && (
                <button
                  onClick={openNewAdSpendModal}
                  className="px-3 py-2 rounded-xl bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium transition-colors"
                >
                  Novo lançamento
                </button>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-ink-faint">
                      <th className={UI_TD}>Data</th>
                      <th className={UI_TD}>Plataforma</th>
                      <th className={UI_TD}>Campanha</th>
                      <th className={UI_TD}>Valor</th>
                      <th className={UI_TD}>Observações</th>
                      <th className="py-2 px-2 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adSpendItems
                      .slice()
                      .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')))
                      .slice(0, 8)
                      .map((entry) => (
                        <tr key={entry.id} className="border-b last:border-none">
                          <td className={UI_TD}>{formatDateBR(entry.date)}</td>
                          <td className={UI_TD}>{entry.platform || '-'}</td>
                          <td className={UI_TD}>{entry.campaign || '-'}</td>
                          <td className={UI_TD}>{formatCurrencyBR(entry.amount || 0)}</td>
                          <td className={UI_TD}>{entry.notes || '-'}</td>
                          <td className="py-2 px-2 text-right space-x-2">
                            {isAdmin && (
                              <>
                                <button onClick={() => openEditAdSpendModal(entry)} className="text-brand-ink text-xs">
                                  Editar
                                </button>
                                <button onClick={() => deleteAdSpend(entry.id)} className="text-risk-ink text-xs">
                                  Excluir
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    {adSpendItems.length === 0 && (
                      <tr>
                        <td colSpan={6} className="py-4 text-center text-ink-faint text-sm">
                          Nenhum gasto lançado ainda.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          )}
          </section>
        )}

        {activeTab === 'emkt' && (
          <section className="space-y-6">
            <div className="bg-surface-card rounded-3xl shadow-xl p-6 border border-line">
              <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.24em] text-ink-faint font-bold">Email Marketing</p>
                  <h2 className="mt-2 text-3xl font-black text-ink">Emkt | Mailrelay</h2>
                  <p className="mt-2 text-sm text-ink-faint">
                    KPIs de engajamento, campanhas e leads impactados pela Mailrelay.
                  </p>
                </div>
                <div className="flex gap-3 flex-wrap">
                  {isAdmin && (
                    <button
                      onClick={syncMailrelayEngagement}
                      disabled={syncingMailrelay || !mailrelaySettings.configured}
                      className="px-4 py-3 rounded-2xl bg-brand-600 text-white font-semibold shadow hover:bg-brand-700 transition disabled:opacity-60"
                    >
                      {syncingMailrelay ? 'Sincronizando...' : 'Sincronizar Mailrelay'}
                    </button>
                  )}
                </div>
              </div>

              <div className="mt-5 rounded-2xl border border-line bg-surface-low p-4">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div>
                    <p className={UI_EYEBROW}>Filtros do Emkt</p>
                    <p className="text-sm text-ink-faint">Leitura separada da dashboard principal.</p>
                  </div>
                  <button
                    onClick={() => {
                      setEmktPeriod('90d');
                      setEmktStartDate('');
                      setEmktEndDate('');
                      setEmktEventFilter('all');
                      setEmktCampaignFilter('');
                      setEmktSearch('');
                    }}
                    className="px-3 py-2 rounded-xl border border-line bg-surface-card text-sm text-ink-soft shadow-sm"
                  >
                    Limpar filtros
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                  <select value={emktPeriod} onChange={(e) => setEmktPeriod(e.target.value)} className={UI_INPUT_PILL}>
                    <option value="30d">Últimos 30 dias</option>
                    <option value="90d">Últimos 90 dias</option>
                    <option value="6m">Últimos 6 meses</option>
                    <option value="12m">Últimos 12 meses</option>
                    <option value="custom">Período personalizado</option>
                    <option value="all">Todo o período</option>
                  </select>
                  <select value={emktEventFilter} onChange={(e) => setEmktEventFilter(e.target.value)} className={UI_INPUT_PILL}>
                    {EMAIL_EVENT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={emktCampaignFilter}
                    onChange={(e) => setEmktCampaignFilter(e.target.value)}
                    placeholder="Filtrar campanha"
                    className={UI_INPUT_PILL}
                  />
                  <input
                    type="text"
                    value={emktSearch}
                    onChange={(e) => setEmktSearch(e.target.value)}
                    placeholder="Buscar lead ou email"
                    className={UI_INPUT_PILL}
                  />
                </div>

                {emktPeriod === 'custom' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                    <input type="date" value={emktStartDate} onChange={(e) => setEmktStartDate(e.target.value)} className={UI_INPUT_PILL} />
                    <input type="date" value={emktEndDate} onChange={(e) => setEmktEndDate(e.target.value)} className={UI_INPUT_PILL} />
                  </div>
                )}
              </div>

              <div className="mt-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                {/* 23/09/2026: este card dizia so "Campanhas" e o dono perguntou por
                    que mostrava 1 se havia mais. O numero estava certo — conta as
                    campanhas que geraram evento de um LEAD DO CRM, nao as que o
                    Mailrelay enviou. Faltava o rotulo dizer isso. */}
                <StatCard label="Campanhas que tocaram o CRM" value={emktSummary.campaigns || 0} helper="campanhas com abertura, clique ou descadastro de um lead daqui" tone="slate" />
                <StatCard label="Leads impactados" value={emktSummary.impactedLeads || 0} helper={`${emktSummary.totalEvents || 0} evento(s)`} tone="blue" />
                <StatCard label="Aberturas" value={emktSummary.opens || 0} helper={`${emktSummary.clicks || 0} clique(s)`} tone="emerald" />
                <StatCard label="Descadastros" value={emktSummary.unsubscribes || 0} helper={`${emktSummary.unsubscribeRate || 0}% sobre aberturas`} tone="amber" />
              </div>

              {/* Card proprio das campanhas do Mailrelay (23/09/2026).
                  Separado dos numeros de engajamento porque responde OUTRA
                  pergunta: o que foi enviado, e nao o que voltou como evento de
                  lead. Cada numero aqui tem o nome do que ele conta. */}
              {podeVerEmailMkt && (
                <div className={`${UI_CARD} mt-4`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className={UI_EYEBROW}>Direto do Mailrelay</p>
                      <h3 className={UI_H2}>Campanhas enviadas</h3>
                      <p className="text-xs text-ink-faint mt-1">
                        O que a ferramenta disparou no período — independente de ter chegado em alguém do CRM.
                      </p>
                    </div>
                    <button
                      onClick={() => carregarCampanhasMailrelay(emktPeriod)}
                      disabled={mrCampanhasCarregando}
                      className="px-3 py-2 rounded-lg border border-line bg-surface-card text-sm text-ink-soft hover:bg-surface-low disabled:opacity-60"
                    >
                      {mrCampanhasCarregando ? 'Lendo...' : mrCampanhas ? 'Atualizar' : 'Buscar no Mailrelay'}
                    </button>
                  </div>

                  {mrCampanhasErro && (
                    <p className="mt-3 text-xs px-3 py-2 rounded-lg bg-risk border border-risk-line text-risk-ink">
                      {mrCampanhasErro}
                    </p>
                  )}

                  {!mrCampanhas && !mrCampanhasCarregando && !mrCampanhasErro && (
                    <p className="mt-3 text-sm text-ink-soft">
                      Clique em “Buscar no Mailrelay” para ver o que foi enviado. Os números acima contam outra coisa:
                      só o que voltou como abertura, clique ou descadastro de um lead do CRM.
                    </p>
                  )}

                  {mrCampanhas && (
                    <>
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="rounded-xl border border-line bg-surface-low p-3">
                          <p className="text-[11px] uppercase font-bold text-ink-faint">Enviadas no período</p>
                          <p className={UI_STAT}>{mrCampanhas.enviadas_no_periodo}</p>
                          <p className="text-[11px] text-ink-faint">de {mrCampanhas.enviadas_no_total} que o Mailrelay guarda</p>
                        </div>
                        <div className="rounded-xl border border-line bg-surface-low p-3">
                          <p className="text-[11px] uppercase font-bold text-ink-faint">Tocaram algum lead daqui</p>
                          <p className={UI_STAT}>{mrCampanhas.tocaram_o_crm}</p>
                          <p className="text-[11px] text-ink-faint">geraram abertura, clique ou descadastro de um lead</p>
                        </div>
                        <div className="rounded-xl border border-line bg-surface-low p-3">
                          <p className="text-[11px] uppercase font-bold text-ink-faint">Sem eco no CRM</p>
                          <p className={UI_STAT}>{mrCampanhas.sem_eco_no_crm}</p>
                          <p className="text-[11px] text-ink-faint">foram enviadas, mas ninguém daqui reagiu</p>
                        </div>
                      </div>

                      {mrCampanhas.parado_demais && (
                        <p className="mt-3 text-xs px-3 py-2 rounded-lg bg-risk border border-risk-line text-risk-ink">
                          <strong>{mrCampanhas.dias_sem_campanha} dias sem disparar campanha.</strong>{' '}
                          A última saiu em {new Date(mrCampanhas.ultima_campanha_em).toLocaleDateString('pt-BR')}.
                          O aviso aparece acima de {mrCampanhas.alerta_dias} dias.
                        </p>
                      )}

                      {!mrCampanhas.parado_demais && mrCampanhas.dias_sem_campanha !== null && (
                        <p className="mt-3 text-xs text-ink-faint">
                          Última campanha há {mrCampanhas.dias_sem_campanha} dia(s). O aviso de parado aparece acima de {mrCampanhas.alerta_dias}.
                        </p>
                      )}

                      {mrCampanhas.eventos_sem_lead > 0 && (
                        <div className="mt-3 text-xs px-3 py-2 rounded-lg bg-warn border border-warn-line text-warn-ink">
                          <p>
                            {mrCampanhas.eventos_sem_lead} evento(s) de quem abriu ou clicou e <strong>não é lead do CRM</strong>.
                            {' '}Desde 24/09/2026 essas pessoas ficam guardadas — medido na sync de{' '}
                            {mrCampanhas.eventos_sem_lead_medido_em
                              ? new Date(mrCampanhas.eventos_sem_lead_medido_em).toLocaleString('pt-BR')
                              : 'data não registrada'}.
                          </p>
                          {podeVerEmailMkt && (
                            <button
                              onClick={abrirEngajou}
                              className="mt-2 px-3 py-1.5 rounded-lg border border-warn-line bg-surface-card text-ink text-xs font-medium hover:bg-surface-low"
                            >
                              Ver quem engajou sem virar lead
                            </button>
                          )}
                        </div>
                      )}

                      <div className="mt-4 space-y-2 max-h-[340px] overflow-y-auto">
                        {mrCampanhas.campanhas.length === 0 && (
                          <p className="text-sm text-ink-soft">Nenhuma campanha enviada nesse período.</p>
                        )}
                        {mrCampanhas.campanhas.map((c) => (
                          <div key={c.id} className="border border-line rounded-lg px-3 py-2 flex flex-wrap items-center justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-sm text-ink truncate">{c.nome}</p>
                              <p className="text-[11px] text-ink-faint">
                                {c.enviada_em ? new Date(c.enviada_em).toLocaleDateString('pt-BR') : 'sem data de envio'}
                                {c.situacao ? ` · ${c.situacao}` : ''}
                              </p>
                              {/* Numeros do proprio Mailrelay. Campo que a conta nao
                                  devolve fica de fora, em vez de virar zero mentiroso. */}
                              <p className="text-[11px] text-ink-soft mt-0.5">
                                {[
                                  c.enviados !== null && c.enviados !== undefined ? `${c.enviados} enviado(s)` : null,
                                  c.entregues !== null && c.entregues !== undefined ? `${c.entregues} entregue(s)` : null,
                                  c.abriram !== null && c.abriram !== undefined ? `${c.abriram} abriram` : null,
                                  c.clicaram !== null && c.clicaram !== undefined ? `${c.clicaram} clicaram` : null,
                                  c.rejeitados ? `${c.rejeitados} rejeitado(s)` : null,
                                  c.descadastraram ? `${c.descadastraram} descadastro(s)` : null,
                                ].filter(Boolean).join('  ·  ') || 'a conta não devolveu números desta campanha'}
                              </p>
                            </div>
                            {c.tocou_o_crm ? (
                              <span className="text-[11px] px-2 py-1 rounded-full bg-ok border border-ok-line text-ok-ink shrink-0">
                                {c.leads_tocados} lead(s) · {c.eventos_no_crm} evento(s)
                              </span>
                            ) : (
                              <span className="text-[11px] px-2 py-1 rounded-full bg-surface-low border border-line text-ink-faint shrink-0">
                                sem eco no CRM
                              </span>
                            )}
                          </div>
                        ))}
                      </div>

                      {mrCampanhas.campanhas.length > 0 && (
                        <p className="mt-3 text-[10px] text-ink-faint">
                          Data do disparo lida do campo <strong>{mrCampanhas.campanhas[0].origem_da_data}</strong> do Mailrelay.
                        </p>
                      )}

                      {(mrCampanhas.campos_disponiveis || []).length > 0 && (
                        <p className="mt-1 text-[10px] text-ink-faint">
                          Campos que a sua conta devolve: {mrCampanhas.campos_disponiveis.join(', ')}
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <div className={UI_CARD}>
                  <p className={UI_EYEBROW}>Taxa de clique</p>
                  <p className={UI_STAT}>{emktSummary.clickRate || 0}%</p>
                </div>
                <div className={UI_CARD}>
                  <p className={UI_EYEBROW}>Eventos gravados</p>
                  <p className={UI_STAT}>{mailrelaySettings.email_events || 0}</p>
                </div>
                <div className={UI_CARD}>
                  <p className={UI_EYEBROW}>Sem match</p>
                  <p className={UI_STAT}>{mailrelaySettings.last_sync?.unmatched_leads_count || 0}</p>
                </div>
                <div className={UI_CARD}>
                  <p className={UI_EYEBROW}>Duplicados ignorados</p>
                  <p className={UI_STAT}>{mailrelaySettings.last_sync?.duplicate_events_skipped || 0}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
              <div className={UI_CARD}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className={UI_EYEBROW}>Tendência</p>
                    <h3 className="text-lg font-bold text-ink">Aberturas por mês</h3>
                  </div>
                </div>
                <MiniLineChart data={emktEventEvolution.map((item) => ({ label: item.label, value: item.opens }))} color="#0f766e" />
              </div>
              <div className={UI_CARD}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className={UI_EYEBROW}>Conversão</p>
                    <h3 className="text-lg font-bold text-ink">Cliques por mês</h3>
                  </div>
                </div>
                <MiniLineChart data={emktEventEvolution.map((item) => ({ label: item.label, value: item.clicks }))} color="#2563eb" />
              </div>
              <div className={UI_CARD}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className={UI_EYEBROW}>Campanhas</p>
                    <h3 className="text-lg font-bold text-ink">Top campanhas</h3>
                  </div>
                </div>
                <MiniBarChart data={emktCampaignRows.slice(0, 6).map((row) => ({ label: row.campaign, value: row.opens + row.clicks }))} color="#7c3aed" />
              </div>
            </div>

            <div className={UI_CARD}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className={UI_EYEBROW}>Campanhas</p>
                  <h3 className="text-lg font-bold text-ink">Resumo por campanha</h3>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-ink-faint">
                      <th className={UI_TD}>Campanha</th>
                      <th className={UI_TD}>Aberturas</th>
                      <th className={UI_TD}>Cliques</th>
                      <th className={UI_TD}>CTR</th>
                      <th className={UI_TD}>Descadastros</th>
                      <th className={UI_TD}>Leads impactados</th>
                      <th className={UI_TD}>Último evento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emktCampaignRows.map((row) => (
                      <tr key={row.campaign} className="border-b last:border-none">
                        <td className="py-2 px-2 font-medium text-ink">{row.campaign}</td>
                        <td className={UI_TD}>{row.opens}</td>
                        <td className={UI_TD}>{row.clicks}</td>
                        <td className={UI_TD}>{row.clickRate}%</td>
                        <td className={UI_TD}>{row.unsubscribes}</td>
                        <td className={UI_TD}>{row.impactedLeads}</td>
                        <td className="py-2 px-2 text-ink-faint">{row.lastEventAt ? formatDateTimeBR(row.lastEventAt) : '-'}</td>
                      </tr>
                    ))}
                    {emktCampaignRows.length === 0 && (
                      <tr>
                        <td colSpan={7} className="py-4 text-center text-ink-faint text-sm">
                          Nenhuma campanha encontrada para os filtros atuais.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className={UI_CARD}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className={UI_EYEBROW}>Leads</p>
                  <h3 className="text-lg font-bold text-ink">Leads impactados</h3>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-ink-faint">
                      <th className={UI_TD}>Lead</th>
                      <th className={UI_TD}>Empresa</th>
                      <th className={UI_TD}>Email</th>
                      <th className={UI_TD}>Campanha</th>
                      <th className={UI_TD}>Aberturas</th>
                      <th className={UI_TD}>Cliques</th>
                      <th className={UI_TD}>Descadastros</th>
                      <th className={UI_TD}>Último evento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emktLeadRows.map((row) => (
                      <tr key={`${row.leadId || row.email}-${row.lastEventAt}`} className="border-b last:border-none">
                        <td className="py-2 px-2 font-medium text-ink">{row.name}</td>
                        <td className={UI_TD}>{row.company}</td>
                        <td className={UI_TD}>{row.email}</td>
                        <td className={UI_TD}>{row.campaign}</td>
                        <td className={UI_TD}>{row.opens}</td>
                        <td className={UI_TD}>{row.clicks}</td>
                        <td className={UI_TD}>{row.unsubscribes}</td>
                        <td className="py-2 px-2 text-ink-faint">{row.lastEventAt ? formatDateTimeBR(row.lastEventAt) : '-'}</td>
                      </tr>
                    ))}
                    {emktLeadRows.length === 0 && (
                      <tr>
                        <td colSpan={8} className="py-4 text-center text-ink-faint text-sm">
                          Nenhum lead impactado encontrado para os filtros atuais.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        {activeTab === 'recuperacao' && (
          <Recuperacao
            apiUrl={API_URL}
            token={token}
            user={user}
            users={users}
            isAdmin={isAdmin}
            onVoltar={() => {
              // Recarrega: as ligacoes registradas mudam "Último resultado" e
              // "Próximo retorno" da lista (achado pelo teste de tela, 21/10/2026).
              loadBudgets();
              setActiveTab('orcamentos');
            }}
          />
        )}

        {activeTab === 'orcamentos' && (
          <section className="space-y-6">
            <div className="bg-surface-card rounded-3xl shadow-xl p-6 border border-line">
              <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.24em] text-ink-faint font-bold">Budget Dashboard</p>
                  <h2 className="mt-2 text-3xl font-black text-ink">Orçamentos</h2>
                  <p className="mt-2 text-sm text-ink-faint">
                    Indicadores de produção comercial e de orçamentação para acompanhamento da diretoria.
                  </p>
                </div>
                <div className="flex gap-3 flex-wrap">
                  <input
                    ref={budgetImportInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    className="hidden"
                    onChange={handleBudgetImportFile}
                  />
                  <button
                    onClick={() => budgetImportInputRef.current?.click()}
                    disabled={importingBudgets}
                    className="px-4 py-3 rounded-2xl border border-line bg-surface-card text-ink-soft font-semibold hover:bg-surface-low transition disabled:opacity-60"
                  >
                    {importingBudgets ? 'Importando...' : 'Importar planilha'}
                  </button>
                  <button
                    onClick={exportBudgetsXlsx}
                    className="px-4 py-3 rounded-2xl border border-line bg-surface-card text-ink font-semibold shadow-sm hover:bg-surface-low transition"
                  >
                    Exportar planilha
                  </button>
                  <button
                    onClick={() => setActiveTab('recuperacao')}
                    className="px-4 py-3 rounded-2xl border border-line bg-surface-card text-brand-ink font-semibold shadow-sm hover:bg-surface-low transition"
                  >
                    Recuperação
                  </button>
                  <button
                    onClick={openNewBudgetModal}
                    className="px-4 py-3 rounded-2xl bg-brand-600 text-white font-semibold shadow hover:bg-brand-700 transition"
                  >
                    Novo orçamento
                  </button>
                </div>
              </div>

              <div className="mt-5 rounded-2xl border border-line bg-surface-low p-4">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div>
                    <p className={UI_EYEBROW}>Filtros de orçamentos</p>
                    <p className="text-sm text-ink-faint">Filtros próprios do módulo de orçamentos.</p>
                  </div>
                  <button
                    onClick={() => {
                      setBudgetPeriod('90d');
                      setBudgetStartDate('');
                      setBudgetEndDate('');
                      setBudgetStatusFilter('all');
                      setBudgetOwnerFilter('all');
                      setBudgetEstimatorFilter('all');
                      setBudgetLossReasonFilter('all');
                      setBudgetRepresentanteFilter('');
                      setBudgetSearch('');
                    }}
                    className="px-3 py-2 rounded-xl border border-line bg-surface-card text-sm text-ink-soft shadow-sm"
                  >
                    Limpar filtros
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                  <select value={budgetPeriod} onChange={(e) => setBudgetPeriod(e.target.value)} className={UI_INPUT_PILL}>
                    <option value="30d">Últimos 30 dias</option>
                    <option value="90d">Últimos 90 dias</option>
                    <option value="6m">Últimos 6 meses</option>
                    <option value="12m">Últimos 12 meses</option>
                    <option value="custom">Período personalizado</option>
                    <option value="all">Todo o período</option>
                  </select>
                  <select value={budgetStatusFilter} onChange={(e) => setBudgetStatusFilter(e.target.value)} className={UI_INPUT_PILL}>
                    <option value="all">Todos os status</option>
                    {BUDGET_STATUS_OPTIONS.map((status) => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                  <select value={budgetOwnerFilter} onChange={(e) => setBudgetOwnerFilter(e.target.value)} className={UI_INPUT_PILL}>
                    <option value="all">Todos os vendedores</option>
                    <optgroup label="Somente um vendedor">
                      {users.map((u) => (
                        <option key={`somente-${u.id}`} value={u.id}>{u.name}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Excluir um vendedor">
                      {users.map((u) => (
                        <option key={`exceto-${u.id}`} value={`exceto:${u.id}`}>Todos, exceto {u.name}</option>
                      ))}
                    </optgroup>
                  </select>
                  <select value={budgetEstimatorFilter} onChange={(e) => setBudgetEstimatorFilter(e.target.value)} className={UI_INPUT_PILL}>
                    <option value="all">Todos os orçamentistas</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                    <option value={ESTIMATOR_REPRESENTANTE_ID}>{ESTIMATOR_REPRESENTANTE_LABEL}</option>
                  </select>
                  <select value={budgetLossReasonFilter} onChange={(e) => setBudgetLossReasonFilter(e.target.value)} className={UI_INPUT_PILL}>
                    <option value="all">Todos os motivos de perda</option>
                    {BUDGET_LOSS_REASON_OPTIONS.map((reason) => (
                      <option key={reason.value} value={reason.value}>{reason.label}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    list="lista-representantes"
                    value={budgetRepresentanteFilter}
                    onChange={(e) => setBudgetRepresentanteFilter(e.target.value)}
                    className={UI_INPUT_PILL}
                    placeholder="Filtrar representante"
                  />
                  <input
                    type="text"
                    value={budgetSearch}
                    onChange={(e) => setBudgetSearch(e.target.value)}
                    className={UI_INPUT_PILL}
                    placeholder="Buscar cliente, empresa, campanha..."
                  />
                  <datalist id="lista-representantes">
                    {representanteOptions.map((nome) => (
                      <option key={nome} value={nome} />
                    ))}
                  </datalist>
                </div>

                {/* Datas sempre visiveis. Antes ficavam escondidas atras da opcao
                    "Periodo personalizado" do seletor e o time nao achava o filtro por data.
                    Preencher qualquer uma das datas ja troca o periodo para personalizado. */}
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="rounded-2xl border border-brand-200 bg-brand-50 p-3">
                    <label className="block text-xs font-semibold text-ink-soft mb-2">Data inicial</label>
                    <input
                      type="date"
                      value={budgetStartDate}
                      onChange={(e) => {
                        setBudgetStartDate(e.target.value);
                        if (e.target.value) setBudgetPeriod('custom');
                      }}
                      className="w-full px-3 py-2 border border-line rounded-xl text-sm bg-surface-card"
                    />
                  </div>
                  <div className="rounded-2xl border border-brand-200 bg-brand-50 p-3">
                    <label className="block text-xs font-semibold text-ink-soft mb-2">Data final</label>
                    <input
                      type="date"
                      value={budgetEndDate}
                      onChange={(e) => {
                        setBudgetEndDate(e.target.value);
                        if (e.target.value) setBudgetPeriod('custom');
                      }}
                      className="w-full px-3 py-2 border border-line rounded-xl text-sm bg-surface-card"
                    />
                  </div>
                </div>
                <p className="mt-2 text-[11px] text-ink-soft">
                  Deixe as duas datas em branco para usar o periodo do seletor acima. Preencher uma delas filtra de dia a dia.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <StatCard
                label="Orçamentos"
                value={budgetStats.total || 0}
                helper={(
                  <>
                    <span className="block">{`${budgetStats.enviados || 0} enviados · ${formatCurrencyBR(budgetStats.valorEnviado || 0)}`}</span>
                    <span className="block">{`${budgetStats.naoFeitos || 0} não feitos · ${formatCurrencyBR(budgetStats.valorNaoFeito || 0)}`}</span>
                  </>
                )}
                tone="slate"
              />
              <StatCard label="Taxa de Aprovação" value={`${budgetStats.taxaAprovacao || 0}%`} helper={`${budgetStats.aprovados || 0} aprovados`} tone="blue" />
              <StatCard label="Valor Orçado" value={formatCurrencyBR(budgetStats.valorOrcado || 0)} helper={`Ticket médio ${formatCurrencyBR(budgetStats.ticketMedio || 0)}`} tone="emerald" />
              <StatCard label="Valor Fechado" value={formatCurrencyBR(budgetStats.valorFechado || 0)} helper={`${budgetStats.reprovados || 0} reprovados · ${formatCurrencyBR(budgetStats.valorReprovado || 0)}`} tone="amber" />
            </div>


            <div className="bg-surface-card rounded-2xl shadow p-5 border border-line" data-teste="card-orcamentos">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                  <p className={UI_EYEBROW}>Tabela operacional</p>
                  <h3 className="text-lg font-bold text-ink">Lista de orçamentos</h3>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => budgetImportInputRef.current?.click()}
                    disabled={importingBudgets}
                    className="px-4 py-2 rounded-xl border border-line bg-surface-card text-ink-soft text-sm font-medium hover:bg-surface-low disabled:opacity-60"
                  >
                    {importingBudgets ? 'Importando...' : 'Importar'}
                  </button>
                  <button
                    onClick={exportBudgetsXlsx}
                    className="px-4 py-2 rounded-xl border border-line bg-surface-card text-ink text-sm font-medium"
                  >
                    Exportar
                  </button>
                  <button onClick={openNewBudgetModal} className="px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium transition-colors">Cadastrar</button>
                </div>
              </div>
              {/* 22/10/2026: 14 colunas viraram 9 (Cliente+Empresa, Responsaveis, Retorno)
                  para caber na tela sem rolar o card de lado. A rolagem lateral, se um dia
                  precisar, fica so na tabela: o titulo e os botoes nao saem do lugar. */}
              <div className="overflow-x-auto">
              <table className="w-full text-sm" data-teste="tabela-orcamentos">
                <thead>
                  <tr className="text-left text-ink-faint border-b border-line">
                    <ThOrdenavel className="py-2 pr-3" chave="client_name" tipo="texto" ordemAtual={budgetSortKey} direcao={budgetSortDir} aoOrdenar={aplicarOrdenacaoOrcamento}>Cliente</ThOrdenavel>
                    <ThOrdenavel className="py-2 pr-3" chave="status" tipo="texto" ordemAtual={budgetSortKey} direcao={budgetSortDir} aoOrdenar={aplicarOrdenacaoOrcamento}>Status</ThOrdenavel>
                    <ThOrdenavel className="py-2 pr-3" chave="owner_name" tipo="texto" ordemAtual={budgetSortKey} direcao={budgetSortDir} aoOrdenar={aplicarOrdenacaoOrcamento}>Responsáveis</ThOrdenavel>
                    <ThOrdenavel className="py-2 pr-3 whitespace-nowrap" chave="budget_value" tipo="numero" ordemAtual={budgetSortKey} direcao={budgetSortDir} aoOrdenar={aplicarOrdenacaoOrcamento}>Valor Orçado</ThOrdenavel>
                    <ThOrdenavel className="py-2 pr-3 whitespace-nowrap" chave="closed_value" tipo="numero" ordemAtual={budgetSortKey} direcao={budgetSortDir} aoOrdenar={aplicarOrdenacaoOrcamento}>Valor Fechado</ThOrdenavel>
                    <ThOrdenavel className="py-2 pr-3" chave="requested_at" tipo="data" ordemAtual={budgetSortKey} direcao={budgetSortDir} aoOrdenar={aplicarOrdenacaoOrcamento}>Solicitado</ThOrdenavel>
                    <ThOrdenavel className="py-2 pr-3 whitespace-nowrap" chave="loss_reason" tipo="texto" ordemAtual={budgetSortKey} direcao={budgetSortDir} aoOrdenar={aplicarOrdenacaoOrcamento}>Motivo perda</ThOrdenavel>
                    {/* Recuperacao (21/10/2026): proximo retorno e ultimo resultado sao calculados das interacoes. */}
                    <ThOrdenavel className="py-2 pr-3" chave="proximo_retorno" tipo="texto" ordemAtual={budgetSortKey} direcao={budgetSortDir} aoOrdenar={aplicarOrdenacaoOrcamento}>Retorno</ThOrdenavel>
                    <th className="py-2 pr-0">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {budgetSortedItems.slice(0, budgetFatia.mostrados).map((budget) => {
                    const empresaDiferente = budget.company && String(budget.company).trim().toLowerCase() !== String(budget.client_name || '').trim().toLowerCase();
                    return (
                    <tr key={budget.id} className="border-b border-surface-mid align-top">
                      <td className="py-3 pr-3">
                        <div className="font-medium text-ink break-words">
                          {budget.client_name || '-'}
                          {Number(budget.interactions_count || 0) > 0 ? (
                            <span
                              className="ml-2 text-[11px] font-normal text-ink-faint tabular-nums whitespace-nowrap"
                              title={
                                budget.last_interaction_at
                                  ? `Última interação: ${formatDateTimeBR(budget.last_interaction_at)}`
                                  : undefined
                              }
                            >
                              {Number(budget.interactions_count)} int.
                            </span>
                          ) : null}
                        </div>
                        {empresaDiferente ? <div className="text-xs text-ink-faint break-words">{budget.company}</div> : null}
                      </td>
                      <td className="py-3 pr-3 text-ink-soft">{BUDGET_STATUS_OPTIONS.find((item) => item.value === budget.status)?.label || budget.status}</td>
                      <td className="py-3 pr-3 text-ink-soft">
                        <div>{budget.owner_name || '-'}</div>
                        {budget.estimator_name ? <div className="text-xs text-ink-faint">Orç.: {budget.estimator_name}</div> : null}
                        {budget.representante ? <div className="text-xs text-ink-faint">Rep.: {budget.representante}</div> : null}
                      </td>
                      <td className="py-3 pr-3 text-ink-soft tabular-nums whitespace-nowrap">{formatCurrencyBR(budget.budget_value || 0)}</td>
                      <td className="py-3 pr-3 text-ink-soft tabular-nums whitespace-nowrap">{formatCurrencyBR(budget.closed_value || 0)}</td>
                      <td className="py-3 pr-3 text-ink-soft tabular-nums whitespace-nowrap">{formatDateBR(budget.requested_at)}</td>
                      <td className="py-3 pr-3 text-ink-soft">{BUDGET_LOSS_REASON_OPTIONS.find((item) => item.value === budget.loss_reason)?.label || '-'}</td>
                      <td className="py-3 pr-3 text-ink-soft">
                        <div className="tabular-nums whitespace-nowrap">{budget.proximo_retorno ? formatDateBR(`${budget.proximo_retorno}T12:00:00`) : '-'}</div>
                        {ROTULO_RESULTADO_LIGACAO[budget.ultimo_resultado] ? <div className="text-xs text-ink-faint">{ROTULO_RESULTADO_LIGACAO[budget.ultimo_resultado]}</div> : null}
                      </td>
                      <td className="py-3 pr-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <button onClick={() => openEditBudgetModal(budget)} className="px-3 py-1 rounded-lg border border-line text-ink-soft">Editar</button>
                          {/* Admin, dono, orcamentista ou orcamento sem dono (06/10/2026). O servidor usa a mesma regra. */}
                          {podeApagarOrcamentoNaTela(budget, user, isAdmin) && (
                            <button onClick={() => deleteBudget(budget.id)} className="px-3 py-1 rounded-lg border border-rose-200 text-rose-600">Excluir</button>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                  {!budgetFilteredItems.length && (
                    <tr>
                      <td colSpan="9" className="py-8 text-center text-ink-faint">Nenhum orçamento encontrado.</td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
              {budgetSortedItems.length > 0 && (
                <div className="flex flex-col items-center gap-2 pt-4">
                  <p className="text-xs text-ink-faint tabular-nums">Mostrando {budgetFatia.mostrados.toLocaleString('pt-BR')} de {budgetSortedItems.length.toLocaleString('pt-BR')}</p>
                  {budgetFatia.temBotao && (
                    <button
                      type="button"
                      onClick={() => setBudgetVisiveis((v) => v + ORCAMENTOS_POR_PAGINA)}
                      className="px-4 py-2 text-sm rounded-lg border border-line bg-surface-card text-ink-soft hover:bg-surface-low"
                      data-teste="ver-mais-orcamentos"
                    >
                      {budgetFatia.rotulo}
                    </button>
                  )}
                </div>
              )}
            </div>
          </section>
        )}

        <div className={activeTab === 'crm' ? '' : 'hidden'}>
        <section className="mb-3">
          {/* Quatro numeros que pedem acao. So o vencido recebe cor: e o unico
              que muda o que a pessoa faz agora. O resto (perfis, empresas,
              perdidos) e referencia e fica atras do "ver todos os numeros". */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-line border border-line rounded-2xl overflow-hidden">
            <button
              onClick={() => setUrgencyFilter('overdue')}
              className="text-left bg-surface-card hover:bg-risk/30 transition px-4 py-3 border-l-[3px] border-l-risk-ink"
              title="Filtrar a lista pelos contatos vencidos"
            >
              <p className="text-[10px] uppercase tracking-[0.09em] font-bold text-risk-ink">Contatos vencidos</p>
              <p className="text-2xl font-bold text-risk-ink tabular-nums leading-tight mt-0.5">
                {(agendaStats.overdue || 0).toLocaleString('pt-BR')}
              </p>
              <span className="text-[11px] font-semibold text-brand-ink">Ver na lista →</span>
            </button>
            <div className="bg-surface-card px-4 py-3">
              <p className="text-[10px] uppercase tracking-[0.09em] font-bold text-ink-faint">Novos sem contato</p>
              <p className="text-2xl font-bold text-ink tabular-nums leading-tight mt-0.5">
                {(localStats.novos || 0).toLocaleString('pt-BR')}
              </p>
              <p className="text-[11px] text-ink-faint">de {(localStats.total || 0).toLocaleString('pt-BR')} leads</p>
            </div>
            <div className="bg-surface-card px-4 py-3">
              <p className="text-[10px] uppercase tracking-[0.09em] font-bold text-ink-faint">Em negociação</p>
              <p className="text-2xl font-bold text-ink tabular-nums leading-tight mt-0.5">
                {(localStats.qtdNegociacao || 0).toLocaleString('pt-BR')}
              </p>
              <p className="text-[11px] text-ink-faint">{formatCurrencyBR(localStats.valorNegociacao || 0)}</p>
            </div>
            <div className="bg-surface-card px-4 py-3">
              <p className="text-[10px] uppercase tracking-[0.09em] font-bold text-ink-faint">Conversão</p>
              <p className="text-2xl font-bold text-ink tabular-nums leading-tight mt-0.5">
                {formatarPercentualBR(localStats.taxaConversao || 0)}
              </p>
              <p className="text-[11px] text-ink-faint">
                {(localStats.ganhos || 0).toLocaleString('pt-BR')} {localStats.ganhos === 1 ? 'ganho' : 'ganhos'} · {formatCurrencyBR(localStats.valorTotal || 0)}
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowStats((prev) => !prev)}
            className="mt-2 text-[11px] font-semibold text-brand-ink hover:underline"
          >
            {showStats ? 'Ocultar os outros números' : 'Ver todos os números (perfis, empresas, perdidos)'} ▾
          </button>
          {showStats && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              <div className="rounded-2xl border border-line bg-surface-card p-4 shadow-card">
                <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-ink-soft">Total de Leads</p>
                <p className="text-xl font-bold text-ink">{localStats.total || 0}</p>
              </div>
              <div className="rounded-2xl border border-line bg-surface-card p-4 shadow-card">
                <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-ink-faint">Novos</p>
                <p className="text-xl font-bold text-ink">{localStats.novos || 0}</p>
              </div>
              <div className="rounded-2xl border border-line bg-surface-card p-4 shadow-card">
                <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-ink-faint">Em contato</p>
                <p className="text-xl font-bold text-ink">{localStats.emContato || 0}</p>
              </div>
              <div className="rounded-2xl border border-line bg-surface-card p-4 shadow-card">
                <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-ink-faint">Taxa de Conversão</p>
                <p className="text-xl font-bold text-ink">{formatarPercentualBR(localStats.taxaConversao || 0)}</p>
              </div>
              <div className="rounded-2xl border border-line bg-surface-card p-4 shadow-card">
                <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-ink-faint">Valor Convertido</p>
                <p className="text-xl font-bold text-ink">
                  {formatCurrencyBR(localStats.valorTotal || 0)}
                </p>
                <p className="text-[11px] text-ink-soft mt-1">
                  {`Soma de ${localStats.ganhosComValor || 0} de ${localStats.ganhos || 0} ganhos com valor`}
                </p>
              </div>
              <div className="rounded-2xl border border-line bg-surface-card p-4 shadow-card">
                <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-ink-faint">Perdidos</p>
                <p className="text-xl font-bold text-ink">{localStats.perdidos || 0}</p>
                <p className="text-[11px] text-ink-soft mt-1">
                  Valor perdido: {formatCurrencyBR(localStats.valorPerdido || 0)}
                </p>
              </div>
              <div className="rounded-2xl border border-line bg-surface-card p-4 shadow-card">
                <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-ink-faint">Em negociação</p>
                <p className="text-xl font-bold text-ink">{localStats.qtdNegociacao || 0}</p>
                <p className="text-[11px] text-ink-soft mt-1">
                  Valor em neg.: {formatCurrencyBR(localStats.valorNegociacao || 0)}
                </p>
              </div>
              <div className="rounded-2xl border border-line bg-surface-card p-4 shadow-card">
                <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-ink-soft">Empresas (company preenchido)</p>
                <p className="text-xl font-bold text-ink">{statsSegments.empresas || 0}</p>
              </div>
              {CLIENT_SEGMENT_OPTIONS.filter((s) => s.value && s.value !== 'usuario_final').map((opt) => (
                <div key={opt.value} className="rounded-2xl border border-line bg-surface-card p-4 shadow-card">
                  <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-ink-soft">{opt.label}</p>
                  <p className="text-xl font-bold text-ink">{statsSegments.bySegment[opt.value] || 0}</p>
                </div>
              ))}
            </div>
          )}
        </section>


        <section className="mb-3">
          <div className="rounded-2xl border border-line bg-surface-card shadow-card overflow-hidden">
            <div className={`flex flex-wrap items-center justify-between gap-3 px-4 py-3 ${agendaAberta ? 'border-b border-surface-mid' : ''}`}>
              <button
                type="button"
                onClick={() => setAgendaAberta((v) => !v)}
                aria-expanded={agendaAberta}
                title={agendaAberta ? 'Fechar a lista' : 'Abrir a lista'}
                className="flex items-center gap-2 text-[15px] font-semibold text-ink hover:text-brand-ink transition-colors"
              >
                <span
                  aria-hidden="true"
                  className={`text-ink-soft transition-transform duration-150 ${agendaAberta ? 'rotate-90' : ''}`}
                >
                  ▸
                </span>
                Precisa de você
              </button>
              <div className="flex items-center gap-3">
                <div className="inline-flex rounded-lg border border-line overflow-hidden">
                  <button
                    onClick={() => setAgendaPane('agenda')}
                    className={`px-3 py-1.5 text-[11px] border-r border-line ${agendaPane === 'agenda' ? 'bg-brand-600 text-white font-semibold' : 'bg-surface-card text-ink-soft hover:bg-surface-low'}`}
                  >
                    Agenda · {agendaStats.total}
                  </button>
                  <button
                    onClick={() => setAgendaPane('followup')}
                    className={`px-3 py-1.5 text-[11px] ${agendaPane === 'followup' ? 'bg-brand-600 text-white font-semibold' : 'bg-surface-card text-ink-soft hover:bg-surface-low'}`}
                  >
                    Follow-up · {followUpLeads.length}
                  </button>
                </div>
                {agendaPane === 'agenda' && agendaBase.length > 5 && (
                  <button
                    onClick={() => setShowAllAgenda((v) => !v)}
                    className="text-xs text-brand-ink hover:underline whitespace-nowrap"
                  >
                    {showAllAgenda ? 'Mostrar menos' : 'Ver toda agenda'}
                  </button>
                )}
              </div>
            </div>
            {agendaAberta && agendaPane === 'agenda' && (
            <div className="max-h-64 overflow-y-auto">
              {agendaLeads.length === 0 && (
                <p className="text-xs text-ink-faint">Nenhum contato agendado.</p>
              )}
              {agendaLeads.map((lead) => {
                const now = new Date();
                now.setHours(0, 0, 0, 0);
                const date = (dataDeCalendario(lead.next_contact) || new Date(NaN));
                const responsible = lead.owner || lead.responsible_name;
                let rowClasses =
                  'flex items-center justify-between gap-3 px-3 py-2 border-l-[3px] border-b border-b-surface-mid last:border-b-0 text-sm cursor-pointer ';
                if (!Number.isNaN(date.getTime())) {
                  const today = now.getTime();
                  const day = new Date(
                    date.getFullYear(),
                    date.getMonth(),
                    date.getDate()
                  ).getTime();
                  const isToday = day === today;
                  const isOverdue = day < today;
                  // Tarja lateral em vez de fundo cheio: com 1.201 vencidos, o
                  // fundo vermelho virava parede e parava de sinalizar.
                  if (isOverdue) {
                    rowClasses += 'border-l-risk-ink bg-risk/25 hover:bg-risk/40';
                  } else if (isToday) {
                    rowClasses += 'border-l-warn-ink hover:bg-surface-low';
                  } else {
                    rowClasses += 'border-l-transparent hover:bg-surface-low';
                  }
                } else {
                  rowClasses += 'border-l-transparent hover:bg-surface-low';
                }

                return (
                  <div
                    key={lead.id}
                    className={rowClasses}
                    onClick={() => openEditLeadModal(lead)}
                  >
                    <div>
                      <p className={UI_STRONG}>
                        {lead.company || lead.name} {lead.contact ? `- ${lead.contact}` : ''}
                      </p>
                      {responsible && (
                        <p className="text-xs text-ink-faint">
                          Responsável: {responsible}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-xs text-ink-soft">
                          {lead.next_contact
                            ? formatFollowupBR(lead.next_contact)
                            : '-'}
                        </p>
                        <p className={UI_META}>
                          {lead.status || '-'}
                        </p>
                        {lead.followup_status && (
                          <p className={`mt-1 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                            lead.followup_status === 'pending'
                              ? 'bg-warn text-warn-ink'
                              : lead.followup_status === 'done'
                                ? 'bg-ok text-ok-ink'
                                : lead.followup_status === 'dismissed'
                                  ? 'bg-surface-mid text-ink-soft'
                                  : 'bg-risk text-risk-ink'
                          }`}>
                            {lead.followup_status}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <label className="inline-flex items-center gap-1 text-[11px] text-ink-soft">
                          <input
                            type="checkbox"
                            className="h-3 w-3 text-brand-ink border-line rounded"
                            onChange={() => handleAgendaContactDone(lead)}
                            checked={agendaUpdatingId === lead.id ? true : false}
                            disabled={agendaUpdatingId === lead.id}
                          />
                          Feito
                        </label>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleAgendaReschedule(lead, 1)}
                            className="text-[11px] text-brand-ink hover:underline disabled:opacity-50"
                            disabled={agendaUpdatingId === lead.id}
                          >
                            +1d
                          </button>
                          <button
                            onClick={() => handleAgendaReschedule(lead, 3)}
                            className="text-[11px] text-brand-ink hover:underline disabled:opacity-50"
                            disabled={agendaUpdatingId === lead.id}
                          >
                            +3d
                          </button>
                        </div>
                        {agendaUpdatingId === lead.id && (
                          <div className="w-3 h-3 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            )}
            {agendaAberta && agendaPane === 'followup' && (
            <div className="max-h-64 overflow-y-auto">
              {followUpLeads.length === 0 && (
                <p className="text-xs text-ink-faint">Nenhum lead pendente de follow-up.</p>
              )}
              {followUpLeads.map((lead) => (
                <div
                  key={lead.id}
                  className="flex items-center justify-between gap-3 px-3 py-2 border-l-[3px] border-l-transparent border-b border-b-surface-mid last:border-b-0 text-sm cursor-pointer hover:bg-surface-low"
                  onClick={() => openEditLeadModal(lead)}
                >
                  <div className="min-w-0">
                    <p className={UI_STRONG}>
                      {lead.name} {lead.contact ? `- ${lead.contact}` : ''}
                    </p>
                    <p className="text-[11px] text-ink-faint">
                      {lead.owner || lead.responsible_name || 'Sem responsável'}
                      {lead.status ? ` · ${lead.status}` : ''}
                    </p>
                    {lead.first_contact && (
                      <p className={UI_META}>
                        Primeiro contato: {formatDateBR(lead.first_contact)}
                      </p>
                    )}
                  </div>
                  <div className="text-right whitespace-nowrap">
                    <p className="text-xs text-ink-soft tabular-nums">{lead._daysSince || 0} dias parado</p>
                  </div>
                </div>
              ))}
            </div>
            )}
          </div>
        </section>

        <section className="rounded-2xl border border-line bg-surface-card shadow-card p-4">
          {/* Um azul solido por area (a acao principal); tudo mais e contorno.
              Lista/Kanban vira o que sempre foi: uma chave de dois estados. */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-3">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-lg font-bold text-ink">Leads</h2>
              <span className="text-[11px] text-ink-faint tabular-nums">
                {displayedLeads.length.toLocaleString('pt-BR')} na lista
              </span>
              <div className="inline-flex rounded-lg border border-line overflow-hidden">
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-1.5 text-xs border-r border-line ${viewMode === 'list'
                    ? 'bg-brand-600 text-white font-semibold'
                    : 'bg-surface-card text-ink-soft hover:bg-surface-low'
                    }`}
                >
                  Lista
                </button>
                <button
                  onClick={() => setViewMode('kanban')}
                  className={`px-3 py-1.5 text-xs ${viewMode === 'kanban'
                    ? 'bg-brand-600 text-white font-semibold'
                    : 'bg-surface-card text-ink-soft hover:bg-surface-low'
                    }`}
                >
                  Kanban
                </button>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <input
                ref={clientesImportInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                onChange={handleImportClientesFile}
              />
              <button
                onClick={() => clientesImportInputRef.current?.click()}
                disabled={importClientesOcupado}
                title="Subir a planilha de clientes baixada do AM Gestor"
                className="px-3 py-2 bg-surface-card text-ink-soft rounded-lg text-sm border border-line hover:bg-surface-low transition-colors disabled:opacity-60"
              >
                {importClientesOcupado ? 'Lendo...' : 'Importar'}
              </button>
              {isAdmin && (
                <button
                  onClick={abrirLixeira}
                  title="Leads excluidos. So administrador ve e restaura."
                  className="px-3 py-2 bg-surface-card text-ink-soft rounded-lg text-sm border border-line hover:bg-surface-low transition-colors"
                >
                  Lixeira
                </button>
              )}
              <button
                onClick={exportCsv}
                className="px-3 py-2 bg-surface-card text-ink-soft rounded-lg text-sm border border-line hover:bg-surface-low transition-colors"
              >
                Exportar
              </button>
              <button
                onClick={clearFilters}
                className="px-3 py-2 bg-surface-card text-ink-soft rounded-lg text-sm border border-line hover:bg-surface-low transition-colors"
              >
                Limpar filtros
              </button>
              <button
                onClick={openNewLeadModal}
                className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-semibold transition-colors"
              >
                Novo lead
              </button>
            </div>
          </div>

          <div className="mb-4 rounded-xl border border-line bg-surface-low p-3 flex flex-col gap-3">
            {/* Barra de filtros numa fileira unica que flui — 23/09/2026.
                Antes eram DUAS colunas fixas com quantidades diferentes de
                filtros, e dali sairam os dois defeitos que ele viu na tela:
                  1. a coluna curta era esticada ate a altura da outra, e os
                     seletores viravam blocos ("lajota"): medido, 132px de
                     altura onde o normal e 39px;
                  2. parado o esticamento, sobravam 187px de branco embaixo da
                     coluna esquerda, enquanto Temperatura, SLA e Mailrelay
                     ficavam espremidos em 86px, com o rotulo cortado
                     ("Toda t...", "Todo S...", "Mailrel...").
                Numa fileira unica cada filtro pede a largura que o proprio
                rotulo precisa (`min-w-[...]`), cresce para ocupar a sobra da
                linha (`grow`) e quebra para a linha de baixo quando a tela
                estreita. Nao existe mais coluna curta para esticar, nem sobra
                para aparecer, nem celula estreita para cortar texto.
                `items-start` continua aqui de proposito: sem ele o alinhamento
                padrao de flex esticaria os itens de novo. */}
            <div className="flex flex-wrap items-start gap-2">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Buscar por nome, email, telefone, campanha..."
                className="px-3 py-2 border border-line rounded-lg text-sm grow min-w-[260px] max-w-[360px]"
              />
              <input
                type="text"
                value={campaignFilter}
                onChange={(e) => setCampaignFilter(e.target.value)}
                placeholder="Filtrar campanha"
                className="px-3 py-2 border border-line rounded-lg text-sm grow min-w-[190px] max-w-[300px]"
              />
              <select
                value={ownerFilter}
                onChange={(e) => {
                  const val = e.target.value;
                  setOwnerFilter(val);
                  setAgendaOwnerFilter(val === 'all' ? 'todos' : val);
                }}
                className="px-3 py-2 border border-line rounded-lg text-sm bg-surface-card grow min-w-[200px] max-w-[300px]"
              >
                <option value="all">Todos os responsáveis</option>
                <option value="unassigned">Sem proprietário</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} ({u.role})
                  </option>
                ))}
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-line rounded-lg text-sm bg-surface-card grow min-w-[170px] max-w-[300px]"
              >
                <option value="todos">Todos os status</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
              <select
                value={urgencyFilter}
                onChange={(e) => setUrgencyFilter(e.target.value)}
                className="px-3 py-2 border border-line rounded-lg text-sm bg-surface-card grow min-w-[150px] max-w-[300px]"
              >
                <option value="all">Toda agenda</option>
                <option value="overdue">Vencidos</option>
                <option value="today">Hoje</option>
                <option value="next3">Próx. 3 dias</option>
              </select>
              <select
                value={channelFilter}
                onChange={(e) => setChannelFilter(e.target.value)}
                className="px-3 py-2 border border-line rounded-lg text-sm bg-surface-card grow min-w-[190px] max-w-[300px]"
              >
                <option value="all">Todos os canais</option>
                {channels.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <select
                value={temperatureFilter}
                onChange={(e) => setTemperatureFilter(e.target.value)}
                className="px-3 py-2 border border-line rounded-lg text-sm bg-surface-card grow min-w-[175px] max-w-[300px]"
              >
                <option value="all">Toda temperatura</option>
                {LEAD_TEMPERATURE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
                <option value="esfriou">Esfriou (funil)</option>
                <option value="sem_marcacao">Sem marcação no funil</option>
              </select>
              <select
                value={slaFilter}
                onChange={(e) => setSlaFilter(e.target.value)}
                className="px-3 py-2 border border-line rounded-lg text-sm bg-surface-card grow min-w-[150px] max-w-[300px]"
              >
                <option value="all">Todo SLA</option>
                {SLA_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <select
                value={emailEngagementFilter}
                onChange={(e) => setEmailEngagementFilter(e.target.value)}
                className="px-3 py-2 border border-line rounded-lg text-sm bg-surface-card grow min-w-[190px] max-w-[300px]"
              >
                <option value="all">Mailrelay: todos</option>
                <option value="engaged">Com engajamento</option>
                <option value="opened">Abriu email</option>
                <option value="clicked">Clicou email</option>
                <option value="unsubscribed">Descadastrado</option>
              </select>
              <select
                value={segmentFilter}
                onChange={(e) => setSegmentFilter(e.target.value)}
                className="px-3 py-2 border border-line rounded-lg text-sm bg-surface-card grow min-w-[180px] max-w-[300px]"
              >
                <option value="all">Todos os perfis</option>
                {CLIENT_SEGMENT_OPTIONS.filter((s) => s.value !== '').map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
              <select
                value={customerFilter}
                onChange={(e) => setCustomerFilter(e.target.value)}
                className="px-3 py-2 border border-line rounded-lg text-sm bg-surface-card grow min-w-[245px] max-w-[300px]"
              >
                <option value="all">Todos (prospects e clientes)</option>
                <option value="prospect">Somente prospects</option>
                <option value="customer">Somente clientes</option>
              </select>
              <select
                value={contatoFilter}
                onChange={(e) => setContatoFilter(e.target.value)}
                className="px-3 py-2 border border-line rounded-lg text-sm bg-surface-card grow min-w-[225px] max-w-[300px]"
              >
                <option value="all">Contato: todos</option>
                <option value="sem_telefone">Sem telefone</option>
                <option value="sem_email">Sem e-mail</option>
                <option value="sem_nada">Sem telefone e sem e-mail</option>
              </select>
              {/* Periodo por MOVIMENTACAO: quando o lead foi trabalhado pela
                  ultima vez, nao quando entrou. Decisao dele em 23/09/2026 —
                  a pergunta e "onde esta a grana que esta viva". O quadro
                  "Em negociacao" acompanha este filtro, como todos os outros. */}
              <select
                value={periodoFilter}
                onChange={(e) => setPeriodoFilter(e.target.value)}
                title="Filtra pela última movimentação do lead (interação registrada ou alteração)"
                className="px-3 py-2 border border-line rounded-lg text-sm bg-surface-card grow min-w-[175px] max-w-[300px]"
              >
                {PERIODO_LEAD_OPTIONS.map((opcao) => (
                  <option key={opcao.value} value={opcao.value}>{opcao.label}</option>
                ))}
              </select>
              {periodoFilter === 'custom' && (
                /* O par de datas anda junto e ocupa a largura dos dois campos.
                   `min-w-0` nos inputs continua necessario: o campo de data do
                   Chrome tem largura minima propria, e item de flex nao encolhe
                   abaixo do conteudo sem isso. Medido em 23/09/2026: sem essas
                   travas o segundo campo vazava 54px para fora do cartao. */
                <div className="flex items-center gap-2 grow min-w-[330px] max-w-[420px]">
                  <input
                    type="date"
                    value={periodoInicio}
                    onChange={(e) => setPeriodoInicio(e.target.value)}
                    aria-label="Movimentado de"
                    className="px-3 py-2 border border-line rounded-lg text-sm bg-surface-card w-full min-w-0"
                  />
                  <span className="text-xs text-ink-faint shrink-0">até</span>
                  <input
                    type="date"
                    value={periodoFim}
                    onChange={(e) => setPeriodoFim(e.target.value)}
                    aria-label="Movimentado até"
                    className="px-3 py-2 border border-line rounded-lg text-sm bg-surface-card w-full min-w-0"
                  />
                </div>
              )}
              <select
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value)}
                className="px-3 py-2 border border-line rounded-lg text-sm bg-surface-card grow min-w-[170px] max-w-[300px]"
              >
                <option value="id">ID</option>
                <option value="created_at">Data de entrada</option>
                <option value="name">Nome</option>
                <option value="status">Status</option>
                <option value="value">Valor</option>
                <option value="next_contact">Próximo contato</option>
              </select>
              <select
                value={sortDir}
                onChange={(e) => setSortDir(e.target.value)}
                className="px-3 py-2 border border-line rounded-lg text-sm bg-surface-card grow min-w-[170px] max-w-[300px]"
              >
                <option value="desc">{SORT_TIPOS[TIPO_POR_CAMPO_LEAD[sortKey] || 'texto'].desc}</option>
                <option value="asc">{SORT_TIPOS[TIPO_POR_CAMPO_LEAD[sortKey] || 'texto'].asc}</option>
              </select>
            </div>
          </div>

          {/* 23/09/2026: a barra de acao em massa ficava ao lado do texto
              "Selecionados", numa segunda coluna de 679px. Os controles
              precisam 766px, entao 87px ficavam escondidos atras de uma
              barra de rolagem e o botao "Contato feito" saia cortado.
              Sem as duas colunas, a fileira de acoes usa a largura toda
              (1115px medidos) e nada mais rola. O overflow-x-auto fica de
              rede de seguranca para telas estreitas. */}
          <div className="mb-3 bg-surface-low border border-line rounded-lg p-3 flex flex-col gap-3">
            <div className="flex items-center gap-3 text-sm flex-wrap">
              <span className={UI_STRONG}>Selecionados: {selectedCount}</span>
              <span className="text-ink-faint text-xs">Disponíveis: {selectableLeadIds.length}</span>
              {/* Ate 18/09/2026 os botoes ficavam cinza sem dizer por que. Sao
                  DUAS condicoes — ter lead marcado e ter o destino escolhido —
                  e a barra nao contava qual estava faltando. */}
              {faltaParaAcaoEmMassa && (
                <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-900 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-800">
                  {faltaParaAcaoEmMassa}
                </span>
              )}
            </div>
            <div className="flex flex-wrap md:flex-nowrap items-center gap-3 overflow-x-auto pb-1 md:pb-0 scrollbar-hide">
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-[10px] uppercase font-bold text-ink-faint">Status</span>
                <select
                  value={bulkStatus}
                  onChange={(e) => setBulkStatus(e.target.value)}
                  className="px-2 py-1.5 border border-line rounded-lg text-sm bg-surface-card focus:ring-2 focus:ring-brand-500 outline-none"
                >
                  <option value="">Mudar status para...</option>
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <button
                  onClick={bulkChangeStatus}
                  disabled={!selectedEditableCount || !bulkStatus}
                  className="px-3 py-1.5 text-sm font-medium rounded-lg bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-40 transition-colors whitespace-nowrap"
                >
                  Aplicar
                </button>
              </div>

              <div className="w-[1px] h-6 bg-surface-high hidden md:block flex-shrink-0"></div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-[10px] uppercase font-bold text-ink-faint">Dono</span>
                <select
                  value={bulkOwnerId}
                  onChange={(e) => setBulkOwnerId(e.target.value)}
                  className="px-2 py-1.5 border border-line rounded-lg text-sm bg-surface-card focus:ring-2 focus:ring-brand-500 outline-none min-w-[140px]"
                >
                  <option value="">Mover para o responsável...</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
                <button
                  onClick={bulkReassignOwner}
                  disabled={!selectedCount || !bulkOwnerId}
                  className="px-3 py-1.5 text-sm font-medium rounded-lg border border-line bg-surface-card text-ink-soft hover:bg-surface-low disabled:opacity-40 transition-colors whitespace-nowrap"
                >
                  Mover
                </button>
              </div>

              <div className="w-[1px] h-6 bg-surface-high hidden md:block flex-shrink-0"></div>

              <button
                onClick={bulkMarkContactDone}
                disabled={!selectedEditableCount}
                className="px-4 py-1.5 text-sm font-medium rounded-lg border border-line bg-surface-card text-ink-soft hover:bg-surface-low disabled:opacity-40 transition-colors flex items-center gap-2 whitespace-nowrap flex-shrink-0"
              >
                <span>Contato feito</span>
              </button>
            </div>
          </div>

          {viewMode === 'list' ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-ink-faint">
                      <th className="py-2 px-2 w-10">
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={allSelectableSelected && selectableLeadIds.length > 0}
                          onChange={toggleSelectAll}
                        />
                      </th>
                      <ThOrdenavel className={UI_TD} chave="name" tipo="texto" ordemAtual={sortKey} direcao={sortDir} aoOrdenar={aplicarOrdenacaoLead}>Lead</ThOrdenavel>
                      <ThOrdenavel className={UI_TD} chave="channel_name" tipo="texto" ordemAtual={sortKey} direcao={sortDir} aoOrdenar={aplicarOrdenacaoLead}>Canal</ThOrdenavel>
                      <ThOrdenavel className={UI_TD} chave="region" tipo="texto" ordemAtual={sortKey} direcao={sortDir} aoOrdenar={aplicarOrdenacaoLead}>UF</ThOrdenavel>
                      <ThOrdenavel className={UI_TD} chave="status" tipo="texto" ordemAtual={sortKey} direcao={sortDir} aoOrdenar={aplicarOrdenacaoLead}>Status</ThOrdenavel>
                      <ThOrdenavel className={UI_TD} chave="sla_status" tipo="texto" ordemAtual={sortKey} direcao={sortDir} aoOrdenar={aplicarOrdenacaoLead}>Situação</ThOrdenavel>
                      <ThOrdenavel className={UI_TD} chave="owner" tipo="texto" ordemAtual={sortKey} direcao={sortDir} aoOrdenar={aplicarOrdenacaoLead}>Responsável</ThOrdenavel>
                      <ThOrdenavel className={UI_TD} chave="next_contact" tipo="data" ordemAtual={sortKey} direcao={sortDir} aoOrdenar={aplicarOrdenacaoLead}>Próximo contato</ThOrdenavel>
                      <th className="py-2 px-2 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedLeads.slice(0, visibleCount).map((lead) => {
                      const normalizedId = String(lead.id);
                      const canEdit = canEditLead(lead);
                      const canSelect = canReassignLead(lead);
                      const hasCompany = !!lead.company;
                      // O fundo verde marcava "tem empresa preenchida" — e 2.226
                      // dos 2.244 leads tem. Pintava a tabela inteira sem separar
                      // nada. A empresa agora aparece como segunda linha do nome.
                      const rowClass = 'border-b border-surface-mid last:border-none hover:bg-surface-low';
                      const segmentLabel = lead.segment
                        ? SEGMENT_OPTIONS.find((s) => s.value === lead.segment)?.label || null
                        : null;
                      const companyLine =
                        hasCompany && String(lead.company).trim() !== String(lead.name || '').trim()
                          ? lead.company
                          : null;
                      return (
                        <tr key={lead.id} className={rowClass}>
                          <td className={UI_TD}>
                            <input
                              type="checkbox"
                              className="h-4 w-4"
                              disabled={!canSelect}
                              checked={selectedLeadIds.includes(normalizedId)}
                              onChange={() => toggleSelectLead(normalizedId)}
                            />
                          </td>
                          <td className={UI_TD}>
                            <div className="font-semibold text-ink leading-tight">{lead.name}</div>
                            {(companyLine || segmentLabel) && (
                              <div className="text-[11px] text-ink-faint leading-tight">
                                {[companyLine, segmentLabel].filter(Boolean).join(' · ')}
                              </div>
                            )}
                          </td>
                          <td className={UI_TD}>{lead.channel_name || '-'}</td>
                          <td className={UI_TD}>
                            {lead.region ? (
                              <span className="text-[11px] uppercase font-semibold text-ink-soft">
                                {lead.region}
                              </span>
                            ) : <span className="text-ink-faint">—</span>}
                          </td>
                          <td className={UI_TD}>
                            <span className="px-2 py-[2px] rounded-full text-[11px] border bg-surface-low text-ink-soft border-line">
                              {lead.status}
                            </span>
                          </td>
                          <td className={UI_TD}>
                            <div className="flex flex-nowrap items-center gap-1">
                              <FunilDoLead lead={lead} podeMarcar={podeMarcarFunil(lead)} aoMarcar={marcarFunil} compacto />
                              <LeadSlaBadge value={lead.sla_status} remainingMinutes={lead.sla_remaining_minutes} />
                              <LeadEmailEngagementBadges lead={lead} />
                              <LeadContatoFaltandoBadge lead={lead} />
                              <span className="text-[11px] text-ink-faint tabular-nums whitespace-nowrap">
                                {Number(lead.interactions_count || 0)} int.
                              </span>
                            </div>
                          </td>
                          <td className={UI_TD}>{lead.owner || lead.responsible_name || '-'}</td>
                          <td className="py-2 px-2 text-xs text-ink-faint">
                            {lead.next_contact
                              ? formatFollowupBR(lead.next_contact)
                              : '-'}
                          </td>
                          <td className="py-2 px-2">
                            {/* Excluir sai de junto de Editar: separador visual e
                                vermelho so no hover, para nao competir com o SLA. */}
                            <div className="flex items-center justify-end gap-1 whitespace-nowrap">
                              <button
                                onClick={() => openEditLeadModal(lead)}
                                className="px-2 py-1 rounded-md border border-line text-[11px] text-ink-soft hover:bg-surface-low hover:text-brand-ink"
                              >
                                Editar
                              </button>
                              <button
                                onClick={() => copyToClipboard(buildWhatsappText(lead))}
                                className="px-2 py-1 rounded-md border border-line text-[11px] text-ink-soft hover:bg-surface-low hover:text-brand-ink"
                                title="Copiar texto de WhatsApp"
                              >
                                WhatsApp
                              </button>
                              <span className="mx-1 h-4 w-px bg-line" aria-hidden />
                              <button
                                onClick={() => deleteLead(lead.id)}
                                className="px-2 py-1 rounded-md border border-transparent text-[11px] text-ink-faint hover:border-risk-line hover:bg-risk hover:text-risk-ink"
                              >
                                Excluir
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {filteredLeads.length === 0 && (
                      <tr>
                        <td
                          colSpan={9}
                          className="py-4 text-center text-ink-faint text-xs"
                        >
                          Nenhum lead cadastrado
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {visibleCount < displayedLeads.length && (
                <div className="flex justify-center py-3">
                  <button
                    onClick={() => setVisibleCount((v) => v + 20)}
                    className="px-4 py-2 text-sm rounded-lg border border-line bg-surface-card text-ink-soft hover:bg-surface-low"
                  >
                    + Ver mais
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {STATUS_OPTIONS.map((col) => {
                const colLeads = filteredLeads.filter(
                  (l) => (l.status || '').toLowerCase() === col.value
                );
                const colLeadsSorted = [...colLeads].sort(sorter);
                const visiveisAqui = kanbanVisivel[col.value] ?? KANBAN_POR_COLUNA;
                const fatia = fatiaDoKanban(colLeadsSorted.length, visiveisAqui);
                return (
                  <div
                    key={col.value}
                    className="border border-line rounded-xl p-3 bg-surface-low/70"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => handleDropStatus(col.value)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-ink">{col.label}</h3>
                      <span className="text-xs px-2 py-1 rounded-full bg-surface-card border border-line">
                        {colLeads.length}
                      </span>
                    </div>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {colLeadsSorted.slice(0, fatia.mostrados).map((lead) => (
                        <div
                          key={lead.id}
                          className={`relative p-3 rounded-lg border bg-surface-card shadow-sm cursor-pointer hover:border-brand-300 ${classeDoFunilNoCard(lead)} ${
                            selectedLeadIds.includes(String(lead.id)) ? 'border-brand-400 ring-1 ring-brand-300' : 'border-line'
                          }`}
                          onClick={() => openEditLeadModal(lead)}
                          draggable
                          onDragStart={() => handleCardDragStart(lead.id)}
                          onDragEnd={handleCardDragEnd}
                        >
                          {/* A acao em massa existia so na Lista: no Kanban a barra
                              aparecia e nao havia como marcar nada. `stopPropagation`
                              nos dois eventos porque o cartao inteiro abre o lead ao
                              clicar e e arrastavel — sem isso, marcar viraria abrir. */}
                          {canReassignLead(lead) && (
                            <input
                              type="checkbox"
                              className="absolute top-2 right-2 h-4 w-4 cursor-pointer"
                              title="Selecionar para ação em massa"
                              checked={selectedLeadIds.includes(String(lead.id))}
                              onClick={(e) => e.stopPropagation()}
                              onMouseDown={(e) => e.stopPropagation()}
                              onChange={(e) => { e.stopPropagation(); toggleSelectLead(lead.id); }}
                            />
                          )}

                          {statusUpdatingId && String(statusUpdatingId) === String(lead.id) && (
                            <div className="absolute top-2 right-2 w-3 h-3 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
                          )}
                          <div className="flex items-center justify-between">
                            <p className="font-semibold text-ink text-sm">{lead.name}</p>
                            <span className="text-xs text-ink-faint">
                              {lead.value ? `R$ ${Number(lead.value).toLocaleString('pt-BR')}` : ''}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-1 text-[11px] text-ink-soft mt-1">
                            {lead.company && (
                              <span className="text-ink-faint">{lead.company}</span>
                            )}
                            {lead.segment && (
                              <span className="px-2 py-[2px] rounded-full bg-brand-50 text-brand-ink border border-brand-100">
                                {
                                  SEGMENT_OPTIONS.find((s) => s.value === lead.segment)?.label ||
                                  lead.segment
                                }
                              </span>
                            )}
                            {lead.region && (
                              <span className="uppercase font-semibold text-ink-soft">{lead.region}</span>
                            )}
                            <FunilDoLead lead={lead} podeMarcar={podeMarcarFunil(lead)} aoMarcar={marcarFunil} />
                            <LeadSlaBadge value={lead.sla_status} remainingMinutes={lead.sla_remaining_minutes} />
                            <LeadEmailEngagementBadges lead={lead} />
                            <LeadContatoFaltandoBadge lead={lead} />
                            <span className="text-ink-faint tabular-nums">
                              {Number(lead.interactions_count || 0)} int.
                            </span>
                          </div>
                          <p className="text-xs text-ink-soft mt-1">{lead.owner || lead.responsible_name || '-'}</p>
                          <div className="flex items-center justify-between text-[11px] text-ink-faint mt-1">
                            <span>{lead.email || lead.phone || '-'}</span>
                            <div className="flex items-center gap-2">
                              <span>
                                {lead.next_contact
                                  ? formatFollowupBR(lead.next_contact)
                                  : '-'}
                              </span>
                              <span className="text-[10px] text-ink-faint">
                                {lead.created_at
                                  ? new Date(lead.created_at).toLocaleDateString('pt-BR')
                                  : '-'}
                              </span>
                              <div className="relative">
                                <button
                                  aria-label="Mais ações deste lead"
                                  title="Mais ações"
                                  className={`px-1.5 rounded-md leading-none ${cardMenuId === String(lead.id) ? 'bg-surface-high text-ink' : 'text-ink-faint hover:text-ink'}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCardMenuId((atual) => (atual === String(lead.id) ? null : String(lead.id)));
                                  }}
                                >
                                  •••
                                </button>
                                {cardMenuId === String(lead.id) && (
                                  <div
                                    className="absolute right-0 bottom-full mb-1 z-20 bg-surface-card border border-line rounded-lg shadow-lg py-1 min-w-[132px]"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <button
                                      className="w-full text-left px-3 py-1.5 text-[11px] text-risk-ink hover:bg-risk"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setCardMenuId(null);
                                        deleteLead(lead.id);
                                      }}
                                    >
                                      Excluir lead
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                      {fatia.temBotao && (
                        <button
                          type="button"
                          onClick={() => setKanbanVisivel((atual) => ({
                            ...atual,
                            [col.value]: visiveisAqui + KANBAN_POR_COLUNA,
                          }))}
                          className="w-full py-2 text-xs rounded-lg border border-line bg-surface-card text-ink-soft hover:bg-surface-low transition-colors"
                        >
                          {fatia.rotulo}
                        </button>
                      )}
                      {colLeads.length === 0 && (
                        <p className="text-xs text-ink-faint">Nenhum lead</p>
                      )}
                    </div>
                    {/* Reatribuição em massa por coluna removida para simplificar Kanban; drag-and-drop já atualiza */}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        </div>

        {/* Lixeira de leads (23/09/2026). So admin abre. Nada e apagado daqui
            automaticamente: o aviso de vencido e so um aviso. */}
        {/* Engajou sem lead (24/09/2026).
            Quem abriu, clicou ou se descadastrou de campanha do Mailrelay e
            nao tem cadastro aqui. Ate esta data essa gente era so um numero e
            era descartada a cada sync.
            Regras que a tela obedece, decididas por ele em 24/09/2026:
              - uma linha por PESSOA, nao por evento;
              - e-mail da propria casa fica de fora da lista;
              - NADA vira lead sozinho: botao por pessoa, com responsavel e
                canal escolhidos na hora.
            Os numeros sao o retrato da ultima sync. A data da medicao fica
            escrita na tela de proposito: numero de sistema de terceiro sem
            dizer de quando e ja me fez afirmar coisa errada uma vez. */}
        {showEngajou && (
          <div className="fixed inset-0 bg-black/55 z-50 scrim-enter flex items-center justify-center p-4">
            <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-surface-card border border-line rounded-2xl shadow-2xl">
              {/* Cabecalho fixo (19/10/2026): com 1.700+ pessoas, o "Fechar"
                  sumia ao rolar. Esc tambem fecha. */}
              <div className="sticky top-0 z-10 bg-surface-card rounded-t-2xl p-4 border-b border-line flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-ink">Engajou, mas não é lead</h2>
                  <p className="text-xs text-ink-faint mt-1">
                    {engajouCarregando
                      ? 'Lendo...'
                      : engajou
                        ? `${engajou.pendentes} pessoa(s) esperando. ${engajou.ja_viraram_lead} já viraram lead.`
                        : 'Quem abriu ou clicou numa campanha e não tem cadastro aqui.'}
                  </p>
                </div>
                <button
                  onClick={() => setShowEngajou(false)}
                  className="px-2 py-1 rounded-lg border border-line text-sm text-ink-soft hover:bg-surface-low"
                >
                  Fechar
                </button>
              </div>

              <div className="p-4 space-y-3">
                {engajouCarregando && <p className="text-sm text-ink-faint">Lendo a lista...</p>}

                {!engajouCarregando && engajou && (
                  <div className="text-xs text-ink-faint space-y-1 px-3 py-2 rounded-lg bg-surface-low border border-line">
                    <p>
                      <strong className="text-ink">{engajou.total}</strong> pessoa(s) no total ·{' '}
                      <strong className="text-ink">{engajou.com_clique}</strong> já clicaram em algum link ·{' '}
                      <strong className="text-ink">{engajou.sem_email}</strong> sem endereço (o Mailrelay não devolveu).
                    </p>
                    <p>
                      Números medidos na sincronização de{' '}
                      {engajou.medido_em
                        ? new Date(engajou.medido_em).toLocaleString('pt-BR')
                        : 'data não registrada'}.
                    </p>
                    <p>
                      Fora da lista, sempre: {(engajou.dominios_da_casa_fora_da_lista || []).join(', ') || '—'} (e-mail da própria casa).
                    </p>
                  </div>
                )}

                {!engajouCarregando && engajou && engajou.total === 0 && (
                  <p className="text-sm text-ink-soft">
                    Ninguém na lista. Ela é preenchida pela sincronização diária do Mailrelay.
                  </p>
                )}

                {!engajouCarregando && (engajou?.pessoas || []).map((p) => {
                  const form = engajouForm[String(p.id)] || {};
                  const mudar = (campo, valor) =>
                    setEngajouForm((atual) => ({
                      ...atual,
                      [String(p.id)]: { ...(atual[String(p.id)] || {}), [campo]: valor },
                    }));
                  return (
                    <div key={p.id} className="border border-line rounded-xl p-3 bg-surface-low space-y-2">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-ink truncate">
                            {p.email || `(sem e-mail — assinante ${p.subscriber_id})`}
                          </p>
                          <p className="text-xs text-ink-faint mt-0.5">
                            {p.aberturas} abertura(s) · {p.cliques} clique(s)
                            {p.descadastros > 0 ? ` · ${p.descadastros} descadastro(s)` : ''} ·{' '}
                            {p.campanhas_tocadas} campanha(s)
                          </p>
                          <p className="text-xs text-ink-faint">
                            {p.ultima_campanha ? `Última: ${p.ultima_campanha}` : 'Campanha não identificada'}
                            {p.ultimo_evento_em
                              ? ` em ${new Date(p.ultimo_evento_em).toLocaleDateString('pt-BR')}`
                              : ''}
                            {p.local ? ` · ${p.local}` : ''}
                          </p>
                          {(p.links_clicados || []).length > 0 && (
                            <p className="text-xs text-ink-soft mt-0.5 break-words">
                              Clicou em:{' '}
                              {p.links_clicados.map((l, i) => (
                                <span key={l.url}>
                                  {i > 0 ? ', ' : ''}
                                  <a href={l.url} target="_blank" rel="noopener noreferrer" className="underline text-brand-600" title={l.url}>
                                    {rotuloDoLink(l.url)}
                                  </a>
                                  {l.vezes > 1 ? ` (${l.vezes}x)` : ''}
                                </span>
                              ))}
                            </p>
                          )}
                        </div>
                        {p.virou_lead_id ? (
                          <span className="text-[11px] px-2 py-1 rounded-full bg-surface-card border border-line text-ink-soft whitespace-nowrap">
                            virou o lead {p.virou_lead_id}
                            {p.virou_lead_por ? ` (${p.virou_lead_por})` : ''}
                          </span>
                        ) : !p.email ? (
                          <span className="text-[11px] px-2 py-1 rounded-full bg-warn border border-warn-line text-warn-ink whitespace-nowrap">
                            sem e-mail — não dá para criar lead
                          </span>
                        ) : null}
                      </div>

                      {p.pode_virar_lead && (
                        <div className="flex flex-wrap items-end gap-2 pt-1">
                          <input
                            value={form.name || ''}
                            onChange={(e) => mudar('name', e.target.value)}
                            placeholder="Nome (opcional)"
                            className="grow min-w-[150px] max-w-[220px] px-2 py-1.5 text-xs rounded-lg bg-surface-card border border-line text-ink"
                          />
                          <input
                            value={form.company || ''}
                            onChange={(e) => mudar('company', e.target.value)}
                            placeholder="Empresa (opcional)"
                            className="grow min-w-[150px] max-w-[220px] px-2 py-1.5 text-xs rounded-lg bg-surface-card border border-line text-ink"
                          />
                          <input
                            value={form.phone || ''}
                            onChange={(e) => mudar('phone', e.target.value)}
                            placeholder="Telefone (opcional)"
                            className="grow min-w-[130px] max-w-[180px] px-2 py-1.5 text-xs rounded-lg bg-surface-card border border-line text-ink"
                          />
                          <select
                            value={form.ownerId || ''}
                            onChange={(e) => mudar('ownerId', e.target.value)}
                            className="grow min-w-[140px] max-w-[200px] px-2 py-1.5 text-xs rounded-lg bg-surface-card border border-line text-ink"
                          >
                            <option value="">Responsável...</option>
                            {(users || []).map((u) => (
                              <option key={u.id} value={u.id}>{u.name || u.username}</option>
                            ))}
                          </select>
                          <select
                            value={form.channel_id || ''}
                            onChange={(e) => mudar('channel_id', e.target.value)}
                            className="grow min-w-[140px] max-w-[200px] px-2 py-1.5 text-xs rounded-lg bg-surface-card border border-line text-ink"
                          >
                            <option value="">Canal...</option>
                            {(channels || []).map((c) => (
                              <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                          </select>
                          <button
                            onClick={() => criarLeadDoEngajou(p)}
                            disabled={engajouOcupadoId === String(p.id)}
                            className="px-3 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-xs font-medium disabled:opacity-50 transition-colors whitespace-nowrap"
                          >
                            {engajouOcupadoId === String(p.id) ? 'Criando...' : 'Criar lead'}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {showLixeira && (
          <div className="fixed inset-0 bg-black/55 z-50 scrim-enter flex items-center justify-center p-4">
            <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-surface-card border border-line rounded-2xl shadow-2xl">
              <div className="p-4 border-b border-line flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-ink">Lixeira</h2>
                  <p className="text-xs text-ink-faint mt-1">
                    {lixeiraCarregando
                      ? 'Lendo...'
                      : lixeira
                        ? `${lixeira.total} lead(s) excluido(s). Nada e apagado sozinho.`
                        : 'Leads excluidos ficam aqui.'}
                  </p>
                </div>
                <button
                  onClick={() => setShowLixeira(false)}
                  className="px-2 py-1 rounded-lg border border-line text-sm text-ink-soft hover:bg-surface-low"
                >
                  Fechar
                </button>
              </div>

              <div className="p-4 space-y-2">
                {lixeiraCarregando && (
                  <p className="text-sm text-ink-faint">Lendo a lixeira...</p>
                )}

                {!lixeiraCarregando && lixeira && lixeira.total === 0 && (
                  <p className="text-sm text-ink-soft">
                    Nenhum lead excluido. Quando alguem excluir, a copia aparece aqui e pode ser restaurada.
                  </p>
                )}

                {!lixeiraCarregando && lixeira && lixeira.vencidos > 0 && (
                  <p className="text-xs px-3 py-2 rounded-lg bg-warn border border-warn-line text-warn-ink">
                    {lixeira.vencidos} passou(aram) de {lixeira.prazo_dias} dias na lixeira. Nada foi apagado — isso e so um aviso.
                  </p>
                )}

                {!lixeiraCarregando && (lixeira?.itens || []).map((item) => (
                  <div
                    key={item.id}
                    className="border border-line rounded-xl p-3 flex flex-wrap items-center justify-between gap-3 bg-surface-low"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-ink truncate">
                        {item.name || '(sem nome)'}
                        {item.vencido && (
                          <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-warn border border-warn-line text-warn-ink align-middle">
                            passou de {lixeira.prazo_dias} dias
                          </span>
                        )}
                      </p>
                      {item.company && (
                        <p className="text-xs text-ink-soft truncate">{item.company}</p>
                      )}
                      <p className="text-[11px] text-ink-faint mt-1">
                        {[
                          item.status ? `status: ${item.status}` : null,
                          item.owner ? `responsavel: ${item.owner}` : null,
                          Number(item.value) > 0 ? formatCurrencyBR(Number(item.value)) : null,
                        ].filter(Boolean).join('  |  ')}
                      </p>
                      <p className="text-[11px] text-ink-faint">
                        excluido por {item.apagado_por || '(nao registrado)'}
                        {item.apagado_em ? ` em ${new Date(item.apagado_em).toLocaleString('pt-BR')}` : ''}
                        {item.dias_na_lixeira !== null && item.dias_na_lixeira !== undefined
                          ? ` (${item.dias_na_lixeira} dia(s) atras)`
                          : ''}
                      </p>
                    </div>
                    <button
                      onClick={() => restaurarDaLixeira(item)}
                      disabled={lixeiraOcupadoId === String(item.id)}
                      className="px-3 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold disabled:opacity-60 shrink-0"
                    >
                      {lixeiraOcupadoId === String(item.id) ? 'Restaurando...' : 'Restaurar'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {importClientesAberto && (
          <div className="fixed inset-0 bg-black/55 z-50 scrim-enter flex items-center justify-center p-4">
            <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-surface-card border border-line rounded-2xl shadow-2xl">
              <div className="p-4 border-b border-line flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-ink">Importar clientes</h2>
                  <p className="text-xs text-ink-faint mt-1">
                    {importClientesArquivo || 'Planilha do AM Gestor'}
                  </p>
                </div>
                <button
                  onClick={fecharImportClientes}
                  className="text-sm text-ink-soft hover:text-ink px-2 py-1 rounded-lg border border-line"
                  aria-label="Fechar"
                >
                  Fechar
                </button>
              </div>

              <div className="p-4 space-y-4">
                {importClientesErro && (
                  <div className="rounded-xl border border-risk-line bg-risk px-3 py-2 text-sm text-risk-ink">
                    {importClientesErro}
                  </div>
                )}

                {importClientesOcupado && !importClientesResultado && (
                  <p className="text-sm text-ink-soft">Conferindo a planilha...</p>
                )}

                {/* Depois de gravar, a tela vira relatorio: o que entrou, o que
                    foi completado e quem ficou sem telefone. */}
                {importClientesResultado ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <div className="rounded-xl border border-line bg-surface-low p-3">
                        <p className="text-[11px] uppercase text-ink-faint font-bold">Criados</p>
                        <p className="text-2xl font-black text-ink tabular-nums">{importClientesResultado.criados || 0}</p>
                      </div>
                      <div className="rounded-xl border border-line bg-surface-low p-3">
                        <p className="text-[11px] uppercase text-ink-faint font-bold">Completados</p>
                        <p className="text-2xl font-black text-ink tabular-nums">{importClientesResultado.completados || 0}</p>
                      </div>
                      <div className="rounded-xl border border-line bg-surface-low p-3">
                        <p className="text-[11px] uppercase text-ink-faint font-bold">Já iguais</p>
                        <p className="text-2xl font-black text-ink tabular-nums">{importClientesResultado.ja_estavam_iguais || 0}</p>
                      </div>
                      <div className="rounded-xl border border-warn-line bg-warn p-3">
                        <p className="text-[11px] uppercase text-warn-ink font-bold">Sem telefone</p>
                        <p className="text-2xl font-black text-warn-ink tabular-nums">{importClientesResultado.novos_sem_telefone || 0}</p>
                      </div>
                    </div>
                    {Number(importClientesResultado.novos_sem_telefone) > 0 && (
                      <p className="text-sm text-ink-soft">
                        Os leads sem telefone entraram e estão marcados com o aviso
                        <span className="font-semibold"> Falta telefone </span>
                        no cartão. Para achar todos de uma vez, use o filtro
                        <span className="font-semibold"> Sem telefone </span>
                        na lista.
                      </p>
                    )}
                    <div className="flex justify-end">
                      <button
                        onClick={fecharImportClientes}
                        className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-semibold transition-colors"
                      >
                        Concluir
                      </button>
                    </div>
                  </div>
                ) : (
                  importClientesSimulacao && (
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm text-ink-soft mb-2">
                          Li <span className="font-semibold text-ink">{importClientesSimulacao.linhas_lidas}</span> linha(s).
                          Nada foi gravado ainda. Isto é o que vai acontecer:
                        </p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          <div className="rounded-xl border border-line bg-surface-low p-3">
                            <p className="text-[11px] uppercase text-ink-faint font-bold">Leads novos</p>
                            <p className="text-2xl font-black text-ink tabular-nums">{importClientesSimulacao.criar}</p>
                          </div>
                          <div className="rounded-xl border border-line bg-surface-low p-3">
                            <p className="text-[11px] uppercase text-ink-faint font-bold">A completar</p>
                            <p className="text-2xl font-black text-ink tabular-nums">{importClientesSimulacao.completar}</p>
                          </div>
                          <div className="rounded-xl border border-line bg-surface-low p-3">
                            <p className="text-[11px] uppercase text-ink-faint font-bold">Já iguais</p>
                            <p className="text-2xl font-black text-ink tabular-nums">{importClientesSimulacao.ja_estavam_iguais}</p>
                          </div>
                          <div className="rounded-xl border border-warn-line bg-warn p-3">
                            <p className="text-[11px] uppercase text-warn-ink font-bold">Sem telefone</p>
                            <p className="text-2xl font-black text-warn-ink tabular-nums">{importClientesSimulacao.novos_sem_telefone}</p>
                          </div>
                        </div>
                        <p className="text-xs text-ink-faint mt-2">
                          Quem já existe no CRM só tem campo <span className="font-semibold">vazio</span> completado.
                          Telefone, e-mail, status, dono e observação que já estão preenchidos não são tocados.
                          {Number(importClientesSimulacao.repetidos_na_planilha?.length) > 0 && (
                            <> {importClientesSimulacao.repetidos_na_planilha.length} linha(s) repetida(s) na própria planilha foram descartadas.</>
                          )}
                          {Number(importClientesSimulacao.ignorados) > 0 && (
                            <> {importClientesSimulacao.ignorados} linha(s) sem nome e sem empresa foram ignoradas.</>
                          )}
                        </p>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-semibold text-ink-soft mb-1">
                            Responsável <span className="text-risk-ink">*</span>
                          </label>
                          <select
                            value={importClientesResponsavel}
                            onChange={(e) => setImportClientesResponsavel(e.target.value)}
                            className="px-3 py-2 border border-line rounded-lg text-sm bg-surface-card w-full"
                          >
                            <option value="">Escolha quem fica com os leads</option>
                            {users.map((u) => (
                              <option key={u.id} value={u.id}>{u.name || u.username || u.email}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-ink-soft mb-1">
                            Canal <span className="text-risk-ink">*</span>
                          </label>
                          <select
                            value={importClientesCanal}
                            onChange={(e) => setImportClientesCanal(e.target.value)}
                            className="px-3 py-2 border border-line rounded-lg text-sm bg-surface-card w-full"
                          >
                            <option value="">Escolha o canal</option>
                            {channels.map((c) => (
                              <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="sm:col-span-2">
                          <label className="block text-xs font-semibold text-ink-soft mb-1">
                            Campanha (opcional)
                          </label>
                          <input
                            type="text"
                            value={importClientesCampanha}
                            onChange={(e) => setImportClientesCampanha(e.target.value)}
                            placeholder="Ex.: Reativação AM Gestor - set/2026"
                            className="px-3 py-2 border border-line rounded-lg text-sm bg-surface-card w-full"
                          />
                          <p className="text-[11px] text-ink-faint mt-1">
                            Serve para medir o resultado depois: é por este rótulo que se filtra quanto essa leva gerou.
                          </p>
                        </div>
                      </div>

                      <label className="flex items-center gap-2 text-sm text-ink-soft">
                        <input
                          type="checkbox"
                          checked={importClientesMarcarCliente}
                          onChange={(e) => setImportClientesMarcarCliente(e.target.checked)}
                          className="h-4 w-4 rounded border-line"
                        />
                        Marcar como clientes da casa (já compraram da BHS)
                      </label>

                      <div className="flex justify-end gap-2 pt-2 border-t border-line">
                        <button
                          onClick={fecharImportClientes}
                          className="px-4 py-2 bg-surface-card text-ink-soft rounded-lg text-sm border border-line hover:bg-surface-low transition-colors"
                        >
                          Cancelar
                        </button>
                        <button
                          onClick={confirmarImportacaoDeClientes}
                          disabled={importClientesOcupado || !importClientesResponsavel || !importClientesCanal}
                          className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-60"
                        >
                          {importClientesOcupado
                            ? 'Importando...'
                            : `Importar ${importClientesSimulacao.criar} novo(s) e completar ${importClientesSimulacao.completar}`}
                        </button>
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        )}

        {showLeadModal && (
          <div className="fixed inset-0 bg-black/55 z-50 scrim-enter">
            {/* Drawer: entra pela direita. Largura mantida em max-w-lg (512px),
                nao nos 400px do DESIGN.md, porque o formulario usa grade de 2
                colunas dimensionada para essa largura — estreitar exigiria
                refluir os 34 campos, que e outra tarefa. */}
            <div className="fixed top-0 right-0 h-full w-full max-w-lg bg-surface-card border-l border-line shadow-2xl overflow-y-auto drawer-enter">
              <div className="p-4 border-b border-line flex items-start justify-between">
                <h2 className="text-lg font-semibold text-ink">
                  {editingLead ? 'Editar Lead' : 'Novo Lead'}
                </h2>
                <button
                  onClick={closeLeadModal}
                  className="text-sm text-ink-soft hover:text-ink px-2 py-1 rounded-lg border border-line"
                  aria-label="Fechar"
                >
                  Fechar
                </button>
              </div>
              <div className="p-4 space-y-3">
                <div className="rounded-xl border border-line bg-surface-card px-3 py-3">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-ink-faint font-bold mb-2">Termômetro comercial</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <LeadTemperatureBadge value={leadForm.temperature} />
                    <LeadSlaBadge value={leadForm.sla_status} remainingMinutes={editingLead?.sla_remaining_minutes} />
                  </div>
                  <div className="mt-2 text-xs text-ink-faint space-y-1">
                    <p>SLA esperado para este lead: {editingLead?.funil_etapa
                      ? `${Math.round(Number(editingLead.sla_minutes || 0) / 1440)} dia(s), pelo funil (${LEAD_TEMPERATURE_OPTIONS.find((op) => op.value === editingLead.funil_etapa)?.label || editingLead.funil_etapa})`
                      : (SLA_TARGET_LABELS[normalizeOptionValue(leadForm.temperature)] || '-')}</p>
                    <p>Última atividade: {leadForm.last_activity_at ? formatDateTimeBR(leadForm.last_activity_at) : '-'}</p>
                    <p>Prazo alvo: {leadForm.sla_due_at ? formatDateTimeBR(leadForm.sla_due_at) : '-'}</p>
                  </div>
                </div>
                <div className="rounded-xl border border-line bg-surface-card px-3 py-3">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-ink-faint font-bold mb-2">Engajamento por email</p>
                  <div className="flex flex-wrap gap-2 mb-2 text-[11px]">
                    <span className="px-2 py-1 rounded-full bg-surface-mid text-ink-soft">
                      Aberturas: {leadForm.email_open_count || 0}
                    </span>
                    <span className="px-2 py-1 rounded-full bg-surface-mid text-ink-soft">
                      Cliques: {leadForm.email_click_count || 0}
                    </span>
                    <span className={`px-2 py-1 rounded-full ${leadForm.email_unsubscribed ? 'bg-risk text-risk-ink' : 'bg-ok text-ok-ink'}`}>
                      {leadForm.email_unsubscribed ? 'Descadastrado' : 'Ativo'}
                    </span>
                  </div>
                  <div className="space-y-1 text-xs text-ink-faint">
                    <p>Última abertura: {leadForm.last_email_open_at ? formatDateTimeBR(leadForm.last_email_open_at) : '-'}</p>
                    <p>Último clique: {leadForm.last_email_click_at ? formatDateTimeBR(leadForm.last_email_click_at) : '-'}</p>
                    <p>Última campanha: {leadForm.last_email_campaign || '-'}</p>
                    <p>Último evento: {leadForm.last_email_event_at ? formatDateTimeBR(leadForm.last_email_event_at) : '-'}</p>
                  </div>
                </div>
                <div className="rounded-xl border border-line bg-surface-card px-3 py-3">
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.18em] text-ink-faint font-bold">Interações manuais</p>
                      <p className="text-xs text-ink-faint mt-1">
                        Registre cada contato manualmente. O histórico fica oculto por padrão.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setLeadInteractionForm({
                            id: null,
                            interaction_at: toDateTimeLocalInput(new Date().toISOString()),
                            channel: '',
                            notes: '',
                          });
                        }}
                        className="px-3 py-2 rounded-lg text-xs font-semibold border border-line text-ink-soft bg-surface-card"
                      >
                        Limpar
                      </button>
                      <button
                        type="button"
                        onClick={saveLeadInteraction}
                        disabled={savingLeadInteraction || !editingLead?.id}
                        className="px-3 py-2 rounded-lg text-xs font-semibold bg-brand-600 hover:bg-brand-700 text-white transition-colors disabled:opacity-50"
                      >
                        {savingLeadInteraction ? (
                          <span className="inline-flex items-center gap-2">
                            <span className="h-3.5 w-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                            Salvando...
                          </span>
                        ) : leadInteractionForm.id ? (
                          'Atualizar interação'
                        ) : (
                          'Registrar interação'
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    <div>
                      <label className="block text-[11px] font-semibold text-ink-soft mb-1">
                        Data e hora
                      </label>
                      <input
                        type="datetime-local"
                        value={leadInteractionForm.interaction_at || ''}
                        onChange={(e) =>
                          setLeadInteractionForm({ ...leadInteractionForm, interaction_at: e.target.value })
                        }
                        className={UI_INPUT_BG}
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-ink-soft mb-1">
                        Canal
                      </label>
                      <input
                        list="lead-interaction-channel-options"
                        type="text"
                        value={leadInteractionForm.channel || ''}
                        onChange={(e) =>
                          setLeadInteractionForm({ ...leadInteractionForm, channel: e.target.value })
                        }
                        placeholder="Ex.: WhatsApp, telefone, email, visita"
                        className={UI_INPUT_BG}
                      />
                      <datalist id="lead-interaction-channel-options">
                        {channels.map((channel) => (
                          <option key={channel.id} value={channel.name} />
                        ))}
                        <option value="WhatsApp" />
                        <option value="Telefone" />
                        <option value="Email" />
                        <option value="Visita" />
                      </datalist>
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-ink-soft mb-1">
                        Observação
                      </label>
                      <textarea
                        value={leadInteractionForm.notes || ''}
                        onChange={(e) =>
                          setLeadInteractionForm({ ...leadInteractionForm, notes: e.target.value })
                        }
                        rows={3}
                        placeholder="Resumo do contato, pedido, objeção, retorno..."
                        className={UI_INPUT_BG}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end mt-2">
                    <button
                      type="button"
                      onClick={() => setLeadInteractionsExpanded((value) => !value)}
                      className="inline-flex items-center gap-2 text-xs font-semibold text-ink-soft hover:text-ink"
                    >
                      <span aria-hidden="true">{leadInteractionsExpanded ? '▴' : '▾'}</span>
                      <span>{leadInteractionsExpanded ? 'Ocultar Histórico' : 'Ver Histórico'}</span>
                    </button>
                  </div>
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                    <div className="rounded-lg border border-surface-mid bg-surface-low px-3 py-2">
                      <p className="text-[10px] uppercase font-bold text-ink-faint">Total</p>
                      <p className={UI_STRONG}>{leadForm.interactions_count || 0}</p>
                    </div>
                    <div className="rounded-lg border border-surface-mid bg-surface-low px-3 py-2">
                      <p className="text-[10px] uppercase font-bold text-ink-faint">Última interação</p>
                      <p className={UI_STRONG}>
                        {leadForm.last_interaction_at ? formatDateTimeBR(leadForm.last_interaction_at) : '-'}
                      </p>
                    </div>
                    <div className="rounded-lg border border-surface-mid bg-surface-low px-3 py-2">
                      <p className="text-[10px] uppercase font-bold text-ink-faint">Canal</p>
                      <p className={UI_STRONG}>
                        {leadForm.last_interaction_channel || '-'}
                      </p>
                    </div>
                  </div>
                  {leadInteractionsExpanded && (
                    <div className="mt-3 border-t border-surface-mid pt-3">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[11px] uppercase tracking-[0.18em] text-ink-faint font-bold">
                          Histórico recente
                        </p>
                        {leadInteractionsLoading ? (
                          <span className={UI_META}>Carregando...</span>
                        ) : null}
                      </div>
                      <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                        {leadInteractions.length === 0 ? (
                          <p className="text-xs text-ink-faint">Nenhuma interação registrada ainda.</p>
                        ) : (
                          leadInteractions.slice(0, 12).map((item) => (
                            <div key={item.id} className="rounded-lg border border-surface-mid bg-surface-card px-3 py-2">
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <p className="text-xs font-semibold text-ink">
                                    {item.channel || 'Canal não informado'}
                                  </p>
                                  <p className={UI_META}>
                                    {item.interaction_at ? formatDateTimeBR(item.interaction_at) : '-'}
                                    {item.operator ? ` • ${item.operator}` : ''}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() => editLeadInteraction(item)}
                                    className="text-[11px] text-brand-ink hover:underline"
                                  >
                                    Editar
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => deleteLeadInteraction(item)}
                                    className="text-[11px] text-risk-ink hover:underline"
                                  >
                                    Excluir
                                  </button>
                                </div>
                              </div>
                              {item.notes ? (
                                <p className="text-xs text-ink-soft mt-1 whitespace-pre-wrap">
                                  {item.notes}
                                </p>
                              ) : null}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <label className={UI_LABEL}>
                    Nome *
                  </label>
                  <input
                    type="text"
                    value={leadForm.name}
                    onChange={(e) =>
                      setLeadForm({ ...leadForm, name: e.target.value })
                    }
                    className={UI_INPUT}
                  />
                </div>
                <div>
                  <label className={UI_LABEL}>
                    Empresa
                  </label>
                  <input
                    type="text"
                    value={leadForm.company || ''}
                    onChange={(e) =>
                      setLeadForm({ ...leadForm, company: e.target.value })
                    }
                    className={UI_INPUT}
                  />
                </div>
                <div>
                  <label className={UI_LABEL}>
                    Perfil
                  </label>
                  <select
                    value={leadForm.segment || ''}
                    onChange={(e) => setLeadForm({ ...leadForm, segment: e.target.value })}
                    className={UI_INPUT_BG}
                  >
                    {CLIENT_SEGMENT_OPTIONS.map((opt) => (
                      <option key={opt.value || 'none'} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={UI_LABEL}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={leadForm.email}
                    onChange={(e) =>
                      setLeadForm({ ...leadForm, email: e.target.value })
                    }
                    className={UI_INPUT}
                  />
                </div>
                <div>
                  <label className={UI_LABEL}>
                    Telefone *
                  </label>
                  <input
                    type="text"
                    value={leadForm.phone || ''}
                    onChange={(e) =>
                      setLeadForm({ ...leadForm, phone: formatPhone(e.target.value) })
                    }
                    maxLength={16}
                    className={UI_INPUT}
                    required
                  />
                </div>
                <div>
                  <label className={UI_LABEL}>
                    Telefone 2
                  </label>
                  <input
                    type="text"
                    value={leadForm.phone2 || ''}
                    onChange={(e) =>
                      setLeadForm({ ...leadForm, phone2: formatPhone(e.target.value) })
                    }
                    maxLength={16}
                    className={UI_INPUT}
                  />
                </div>
                <div>
                  <label className={UI_LABEL}>
                    Responsável
                  </label>
                  <select
                    value={leadForm.ownerId || ''}
                    onChange={(e) => {
                      const selectedId = e.target.value ? Number(e.target.value) : null;
                      const selectedUser = users.find((u) => u.id === selectedId);
                      setLeadForm({
                        ...leadForm,
                        ownerId: selectedId,
                        owner: selectedUser ? selectedUser.name : user?.name || '',
                      });
                    }}
                    className={UI_INPUT_BG}
                  >
                    <option value={user?.id || ''}>
                      {user?.name ? `${user.name} (Você)` : 'Selecione'}
                    </option>
                    {users
                      .filter((u) => u.id !== user?.id)
                      .map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name} ({u.role})
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className={UI_LABEL}>
                    Canal
                  </label>
                  <select
                    value={leadForm.channel_id || ''}
                    onChange={(e) =>
                      setLeadForm({ ...leadForm, channel_id: e.target.value })
                    }
                    className={UI_INPUT_BG}
                  >
                    <option value="">Selecione</option>
                    {channels.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={UI_LABEL}>
                    Campanha
                  </label>
                  <input
                    type="text"
                    value={leadForm.campaign || ''}
                    onChange={(e) =>
                      setLeadForm({ ...leadForm, campaign: e.target.value })
                    }
                    className={UI_INPUT}
                  />
                </div>
                <div>
                  <label className={UI_LABEL}>
                    Valor (R$)
                  </label>
                  {/* Campo de dinheiro com mascara de centavos, 23/09/2026.
                      Antes era `type="number"` cru, e o navegador em pt-BR dava
                      TRES resultados diferentes para o mesmo valor (medido no
                      Chrome dele, digitando de verdade):
                        4835,25    -> 4835,25      certo
                        483525     -> 483.525,00   cem vezes maior
                        4.835,25   -> 4,83525      o ponto do milhar virava
                                                   virgula decimal
                      Com a mascara os tres caem no mesmo lugar certo, porque so
                      os digitos contam. */}
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="0,00"
                    value={leadForm.value}
                    onChange={(e) =>
                      setLeadForm({ ...leadForm, value: formatCurrencyInputBR(e.target.value) })
                    }
                    className={`${UI_INPUT} text-right tabular-nums`}
                  />
                </div>
                <div>
                  <label className={UI_LABEL}>
                    Status
                  </label>
                  <select
                    value={leadForm.status}
                    onChange={(e) =>
                      setLeadForm({ ...leadForm, status: e.target.value })
                    }
                    className={UI_INPUT_BG}
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={UI_LABEL}>
                    Primeiro contato
                  </label>
                  <input
                    type="date"
                    value={leadForm.first_contact || ''}
                    onChange={(e) =>
                      setLeadForm({ ...leadForm, first_contact: e.target.value })
                    }
                    className={UI_INPUT}
                  />
                </div>
                <div>
                  <label className={UI_LABEL}>
                    Próximo contato (agenda)
                  </label>
                  <input
                    type="datetime-local"
                    value={leadForm.next_contact || ''}
                    onChange={(e) =>
                      setLeadForm({ ...leadForm, next_contact: e.target.value })
                    }
                    className={UI_INPUT}
                  />
                </div>
                <div>
                  <label className={UI_LABEL}>
                    Data de adição
                  </label>
                  <input
                    type="text"
                    value={
                      editingLead?.created_at
                        ? new Date(editingLead.created_at).toLocaleDateString('pt-BR')
                        : '-'
                    }
                    className="w-full px-3 py-2 border border-line rounded-lg text-sm bg-surface-low text-ink-soft"
                    readOnly
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="lead-customer"
                    checked={!!leadForm.is_customer}
                    onChange={(e) =>
                      setLeadForm({
                        ...leadForm,
                        is_customer: e.target.checked,
                      })
                    }
                    className="h-4 w-4 text-brand-ink border-line rounded"
                  />
                  <label
                    htmlFor="lead-customer"
                    className="text-xs font-semibold text-ink-soft"
                  >
                    Já é cliente
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="lead-out-of-scope"
                    checked={!!leadForm.is_out_of_scope}
                    onChange={(e) =>
                      setLeadForm({
                        ...leadForm,
                        is_out_of_scope: e.target.checked,
                      })
                    }
                    className="h-4 w-4 text-brand-ink border-line rounded"
                  />
                  <label
                    htmlFor="lead-out-of-scope"
                    className="text-xs font-semibold text-ink-soft"
                  >
                    Fora do perfil
                  </label>
                </div>

                <div className="pt-2 border-t border-surface-mid">
                  <label className="block text-xs font-semibold text-ink-soft mb-2">
                    Tipo de Cliente
                  </label>
                  <div className="flex gap-2">
                    {CUSTOMER_TYPE_OPTIONS.map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setLeadForm({ ...leadForm, customer_type: type })}
                        className={`flex-1 py-1 px-3 text-sm rounded-lg border transition ${leadForm.customer_type === type
                          ? 'bg-brand-600 text-white border-brand-600'
                          : 'bg-surface-card text-ink-soft border-line hover:border-brand-300'
                          }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>
                {leadForm.region && (
                  <div className="bg-surface-low p-2 rounded-lg border border-line">
                    <p className="text-[10px] uppercase font-bold text-ink-faint">Região Identificada (DDD)</p>
                    <p className="text-sm font-semibold text-ink-soft">{leadForm.region}</p>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-semibold text-ink-soft mb-2">
                    Categorias em destaque (marque o que se aplicar)
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {HIGHLIGHTED_CATEGORIES_OPTIONS.map((cat) => {
                      const selected = containsNormalized(highlightedField, cat);
                      return (
                        <label
                          key={cat}
                          className="flex items-center gap-2 text-sm text-ink-soft"
                        >
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => toggleLeadListField('highlighted_categories', cat)}
                            className="h-4 w-4 rounded text-emerald-600 border-line"
                          />
                          <span className="text-sm text-ink-soft">{cat}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-ink-soft mb-2">
                    Motivo de esfriamento (marque o que se aplicar)
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {COOLING_REASON_OPTIONS.map((reason) => {
                      const selected = containsNormalized(coolingField, reason);
                      return (
                        <label
                          key={reason}
                          className="flex items-center gap-2 text-sm text-ink-soft"
                        >
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => toggleLeadListField('cooling_reason', reason)}
                            className="h-4 w-4 rounded text-amber-600 border-line"
                          />
                          <span className="text-sm text-ink-soft">{reason}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="lead-private"
                    checked={!!leadForm.is_private}
                    onChange={(e) =>
                      setLeadForm({
                        ...leadForm,
                        is_private: e.target.checked,
                      })
                    }
                    className="h-4 w-4 text-brand-ink border-line rounded"
                  />
                  <label
                    htmlFor="lead-private"
                    className="text-xs font-semibold text-ink-soft"
                  >
                    Visível apenas para mim
                  </label>
                </div>
                <div>
                  <label className={UI_LABEL}>
                    Observações da origem
                  </label>
                  <textarea
                    readOnly
                    value={leadForm.notes}
                    className="w-full px-3 py-2 border border-line rounded-lg text-sm bg-surface-low text-ink-soft cursor-not-allowed"
                    rows={3}
                    title="Campo bloqueado para preservar a observação importada da campanha"
                  />
                </div>
                <div>
                  <label className={UI_LABEL}>
                    Observações do operador
                  </label>
                  <textarea
                    value={leadForm.operator_notes || ''}
                    onChange={(e) =>
                      setLeadForm({ ...leadForm, operator_notes: e.target.value })
                    }
                    className={UI_INPUT}
                    rows={3}
                  />
                </div>
              </div>
              <div className="p-4 border-t border-line flex justify-end gap-2">
                <button
                  onClick={closeLeadModal}
                  className="px-4 py-2 text-sm border border-line rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  onClick={saveLead}
                  disabled={savingLead}
                  className="px-4 py-2 text-sm bg-brand-600 text-white rounded-lg disabled:opacity-50"
                >
                  {savingLead ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="h-3.5 w-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                      Salvando...
                    </span>
                  ) : (
                    'Salvar'
                  )}
                </button>
              </div>
              {(savingLead || savingLeadInteraction) && (
                <div className="px-4 pb-4 flex justify-end">
                  <div className="inline-flex items-center gap-2 rounded-full border border-line bg-surface-low px-3 py-2 text-[11px] font-semibold text-ink-soft">
                    <span className="h-3.5 w-3.5 rounded-full border-2 border-line border-t-slate-900 animate-spin" />
                    {savingLead ? 'Salvando lead...' : 'Salvando interação...'}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {showBudgetModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-surface-card rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-4 border-b border-line flex items-start justify-between">
                <h2 className="text-lg font-semibold text-ink">
                  {editingBudget ? 'Editar Orçamento' : 'Novo Orçamento'}
                </h2>
                <button
                  onClick={() => {
                    setShowBudgetModal(false);
                    setEditingBudget(null);
                  }}
                  className="text-sm text-ink-soft hover:text-ink px-2 py-1 rounded-lg border border-line"
                >
                  Fechar
                </button>
              </div>
              <div className="p-4 space-y-3">
                {editingBudget?.id ? (
                  <div className="rounded-xl border border-line bg-surface-low px-3 py-3">
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                      <div>
                        <p className="text-[11px] uppercase tracking-[0.18em] text-ink-faint font-bold">
                          Interações com o cliente
                        </p>
                        <p className="text-xs text-ink-faint mt-1">
                          Registre cada contato sobre este orçamento. O histórico fica oculto por padrão.
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={resetBudgetInteractionForm}
                          className="px-3 py-2 rounded-lg text-xs font-semibold border border-line text-ink-soft bg-surface-card"
                        >
                          Limpar
                        </button>
                        <button
                          type="button"
                          onClick={saveBudgetInteraction}
                          disabled={savingBudgetInteraction}
                          className="px-3 py-2 rounded-lg text-xs font-semibold bg-brand-600 hover:bg-brand-700 text-white transition-colors disabled:opacity-50"
                        >
                          {savingBudgetInteraction ? (
                            <span className="inline-flex items-center gap-2">
                              <span className="h-3.5 w-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                              Salvando...
                            </span>
                          ) : budgetInteractionForm.id ? (
                            'Atualizar interação'
                          ) : (
                            'Registrar interação'
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-3">
                      <div>
                        <label className="block text-[11px] font-semibold text-ink-soft mb-1">Data e hora</label>
                        <input
                          type="datetime-local"
                          value={budgetInteractionForm.interaction_at || ''}
                          onChange={(e) =>
                            setBudgetInteractionForm({ ...budgetInteractionForm, interaction_at: e.target.value })
                          }
                          className={UI_INPUT_BG}
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold text-ink-soft mb-1">Canal</label>
                        <input
                          list="budget-interaction-channel-options"
                          type="text"
                          value={budgetInteractionForm.channel || ''}
                          onChange={(e) =>
                            setBudgetInteractionForm({ ...budgetInteractionForm, channel: e.target.value })
                          }
                          placeholder="Ex.: WhatsApp, telefone, email, visita"
                          className={UI_INPUT_BG}
                        />
                        <datalist id="budget-interaction-channel-options">
                          {channels.map((channel) => (
                            <option key={channel.id} value={channel.name} />
                          ))}
                          <option value="WhatsApp" />
                          <option value="Telefone" />
                          <option value="Email" />
                          <option value="Visita" />
                        </datalist>
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold text-ink-soft mb-1">Observação</label>
                        <textarea
                          value={budgetInteractionForm.notes || ''}
                          onChange={(e) =>
                            setBudgetInteractionForm({ ...budgetInteractionForm, notes: e.target.value })
                          }
                          rows={3}
                          placeholder="Resumo do contato, pedido, objeção, retorno..."
                          className={UI_INPUT_BG}
                        />
                      </div>
                    </div>
                    <div className="flex justify-end mt-2">
                      <button
                        type="button"
                        onClick={() => setBudgetInteractionsExpanded((value) => !value)}
                        className="inline-flex items-center gap-2 text-xs font-semibold text-ink-soft hover:text-ink"
                      >
                        <span aria-hidden="true">{budgetInteractionsExpanded ? '▴' : '▾'}</span>
                        <span>{budgetInteractionsExpanded ? 'Ocultar Histórico' : 'Ver Histórico'}</span>
                      </button>
                    </div>
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                      <div className="rounded-lg border border-surface-mid bg-surface-card px-3 py-2">
                        <p className="text-[10px] uppercase font-bold text-ink-faint">Total</p>
                        <p className={UI_STRONG}>{budgetInteractions.length}</p>
                      </div>
                      <div className="rounded-lg border border-surface-mid bg-surface-card px-3 py-2">
                        <p className="text-[10px] uppercase font-bold text-ink-faint">Última interação</p>
                        <p className={UI_STRONG}>
                          {budgetInteractions[0]?.interaction_at
                            ? formatDateTimeBR(budgetInteractions[0].interaction_at)
                            : '-'}
                        </p>
                      </div>
                      <div className="rounded-lg border border-surface-mid bg-surface-card px-3 py-2">
                        <p className="text-[10px] uppercase font-bold text-ink-faint">Canal</p>
                        <p className={UI_STRONG}>{budgetInteractions[0]?.channel || '-'}</p>
                      </div>
                    </div>
                    {budgetInteractionsExpanded && (
                      <div className="mt-3 border-t border-surface-mid pt-3">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-[11px] uppercase tracking-[0.18em] text-ink-faint font-bold">
                            Histórico recente
                          </p>
                          {budgetInteractionsLoading ? <span className={UI_META}>Carregando...</span> : null}
                        </div>
                        <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                          {budgetInteractions.length === 0 ? (
                            <p className="text-xs text-ink-faint">Nenhuma interação registrada ainda.</p>
                          ) : (
                            budgetInteractions.slice(0, 12).map((item) => (
                              <div key={item.id} className="rounded-lg border border-surface-mid bg-surface-card px-3 py-2">
                                <div className="flex items-start justify-between gap-2">
                                  <div>
                                    <p className="text-xs font-semibold text-ink">
                                      {item.channel || 'Canal não informado'}
                                    </p>
                                    <p className={UI_META}>
                                      {item.interaction_at ? formatDateTimeBR(item.interaction_at) : '-'}
                                      {item.operator ? ` • ${item.operator}` : ''}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() => editBudgetInteraction(item)}
                                      className="text-[11px] text-brand-ink hover:underline"
                                    >
                                      Editar
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => deleteBudgetInteraction(item)}
                                      className="text-[11px] text-risk-ink hover:underline"
                                    >
                                      Excluir
                                    </button>
                                  </div>
                                </div>
                                {item.notes ? (
                                  <p className="text-xs text-ink-soft mt-1 whitespace-pre-wrap">{item.notes}</p>
                                ) : null}
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
                {editingBudget?.id && budgetLeadInteractions.length > 0 ? (
                  <div className="rounded-xl border border-line bg-surface-card px-3 py-3">
                    <button
                      type="button"
                      onClick={() => setBudgetLeadHistoryExpanded((value) => !value)}
                      className="w-full flex items-center justify-between gap-3 text-left"
                    >
                      <span>
                        <span className="block text-[11px] uppercase tracking-[0.18em] text-ink-faint font-bold">
                          Contatos do lead de origem
                        </span>
                        <span className="block text-xs text-ink-faint mt-1">
                          {budgetLeadInteractions.length} registro(s) da captação. Somente leitura — para alterar,
                          abra o lead.
                        </span>
                      </span>
                      <span aria-hidden="true" className="text-ink-soft">
                        {budgetLeadHistoryExpanded ? '▴' : '▾'}
                      </span>
                    </button>
                    {budgetLeadHistoryExpanded && (
                      <div className="space-y-2 max-h-48 overflow-y-auto pr-1 mt-3 border-t border-surface-mid pt-3">
                        {budgetLeadInteractions.slice(0, 12).map((item) => (
                          <div key={`lead-${item.id}`} className="rounded-lg border border-surface-mid bg-surface-low px-3 py-2">
                            <p className="text-xs font-semibold text-ink">{item.channel || 'Canal não informado'}</p>
                            <p className={UI_META}>
                              {item.interaction_at ? formatDateTimeBR(item.interaction_at) : '-'}
                              {item.operator ? ` • ${item.operator}` : ''}
                            </p>
                            {item.notes ? (
                              <p className="text-xs text-ink-soft mt-1 whitespace-pre-wrap">{item.notes}</p>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : null}
                <div className={UI_GRID2}>
                  <div>
                    <label className={UI_LABEL}>ID externo (ERP)</label>
                    <input
                      type="text"
                      value={budgetForm.external_id || ''}
                      onChange={(e) => setBudgetForm({ ...budgetForm, external_id: e.target.value })}
                      className={UI_INPUT}
                    />
                  </div>
                  <div>
                    <label className={UI_LABEL}>Lead relacionado</label>
                    <select
                      value={budgetForm.lead_id || ''}
                      onChange={(e) => setBudgetForm({ ...budgetForm, lead_id: e.target.value })}
                      className={UI_INPUT_BG}
                    >
                      <option value="">Sem lead relacionado</option>
                      {leads.map((lead) => (
                        <option key={lead.id} value={lead.id}>{lead.name} {lead.company ? `- ${lead.company}` : ''}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={UI_LABEL}>Status</label>
                    <select
                      value={budgetForm.status}
                      onChange={(e) => setBudgetForm({ ...budgetForm, status: e.target.value })}
                      className={UI_INPUT_BG}
                    >
                      {BUDGET_STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className={UI_GRID2}>
                  <div>
                    <label className={UI_LABEL}>Cliente</label>
                    <input
                      type="text"
                      value={budgetForm.client_name}
                      onChange={(e) => setBudgetForm({ ...budgetForm, client_name: e.target.value })}
                      className={UI_INPUT}
                    />
                  </div>
                  <div>
                    <label className={UI_LABEL}>Empresa</label>
                    <input
                      type="text"
                      value={budgetForm.company}
                      onChange={(e) => setBudgetForm({ ...budgetForm, company: e.target.value })}
                      className={UI_INPUT}
                    />
                  </div>
                </div>
                <div className={UI_GRID2}>
                  <div>
                    <label className={UI_LABEL}>Perfil</label>
                    <select
                      value={budgetForm.segment || ''}
                      onChange={(e) => setBudgetForm({ ...budgetForm, segment: e.target.value })}
                      className={UI_INPUT_BG}
                    >
                      {CLIENT_SEGMENT_OPTIONS.map((opt) => (
                        <option key={opt.value || 'none'} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={UI_LABEL}>Fase</label>
                    <select
                      value={budgetForm.stage || ''}
                      onChange={(e) => setBudgetForm({ ...budgetForm, stage: e.target.value })}
                      className={UI_INPUT_BG}
                    >
                      <option value="">Selecione</option>
                      <option value="prospeccao">Prospecção</option>
                      <option value="qualificacao">Qualificação</option>
                      <option value="negociacao">Negociação</option>
                      <option value="fechamento">Fechamento</option>
                    </select>
                  </div>
                </div>
                <div className={UI_GRID2}>
                  <div>
                    <label className={UI_LABEL}>Motivo da perda</label>
                    <select
                      value={budgetForm.loss_reason || ''}
                      onChange={(e) => setBudgetForm({ ...budgetForm, loss_reason: e.target.value })}
                      className={UI_INPUT_BG}
                    >
                      <option value="">Selecione</option>
                      {BUDGET_LOSS_REASON_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={UI_LABEL}>Vendedor</label>
                    <select
                      value={budgetForm.owner_id || ''}
                      onChange={(e) => {
                        const selectedUser = users.find((u) => String(u.id) === String(e.target.value));
                        setBudgetForm({
                          ...budgetForm,
                          owner_id: e.target.value,
                          owner_name: selectedUser?.name || '',
                        });
                      }}
                      className={UI_INPUT_BG}
                    >
                      <option value="">Selecione</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>{u.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={UI_LABEL}>Orçamentista</label>
                    <select
                      value={budgetForm.estimator_id || ''}
                      onChange={(e) => {
                        const valor = e.target.value;
                        const selectedUser = users.find((u) => String(u.id) === String(valor));
                        setBudgetForm({
                          ...budgetForm,
                          estimator_id: valor,
                          estimator_name:
                            valor === ESTIMATOR_REPRESENTANTE_ID
                              ? ESTIMATOR_REPRESENTANTE_LABEL
                              : (selectedUser?.name || ''),
                        });
                      }}
                      className={UI_INPUT_BG}
                    >
                      <option value="">Selecione</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>{u.name}</option>
                      ))}
                      <option value={ESTIMATOR_REPRESENTANTE_ID}>{ESTIMATOR_REPRESENTANTE_LABEL}</option>
                    </select>
                  </div>
                  <div>
                    <label className={UI_LABEL}>Representante</label>
                    <input
                      type="text"
                      list="lista-representantes-form"
                      value={budgetForm.representante || ''}
                      onChange={(e) => setBudgetForm({ ...budgetForm, representante: e.target.value })}
                      className={UI_INPUT}
                      placeholder="Deixe em branco se não houver"
                    />
                    <datalist id="lista-representantes-form">
                      {representanteOptions.map((nome) => (
                        <option key={nome} value={nome} />
                      ))}
                    </datalist>
                    <span className="text-[11px] text-ink-soft">Opcional. Sugere os representantes já registrados; escolher da lista mantém a grafia igual e as métricas confiáveis.</span>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className={UI_LABEL}>Filial</label>
                    <input type="text" value={budgetForm.branch || ''} onChange={(e) => setBudgetForm({ ...budgetForm, branch: e.target.value })} className={UI_INPUT} />
                  </div>
                  <div>
                    <label className={UI_LABEL}>Pedido do cliente</label>
                    <input type="text" value={budgetForm.customer_order || ''} onChange={(e) => setBudgetForm({ ...budgetForm, customer_order: e.target.value })} className={UI_INPUT} />
                  </div>
                  <div>
                    <label className={UI_LABEL}>Plano de pagamento</label>
                    <input type="text" value={budgetForm.payment_terms || ''} onChange={(e) => setBudgetForm({ ...budgetForm, payment_terms: e.target.value })} className={UI_INPUT} />
                  </div>
                </div>
                <div className={UI_GRID2}>
                  <div>
                    <label className={UI_LABEL}>Valor orçado</label>
                    <input type="text" inputMode="numeric" placeholder="0,00" value={budgetForm.budget_value} onChange={(e) => setBudgetForm({ ...budgetForm, budget_value: formatCurrencyInputBR(e.target.value) })} className={`${UI_INPUT} text-right tabular-nums`} />
                  </div>
                  <div>
                    <label className={UI_LABEL}>Valor fechado</label>
                    <input type="text" inputMode="numeric" placeholder="0,00" value={budgetForm.closed_value} onChange={(e) => setBudgetForm({ ...budgetForm, closed_value: formatCurrencyInputBR(e.target.value) })} className={`${UI_INPUT} text-right tabular-nums`} />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className={UI_LABEL}>Solicitado em</label>
                    <input type="date" value={budgetForm.requested_at || ''} onChange={(e) => setBudgetForm({ ...budgetForm, requested_at: e.target.value })} className={UI_INPUT} />
                  </div>
                  <div>
                    <label className={UI_LABEL}>Enviado em</label>
                    <input type="date" value={budgetForm.sent_at || ''} onChange={(e) => setBudgetForm({ ...budgetForm, sent_at: e.target.value })} className={UI_INPUT} />
                  </div>
                  <div>
                    <label className={UI_LABEL}>Fechado em</label>
                    <input type="date" value={budgetForm.closed_at || ''} onChange={(e) => setBudgetForm({ ...budgetForm, closed_at: e.target.value })} className={UI_INPUT} />
                  </div>
                </div>
                <div className={UI_GRID2}>
                  <div>
                    <label className={UI_LABEL}>Canal</label>
                    <input type="text" value={budgetForm.channel_name || ''} onChange={(e) => setBudgetForm({ ...budgetForm, channel_name: e.target.value })} className={UI_INPUT} />
                  </div>
                  <div>
                    <label className={UI_LABEL}>Campanha</label>
                    <input type="text" value={budgetForm.campaign || ''} onChange={(e) => setBudgetForm({ ...budgetForm, campaign: e.target.value })} className={UI_INPUT} />
                  </div>
                </div>
                <div>
                  <label className={UI_LABEL}>Observações</label>
                  <textarea value={budgetForm.notes} onChange={(e) => setBudgetForm({ ...budgetForm, notes: e.target.value })} className={UI_INPUT} rows={3} />
                </div>
              </div>
              <div className="p-4 border-t border-line flex justify-end gap-2">
                <button
                  onClick={() => {
                    setShowBudgetModal(false);
                    setEditingBudget(null);
                  }}
                  className="px-4 py-2 text-sm border border-line rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  onClick={saveBudget}
                  disabled={savingBudget}
                  className="px-4 py-2 text-sm bg-brand-600 text-white rounded-lg disabled:opacity-50"
                >
                  {savingBudget ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showAdSpendModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-surface-card rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-4 border-b border-line flex items-start justify-between">
                <h2 className="text-lg font-semibold text-ink">
                  {editingAdSpend ? 'Editar lançamento de Ads' : 'Novo lançamento de Ads'}
                </h2>
                <button
                  onClick={() => {
                    setShowAdSpendModal(false);
                    setEditingAdSpend(null);
                  }}
                  className="text-sm text-ink-soft hover:text-ink px-2 py-1 rounded-lg border border-line"
                >
                  Fechar
                </button>
              </div>
              <div className="p-4 space-y-3">
                <div className={UI_GRID2}>
                  <div>
                    <label className={UI_LABEL}>Data</label>
                    <input
                      type="date"
                      value={adSpendForm.date || ''}
                      onChange={(e) => setAdSpendForm({ ...adSpendForm, date: e.target.value })}
                      className={UI_INPUT}
                    />
                  </div>
                  <div>
                    <label className={UI_LABEL}>Canal</label>
                    <select
                      value={adSpendForm.channel_id || ''}
                      onChange={(e) => {
                        const selectedChannel = channels.find((channel) => String(channel.id) === String(e.target.value));
                        setAdSpendForm({
                          ...adSpendForm,
                          channel_id: e.target.value,
                          channel_name: selectedChannel?.name || '',
                          platform: selectedChannel?.name || '',
                        });
                      }}
                      className={UI_INPUT_BG}
                    >
                      <option value="">Selecione</option>
                      {channels.map((channel) => (
                        <option key={channel.id} value={channel.id}>{channel.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className={UI_GRID2}>
                  <div>
                    <label className={UI_LABEL}>Campanha</label>
                    <input
                      type="text"
                      value={adSpendForm.campaign || ''}
                      onChange={(e) => setAdSpendForm({ ...adSpendForm, campaign: e.target.value })}
                      className={UI_INPUT}
                    />
                  </div>
                  <div>
                    <label className={UI_LABEL}>Valor investido</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={adSpendForm.amount}
                      onChange={(e) => setAdSpendForm({ ...adSpendForm, amount: formatCurrencyInputBR(e.target.value) })}
                      placeholder="0,00"
                      className={UI_INPUT}
                    />
                  </div>
                </div>
                <div>
                  <label className={UI_LABEL}>Observações</label>
                  <textarea
                    value={adSpendForm.notes}
                    onChange={(e) => setAdSpendForm({ ...adSpendForm, notes: e.target.value })}
                    className={UI_INPUT}
                    rows={3}
                  />
                </div>
              </div>
              <div className="p-4 border-t border-line flex justify-end gap-2">
                <button
                  onClick={() => {
                    setShowAdSpendModal(false);
                    setEditingAdSpend(null);
                  }}
                  className="px-4 py-2 text-sm border border-line rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  onClick={saveAdSpend}
                  disabled={savingAdSpend}
                  className="px-4 py-2 text-sm bg-brand-600 text-white rounded-lg disabled:opacity-50"
                >
                  {savingAdSpend ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showProfileModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-surface-card rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-4 border-b border-line flex items-center justify-between">
                <div className="flex gap-2">
                  <button
                    onClick={() => setProfileTab('me')}
                    className={`px-3 py-2 text-sm rounded-lg ${profileTab === 'me' ? 'bg-brand-600 text-white' : 'bg-surface-mid text-ink-soft'
                      }`}
                  >
                    Meu perfil
                  </button>
                  {isAdmin && (
                    <button
                      onClick={() => {
                        setProfileTab('users');
                        loadUsers();
                        setUserForm({
                          id: null,
                          name: '',
                          phone: '',
                          email: '',
                          password: '',
                          role: 'vendedor',
                        });
                      }}
                      className={`px-3 py-2 text-sm rounded-lg ${profileTab === 'users'
                        ? 'bg-brand-600 text-white'
                        : 'bg-surface-mid text-ink-soft'
                        }`}
                    >
                      Usuários
                    </button>
                  )}
                  {isAdmin && (
                    <button
                      onClick={() => {
                        setProfileTab('mailrelay');
                        loadMailrelaySettings();
                      }}
                      className={`px-3 py-2 text-sm rounded-lg ${profileTab === 'mailrelay'
                        ? 'bg-brand-600 text-white'
                        : 'bg-surface-mid text-ink-soft'
                        }`}
                    >
                      Mailrelay
                    </button>
                  )}
                  {isAdmin && (
                    <button
                      onClick={() => {
                        setProfileTab('omnichat');
                        loadOmnichatSettings();
                      }}
                      className={`px-3 py-2 text-sm rounded-lg ${profileTab === 'omnichat'
                        ? 'bg-brand-600 text-white'
                        : 'bg-surface-mid text-ink-soft'
                        }`}
                    >
                      Omnichat
                    </button>
                  )}
                </div>
                <button
                  onClick={() => setShowProfileModal(false)}
                  className="text-sm text-ink-faint hover:text-ink"
                >
                  Fechar
                </button>
              </div>

              {profileTab === 'me' && (
                <div className="p-4 space-y-3">
                  <div className={UI_GRID2}>
                    <div>
                      <label className={UI_LABEL}>Nome</label>
                      <input
                        type="text"
                        value={profileForm.name}
                        onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                        className={UI_INPUT}
                      />
                    </div>
                    <div>
                      <label className={UI_LABEL}>Telefone</label>
                      <input
                        type="tel"
                        value={profileForm.phone}
                        onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                        className={UI_INPUT}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={UI_LABEL}>Email</label>
                    <input
                      type="email"
                      value={profileForm.email}
                      onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                      className={UI_INPUT}
                    />
                  </div>
                  <div>
                    <label className={UI_LABEL}>
                      Nova senha (opcional)
                    </label>
                    <input
                      type="password"
                      value={profileForm.password}
                      onChange={(e) => setProfileForm({ ...profileForm, password: e.target.value })}
                      className={UI_INPUT}
                      placeholder="Deixe em branco para manter"
                    />
                  </div>
                  <div className="flex justify-end gap-2 pt-2 border-t border-line">
                    <button
                      onClick={() => setShowProfileModal(false)}
                      className="px-4 py-2 text-sm border border-line rounded-lg"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={saveProfile}
                      className="px-4 py-2 text-sm bg-brand-600 text-white rounded-lg"
                    >
                      Salvar
                    </button>
                  </div>
                </div>
              )}

              {profileTab === 'users' && isAdmin && (
                <div className="p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-ink">
                      {userForm.id ? 'Editar usuário' : 'Novo usuário'}
                    </h3>
                    <button
                      onClick={startNewUser}
                      className="text-xs text-brand-ink hover:underline"
                    >
                      Novo
                    </button>
                  </div>
                  <div className={UI_GRID2}>
                    <div>
                      <label className={UI_LABEL}>Nome</label>
                      <input
                        type="text"
                        value={userForm.name}
                        onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                        className={UI_INPUT}
                      />
                    </div>
                    <div>
                      <label className={UI_LABEL}>Usuário (login)</label>
                      <input
                        type="text"
                        value={userForm.username}
                        onChange={(e) =>
                          setUserForm({ ...userForm, username: e.target.value.toLowerCase() })
                        }
                        className={UI_INPUT}
                        placeholder="ex: leandro, ines..."
                      />
                    </div>
                    <div>
                      <label className={UI_LABEL}>Telefone</label>
                      <input
                        type="tel"
                        value={userForm.phone}
                        onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })}
                        className={UI_INPUT}
                      />
                    </div>
                    <div>
                      <label className={UI_LABEL}>Email</label>
                      <input
                        type="email"
                        value={userForm.email}
                        onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                        className={UI_INPUT}
                      />
                    </div>
                    <div>
                      <label className={UI_LABEL}>
                        Senha {userForm.id ? '(opcional)' : ''}
                      </label>
                      <input
                        type="password"
                        value={userForm.password}
                        onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                        className={UI_INPUT}
                        placeholder={userForm.id ? 'Deixe em branco para manter' : ''}
                      />
                    </div>
                    <div>
                      <label className={UI_LABEL}>Papel</label>
                      <select
                        value={userForm.role}
                        onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                        className={UI_INPUT_BG}
                      >
                        <option value="vendedor">Vendedor</option>
                        <option value="representante">Representante</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setShowProfileModal(false)}
                      className="px-4 py-2 text-sm border border-line rounded-lg"
                    >
                      Fechar
                    </button>
                    <button
                      onClick={saveAdminUser}
                      className="px-4 py-2 text-sm bg-brand-600 text-white rounded-lg"
                    >
                      {userForm.id ? 'Salvar alterações' : 'Criar usuário'}
                    </button>
                  </div>
                  <div className="border-t border-line pt-4">
                    <h4 className="text-xs font-semibold text-ink-soft mb-2">Usuários</h4>
                    <div className="max-h-64 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b text-left text-ink-faint">
                            <th className={UI_TD}>Nome</th>
                            <th className={UI_TD}>Email</th>
                            <th className={UI_TD}>Telefone</th>
                            <th className={UI_TD}>Papel</th>
                            <th className="py-2 px-2 text-right">Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {users.map((u) => (
                            <tr key={u.id} className="border-b last:border-none">
                              <td className={UI_TD}>{u.name}</td>
                              <td className={UI_TD}>{u.email}</td>
                              <td className={UI_TD}>{u.phone || '-'}</td>
                              <td className={UI_TD}>{u.role}</td>
                              <td className="py-2 px-2 text-right space-x-2">
                                <button
                                  onClick={() => editExistingUser(u)}
                                  className="text-brand-ink text-xs"
                                >
                                  Editar
                                </button>
                                <button
                                  onClick={() => deleteUser(u.id)}
                                  className="text-risk-ink text-xs"
                                >
                                  Excluir
                                </button>
                              </td>
                            </tr>
                          ))}
                          {users.length === 0 && (
                            <tr>
                              <td
                                colSpan={5}
                                className="py-3 text-center text-ink-faint text-xs"
                              >
                                Nenhum usuário cadastrado
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {profileTab === 'mailrelay' && isAdmin && (
                <div className="p-4 space-y-4">
                  <div className="rounded-xl border border-line bg-surface-low p-4 space-y-2">
                    <h3 className="text-sm font-semibold text-ink">Integração Mailrelay</h3>
                    <p className="text-xs text-ink-soft">
                      Configure a URL base da API e a chave de acesso. O CRM usa essa conexão para sincronizar aberturas, cliques e descadastros.
                    </p>
                    <div className="flex flex-wrap gap-2 text-[11px]">
                      <span className={`px-2 py-1 rounded-full ${mailrelaySettings.configured ? 'bg-ok text-ok-ink' : 'bg-warn text-warn-ink'}`}>
                        {mailrelaySettings.configured ? 'Configurado' : 'Não configurado'}
                      </span>
                      <span className="px-2 py-1 rounded-full bg-surface-mid text-ink-soft">
                        Origem: {mailrelaySettings.source === 'crm' ? 'CRM' : mailrelaySettings.source === 'env' ? '.env' : 'Nenhuma'}
                      </span>
                      <span className="px-2 py-1 rounded-full bg-surface-mid text-ink-soft">
                        Chave salva: {mailrelaySettings.has_api_key ? 'Sim' : 'Não'}
                      </span>
                    </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-ink-soft">
                    <div className="rounded-lg bg-surface-card border border-line px-3 py-2">
                      Leads com engajamento: <span className={UI_STRONG}>{mailrelaySettings.leads_with_engagement || 0}</span>
                    </div>
                    <div className="rounded-lg bg-surface-card border border-line px-3 py-2">
                      Eventos gravados: <span className={UI_STRONG}>{mailrelaySettings.email_events || 0}</span>
                    </div>
                  </div>
                  {mailrelaySettings.last_sync && (
                    <div className="rounded-xl border border-line bg-surface-card p-3 space-y-2 text-xs text-ink-soft">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={UI_STRONG}>Última sincronização:</span>
                        <span>{mailrelaySettings.last_sync.synced_at ? formatDateTimeBR(mailrelaySettings.last_sync.synced_at) : '-'}</span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <div className="rounded-lg bg-surface-low px-2 py-2">Campanhas: <span className={UI_STRONG}>{mailrelaySettings.last_sync.campaigns_processed || 0}</span></div>
                        <div className="rounded-lg bg-surface-low px-2 py-2">Eventos: <span className={UI_STRONG}>{mailrelaySettings.last_sync.events_processed || 0}</span></div>
                        <div className="rounded-lg bg-surface-low px-2 py-2">Casados: <span className={UI_STRONG}>{mailrelaySettings.last_sync.leads_updated || 0}</span></div>
                        <div className="rounded-lg bg-surface-low px-2 py-2">Sem match: <span className={UI_STRONG}>{mailrelaySettings.last_sync.unmatched_leads_count || 0}</span></div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 items-start">
                        <div className="rounded-lg bg-surface-low px-2 py-2">
                          Duplicados ignorados: <span className={UI_STRONG}>{mailrelaySettings.last_sync.duplicate_events_skipped || 0}</span>
                        </div>
                        <div className="rounded-lg bg-surface-low px-2 py-2">
                          Sem identidade: <span className={UI_STRONG}>{mailrelaySettings.last_sync.missing_identity_skipped || 0}</span>
                        </div>
                      </div>
                      {Array.isArray(mailrelaySettings.last_sync.unmatched_emails_sample) && mailrelaySettings.last_sync.unmatched_emails_sample.length > 0 && (
                        <div className="rounded-lg bg-warn border border-warn-line px-3 py-2">
                          <p className="font-semibold text-amber-800 mb-1">Exemplos sem match no CRM</p>
                          <div className="flex flex-wrap gap-1">
                            {mailrelaySettings.last_sync.unmatched_emails_sample.map((email) => (
                              <span key={email} className="px-2 py-1 rounded-full bg-surface-card border border-amber-200 text-amber-800">
                                {email}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                  <div>
                    <label className={UI_LABEL}>URL da API</label>
                    <input
                      type="text"
                      value={mailrelaySettings.api_base}
                      onChange={(e) => setMailrelaySettings({ ...mailrelaySettings, api_base: e.target.value })}
                      className={UI_INPUT}
                      placeholder="https://SEU_ACCOUNT/api/v1"
                    />
                  </div>

                  <div>
                    <label className={UI_LABEL}>Chave da API</label>
                    <input
                      type="password"
                      value={mailrelaySettings.api_key}
                      onChange={(e) => setMailrelaySettings({ ...mailrelaySettings, api_key: e.target.value })}
                      className={UI_INPUT}
                      placeholder={mailrelaySettings.has_api_key ? 'Informe uma nova chave para substituir a atual' : 'Cole a chave aqui'}
                    />
                  </div>

                  <div className="flex justify-end gap-2">
                    <button
                      onClick={syncMailrelayEngagement}
                      disabled={syncingMailrelay || !mailrelaySettings.configured}
                      className="px-4 py-2 text-sm border border-line rounded-lg disabled:opacity-50"
                    >
                      {syncingMailrelay ? 'Sincronizando...' : 'Sincronizar agora'}
                    </button>
                    <button
                      onClick={() => setShowProfileModal(false)}
                      className="px-4 py-2 text-sm border border-line rounded-lg"
                    >
                      Fechar
                    </button>
                    <button
                      onClick={saveMailrelaySettings}
                      disabled={savingMailrelaySettings}
                      className="px-4 py-2 text-sm bg-brand-600 text-white rounded-lg disabled:opacity-50"
                    >
                      {savingMailrelaySettings ? 'Salvando...' : 'Salvar Mailrelay'}
                    </button>
                  </div>
                </div>
              )}

              {profileTab === 'omnichat' && isAdmin && (
                <div className="p-4 space-y-4">
                  <div className="rounded-xl border border-line bg-surface-low p-4 space-y-2">
                    <h3 className="text-sm font-semibold text-ink">Integração Omnichat</h3>
                    <p className="text-xs text-ink-soft">
                      Configure a URL base da API e a chave x-api-key para agendar os lembretes de follow-up no OmniChat.
                    </p>
                    <div className="flex flex-wrap gap-2 text-[11px]">
                      <span className={`px-2 py-1 rounded-full ${omnichatSettings.configured ? 'bg-ok text-ok-ink' : 'bg-warn text-warn-ink'}`}>
                        {omnichatSettings.configured ? 'Configurado' : 'Não configurado'}
                      </span>
                      <span className="px-2 py-1 rounded-full bg-surface-mid text-ink-soft">
                        Origem: {omnichatSettings.source === 'crm' ? 'CRM' : omnichatSettings.source === 'env' ? '.env' : 'Nenhuma'}
                      </span>
                      <span className="px-2 py-1 rounded-full bg-surface-mid text-ink-soft">
                        Chave salva: {omnichatSettings.has_api_key ? 'Sim' : 'Não'}
                      </span>
                      <span className="px-2 py-1 rounded-full bg-surface-mid text-ink-soft">
                        Auto follow-up: {omnichatSettings.enabled ? 'Ativo' : 'Desativado'}
                      </span>
                    </div>
                  </div>

                  <div className={UI_GRID2}>
                    <div>
                      <label className={UI_LABEL}>URL base da API</label>
                      <input
                        type="text"
                        value={omnichatSettings.send_url}
                        onChange={(e) => setOmnichatSettings({ ...omnichatSettings, send_url: e.target.value })}
                        className={UI_INPUT}
                        placeholder="https://chat.bhseletrica.com.br"
                      />
                    </div>
                    <div>
                      <label className={UI_LABEL}>Chave x-api-key</label>
                      <input
                        type="password"
                        value={omnichatSettings.api_key}
                        onChange={(e) => setOmnichatSettings({ ...omnichatSettings, api_key: e.target.value })}
                        className={UI_INPUT}
                        placeholder={omnichatSettings.has_api_key ? 'Informe uma nova chave para substituir a atual' : 'Cole a chave aqui'}
                      />
                    </div>
                    <div>
                      <label className={UI_LABEL}>Nome do header</label>
                      <input
                        type="text"
                        value={omnichatSettings.auth_header}
                        onChange={(e) => setOmnichatSettings({ ...omnichatSettings, auth_header: e.target.value })}
                        className={UI_INPUT}
                        placeholder="Authorization"
                      />
                    </div>
                    <div>
                      <label className={UI_LABEL}>Janela do lembrete (min)</label>
                      <input
                        type="number"
                        min={1}
                        value={omnichatSettings.reminder_minutes}
                        onChange={(e) =>
                          setOmnichatSettings({
                            ...omnichatSettings,
                            reminder_minutes: Number(e.target.value || 30),
                          })
                        }
                        className={UI_INPUT}
                      />
                    </div>
                  </div>

                  <label className="flex items-center gap-2 text-sm text-ink-soft">
                    <input
                      type="checkbox"
                      checked={!!omnichatSettings.enabled}
                      onChange={(e) =>
                        setOmnichatSettings({ ...omnichatSettings, enabled: e.target.checked })
                      }
                    />
                    Ativar disparo automático de follow-up
                  </label>

                  <div className="flex justify-end gap-2">
                    <button
                      onClick={runFollowupDispatchNow}
                      disabled={runningFollowupDispatch}
                      className="px-4 py-2 text-sm border border-line rounded-lg disabled:opacity-50"
                    >
                      {runningFollowupDispatch ? 'Executando...' : 'Executar agora'}
                    </button>
                    <button
                      onClick={() => setShowProfileModal(false)}
                      className="px-4 py-2 text-sm border border-line rounded-lg"
                    >
                      Fechar
                    </button>
                    <button
                      onClick={saveOmnichatSettings}
                      disabled={savingOmnichatSettings}
                      className="px-4 py-2 text-sm bg-brand-600 text-white rounded-lg disabled:opacity-50"
                    >
                      {savingOmnichatSettings ? 'Salvando...' : 'Salvar Omnichat'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {showChannelModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-surface-card rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                <div className="p-4 border-b border-line">
                  <h2 className="text-lg font-semibold text-ink">Canais</h2>
                </div>
                <div className="p-4 space-y-4">
                  <div>
                    <label className={UI_LABEL}>
                      Novo canal
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newChannel}
                        onChange={(e) => setNewChannel(e.target.value)}
                        className="flex-1 px-3 py-2 border border-line rounded-lg text-sm"
                      />
                      <button
                        onClick={handleAddChannel}
                        className="px-3 py-2 text-sm bg-brand-600 text-white rounded-lg"
                      >
                        Adicionar
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {channels.map((c) => (
                      <div
                        key={c.id}
                        className="flex items-center justify-between px-3 py-2 border border-line rounded-lg text-sm"
                      >
                        <span>{c.name}</span>
                        <button
                          onClick={() => handleDeleteChannel(c.id)}
                          className="text-xs text-risk-ink"
                        >
                          Excluir
                        </button>
                      </div>
                    ))}
                    {channels.length === 0 && (
                      <p className="text-xs text-ink-faint">
                        Nenhum canal cadastrado
                      </p>
                    )}
                  </div>
                </div>
                <div className="p-4 border-t border-line flex justify-end">
                  <button
                    onClick={() => setShowChannelModal(false)}
                    className="px-4 py-2 text-sm border border-line rounded-lg"
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
        )}
      </div>
    </div>
  );
};

export default App;
